/*
 * ============================================
 * 📦 ADDPACKAGEWITHAI - HAUPTKOMPONENTE
 * ============================================
 * 
 * Diese Komponente verwaltet die Paketerfassung, -verwaltung und -synchronisation.
 * Sie nutzt moderne React Hooks für State-Management und Firebase für Cloud-Sync.
 * 
 * FEATURES:
 * - 📦 Package Counter: Zählt noch zu erfassende Pakete
 * - 📝 Draft System: Auto-Save für unvollständige Pakete
 * - 🔐 Authentication: Google Login mit Firebase
 * - 🔄 Cloud Sync: Automatische Synchronisation mit Firestore
 * - 📸 OCR: Texterkennung aus Fotos
 * -  Offline-First: Funktioniert auch ohne Internetverbindung
 * 
 * CUSTOM HOOKS (aus ./hooks):
 * - usePackageCounter: Verwaltet den Paketzähler
 * - useAuthentication: Firebase Auth Wrapper
 * 
 * DRAFT SYSTEM:
 * - Wird in NewPackageModal.js intern verwaltet
 * - Status wird via onDraftChange Callback propagiert
 * 
 * HAUPTFUNKTIONEN:
 * - handleGoogleSignIn: Google Login
 * - syncDataAfterLogin: Daten nach Login synchronisieren
 * - validateAndSavePaket: Paket speichern mit Validierung
 * - handleAusgeben: Paket als ausgegeben markieren
 * - handleDelete: Paket löschen mit Undo-Option
 * ============================================
 */

// ============================================
// 📦 IMPORTS
// ============================================

// Server Services (ersetzt Firebase)
import * as serverDataService from './services/serverDataService';
import * as serverPackageService from './services/serverPackageService';
import { api, connectSSE } from './services/apiService';
import { get, set, del, update } from 'idb-keyval';

import {
  getAuthUser,
  parseRegal,
  regalGleich,
  normalizeRegalplatz as normalizeRegalplatzHelper,
  normStrasseBasis,
  findeShopStrassen,
  istFilialAdresse,
  REGAL_NACHBAR_DISTANZ,
  regaleBenachbart,
  findeHausnummerKonflikt,
  translatePackageColor,
  scanZuSuchtext,
  trackingTreffer,
  getPackagePickupPin,
  SUCH_SCAN_FORMATE,
  NAME_PARTIKEL,
  findeGleicheSendung,
  findeGleichenEmpfaenger,
  findeWeitereOffenePakete,
  normalizeForSearch,
  normalizeForNumericSearch,
  OCR_REPLACEMENTS,
  createOCRVariants,
  levenshteinDistance,
  isSimilar,
  getCssColor,
  isForeignOverwrite,
} from './utils/packageMatchingHelpers';
import { PremiumStatusBadge, TrialBanner } from './components/PremiumBanners';

// React
import { useEffect, useRef, useState, useMemo, useCallback, startTransition } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

// Libraries
import Tesseract from 'tesseract.js';

// Local Imports
import './AddPackageWithAI.css';
import './QuestOptimizations.css'; // 🕶️ Quest 3 Styles
import NewPackageModal from './NewPackageModal';
import ShelfQrScanner from './components/ShelfQrScanner'; // 📷 Abholcode-Scan in der Suche
import VintedImport from './components/VintedImport'; // 📦 Vinted-Namen per Screenshot (Sonderfunktion)
import { offenePlatzhalter, vintedCodeAusTracking, glsInVintedCode } from './services/vintedNames';
import { hasCustomFeature } from './services/customFeatures'; // 🧩 Kunden-Sonderfunktionen pro Konto
// 🧪 Vinted-Anbindung (Sonderfunktion „vinted"): Annahme/Ausgabe werden direkt bei Vinted Go gebucht
import VintedPanel from './components/VintedPanel';
import { isTurboOn, setTurboOn, tt as turboT } from './services/turbo'; // ⚡ Turbo Vinted/DHL (Experiment)
import TurboPaketPilot from './components/TurboPaketPilot'; // ⚡ Turbo PaketPilot: Foto auf Foto, alles im Hintergrund
import {
  turboPpAktiv, turboPpVerfuegbar, isTurboPpOn, setTurboPpOn, tpt,
  isTurboHudOn, setTurboHudOn,
  isTurboAutoFotoOn, setTurboAutoFotoOn,
  isTurboRegalOn, setTurboRegalOn,
  isTurboTonOn, setTurboTonOn,
} from './services/turboPaketPilot';
import { isStationAutoFachOn, setStationAutoFachOn } from './services/stationAnbindung'; // 📸 Foto-Station mit Vinted/DHL
import VintedErfassen from './components/VintedErfassen';
import VintedAusgabe from './components/VintedAusgabe';
import { vintedFreigeschaltet, anzeigeAktiv as vintedAnzeigeAktiv, annahmeAktiv, ausgabeAktiv, istVintedPaket, vintedStatus, vintedUebersicht, vintedFristen as vintedFristenLaden, fristenMitPaket, vt } from './services/vinted';
import DhlBridgePanel from './components/DhlBridgePanel'; // 🧪 DHL-Anbindung (Experiment)
import DpdPanel from './components/DpdPanel'; // 📦 DPD Pickup-Anbindung (Folkert / NL24808)
import { dpdFreigeschaltet, dpdAktiv, dpdAusgabeAktiv, istDpdPaket, dpdAusgabe, dpdT, vorladenStart as dpdVorladenStart } from './services/dpd';
import DhlErfassen from './components/DhlErfassen';
import DhlAusgabe from './components/DhlAusgabe';
import { dhlFreigeschaltet, dhlAktiv, dhlAnnahmeAktiv, dhlAusgabeAktiv, istDhlPaket, dhlStatus, dt } from './services/dhlBridge';
import { isBevollmaechtigterEnabled, isDhlStickEnabled, enqueueDhlStickAusgabe } from './services/dhlUsbStick';
import WiesnShopModule from './components/WiesnShopModule'; // 🥨 Exklusives Modul nur für wiesn2022@ (Stick-Queue & Briefmarken)
import GrenzpaketPanel from './components/GrenzpaketPanel'; // 🇨🇭 Grenzpaket.ch-Anbindung (Konto 1186)
import DevicesModal from './components/DevicesModal'; // 🔌 Geräte, WLAN-Scanner (Zebra TC75) & Ringscanner
import { sendDeviceScan, playWarehouseTone, unlockWarehouseAudio, getClientDeviceId, isPrintBridgeHostEnabled, sendDeviceHeartbeat, getActiveDeviceCount } from './services/deviceService'; // 📡 Stille Hintergrund-Lagerscans & Meine Geräte
import { initRemoteControlAgent, isTrainingModeRequested, setTrainingModeRequested, subscribeRemoteAgentStatus } from './services/remoteControlAgent'; // 🎓 Schulungsmodus & Admin-Fernsteuerung
import { grenzpaketFreigeschaltet, parseGrenzpaketMeta, isDemoModeActive, setDemoModeActive, isDemoPackage, gt } from './services/grenzpaket';
import StaffModeDialog from './components/StaffModeDialog'; // 👷 Mitarbeiter-Modus (Sonderfunktion)
import { isStaffMode, disableStaffMode } from './services/staffMode';
import BatchView from './components/BatchView'; // 📋 Barcode-Stapel (Sonderfunktion)
import { listBatch, syncBatchFromServer, applyServerBatch, getBatchPos, getBatchExpectedCount, completeCurrentBatch } from './services/barcodeBatch';
import PackageThumbnail from './components/PackageThumbnail';
import MatchingModal from './MatchingModal'; // 📬 Wartelisten-Matching
import { markAsNotified } from './waitingListHelper';
import { loadAddressBookFromServer, isAddressBookEnabled, getListNotifyMode, findBestAddressBookMatch } from './services/addressBookService';
import AddressBookModal from './components/AddressBookModal';
import SignatureModal from './components/SignatureModal';
import NotificationSignupModal from './components/NotificationSignupModal';
import { logPaketHinzugefuegt, logPaketGeaendert, logPaketAusgegeben, logPaketGeloescht, logPaketZurueckgeschickt, logSuche, syncChronikAfterLogin, getChronikEntriesSinceHash, extractPaketIdsFromChronik } from './chronikHelper';
import { recordPackageCreated, recordPackageHandedOut, flushPendingPackages } from './services/packageOperations'; // 📦 zentrale Paket-Operationen (eine Quelle für Modal + Bench)
import { 
  savePaket, 
  deletePaket, 
  updatePackagesHash,
  syncPackages,
  loadAllPackages,
  loadSinglePaket, // ✅ NEU: Einzelnes Paket laden (1 Read statt alle!)
  mergePakete,
  preservePendingLocalPackages,
  checkSyncNeeded,
  saveDeletedIds,  // 🗑️ Tombstones für gelöschte Pakete
  loadDeletedIds, // 🗑️ Verhindert Re-Upload gelöschter Pakete
  invalidateDeletedIdsCache, // 🔧 v1.48: Cache invalidieren nach Löschungen
  getConfiguredDeleteDays,
  ACTIVE_WINDOW_DAYS,
  getActiveWindowCutoffMs,
  isPackageInActiveWindow,
} from './packageSyncHelper';
import { matchIntegrationSearch } from './integrations';

// Custom Hooks
import { usePackageCounter, useAuthentication } from './hooks';
import { useAutoUpdate } from './hooks/useAutoUpdate';
import { useTranslation } from './hooks/useTranslation'; // 🌐 Mehrsprachigkeit

// Locales
import locales from './locales'; // Legacy texts für Kompatibilität

// Components
import LanguageSelector from './components/LanguageSelector'; // 🌐 Sprachauswahl
import SetupWizard from './components/SetupWizard'; // 🧭 Setup (Konto-Profil für alle Geräte)
import PrinterWizard from './components/PrinterWizard'; // 🖨️ geführtes Drucker-Einrichten
import WhatsNewHint from './components/WhatsNewHint'; // ✨ einmaliger Hinweis nach dem Update
import { isSetupEnabled } from './services/accountProfile';
import { isCaptureSenderOn, isListCarrierOn } from './services/captureOptions'; // 📥 Absender mit speichern / 🚚 Paketdienst in der Liste (Optionen)
import { reportError } from './services/errorReport'; // 🧯 Fehler fürs Admin-Board
import PrinterSettings from './components/PrinterSettings'; // 🖨️ Etikettendrucker
import PrintLabelDialog from './components/PrintLabelDialog'; // 🖨️ Mehrfach-Etikettendruck
import { printOne, getCopies, getPrinterConfig, isAutoPrintEnabled, isPrinterSupported, extractHausnummer, splitSurname, guessCarrier, druckZeitenZusammenfassung, getPrinterLang, isAutoRegalLetter } from './services/printerService'; // 🖨️ Etikettendruck
import HelpCenter, { HelpTriggerButton } from './components/HelpCenter/HelpCenter'; // 💡 Hilfe-Center
import { PremiumProvider, usePremium, PREMIUM_FEATURES } from './contexts/PremiumContext'; // ⭐ Premium-System
import Paywall, { PremiumFeatureGate } from './components/Paywall'; // ⭐ Paywall-Komponente
import { isCloudSyncAllowed, getCloudSyncStatus, istOnlinePaket } from './utils/premiumSyncGuard'; // 🔒 Premium Sync Guard
import useConnectionStatus from './hooks/useConnectionStatus'; // 🔌 Server-Verbindungsstatus

// Device Detection
import { getDeviceType, isMetaQuest, getDeviceInfo } from './utils/deviceDetection';

// 🕶️ Quest Integration
import QUEST_FEATURES from './config/questFeatures';
import QuestInputLayout from './components/quest/QuestInputLayout'; // ✅ Quest-optimierte Erfassungs-UI
import { stempele, nameVon, mitarbeiterAktiv, anmeldungNoetig, ladeListe as ladeMitarbeiter, aktueller as aktuellerMitarbeiter } from './services/mitarbeiter'; // 👷 wer hat was gemacht
import MitarbeiterAnmeldung from './components/MitarbeiterAnmeldung';
import MitarbeiterVerwalten from './components/MitarbeiterVerwalten';
import NetzwerkDialog from './components/NetzwerkDialog'; // 🏪 Shop-Netzwerk
import { lade as ladeNetzwerk } from './services/netzwerk';

/// ============================================
// 🎯 HAUPTKOMPONENTE
// ============================================
function AddPackageWithAI({ onAddPaket, darkMode, setDarkMode, colors }) {
  // 🌐 Neues Mehrsprachigkeits-System
  const { t, language: lang, setLanguage, availableLanguages, languageNames } = useTranslation();
  
  // ⭐ Premium-System - wird jetzt in AddPackageWithAIContent verwendet
  // HINWEIS: usePremium() kann hier NICHT verwendet werden, da wir noch nicht im PremiumProvider sind!
  // Die Premium-Logik wird in der inneren Komponente AddPackageWithAIContent gehandhabt.
  
  // Legacy texts Object für Kompatibilität (wird schrittweise entfernt)
  // Dynamische Generierung für alle unterstützten Sprachen
  const getTextsForLang = (langCode) => {
    const locale = locales[langCode] || locales.en; // Fallback auf Englisch
    return {
      // Packages
      shelf: locale.packages?.shelf || 'Shelf',
      stored: locale.packages?.stored || 'Stored',
      issued: locale.packages?.issued || 'Issued',
      issue: locale.packages?.issue || 'Issue',
      return: locale.packages?.return || 'Return',
      edit: locale.common?.edit || 'Edit',
      reallyDelete: locale.packages?.reallyDelete || 'Are you sure you want to delete this package?',
      savedSuccess: locale.packages?.savedSuccess || 'Package saved successfully!',
      // Search
      search: locale.search?.search || 'Search',
      searchPlaceholder: locale.search?.searchPlaceholder || 'Name, street, shelf...',
      showOld: locale.search?.showOld || 'Show only packages older than',
      workdays: locale.search?.workdays || 'workdays',
      oldPackages: locale.search?.oldPackages || '',
      // Auth
      loginTitle: locale.auth?.loginTitle || 'Login',
      loginDescription: locale.auth?.loginDescription || 'Sign in to sync your packages:',
      // Common
      loadMore: locale.common?.loadMore || 'Load more',
      newPackage: locale.packages?.newPackage || 'Add new package',
    };
  };
  
  // Erstelle texts-Objekt mit Fallback für alle Sprachen
  const texts = new Proxy({}, {
    get: (target, prop) => getTextsForLang(prop)
  });
  
  // ============================================
  // 📦 STATE MANAGEMENT
  // ============================================
  
  // 🔒 APP VERSION - Schutz vor alten Versionen
  const APP_VERSION = "2.4"; // 🔒 Automatisch aktualisiert aus package.json
  
  // 🚨 NOTFALL-RESTORE FUNKTION (für gelöschte Pakete)
  const notfallRestore = async () => {
    try {
      console.log('🔍 Scanne IndexedDB nach ALLEN Backups...');
      
      // Öffne IndexedDB direkt, um ALLE Keys zu finden
      const request = indexedDB.open('keyval-store');
      
      await new Promise((resolve, reject) => {
        request.onsuccess = async () => {
          try {
            const db = request.result;
            const tx = db.transaction('keyval', 'readonly');
            const store = tx.objectStore('keyval');
            const getAllKeysRequest = store.getAllKeys();
            
            getAllKeysRequest.onsuccess = async () => {
              const allKeys = getAllKeysRequest.result;
              console.log('🗝️ ALLE IndexedDB Keys:', allKeys);
              
              // Filtere nur backup_* Keys
              const backupKeys = allKeys.filter(key => 
                typeof key === 'string' && key.startsWith('backup_')
              );
              
              console.log('📦 Gefundene Backup-Keys:', backupKeys);
              
              if (backupKeys.length === 0) {
                // 🆘 FALLBACK: Prüfe ob 'pakete' Key, Server-Daten oder Server-Backups existieren
                console.log('⚠️ Keine backup_* Keys — prüfe Fallback-Quellen...');
                
                // 1. Lokaler 'pakete' Key
                const lokalePakete = await get('pakete');
                const lokaleAnzahl = Array.isArray(lokalePakete) ? lokalePakete.length : 0;
                
                // 2. Server-Pakete (aktuelle DB-Daten)
                let serverAnzahl = 0;
                let serverPakete = null;
                if (currentUser && isOnline) {
                  try {
                    const serverData = await apiFetch('/packages');
                    if (serverData && Array.isArray(serverData)) {
                      serverPakete = serverData;
                      serverAnzahl = serverData.length;
                    } else if (serverData?.packages && Array.isArray(serverData.packages)) {
                      serverPakete = serverData.packages;
                      serverAnzahl = serverData.packages.length;
                    }
                  } catch (e) {
                    console.warn('Server-Pakete nicht verfügbar:', e.message);
                  }
                }
                
                // 3. Server-Backups (JSON-Dateien auf dem Server)
                let cloudBackups = [];
                if (currentUser && isOnline) {
                  try {
                    cloudBackups = await listCloudBackups();
                    console.log(`☁️ ${cloudBackups.length} Server-Backups gefunden`);
                  } catch (e) {
                    console.warn('Server-Backups nicht verfügbar:', e.message);
                  }
                }
                
                if (lokaleAnzahl === 0 && serverAnzahl === 0 && cloudBackups.length === 0) {
                  alert('❌ ' + t('backup.noBackups') + '\n\n' + t('ui.main.restoreNoSourceFound'));
                  resolve();
                  return;
                }
                
                // Biete verfügbare Quellen an
                let msg = '⚠️ ' + t('ui.main.restoreOtherSources') + '\n\n';
                const optionen = [];
                let nr = 1;
                const quellenMap = {}; // nr → { type, data }
                
                if (lokaleAnzahl > 0) {
                  optionen.push(`${nr}️⃣ ${t('ui.main.restoreOptionLocal', { count: lokaleAnzahl })}`);
                  quellenMap[nr] = { type: 'local', data: lokalePakete };
                  nr++;
                }
                if (serverAnzahl > 0) {
                  optionen.push(`${nr}️⃣ ${t('ui.main.restoreOptionServerDb', { count: serverAnzahl })}`);
                  quellenMap[nr] = { type: 'serverDB', data: serverPakete };
                  nr++;
                }
                if (cloudBackups.length > 0) {
                  const neuestes = cloudBackups[0];
                  optionen.push(`${nr}️⃣ ${t('ui.main.restoreOptionServerBackup')}: ${neuestes.name} (${Math.round(neuestes.size / 1024)} KB, ${new Date(neuestes.modified).toLocaleString('de-DE')})`);
                  quellenMap[nr] = { type: 'cloudBackup', filename: neuestes.name };
                  nr++;
                }
                msg += optionen.join('\n') + '\n\n' + t('ui.main.restoreEnterNumber');
                
                const wahl = prompt(msg, '0');
                const wahlNr = parseInt(wahl);
                const quelle = quellenMap[wahlNr];
                
                if (quelle) {
                  if (quelle.type === 'local') {
                    setPakete(quelle.data);
                    alert(`✅ ${t('ui.main.restoreLoadedLocal', { count: quelle.data.length })}`);
                  } else if (quelle.type === 'serverDB') {
                    await createBackup(pakete, 'before_server_restore');
                    setPakete(quelle.data);
                    await set('pakete', quelle.data);
                    alert(`✅ ${t('ui.main.restoreLoadedServerDb', { count: quelle.data.length })}`);
                  } else if (quelle.type === 'cloudBackup') {
                    const success = await restoreCloudBackup(quelle.filename);
                    if (!success) {
                      alert('❌ ' + t('ui.main.restoreFailed'));
                    }
                  }
                }
                
                resolve();
                return;
              }
              
              // Sammle ALLE Backups
              const alleBackups = [];
              
              for (const key of backupKeys) {
                const backup = await get(key);
                const anzahl = backup?.data?.length || backup?.count || 0;
                
                if (backup?.timestamp) {
                  alleBackups.push({
                    key,
                    backup,
                    anzahl,
                    timestamp: new Date(backup.timestamp)
                  });
                }
              }
              
              // Sortiere nach Datum (neueste zuerst)
              alleBackups.sort((a, b) => b.timestamp - a.timestamp);
              
              if (alleBackups.length === 0) {
                alert('❌ ' + t('backup.noBackups'));
                resolve();
                return;
              }
              
              // 🎯 INTELLIGENTE AUSWAHL MIT ZEITLICHER ABDECKUNG
              // Ziel: Immer 2-3 Tage Rückblick möglich mit vielen Optionen
              const jetzt = Date.now();
              const auswahlBackups = [];
              const verwendeteZeitslots = new Map(); // Speichere beste Backups pro Zeitslot
              
              for (const b of alleBackups) {
                const alter = jetzt - b.timestamp.getTime();
                const stunden = alter / (1000 * 60 * 60);
                const tage = stunden / 24;
                
                // KRITISCHE Backups: IMMER nehmen
                if (b.backup.critical) {
                  auswahlBackups.push(b);
                  continue;
                }
                
                // ZEITBASIERTE STRATEGIE für 2-3 Tage mit HOHER DICHTE:
                let zeitslot;
                
                if (stunden < 6) {
                  // LETZTE 6 STUNDEN: Alle 15 Minuten (mehr Details!)
                  const viertelStunden = Math.floor(stunden * 4);
                  zeitslot = `recent_${viertelStunden}`;
                  
                } else if (stunden < 24) {
                  // HEUTE (6-24h): Alle 30 Minuten (erhöht von jede Stunde)
                  const halbeStunden = Math.floor(stunden * 2);
                  zeitslot = `today_${halbeStunden}`;
                  
                } else if (tage < 2) {
                  // GESTERN (1-2 Tage): Jede Stunde (erhöht von alle 2h)
                  zeitslot = b.timestamp.toISOString().substring(0, 13); // YYYY-MM-DDTHH
                  
                } else if (tage < 4) {
                  // VORGESTERN + VORVORGESTERN (2-4 Tage): Alle 2 Stunden
                  zeitslot = b.timestamp.toISOString().substring(0, 13);
                  const stunde = parseInt(zeitslot.substring(11, 13));
                  zeitslot = zeitslot.substring(0, 11) + String(Math.floor(stunde / 2) * 2).padStart(2, '0');
                  
                } else if (tage < 7) {
                  // DIESE WOCHE (4-7 Tage): Alle 4 Stunden
                  zeitslot = b.timestamp.toISOString().substring(0, 13);
                  const stunde = parseInt(zeitslot.substring(11, 13));
                  zeitslot = zeitslot.substring(0, 11) + String(Math.floor(stunde / 4) * 4).padStart(2, '0');
                  
                } else {
                  // ÄLTER (> 7 Tage): 1x pro Tag
                  zeitslot = b.timestamp.toISOString().substring(0, 10); // YYYY-MM-DD
                }
                
                // Nehme BESTE Backup pro Zeitslot (meiste Pakete)
                const vorhandenes = verwendeteZeitslots.get(zeitslot);
                if (!vorhandenes || b.anzahl > vorhandenes.anzahl) {
                  if (vorhandenes) {
                    // Ersetze schlechteres Backup
                    const index = auswahlBackups.indexOf(vorhandenes);
                    if (index !== -1) auswahlBackups.splice(index, 1);
                  }
                  auswahlBackups.push(b);
                  verwendeteZeitslots.set(zeitslot, b);
                }
                
                // Max 100 zur Auswahl
                if (auswahlBackups.length >= 100) break;
              }
              
              // Sortiere wieder nach Datum (neueste zuerst)
              auswahlBackups.sort((a, b) => b.timestamp - a.timestamp);
              
              console.log(`📋 Zeige ${auswahlBackups.length} von ${alleBackups.length} Backups zur Auswahl`);
              
              // 📋 SCROLLBARES MODAL statt prompt()
              const modal = document.createElement('div');
              modal.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0,0,0,0.9);
                z-index: 999999;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 20px;
              `;
              
              const container = document.createElement('div');
              container.style.cssText = `
                background: white;
                border-radius: 12px;
                max-width: 600px;
                width: 100%;
                max-height: 80vh;
                display: flex;
                flex-direction: column;
                box-shadow: 0 10px 40px rgba(0,0,0,0.5);
              `;
              
              const header = document.createElement('div');
              header.style.cssText = `
                padding: 20px;
                background: #ff0000;
                color: white;
                border-radius: 12px 12px 0 0;
                font-size: 20px;
                font-weight: bold;
              `;
              header.innerHTML = `
                🚨 ${t('ui.main.restoreTitleCaps')}<br>
                <span style="font-size: 14px; font-weight: normal;">
                  ${t('ui.main.restoreFoundShown', { found: alleBackups.length, shown: auswahlBackups.length })}
                </span>
              `;
              
              const list = document.createElement('div');
              list.style.cssText = `
                flex: 1;
                overflow-y: auto;
                padding: 20px;
                max-height: 50vh;
              `;
              
              auswahlBackups.forEach((b, index) => {
                const item = document.createElement('div');
                item.style.cssText = `
                  padding: 12px;
                  margin-bottom: 8px;
                  border: 2px solid #ddd;
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s;
                  background: ${b.backup.critical ? '#fff3cd' : 'white'};
                `;
                
                const datum = b.timestamp.toLocaleString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                
                item.innerHTML = `
                  <div style="font-weight: bold; margin-bottom: 4px;">
                    ${index + 1}. ${datum} ${b.backup.critical ? '🚨' : ''}
                  </div>
                  <div style="color: #666; font-size: 14px;">
                    📦 ${t('ui.main.packagesCount', { count: b.anzahl })} · ${b.backup.reason || 'auto'}
                  </div>
                `;
                
                item.onmouseover = () => {
                  item.style.background = b.backup.critical ? '#ffeaa7' : '#f0f0f0';
                  item.style.borderColor = '#007bff';
                  item.style.transform = 'scale(1.02)';
                };
                
                item.onmouseout = () => {
                  item.style.background = b.backup.critical ? '#fff3cd' : 'white';
                  item.style.borderColor = '#ddd';
                  item.style.transform = 'scale(1)';
                };
                
                item.onclick = () => {
                  document.body.removeChild(modal);
                  restoreBackup(b);
                };
                
                list.appendChild(item);
              });
              
              const footer = document.createElement('div');
              footer.style.cssText = `
                padding: 20px;
                border-top: 1px solid #ddd;
                text-align: center;
              `;
              
              const cancelBtn = document.createElement('button');
              cancelBtn.textContent = '❌ ' + t('ui.main.cancel');
              cancelBtn.style.cssText = `
                padding: 12px 24px;
                background: #dc3545;
                color: white;
                border: none;
                border-radius: 6px;
                font-size: 16px;
                cursor: pointer;
                font-weight: bold;
              `;
              
              cancelBtn.onclick = () => {
                document.body.removeChild(modal);
                resolve();
              };
              
              footer.appendChild(cancelBtn);
              
              container.appendChild(header);
              container.appendChild(list);
              container.appendChild(footer);
              modal.appendChild(container);
              document.body.appendChild(modal);
              
              // Funktion zum Wiederherstellen
              const restoreBackup = async (gewaehltes) => {
                const bestesBackup = gewaehltes.backup;
                const meistePakete = gewaehltes.anzahl;
                const besterKey = gewaehltes.key;
                
                if (!bestesBackup || !bestesBackup.data) {
                  alert('❌ ' + t('backup.invalidBackup'));
                  resolve();
                  return;
                }
                
                const confirmed = window.confirm(
                  `🚨 ${t('backup.restoreConfirm')}\n\n` +
                  `📦 ${meistePakete} ${t('packages.packages')}\n` +
                  `📅 ${gewaehltes.timestamp.toLocaleString('de-DE')}\n` +
                  `📝 ${bestesBackup.reason}\n` +
                  `🔑 ${besterKey}\n\n` +
                  `${t('backup.restoreNow')}`
                );
                
                if (!confirmed) {
                  resolve();
                  return;
                }
                
                console.log('✅ Restore gestartet mit:', meistePakete, 'Paketen');
                
                // DOM-Modal entfernen, React-Overlay übernimmt
                try { document.body.removeChild(modal); } catch(e) {}
                
                setBackupProgress({ type: 'restore', step: t('ui.main.progressEmergencyStart'), current: 0, total: meistePakete, percent: 5, detail: t('ui.main.progressSetMarker') });
                
                // 🚨 WICHTIG: Markiere als Restore (um Localhost-Block zu umgehen)
                await set('restore_in_progress', true);
                
                setBackupProgress({ type: 'restore', step: t('ui.main.progressUpdatingLocal'), current: 0, total: meistePakete, percent: 15, detail: t('ui.main.packagesCount', { count: meistePakete }) });
                
                // Restore!
                setPakete(bestesBackup.data);
                await set('pakete', bestesBackup.data);
                
                // 🆕 Zu Server SUBCOLLECTIONS hochladen (IMMER, auch auf localhost!)
                if (getAuthUser()) {
                  const total = bestesBackup.data.length;
                  let migrated = 0;
                  let failed = 0;
                  
                  for (const paket of bestesBackup.data) {
                    try {
                      await savePaket(paket);
                      migrated++;
                    } catch (error) {
                      failed++;
                      console.error(`Notfall-Restore Fehler für ${paket.id}:`, error);
                    }
                    const percent = 20 + Math.round((migrated + failed) / total * 75);
                    setBackupProgress({ type: 'restore', step: t('ui.main.progressUploading'), current: migrated + failed, total, percent, detail: `${t('ui.main.progressUploaded', { done: migrated, total })}${failed > 0 ? ` (${t('ui.main.progressErrors', { count: failed })})` : ''}` });
                  }
                  
                  // Hash updaten
                  await updatePackagesHash(bestesBackup.data);
                  await set('lastPackageSync', Date.now());
                  
                  console.log(`✅ ${migrated}/${bestesBackup.data.length} Pakete zu Server hochgeladen`);
                }
                
                setBackupProgress({ type: 'restore', step: t('ui.main.progressDone'), current: meistePakete, total: meistePakete, percent: 100, detail: t('ui.main.packagesRestored', { count: meistePakete }) });
                await new Promise(r => setTimeout(r, 1500));
                setBackupProgress(null);
                window.location.reload();
                
                resolve();
              };
            };
            
            getAllKeysRequest.onerror = () => {
              reject(new Error(t('ui.main.idbKeysFailed')));
            };
          } catch (err) {
            reject(err);
          }
        };
        
        request.onerror = () => {
          reject(new Error(t('ui.main.idbOpenFailed')));
        };
      });
      
    } catch (error) {
      console.error('❌ Notfall-Restore Fehler:', error);
      alert(`❌ ${t('common.error')}: ${error.message}`);
    }
  };
  
  const [photo, setPhoto] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [name, setName] = useState('');
  const [streetAndNumber, setStreetAndNumber] = useState('');
  const [postalAndCity, setPostalAndCity] = useState('');
  const [regalplatz, setRegalplatz] = useState('');
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10)); // yyyy-mm-dd
  const [ausgegebenAm, setAusgegebenAm] = useState('');
  const [search, setSearch] = useState('');
  
  // 🔍 DEBUG: Wrapper um setPakete zu tracken + synchrones Ref gegen Stale-Closures bei Schnell-Scans
  const [internalPakete, setInternalPakete] = useState([]);
  const paketeRef = useRef([]);
  const setPakete = (newPaketeOrUpdater) => {
    const stack = new Error().stack;
    const caller = stack.split('\n')[2]?.trim() || 'unknown';
    // Immer gegen die AKTUELLE Liste rechnen und sofort gültig setzen (23.10.2026). Früher wurde paketeRef erst beim
    // Neuzeichnen gesetzt — lief ein verzögertes (startTransition) Neuzeichnen mit einem älteren Stand nach, fiel ein
    // gerade angelegtes Paket wieder heraus (Leistungstest mit Zweitgerät nachgewiesen).
    const next = typeof newPaketeOrUpdater === 'function' ? newPaketeOrUpdater(paketeRef.current) : newPaketeOrUpdater;
    const arr = Array.isArray(next) ? next : [];
    paketeRef.current = arr;
    console.log(`🔍 setPakete called with ${arr.length} packages from:`, caller);
    if (arr.length === 0) {
      console.warn('⚠️ WARNUNG: setPakete([]) - Pakete werden geleert!');
    }
    setInternalPakete(arr);
  };
  const pakete = internalPakete;

  // ⏳ Schweres im Hintergrund (23.10.2026, Folkert: 7.500 Pakete auf dem Gerät, je Scan ~3 s Zusatzzeit):
  // Beim Erfassen per Vinted/DHL wurde je Paket bis zu 3× die GANZE Liste neu gezeichnet und in IndexedDB geschrieben
  // und 2× die Prüfsumme über alle Pakete berechnet. Jetzt: Neuzeichnen mit niedriger Priorität (startTransition),
  // Speichern gesammelt (höchstens alle 1,5 s, sofort beim Verlassen der App), Prüfsumme höchstens alle 3 s.
  const speicherTimerRef = useRef(null);
  const hashTimerRef = useRef(null);
  const paketeJetztSpeichern = useCallback(async () => {
    if (!speicherTimerRef.current) return;
    clearTimeout(speicherTimerRef.current);
    speicherTimerRef.current = null;
    try { await set('pakete', paketeRef.current); } catch (e) { console.warn('📦 Speichern (gesammelt) fehlgeschlagen:', e?.message); }
  }, []);
  const paketeSpaeterSpeichern = useCallback(() => {
    if (speicherTimerRef.current) return;
    speicherTimerRef.current = setTimeout(() => { paketeJetztSpeichern(); }, 1500);
  }, [paketeJetztSpeichern]);
  const hashSpaeter = useCallback(() => {
    if (hashTimerRef.current) return;
    hashTimerRef.current = setTimeout(() => { hashTimerRef.current = null; updatePackagesHash(paketeRef.current).catch(() => {}); }, 3000);
  }, []);
  const imHintergrund = (fn) => startTransition(fn);
  // Änderung auf die AKTUELLE Liste anwenden und sofort speichern (statt einen früher gelesenen Stand zurückzuschreiben).
  // Für Hintergrund-Abläufe wie den Nachlade-Flush: sonst ging ein in der Zwischenzeit angelegtes Paket verloren.
  const listeAnwenden = useCallback(async (fn) => {
    const alt = paketeRef.current;
    const neu = fn(alt);
    if (!Array.isArray(neu) || neu === alt) return;
    paketeRef.current = neu;
    imHintergrund(() => { setPakete(neu); setServerPakete(neu); });
    if (speicherTimerRef.current) { clearTimeout(speicherTimerRef.current); speicherTimerRef.current = null; }
    try { await set('pakete', neu); } catch (e) { console.warn('📦 Speichern (anwenden) fehlgeschlagen:', e?.message); }
  }, []); // eslint-disable-line
  // 🛡️ Abgleich mit dem Server (23.10.2026, Zweitprüfung): der Sync liest den Bestand VOR dem Netz-Abruf (1–4 s bei
  // 7.500 Paketen). Was in der Zeit gescannt wurde, stand nicht im Schnappschuss und wurde vom Ergebnis überschrieben.
  // Darum NACH dem Abruf: ungesyncte Pakete gegen die aktuelle Liste schützen und seit Sync-Start neue Pakete behalten.
  const gegenAktuelleListeSchuetzen = (ergebnis, schnappschuss, ausschluss = null) => {
    // ausschluss: IDs, die auf einem anderen Gerät gelöscht wurden — die dürfen hier NICHT zurückkommen
    const roh = paketeRef.current && paketeRef.current.length ? paketeRef.current : schnappschuss;
    const aktuell = ausschluss && ausschluss.size ? roh.filter((p) => p && !ausschluss.has(p.id)) : roh;
    let liste = preservePendingLocalPackages(aktuell, ergebnis);
    const vorher = new Set((schnappschuss || []).map((p) => p && p.id));
    const drin = new Set((liste || []).map((p) => p && p.id));
    // Nur Pakete übernehmen, die tatsächlich frisch während des laufenden Abgleichs entstanden sind
    // (uploaded === false oder in den letzten 5 Min. angelegt/geändert) — niemals alte Restbestände
    // aus einem vorherigen Konto im Speicher (Fix 25.10.2026: Kontowechsel-Mischung).
    const jetzt = Date.now();
    const dazu = (aktuell || []).filter((p) => {
      if (!p || !p.id || p.deleted || vorher.has(p.id) || drin.has(p.id)) return false;
      if (p.uploaded === false) return true;
      const ts = typeof p.lastUpdated === 'number' ? p.lastUpdated : Date.parse(p.lastUpdated || p.updatedAt || p.createdAt || '');
      return Number.isFinite(ts) && (jetzt - ts) < 5 * 60 * 1000;
    });
    if (dazu.length) {
      console.log(`📦 Sync: ${dazu.length} während des Abgleichs angelegte(s) Paket(e) behalten`);
      liste = [...liste, ...dazu];
    }
    return liste;
  };
  useEffect(() => {
    const hashJetzt = () => {
      if (!hashTimerRef.current) return;
      clearTimeout(hashTimerRef.current); hashTimerRef.current = null;
      updatePackagesHash(paketeRef.current).catch(() => {});
    };
    const weg = () => { if (document.visibilityState === 'hidden') { paketeJetztSpeichern(); hashJetzt(); } };
    const raus = () => { paketeJetztSpeichern(); hashJetzt(); };
    document.addEventListener('visibilitychange', weg);
    window.addEventListener('pagehide', raus);
    return () => { document.removeEventListener('visibilitychange', weg); window.removeEventListener('pagehide', raus); };
  }, [paketeJetztSpeichern]);
  
  // 🚀 PERFORMANCE: Debounced Search - verzögert die Filterlogik um 300ms
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef(null);
  
  const [selectedIndex, setSelectedIndex] = useState(null); // NEU
  const [zurueckgeschickt, setZurueckgeschickt] = useState(false);
  const [saved, setSaved] = useState(false); // NEU
  const [undoInfo, setUndoInfo] = useState(null);
  const undoTimeout = useRef(null);
  const [showOld, setShowOld] = useState(false);
  const [showPrintLabels, setShowPrintLabels] = useState(false); // 🖨️ Etikett-Druck-Buttons in der Liste (Standard aus)
  const [showImages, setShowImages] = useState(() => {
    try { return localStorage.getItem('kiosk_show_images') === '1'; } catch { return false; }
  }); // 🖼️ Paket-Fotos in der Liste anzeigen (Standard aus, Ansicht streckt sich)
  const [showDetails, setShowDetails] = useState(() => {
    try { return localStorage.getItem('kiosk_show_details') === '1'; } catch { return false; }
  }); // ℹ️ Details (Sendungsnr., PLZ/Ort, Paketdienst) in der Liste — Standard aus
  const [showSender, setShowSender] = useState(() => {
    try { return localStorage.getItem('kiosk_show_sender') === '1'; } catch { return false; }
  }); // 📥 Absender in der Liste — nur mit der Option „Absender mit speichern"
  // ⚠️ Quittierpflichtiger Hinweis nach Ausgabe/Rücksendung: gleiche Hausnummer im Nachbarfach
  const [verwechslung, setVerwechslung] = useState(null); // {hausnummer, regalplatz, name, eigen, aktion}
  const [showFilters, setShowFilters] = useState(() => {
    try { return localStorage.getItem('kiosk_show_filters') !== '0'; } catch { return true; }
  }); // Sichtbarkeit der 4 Filter-Checkboxen
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // NEU
  const [trackingImageUrl, setTrackingImageUrl] = useState(null);
  const [trackingPhoto, setTrackingPhoto] = useState(null);
  const [trackingCrop, setTrackingCrop] = useState({ x: 0, y: 0 });
  const [trackingZoom, setTrackingZoom] = useState(1);
  const [trackingCroppedAreaPixels, setTrackingCroppedAreaPixels] = useState(null);
  const [trackingImageTimer, setTrackingImageTimer] = useState(null);
  const [trackingNumberCandidates, setTrackingNumberCandidates] = useState([]);
  const paketPhotoInputRef = useRef(null);
  
  // 🔔 Globale Toast-Benachrichtigung
  const [globalToast, setGlobalToast] = useState({ show: false, message: '', type: '', duration: 3000 });
  const trackingPhotoInputRef = useRef(null);
  const searchLogTimeoutRef = useRef(null); // 📋 Chronik: Timeout für Suchen-Logging
  const [erfassungsStart, setErfassungsStart] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10); // NEU
  const [currentUser, setCurrentUser] = useState(null);
  const [serverPakete, setServerPaketeInternal] = useState([]);
  const serverPaketeRef = useRef([]);
  const setServerPakete = (newServerPaketeOrUpdater) => {
    setServerPaketeInternal((prev) => {
      const next = typeof newServerPaketeOrUpdater === 'function' ? newServerPaketeOrUpdater(prev) : newServerPaketeOrUpdater;
      const arr = Array.isArray(next) ? next : [];
      serverPaketeRef.current = arr;
      return arr;
    });
  };
  const [lastUpdated, setLastUpdated] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false); // 🔒 Lock für Sync-Operationen
  // cleanupInProgress entfernt — Cleanup läuft jetzt serverseitig per Cron-Job
  const syncLockRef = useRef(false); // 🔒 SYNCHRONER Lock für syncDataWithSubcollections (verhindert Doppelaufrufe)
  const loginSyncCompletedRef = useRef(false); // 🔒 v1.46 FIX: Explizites Flag dass Login-Sync abgeschlossen ist (robuster als Timer!)
  const lastWrittenHashRef = useRef(null); // 🔧 v1.49 FIX: Merkt sich den zuletzt geschriebenen Hash → sofortiger Self-Trigger-Check OHNE IndexedDB
  const syncQueueRef = useRef([]); // 📋 Queue für Sync-Anfragen
  const processingQueueRef = useRef(false); // 🔒 Lock für Queue-Processing
  const isMountedRef = useRef(true); // 🛡️ RACE CONDITION FIX: Track ob Komponente noch mounted ist
  
  // 🔌 SSE-Trigger-Refs: Erlauben SSE-Events, die Sync-Funktionen direkt aufzurufen
  const sseSyncTriggerRef = useRef(null);       // Paket-Sync auslösen
  const sseSearchTriggerRef = useRef(null);     // Tablet-Suchen neu laden
  const sseLiveSearchTriggerRef = useRef(null); // Live-Suche verarbeiten
  // 🔒 v1.44 FIX: hasInitialSyncRef ersetzt durch sessionStorage (siehe Login-Sync useEffect)
  // Das überlebt Route-Wechsel (z.B. /pakete → /chronik → /pakete)
  const ocrWorkerRef = useRef(null); // 🚀 OCR Worker wiederverwendbar
  const ocrCounterRef = useRef(0); // 📊 Zähler für OCR-Durchläufe (für periodischen Worker-Reset)
  const lastOcrResultRef = useRef(null); // 🤖 Letztes OCR-Ergebnis für Auto-Feedback
  const [showTabletInterface, setShowTabletInterface] = useState(false);
  const [firebaseSearchResults, setFirebaseSearchResults] = useState([]);
  const [browserOnline, setBrowserOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const onOn = () => setBrowserOnline(true);
    const onOff = () => setBrowserOnline(false);
    window.addEventListener('online', onOn);
    window.addEventListener('offline', onOff);
    return () => {
      window.removeEventListener('online', onOn);
      window.removeEventListener('offline', onOff);
    };
  }, []);
  const connectionStatus = useConnectionStatus();
  // 📡 Online = Gerät hat Netz (wie vor Oktober 2026). NICHT an die Server-Erreichbarkeit koppeln: ein einzelner
  // fehlgeschlagener /health-Abruf setzte sonst isOnline=false → Login-Sync, SSE, Hochladen und ~15 weitere Effekte
  // starteten neu oder wurden übersprungen; nach einer Server-Störung blieb die Paketliste leer (e2e_auth 3b,
  // Review 21.10.2026). Die Server-Erreichbarkeit zeigt weiter die Verbindungsanzeige (connectionStatus).
  const isOnline = browserOnline;
  
  // 🕶️ DEVICE DETECTION
  const [deviceType, setDeviceType] = useState(getDeviceType());
  const [showDeviceSwitcher, setShowDeviceSwitcher] = useState(false);
  const [filterDays, setFilterDays] = useState(7); // Standardmäßig 7 Werktage
  const [filterDaysInternational, setFilterDaysInternational] = useState(14); // 🌍 Internationale Pakete: 14 Werktage
  const [animatingPaketIds, setAnimatingPaketIds] = useState(new Set()); // ✅ Set für mehrere gleichzeitige Animationen
  const [buttonIssuedPaketIds, setButtonIssuedPaketIds] = useState(new Set()); // 📌 Pakete via Button ausgegeben → bleiben an Position
  
  // 🚦 RATE LIMITING für Firebase Writes
  const firebaseWriteQueue = useRef([]);
  const lastFirebaseWrite = useRef(0);
  const FIREBASE_WRITE_COOLDOWN = 1000; // 1 Sekunde zwischen Writes
  const [overlayData, setOverlayData] = useState({ visible: false, names: [], position: { x: 0, y: 0 } });
  const [syncDifferences, setSyncDifferences] = useState(null);
  const [showNotLoggedInWarning, setShowNotLoggedInWarning] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const navigate = useNavigate(); // Diese Zeile hinzufügen
  const searchResultsRef = useRef(null);
  const searchBoxRef = useRef(null); // 🔍 Such-Container zum Nach-oben-Andocken beim Suchen
  const [shelfSuggestions, setShelfSuggestions] = useState([]);
  const [favoriten, setFavoriten] = useState([]);
  const [syncConfirmationStep, setSyncConfirmationStep] = useState(0);
  const [syncDirection, setSyncDirection] = useState(null);

  const [overlayLocked, setOverlayLocked] = useState(false);
  const [lastTapTimestamp, setLastTapTimestamp] = useState({});
  const tapTimerRef = useRef(null);
  const [showNewPackageModal, setShowNewPackageModalInternal] = useState(false); // Neuer State für das Modal
  
  // 🐛 DEBUG: Session-Counter für Race Condition Detection
  const modalSessionRef = useRef(0);
  
  // 🐛 DEBUG: Wrapper um setShowNewPackageModal zu tracken
  const setShowNewPackageModal = (value) => {
    const stack = new Error().stack;
    const caller = stack.split('\n')[2]?.trim() || 'unknown';
    
    if (value) {
      // Modal wird geöffnet → neue Session starten
      modalSessionRef.current++;
      console.log(`🔔 [DEBUG] setShowNewPackageModal(TRUE) - NEUE SESSION #${modalSessionRef.current}`, caller);
    } else {
      console.log(`🔔 [DEBUG] setShowNewPackageModal(FALSE) - Session #${modalSessionRef.current}`, caller);
      if (showNewPackageModal) {
        console.warn('⚠️ [DEBUG] Modal wird GESCHLOSSEN!');
        console.trace('Stack trace:');
      }
    }
    setShowNewPackageModalInternal(value);
  };
  
  // 🛡️ FIX: Getter für aktuelle Session-ID (für onSave)
  const getCurrentModalSession = () => modalSessionRef.current;
  
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [conflictItems, setConflictItems] = useState([]);
  const [conflictIndex, setConflictIndex] = useState(0);
  const [showTabletSearchResults, setShowTabletSearchResults] = useState(() => {
    try { return localStorage.getItem('kiosk_tablet_mode') === '1'; } catch { return false; }
  });
  const [recentTabletSearches, setRecentTabletSearches] = useState([]);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [showSetup, setShowSetup] = useState(false); // 🧭 Setup-Dialog
  const [showAddressBook, setShowAddressBook] = useState(false); // 📒 Empfänger-Adressbuch (CSV / Kontakte)
  const [signatureRequest, setSignatureRequest] = useState(null); // ✍️ Unterschrift bei Abholung ({ paket, fromButton })
  const [druckerKlassisch, setDruckerKlassisch] = useState(false); // 🖨️ alter Drucker-Dialog als Rückfall
  const [setupAktiv] = useState(() => isSetupEnabled()); // 🚧 Vorschau: Setup-Knopf statt Sprachzeile
  const [setupGesehen, setSetupGesehen] = useState(() => { try { return localStorage.getItem('kiosk_setup_seen') === '1'; } catch { return true; } });
  const [paketeGeladen, setPaketeGeladen] = useState(false); // ⏳ Erst nach dem ersten IndexedDB-Laden Erststart-Karte/Demo zeigen (kein Aufblitzen)
  // 👋 Erststart-Karussell & 2 interaktive Beispiel-Pakete (nur solange 0 echte Pakete vorhanden sind)
  const [welcomeSlide, setWelcomeSlide] = useState(0);
  const [demoHidden, setDemoHidden] = useState(() => {
    try { return localStorage.getItem('kiosk_demo_hidden') === '1'; } catch { return false; }
  });
  const createInitialDemoPakete = useCallback(() => {
    const nowIso = new Date().toISOString();
    return [
      {
        id: 'demo-paket-1',
        isDemo: true,
        name: 'Anna Bergmann (Testperson)',
        streetAndNumber: 'Musterweg 12',
        postalAndCity: '10115 Berlin',
        trackingNumber: '00340434161094012591',
        regalplatz: 'A12',
        paketAnzahl: 1,
        paketdienst: 'DHL',
        datum: nowIso,
        ausgegebenAm: '',
        zurueckgeschickt: false,
        updatedAt: nowIso,
        uploaded: true,
      },
      {
        id: 'demo-paket-2',
        isDemo: true,
        name: 'Lukas van Dijk (Testperson)',
        streetAndNumber: 'Musterweg 48',
        postalAndCity: '10115 Berlin',
        trackingNumber: '3SBPB0001928374',
        regalplatz: 'B04',
        paketAnzahl: 2,
        paketdienst: 'PostNL',
        datum: nowIso,
        ausgegebenAm: '',
        zurueckgeschickt: false,
        updatedAt: nowIso,
        uploaded: true,
      },
    ];
  }, []);
  const [demoPakete, setDemoPakete] = useState(() => createInitialDemoPakete());
  const [searchScanOn, setSearchScanOn] = useState(false); // 📷 Abholcode-Scan (Suche)
  const [showVintedImport, setShowVintedImport] = useState(false); // 📦 Vinted-Namen (Sonderfunktion)
  // 👷 Mitarbeiter-Modus: nur mit Konto-Freischaltung UND auf diesem Gerät eingeschaltet.
  // Ohne beides ist staffMode immer false → die normale Ansicht bleibt exakt wie sie ist.
  const [staffModeGespeichert, setStaffModeState] = useState(() => isStaffMode());
  // ⛔ Nie ohne Anmeldung: sonst ersetzt die Mitarbeiter-Leiste den Login-Bereich und man kommt
  // nach einem Abmelden/Session-Verlust nicht mehr hinein (Green Goat, 26.09.2026).
  const staffMode = staffModeGespeichert && !!currentUser;
  const [staffDialog, setStaffDialog] = useState(null); // 'setup' | 'exit' | null
  const [showMitarbeiter, setShowMitarbeiter] = useState(false); // 👥 Mitarbeiter verwalten (Einstellungen)
  const [showNetzwerk, setShowNetzwerk] = useState(false); // 🏪 Netzwerk (Einstellungen)
  const [showVintedPanel, setShowVintedPanel] = useState(false); // 🧪 Vinted-Anbindung (Einstellungen)
  const [showVintedErfassen, setShowVintedErfassen] = useState(false); // 🧪 Annahme mit Buchung bei Vinted
  const [showDhlBridge, setShowDhlBridge] = useState(false); // 🧪 DHL-Anbindung (Einstellungen)
  const [showDpdPanel, setShowDpdPanel] = useState(false); // 📦 DPD Pickup-Anbindung (Einstellungen)
  const [showGrenzpaketPanel, setShowGrenzpaketPanel] = useState(false); // 🇨🇭 Grenzpaket-Anbindung (Einstellungen)
  const [showDevicesModal, setShowDevicesModal] = useState(false); // 📱 Meine Geräte (Live-Übersicht)
  const [activeDeviceCount, setActiveDeviceCount] = useState(() => getActiveDeviceCount()); // 📱 Anzahl aktiver Geräte im Konto
  const [setupStartStep, setSetupStartStep] = useState(0); // 🧭 Direkt-Sprung ins Setup (z. B. Schritt 4: Drucker & Geräte)
  const [gpDemoMode, setGpDemoMode] = useState(() => isDemoModeActive()); // 🧪 Visueller Demo-Modus (blendet echte Pakete aus)
  useEffect(() => {
    const syncDemo = () => setGpDemoMode(isDemoModeActive());
    window.addEventListener('kiosk:demo-mode-changed', syncDemo);
    return () => window.removeEventListener('kiosk:demo-mode-changed', syncDemo);
  }, []);

  // 🧭 Konto-Profil vom Server angewendet (z. B. nach Login auf einem frischen Handy):
  // lokale UI-States sofort nachziehen, ohne dass ein App-Neustart nötig ist.
  useEffect(() => {
    const onProfileApplied = () => {
      try {
        if (localStorage.getItem('kiosk_setup_seen') === '1') setSetupGesehen(true);
        if (localStorage.getItem('kiosk_demo_hidden') === '1') setDemoHidden(true);
        setShowTabletSearchResults(localStorage.getItem('kiosk_tablet_mode') === '1');
        const sd = localStorage.getItem('kiosk_show_details');
        if (sd === '1' || sd === '0') setShowDetails(sd === '1');
        const si = localStorage.getItem('kiosk_show_images');
        if (si === '1' || si === '0') setShowImages(si === '1');
        const sr = localStorage.getItem('kiosk_show_regale');
        if (sr === '1' || sr === '0') setShowRegale(sr === '1');
        const sf = localStorage.getItem('kiosk_show_filters');
        if (sf === '1' || sf === '0') setShowFilters(sf === '1');
        const ss = localStorage.getItem('kiosk_show_sender');
        if (ss === '1' || ss === '0') setShowSender(ss === '1');
        setFeatureTick((n) => n + 1);
      } catch { /* ignore */ }
    };
    window.addEventListener('kiosk:profile-applied', onProfileApplied);
    return () => window.removeEventListener('kiosk:profile-applied', onProfileApplied);
  }, []);

  // 🎓 Schulungsmodus & Admin-Fernsteuerung (Wartung + Stilles Aufschalten im Admin-Board)
  const [trainingModeActive, setTrainingModeActive] = useState(() => isTrainingModeRequested());
  useEffect(() => {
    initRemoteControlAgent();
    const unsub = subscribeRemoteAgentStatus((st) => {
      setTrainingModeActive(Boolean(st?.trainingModeRequested));
    });
    const onRemoteNav = (ev) => {
      const target = ev?.detail?.target;
      if (target === 'setup') {
        if (Number.isFinite(ev?.detail?.step)) setSetupStartStep(ev.detail.step);
        setShowSetup(true);
      } else if (target === 'devices') {
        setShowDevicesModal(true);
      } else if (target === 'maintenance') {
        setShowMaintenanceModal(true);
      } else if (target === 'printer') {
        setShowPrinterSettings(true);
      } else if (target === 'batch') {
        setShowBatch(true);
      } else if (target === 'grenzpaket') {
        setShowGrenzpaketPanel(true);
      } else if (target === 'close_all') {
        setShowSetup(false);
        setShowDevicesModal(false);
        setShowMaintenanceModal(false);
        setShowPrinterSettings(false);
        setShowBatch(false);
        setShowGrenzpaketPanel(false);
      } else if (target === 'refresh_printer_state') {
        try { setConfiguredPrinter(getPrinterConfig()); } catch { /* ignore */ }
      }
    };
    window.addEventListener('kiosk:remote-navigate', onRemoteNav);
    return () => {
      unsub();
      window.removeEventListener('kiosk:remote-navigate', onRemoteNav);
    };
  }, []);

  // 🌐 Shop-Gerätenetzwerk & stille Hintergrund-Lagerscans (ohne extra Reiter/Modal):
  // 1. Druck-Brücke: Nimmt Etiketten-Druckaufträge von anderen Handys/Tablets/Zebra TC75 entgegen.
  // 2. WLAN-Scanner (Zebra TC75 / DataWedge) & Bluetooth-Ringscanner buchen Paket → Regal still im Hintergrund.
  // 3. Live-Präsenz: Meldet dieses Handy + verbundenen Drucker ans Konto (für „📱 Meine Geräte").
  useEffect(() => {
    const bgClientId = getClientDeviceId();
    sendDeviceHeartbeat();
    const hbTimer = setInterval(() => { sendDeviceHeartbeat(); }, 60000);
    const onSseConnected = () => { sendDeviceHeartbeat(); };
    const onActiveDevicesChanged = (ev) => {
      const cnt = ev?.detail?.count;
      setActiveDeviceCount(Number.isFinite(cnt) ? cnt : getActiveDeviceCount());
    };
    const onDeviceStatusSse = (ev) => {
      if (Number.isFinite(ev?.detail?.activeDeviceCount)) {
        setActiveDeviceCount(ev.detail.activeDeviceCount);
      }
    };
    const onPrinterChangedLocal = () => {
      sendDeviceHeartbeat({ force: true });
    };
    window.addEventListener('kiosk:sse-connected', onSseConnected);
    window.addEventListener('kiosk:active-devices-changed', onActiveDevicesChanged);
    window.addEventListener('kiosk:device-status', onDeviceStatusSse);
    window.addEventListener('kiosk:printer-changed', onPrinterChangedLocal);
    const applyShelfUpdateLocally = async (updatedList, shelf) => {
      if (!Array.isArray(updatedList) || updatedList.length === 0) return;
      const byId = new Map(updatedList.map((u) => [String(u.id), u]));
      const nowIso = new Date().toISOString();
      const basis = paketeRef.current;
      if (!Array.isArray(basis) || basis.length === 0) return;
      const next = basis.map((p) => {
        if (!p) return p;
        const hit = byId.get(String(p.id)) || (p.serverId != null ? byId.get(String(p.serverId)) : null);
        if (!hit) return p;
        return {
          ...p,
          regalplatz: shelf || hit.regalplatz || p.regalplatz,
          updatedAt: nowIso,
          uploaded: true,
        };
      });
      setPakete(next);
      setServerPakete(next);
      try { await set('pakete', next); } catch { /* ignore */ }
    };

    const onRemotePrintJob = async (ev) => {
      const job = ev?.detail;
      if (!job || !job.paket) return;
      try {
        if (!isPrintBridgeHostEnabled()) return;
        const cfg = getPrinterConfig();
        if (!cfg?.address || cfg.address === 'remote-bridge' || cfg.remote) return;
        const total = Math.max(1, Math.min(10, Number(job.copies) || 1));
        for (let i = 1; i <= total; i++) {
          await printOne(job.paket, { index: i, total, forceLocal: true });
        }
        if (window.showToast) {
          window.showToast(`🖨️ Netzwerk-Druck (${job.requestedBy || 'Shop'}): ${job.paket.name || job.paket.trackingNumber || 'Etikett'}`, 'success', 3500);
        }
      } catch (err) {
        console.warn('🖨️ Druck-Brücke Fehler:', err?.message || err);
        if (window.showToast) {
          window.showToast(`⚠️ Druck-Brücke Fehler: ${err?.message || 'Bluetooth prüfen'}`, 'warning', 4500);
        }
      }
    };

    const onNetworkDeviceScan = (ev) => {
      const d = ev?.detail;
      if (!d) return;
      if (d.clientId && d.clientId === bgClientId) return; // Eigene lokale Scans nicht doppelt melden
      if (d.action === 'pickup_search' && d.code) {
        setSearch(String(d.code));
        if (window.showToast) {
          window.showToast(`📤 ${d.deviceName || 'Scanner'}: ${d.package?.empfaenger || d.code}`, 'info', 3500);
        }
      } else if (d.action === 'intake_batch' && d.code) {
        import('./services/barcodeBatch').then(({ addToBatch }) => {
          addToBatch(String(d.code));
          if (window.showToast) {
            window.showToast(`📥 ${d.deviceName || 'Scanner'}: ${d.code} in Stapel gelegt`, 'success', 2500);
          }
        }).catch(() => {});
      } else if (d.action === 'package_queued' && d.package) {
        if (window.showToast) {
          window.showToast(`📦 ${d.deviceName || 'Lager'}: ${d.package.empfaenger || d.code} gemerkt — jetzt Regal scannen`, 'info', 3500);
        }
      } else if (d.action === 'stored' && d.shelf) {
        if (window.showToast) {
          const namen = Array.isArray(d.packages) ? d.packages.map((p) => p.empfaenger).filter(Boolean).join(', ') : '';
          window.showToast(`✅ ${d.deviceName || 'Lager'}: ${namen || `${d.count || 1} Paket(e)`} → Regal ${d.shelf}`, 'success', 4000);
        }
      } else if (d.action === 'shelf_armed' && d.armedShelf) {
        if (window.showToast) {
          window.showToast(`📍 ${d.deviceName || 'Lager'}: Ziel-Regal ${d.armedShelf} aktiv`, 'info', 3000);
        }
      }
    };

    // Stiller Hintergrund-Listener für direkt gekoppelte Bluetooth-/USB-Scanner (schneller Tasten-Burst + Enter),
    // wenn kein Eingabefeld fokussiert und kein Dialog offen ist:
    let scanBuf = '';
    let lastKeyAt = 0;
    const onSilentScannerKeyDown = (e) => {
      const active = document.activeElement;
      const tag = String(active?.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active?.isContentEditable) {
        scanBuf = '';
        return;
      }
      if (document.querySelector('[data-testid="devices-modal"], [data-testid="grenzpaket-panel"], [data-testid="setup-wizard"], [data-testid="shelf-codes-modal"]')) {
        scanBuf = '';
        return;
      }
      const now = Date.now();
      if (e.key === 'Enter') {
        const code = scanBuf.trim();
        const isRapidScan = code.length >= 2 && (now - lastKeyAt) < 120;
        scanBuf = '';
        if (!isRapidScan) return;
        e.preventDefault();
        unlockWarehouseAudio();
        sendDeviceScan({ code, clientId: bgClientId })
          .then((res) => {
            if (!res) return;
            if (res.action === 'shelf_assigned') {
              playWarehouseTone('stored');
              applyShelfUpdateLocally(res.updatedPackages, res.shelf);
              const namen = (res.updatedPackages || []).map((p) => p.empfaenger).filter(Boolean).join(', ');
              if (window.showToast) {
                window.showToast(`✅ ${namen || 'Paket'} auf Regal ${res.shelf} eingelagert!`, 'success', 4000);
              }
            } else if (res.action === 'package_queued') {
              playWarehouseTone('queued');
              if (window.showToast) {
                window.showToast(`📦 ${res.queuedPackage?.empfaenger || code} gemerkt — jetzt Regal scannen!`, 'info', 3500);
              }
            } else if (res.action === 'shelf_armed') {
              playWarehouseTone('queued');
              if (window.showToast) {
                window.showToast(`📍 Ziel-Regal ${res.armedShelf} aktiv — jetzt Pakete scannen!`, 'info', 3500);
              }
            } else if (res.message) {
              playWarehouseTone('error');
              if (window.showToast) {
                window.showToast(`⚠️ ${res.message}`, 'warning', 4000);
              }
            }
          })
          .catch(() => {});
        return;
      }
      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (now - lastKeyAt > 75) {
          scanBuf = e.key;
        } else {
          scanBuf += e.key;
        }
        lastKeyAt = now;
      }
    };

    window.addEventListener('kiosk:device-print-job', onRemotePrintJob);
    window.addEventListener('kiosk:device-scan', onNetworkDeviceScan);
    window.addEventListener('keydown', onSilentScannerKeyDown);
    return () => {
      clearInterval(hbTimer);
      window.removeEventListener('kiosk:sse-connected', onSseConnected);
      window.removeEventListener('kiosk:active-devices-changed', onActiveDevicesChanged);
      window.removeEventListener('kiosk:device-status', onDeviceStatusSse);
      window.removeEventListener('kiosk:printer-changed', onPrinterChangedLocal);
      window.removeEventListener('kiosk:device-print-job', onRemotePrintJob);
      window.removeEventListener('kiosk:device-scan', onNetworkDeviceScan);
      window.removeEventListener('keydown', onSilentScannerKeyDown);
    };
  }, []);
  const [turboAn, setTurboAn] = useState(() => isTurboOn()); // ⚡ Turbo für Vinted/DHL erfassen (je Gerät)
  const [turboPpAn, setTurboPpAn] = useState(() => isTurboPpOn()); // ⚡ Turbo PaketPilot (je Gerät)
  const [turboHudAn, setTurboHudAn] = useState(() => isTurboHudOn());
  const [turboAutoFotoAn, setTurboAutoFotoAn] = useState(() => isTurboAutoFotoOn());
  const [turboRegalAn, setTurboRegalAn] = useState(() => isTurboRegalOn());
  const [turboTonAn, setTurboTonAn] = useState(() => isTurboTonOn());
  const ladeTurboOptionen = () => {
    setTurboHudAn(isTurboHudOn());
    setTurboAutoFotoAn(isTurboAutoFotoOn());
    setTurboRegalAn(isTurboRegalOn());
    setTurboTonAn(isTurboTonOn());
  };
  const [showTurboPp, setShowTurboPp] = useState(false);
  // 👁 Turbo-Vorschau (nur Ansicht, nichts wird gespeichert/gedruckt): über ?turbodemo=1 oder den Knopf in den Experimenten
  const [showTurboDemo, setShowTurboDemo] = useState(() => { try { return new URLSearchParams(window.location.search).has('turbodemo'); } catch { return false; } });
  const [stationAutoFach, setStationAutoFach] = useState(() => isStationAutoFachOn()); // 📸 Foto-Station: Vinted/DHL ohne Fach-Scan
  const [showDhlErfassen, setShowDhlErfassen] = useState(false); // 🧪 Annahme über das DHL-Portal
  const [dhlBridgeAn, setDhlBridgeAn] = useState(() => dhlAktiv());
  const [dhlAnnahmeAn, setDhlAnnahmeAn] = useState(() => dhlAnnahmeAktiv());
  const [dhlStoerung, setDhlStoerung] = useState(null); // 'login' | 'down' | null
  useEffect(() => {
    if (!dhlBridgeAn) { setDhlStoerung(null); return undefined; }
    let lebt = true;
    const pruefe = async () => {
      const s = await dhlStatus();
      if (!lebt || s.reason === 'offline' || s.reason === 'nicht_freigeschaltet') return;
      setDhlStoerung(s.browser === 'ok' ? (s.loggedIn === false ? 'login' : null) : 'down');
    };
    pruefe();
    const iv = setInterval(pruefe, 10 * 60 * 1000);
    return () => { lebt = false; clearInterval(iv); };
  }, [dhlBridgeAn, showDhlBridge]);
  const [vintedAusgabePaket, setVintedAusgabePaket] = useState(null); // 🧪 Ausgabe mit Bestätigung bei Vinted (angetipptes Paket)
  const [dhlAusgabePaket, setDhlAusgabePaket] = useState(null); // 🧪 Ausgabe über das DHL-Portal (angetipptes Paket)
  const [vintedAn, setVintedAn] = useState(() => vintedAnzeigeAktiv()); // 🧪 freigeschaltet + Anzeige auf diesem Gerät eingeschaltet (liest nur)
  const [vintedAnnahmeAn, setVintedAnnahmeAn] = useState(() => annahmeAktiv()); // 🔴 Annahme bucht LIVE in Vinted (eigener Schalter, nach Rückfrage)
  // 🧪 Störung der Vinted-Anbindung: 'login' (nicht bzw. nicht mehr bei Vinted angemeldet) | null.
  // Geprüft beim Start, alle 10 Minuten und nach dem Schließen des Panels (z. B. nach einer Anmeldung).
  const [vintedStoerung, setVintedStoerung] = useState(null);
  const [vintedHinweis, setVintedHinweis] = useState(null); // { rueck: n, post: bool } — was bei Vinted ansteht
  const [vintedFristen, setVintedFristen] = useState(null); // Sendungsverfolgung je Paket (Status, Resttage)
  // Laut Sendungsverfolgung schon beim Kunden, in PaketPilot noch offen → Hinweis; übernommen wird erst nach Rückfrage im Panel
  const vintedAbgeholtZahl = useMemo(() => (vintedFristen ? fristenMitPaket(vintedFristen, pakete).abgeholt.length : 0), [vintedFristen, pakete]);
  useEffect(() => {
    if (!vintedAn) { setVintedStoerung(null); setVintedHinweis(null); setVintedFristen(null); return undefined; }
    let lebt = true;
    const pruefe = async () => {
      const s = await vintedStatus();
      if (!lebt || s.reason === 'offline' || s.reason === 'nicht_freigeschaltet' || s.konfiguriert === false) return; // ohne Netz nichts behaupten
      // Nicht angemeldet oder die automatische Verlängerung ist gescheitert → der Shop muss sich neu anmelden
      const stoerung = !s.angemeldet || s.letzterFehler ? 'login' : null;
      setVintedStoerung(stoerung);
      // Was bei Vinted ansteht (nur gelesen): abgelaufene Pakete zurück an den Fahrer, Nachricht mit Handlungsbedarf
      const u = stoerung ? null : await vintedUebersicht();
      if (lebt) setVintedHinweis(u && u.ok ? { rueck: u.zuScannen || 0, post: !!(u.posteingang && u.posteingang.handlung) } : null);
      // Sendungsverfolgung (öffentliche Vinted-Webseite, vom Server abgefragt) — unabhängig von der Anmeldung
      const fr = await vintedFristenLaden();
      if (lebt && fr.ok) setVintedFristen(fr.pakete || []);
    };
    pruefe();
    const iv = setInterval(pruefe, 10 * 60 * 1000);
    return () => { lebt = false; clearInterval(iv); };
  }, [vintedAn, showVintedPanel]);
  // 📦 DPD Pickup: Vorab-Namen (SHIPPED + DELIVERED) beim Start und alle 5 Minuten im Hintergrund aktualisieren
  useEffect(() => {
    if (!dpdAktiv()) return undefined;
    dpdVorladenStart(false).catch(() => {});
    const iv = setInterval(() => {
      if (!document.hidden && dpdAktiv()) dpdVorladenStart(false).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [showDpdPanel]);
  // ⚡ Serienmodus: nach dem Speichern meldet das Formular „nächstes Paket" → neu öffnen
  useEffect(() => {
    const weiter = () => {
      if (!hasCustomFeature('seriesMode')) return;
      setTimeout(() => { resetForm(); setShowNewPackageModal(true); }, 400);
    };
    window.addEventListener('kiosk:series-next', weiter);
    return () => window.removeEventListener('kiosk:series-next', weiter);
    // eslint-disable-next-line
  }, []);
  const [showBatch, setShowBatch] = useState(false); // 📋 Barcode-Stapel
  const [batchItems, setBatchItems] = useState(() => listBatch());
  const [batchCount, setBatchCount] = useState(() => listBatch().length);
  const [batchScannedCount, setBatchScannedCount] = useState(() => listBatch().filter((x) => x.scanned).length);
  const [batchPos, setBatchPosState] = useState(() => getBatchPos());
  const [batchExpected, setBatchExpected] = useState(() => getBatchExpectedCount());
  useEffect(() => {
    const upd = () => {
      const cur = listBatch();
      setBatchItems(cur);
      setBatchCount(cur.length);
      setBatchScannedCount(cur.filter((x) => x.scanned).length);
      setBatchPosState(getBatchPos());
      setBatchExpected(getBatchExpectedCount());
    };
    syncBatchFromServer().catch(() => {});
    const onVis = () => { if (document.visibilityState === 'visible') syncBatchFromServer().catch(() => {}); };
    window.addEventListener('kiosk:batch-changed', upd);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('kiosk:batch-changed', upd);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  // 🔎 Diagnose „Vinted-Kachel aus" (22.10.2026) am 23.10. entfernt: Ursache behoben, sie meldete nur noch jeden Start.
  // Funktionen des Kontos haben sich geändert (z. B. Setup gespeichert) → neu zeichnen, damit
  // Knöpfe wie „Mitarbeiter-Modus“ ohne Neustart erscheinen.
  const [, setFeatureTick] = useState(0);
  useEffect(() => {
    const sync = () => {
      setStaffModeState(isStaffMode());
      try { setShowTabletSearchResults(localStorage.getItem('kiosk_tablet_mode') === '1'); } catch { /* */ }
      // Vinted-/DHL-Kacheln: wurden nur beim Start berechnet — kamen die Kontofunktionen erst danach (Start ohne
      // Netz/abgelaufenes Token), fehlten die Kacheln bis zum Neustart (22.10.2026, Folkert: „Where is vinted button?")
      setVintedAn(vintedAnzeigeAktiv()); setVintedAnnahmeAn(annahmeAktiv());
      setDhlBridgeAn(dhlAktiv()); setDhlAnnahmeAn(dhlAnnahmeAktiv());
      setFeatureTick((n) => n + 1);
    };
    window.addEventListener('kiosk:staff-mode', sync);
    window.addEventListener('kiosk:custom-features', sync);
    return () => { window.removeEventListener('kiosk:staff-mode', sync); window.removeEventListener('kiosk:custom-features', sync); };
  }, []);
  // 👷 Mitarbeiter: Liste beim Start + bei Rückkehr in die App frisch holen; ist niemand angemeldet → nach Name + PIN fragen
  const [showAnmeldung, setShowAnmeldung] = useState(false);
  const [, setMitarbeiterTick] = useState(0);
  useEffect(() => {
    const neu = () => setMitarbeiterTick((n) => n + 1);
    const holen = () => {
      if (!localStorage.getItem('kiosk_jwt_access')) return;
      ladeMitarbeiter().then(() => { if (anmeldungNoetig()) setShowAnmeldung(true); }).catch(() => {});
      ladeNetzwerk().catch(() => {}); // 🏪 merkt sich, ob dieses Konto ein Netzwerk hat (für die Statistik-Seite)
      loadAddressBookFromServer().catch(() => {}); // 📒 Empfänger-Adressbuch für Auto-Vervollständigung & Auto-Notify
    };
    const sichtbar = () => { if (document.visibilityState === 'visible') holen(); };
    holen();
    window.addEventListener('kiosk:mitarbeiter', neu);
    document.addEventListener('visibilitychange', sichtbar);
    return () => { window.removeEventListener('kiosk:mitarbeiter', neu); document.removeEventListener('visibilitychange', sichtbar); };
  }, []);
  // Modal fragt per Event nach dem Import-Dialog (Erfassen eines Vinted-Pakets ohne Namen)
  useEffect(() => {
    const open = () => { if (hasCustomFeature('vintedImport')) setShowVintedImport(true); };
    window.addEventListener('kiosk:open-vinted-import', open);
    return () => window.removeEventListener('kiosk:open-vinted-import', open);
  }, []); // 🖨️ Etikettendrucker-Einstellungen
  const [printDialog, setPrintDialog] = useState(null); // 🖨️ {paket, copies} für Mehrfach-Etikettendruck

  // 🛟 BACKUP-WIEDERHERSTELLUNG
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [availableBackups, setAvailableBackups] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState(null);
  
  // 🛠️ WARTUNGS-MODAL
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  
  // 📊 BACKUP FORTSCHRITTSANZEIGE
  const [backupProgress, setBackupProgress] = useState(null);
  // Format: { type: 'backup'|'restore', step: string, current: number, total: number, percent: number, detail: string }

  // 🔄 Reconnect-Flush: offline ausgegebene/geänderte Pakete (uploaded===false) beim
  // Wieder-Online-Gehen, bei Server-Wiedererreichbarkeit UND regelmäßig im Hintergrund hochladen.
  useEffect(() => {
    let cancelled = false;
    const doFlush = async () => {
      try {
        // Bei Kontowechsel nichts aus dem alten Stand flushen.
        // Im Lokal-Modus (!isCloudSyncAllowed) flusht flushPendingPackages nur bestehende Online-Pakete + Löschungen.
        if (currentUser) {
          const lastIdbUser = localStorage.getItem('kiosk_idb_user');
          if (lastIdbUser && lastIdbUser !== currentUser.uid) return;
        }
        await paketeJetztSpeichern(); // ⏳ gesammelte Änderungen zuerst schreiben, sonst läse der Flush einen alten Stand
        const res = await flushPendingPackages({ anwenden: listeAnwenden });
        if (!cancelled && (res.flushed > 0 || res.uebernommen > 0)) {
          // Liste ist über listeAnwenden schon aktuell — NICHT den Speicherstand zurückholen (könnte älter sein)
          if (res.flushed > 0) {
            setInfoMessage(`✅ ${t('sync.flushed', { count: res.flushed })}`);
            setTimeout(() => { if (isMountedRef.current) setInfoMessage(''); }, 4000);
          }
          console.log(`📤 ${res.flushed} Offline-Paket(e) nachträglich hochgeladen, ${res.uebernommen || 0} vom Server übernommen`);
        }
      } catch (e) { console.warn('Flush-Effekt:', e?.message); }
    };
    if (isOnline) doFlush();
    const onOnline = () => doFlush();
    window.addEventListener('online', onOnline);
    const retryInterval = isOnline ? setInterval(doFlush, 20000) : null;
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      if (retryInterval) clearInterval(retryInterval);
    };
  }, [currentUser, isOnline]);

  // 💡 HILFE-CENTER
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  
  // ⭐ PAYWALL/PREMIUM
  const [showPaywall, setShowPaywall] = useState(false);
  
  // 📬 BENACHRICHTIGUNGSSYSTEM - Wartelisten-Matching
  const [showMatchingModal, setShowMatchingModal] = useState(false);
  const [matchingResults, setMatchingResults] = useState([]);
  const [matchingPaket, setMatchingPaket] = useState(null);
  
  // ☁️ CLOUD-BACKUP SYSTEM
  const [lastCloudBackup, setLastCloudBackup] = useState(null);
  const cloudBackupTimerRef = useRef(null);
  
  // 📅 DATUMSBEREICH-FILTER
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFilterFrom, setDateFilterFrom] = useState('');
  const [dateFilterTo, setDateFilterTo] = useState('');
  
  // 🎁 AUSGABE-OVERLAY SYSTEM (für neue Animation)
  const [issuedPackage, setIssuedPackage] = useState(null);
  const [issuedBatchPackages, setIssuedBatchPackages] = useState([]); // 📦 Weitere gleichzeitig ausgegebene Pakete derselben Person
  const [showIssueOverlay, setShowIssueOverlay] = useState(false);
  const [issueCountdown, setIssueCountdown] = useState(2.5);
  const [issueAnimationPhase, setIssueAnimationPhase] = useState('enter'); // enter, display, pack, drop, exit
  const [selectedAnimation, setSelectedAnimation] = useState(null); // Zufällig gewählte Animation
  
  // 📦 MEHRFACH-PAKET DIALOG (Alle ausgeben oder nur 1?)
  const [showMultiPackageDialog, setShowMultiPackageDialog] = useState(false);
  const [multiPackagePaket, setMultiPackagePaket] = useState(null);
  const [multiPackageCountdown, setMultiPackageCountdown] = useState(2);
  const multiPackageTimerRef = useRef(null);

  // 🎨 50+ VERSCHIEDENE ANIMATIONEN - Zufallsauswahl
  const animations = [
    // Klassische Animationen
    { name: 'spin', label: '🔄 Drehen', pack: 'spin 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'bounce', label: '⚽ Hüpfen', pack: 'bounceScale 0.5s ease-out', drop: 'bounceDrop 0.5s ease-in' },
    { name: 'flip', label: '🔃 Flip', pack: 'flip3D 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'zoom', label: '🔍 Zoom', pack: 'zoomOut 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'shake', label: '📳 Schütteln', pack: 'shake 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    
    // Lustige Animationen
    { name: 'wobble', label: '🌊 Wackeln', pack: 'wobble 0.5s ease-in-out', drop: 'wobbleDrop 0.5s ease-in' },
    { name: 'jelly', label: '🍮 Wackelpudding', pack: 'jelly 0.5s ease-in-out', drop: 'jellyDrop 0.5s ease-in' },
    { name: 'rubber', label: '🎈 Gummi', pack: 'rubberBand 0.5s ease-out', drop: 'bounceDrop 0.5s ease-in' },
    { name: 'swing', label: '🎪 Schaukeln', pack: 'swing 0.5s ease-in-out', drop: 'swingDrop 0.5s ease-in' },
    { name: 'tada', label: '🎉 Ta-Da!', pack: 'tada 0.5s ease-out', drop: 'fallDown 0.5s ease-in' },
    
    // Rotations-Varianten
    { name: 'spinX', label: '↔️ X-Drehen', pack: 'spinX 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'spinY', label: '↕️ Y-Drehen', pack: 'spinY 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'spinDouble', label: '🌀 Doppel-Spin', pack: 'spinDouble 0.5s ease-in-out', drop: 'spiralDrop 0.5s ease-in' },
    { name: 'tornado', label: '🌪️ Tornado', pack: 'tornado 0.5s ease-in-out', drop: 'spiralDrop 0.5s ease-in' },
    { name: 'spiral', label: '🍥 Spirale', pack: 'spiral 0.5s ease-in-out', drop: 'spiralDrop 0.5s ease-in' },
    
    // Zoom-Varianten
    { name: 'explode', label: '💥 Explodieren', pack: 'explode 0.5s ease-out', drop: 'implode 0.5s ease-in' },
    { name: 'implode', label: '🎯 Implodieren', pack: 'implode 0.5s ease-in', drop: 'fallDown 0.5s ease-in' },
    { name: 'pulse', label: '💓 Puls', pack: 'pulseGrow 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'heartbeat', label: '❤️ Herzschlag', pack: 'heartbeat 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'breathe', label: '😮 Atmen', pack: 'breathe 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    
    // Slide-Animationen
    { name: 'slideLeft', label: '⬅️ Links weg', pack: 'slideOutLeft 0.5s ease-in', drop: 'slideInFromBottom 0.5s ease-out' },
    { name: 'slideRight', label: '➡️ Rechts weg', pack: 'slideOutRight 0.5s ease-in', drop: 'slideInFromBottom 0.5s ease-out' },
    { name: 'slideUp', label: '⬆️ Hoch weg', pack: 'slideOutUp 0.5s ease-in', drop: 'fallDown 0.5s ease-in' },
    { name: 'slideDown', label: '⬇️ Runter', pack: 'slideOutDown 0.5s ease-in', drop: 'slideInFromBottom 0.5s ease-out' },
    
    // Spezialeffekte
    { name: 'portal', label: '🌀 Portal', pack: 'portalOut 0.5s ease-in', drop: 'portalIn 0.5s ease-out' },
    { name: 'teleport', label: '✨ Teleport', pack: 'teleportOut 0.5s ease-in', drop: 'teleportIn 0.5s ease-out' },
    { name: 'matrix', label: '💚 Matrix', pack: 'matrixGlitch 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'glitch', label: '📺 Glitch', pack: 'glitch 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'vortex', label: '🌪️ Wirbel', pack: 'vortex 0.5s ease-in', drop: 'spiralDrop 0.5s ease-in' },
    
    // Fun-Animationen
    { name: 'rocket', label: '🚀 Rakete', pack: 'rocketLaunch 0.5s ease-in', drop: 'rocketLand 0.5s ease-out' },
    { name: 'balloon', label: '🎈 Ballon', pack: 'floatAway 0.5s ease-out', drop: 'popAndFall 0.5s ease-in' },
    { name: 'magic', label: '✨ Magie', pack: 'magicSpin 0.5s ease-out', drop: 'magicAppear 0.5s ease-out' },
    { name: 'disco', label: '🕺 Disco', pack: 'discoSpin 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'wave', label: '🌊 Welle', pack: 'wave 0.5s ease-in-out', drop: 'waveDrop 0.5s ease-in' },
    
    // Cartoon-Animationen
    { name: 'squash', label: '📦 Quetschen', pack: 'squash 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'stretch', label: '📏 Strecken', pack: 'stretch 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'cartoonBounce', label: '🎪 Cartoon-Bounce', pack: 'cartoonBounce 0.5s ease-out', drop: 'cartoonDrop 0.5s ease-in' },
    { name: 'elastic', label: '🎸 Elastisch', pack: 'elastic 0.5s ease-out', drop: 'elasticDrop 0.5s ease-in' },
    { name: 'spring', label: '🔧 Feder', pack: 'springCompress 0.5s ease-out', drop: 'springBounce 0.5s ease-out' },
    
    // Rotate-Varianten
    { name: 'rollLeft', label: '🔄 Links rollen', pack: 'rollOutLeft 0.5s ease-in', drop: 'rollInFromBottom 0.5s ease-out' },
    { name: 'rollRight', label: '🔄 Rechts rollen', pack: 'rollOutRight 0.5s ease-in', drop: 'rollInFromBottom 0.5s ease-out' },
    { name: 'helicopter', label: '🚁 Helikopter', pack: 'helicopterSpin 0.5s linear', drop: 'helicopterLand 0.5s ease-out' },
    { name: 'propeller', label: '🌀 Propeller', pack: 'propellerSpin 0.5s linear', drop: 'fallDown 0.5s ease-in' },
    
    // Physik-Animationen
    { name: 'gravity', label: '🌍 Schwerkraft', pack: 'floatUp 0.5s ease-out', drop: 'gravityFall 0.5s cubic-bezier(0.55, 0.085, 0.68, 0.53)' },
    { name: 'pendulum', label: '⏰ Pendel', pack: 'pendulumSwing 0.5s ease-in-out', drop: 'fallDown 0.5s ease-in' },
    { name: 'boomerang', label: '🪃 Bumerang', pack: 'boomerangOut 0.5s ease-in-out', drop: 'boomerangIn 0.5s ease-out' },
    { name: 'slingshot', label: '🎯 Schleuder', pack: 'slingshotPull 0.5s ease-in', drop: 'slingshotRelease 0.5s cubic-bezier(0.6, -0.28, 0.735, 0.045)' },
    
    // Mehr Fun
    { name: 'confetti', label: '🎊 Konfetti', pack: 'confettiExplode 0.5s ease-out', drop: 'confettiFall 0.5s ease-in' },
    { name: 'firework', label: '🎆 Feuerwerk', pack: 'fireworkLaunch 0.5s ease-out', drop: 'fireworkExplode 0.5s ease-in' },
    { name: 'snowflake', label: '❄️ Schneeflocke', pack: 'snowflakeFloat 0.5s ease-in-out', drop: 'snowflakeFall 0.5s linear' },
    { name: 'leaf', label: '🍃 Blatt', pack: 'leafFloat 0.5s ease-in-out', drop: 'leafFall 0.5s ease-in' },
    { name: 'feather', label: '🪶 Feder', pack: 'featherFloat 0.5s ease-out', drop: 'featherDrift 0.5s ease-in-out' }
  ];

  // 📦 PACKAGE COUNTER SYSTEM - Hook verwenden statt lokaler States
  const {
    packageCounter,
    showCounterInput,
    counterInputValue,
    receiptCodes,
    receiptPages,
    removeReceiptPage,
    receiptScanning,
    showReceiptChecklist,
    setShowReceiptChecklist,
    missingReceiptSheets,
    receiptDocsStatus,
    receiptDocsFileName,
    connectAndSaveReceiptToDocs,
    initializeCounter,
    decrementCounter,
    resetCounter,
    toggleCounterInput,
    handleCounterInputChange,
    confirmCounterInput,
    scanDeliveryReceiptFiles,
    getCounterColor,
  } = usePackageCounter(setInfoMessage);
  const receiptCameraInputRef = useRef(null);
  const receiptFileInputRef = useRef(null);

  const [visibleSections, setVisibleSections] = useState({
  basicInfo: true,      // Name, Straße etc.
  deliveryInfo: true,   // Tracking-Nummer
  shelfInfo: false,     // Regalplatz - standardmäßig ausgeblendet
  dateInfo: false,      // Datum, Ausgegeben, Zurückgeschickt
  photoSection: true    // Foto-Bereich
});

// 📝 DRAFT SYSTEM - State (wird von NewPackageModal via onDraftChange gesetzt)
const [hasDraft, setHasDraft] = useState(false);
const [draftData, setDraftData] = useState(null);
const [isAutoSaving, setIsAutoSaving] = useState(false);
const [showNotifySignupModal, setShowNotifySignupModal] = useState(false);
const [waitingNotifyCount, setWaitingNotifyCount] = useState(0);

// �️ RACE CONDITION FIX: Cleanup bei Unmount
useEffect(() => {
  isMountedRef.current = true;
  return () => {
    isMountedRef.current = false;
    console.log('🛡️ AddPackageWithAI unmounted - async callbacks werden blockiert');
  };
}, []);

// 🖨️ Client-Info an den Server melden (für Admin-Sicht): Drucker eingerichtet? Auto-Druck? Plattform?
// Rein informativ, keine PII (KEINE Bluetooth-Adresse) — nur ja/nein + Plattform/Version.
useEffect(() => {
  const melden = () => {
    try {
      if (!getAuthUser() || !navigator.onLine) return;
      serverDataService.updateUser({
        clientInfo: {
          printerConfigured: !!getPrinterConfig()?.address,
          autoPrint: isAutoPrintEnabled(),
          printerSupported: isPrinterSupported(),
          printerTyp: getPrinterLang(),
          druck: druckZeitenZusammenfassung(), // ⏱️ Median Verbinden/Senden/Gesamt der letzten 30 Drucke (23.10.2026)
          platform: (window.Capacitor?.getPlatform?.() || 'web'),
          appVersion: APP_VERSION,
          reportedAt: new Date().toISOString(),
        },
      }).catch(() => {});
    } catch { /* */ }
  };
  const t = setTimeout(melden, 4000);
  window.addEventListener('kiosk:druck-zeiten', melden); // alle 10 Drucke neu melden
  return () => { clearTimeout(t); window.removeEventListener('kiosk:druck-zeiten', melden); };
}, []);

// ⚠️ OFFLINE-WARNUNG: Zeige Banner wenn User kürzlich eingeloggt war (< 76h), jetzt aber nicht
const RECENT_AUTH_THRESHOLD = 76 * 60 * 60 * 1000; // 76 Stunden
useEffect(() => {
  const checkRecentAuth = async () => {
    if (currentUser) {
      setShowNotLoggedInWarning(false);
      return;
    }
    const lastAuth = await get('lastAuthenticatedAt');
    if (lastAuth && (Date.now() - lastAuth) < RECENT_AUTH_THRESHOLD) {
      const stundenSeit = Math.round((Date.now() - lastAuth) / (60 * 60 * 1000));
      setShowNotLoggedInWarning(true);
      console.warn(`⚠️ User war vor ${stundenSeit}h eingeloggt, jetzt aber nicht mehr!`);
    } else {
      setShowNotLoggedInWarning(false);
    }
  };
  checkRecentAuth();
}, [currentUser]);

// � PERFORMANCE: Debounce für Suchfeld - verzögert Filterlogik um 300ms
useEffect(() => {
  // Wenn Suche leer ist, sofort aktualisieren (keine Verzögerung)
  if (search.trim() === '') {
    setDebouncedSearch('');
    return;
  }
  
  // Ansonsten mit 300ms Verzögerung
  if (searchDebounceRef.current) {
    clearTimeout(searchDebounceRef.current);
  }
  
  searchDebounceRef.current = setTimeout(() => {
    if (isMountedRef.current) {
      setDebouncedSearch(search);
    }
  }, 300);
  
  return () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  };
}, [search]);

// 📌 Button-ausgegebene Pakete zurücksetzen wenn Suche sich ändert
useEffect(() => {
  setButtonIssuedPaketIds(new Set());
}, [search]);

// �🔐 Authentication Hook
const {
  currentUser: authUser,
  handleGoogleSignIn: signInWithGoogle,
  handleLogout: logout,
  isAuthenticated,
  authReconnecting,
  retryConnection,
} = useAuthentication(null, (user) => {
  setCurrentUser(user);
  // ⚠️ KEIN syncDataWithSubcollections() hier!
  // Wird bereits im useEffect [currentUser, isOnline] aufgerufen
  // Doppelter Aufruf führte zu doppelten Löschungen!
});

// 🔄 AUTO-UPDATE SYSTEM - DEAKTIVIERT
// Updates kommen über APK/Play Store für Android App
// Browser-User machen einfach Hard Refresh (Ctrl+F5)

// �📦 PACKAGE COUNTER SYSTEM - Funktionen werden jetzt vom Hook bereitgestellt
// initializeCounter, decrementCounter, resetCounter, getCounterColor
// siehe Hook-Destructuring oben

const [tabletSearchResults, setTabletSearchResults] = useState([]);
const [lastTabletSearch, setLastTabletSearch] = useState({
  query: "",
  timestamp: null,
  device: "",
  resultIds: []
});

const tutorialSteps = [
  { description: 'Willkommen! Hier kannst du Pakete hinzufügen.', target: '#add-package-button' },
  { description: 'Hier siehst du dief Liste deiner Pakete.', target: '#package-list' },
  { description: 'Nutze die Suchleiste, um Pakete zu finden.', target: '#search-bar' },
  { description: 'Klicke hier, um dich einzuloggen.', target: '#login-button' },
];
const normalizeDates = (paket) => {
  if (!paket) return paket;
  
  const newPaket = { ...paket };
  
  // Liste aller Felder, die Datumsangaben enthalten können
  const dateFields = ['datum', 'ausgegebenAm', 'zurueckgeschicktAm', 'deletedAt', 'updatedAt'];
  
  dateFields.forEach(field => {
    if (newPaket[field]) {
      try {
        // Konvertiere zu einem standardisierten Format
        const date = new Date(newPaket[field]);
        if (!isNaN(date)) {
          // Speichere als ISO-String für konsistente Verarbeitung
          newPaket[field] = date.toISOString();
        }
      } catch (error) {
        console.warn(`Fehler bei Normalisierung des Datums für Feld ${field}:`, error);
      }
    }
  });
  
  // 🔥 WICHTIG: Entferne alle undefined Werte (Firebase verbietet undefined!)
  // ABER: Konvertiere String-Felder zu leerem String statt zu löschen (Konsistenz!)
  const stringFields = ['name', 'streetAndNumber', 'postalAndCity', 'trackingNumber', 'regalplatz'];
  // ⚠️ WICHTIG: ausgegebenAm, zurueckgeschicktAm, deletedAt sind DATUMS-Felder, KEINE String-Felder!
  
  Object.keys(newPaket).forEach(key => {
    if (newPaket[key] === undefined) {
      // String-Felder: Konvertiere zu leerem String (für Konsistenz)
      if (stringFields.includes(key)) {
        newPaket[key] = '';
      } else {
        // Andere Felder: Lösche sie (z.B. boolean/object Felder)
        delete newPaket[key];
      }
    }
  });
  
  return newPaket;
};

// 🕶️ Device Type Detection & CSS Class
useEffect(() => {
  const updateDeviceType = () => {
    const type = getDeviceType();
    setDeviceType(type);
    
    // Setze CSS-Klasse auf body
    document.body.classList.remove('device-type-quest', 'device-type-android', 'device-type-desktop');
    document.body.classList.add(`device-type-${type}`);
    
    // Log Device Info
    console.log('🕶️ Device erkannt:', getDeviceInfo());
  };
  
  updateDeviceType();
  
  // Storage-Event listener für manuelle Änderungen
  const handleStorageChange = (e) => {
    if (e.key === 'deviceModeOverride') {
      updateDeviceType();
    }
  };
  
  window.addEventListener('storage', handleStorageChange);
  
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
}, []);

// Globale Hilfsfunktion Setup
useEffect(() => {
  // Globale Hilfsfunktion zum Aktualisieren von serverPakete
  window.setServerPaketeGlobal = (data) => {
    setServerPakete(data);
    console.log('Server-Pakete nach Speichern aktualisiert:', data.length);
  };
  
  // 🔔 Globale Toast-Funktion
  window.showToast = (message, type = 'info', duration = 3000) => {
    setGlobalToast({ show: true, message, type, duration });
  };
  
  return () => {
    delete window.setServerPaketeGlobal;
    delete window.showToast;
  };
}, []);

// 🔔 Auto-Hide für globalen Toast
useEffect(() => {
  if (globalToast.show) {
    const timer = setTimeout(() => {
      setGlobalToast({ show: false, message: '', type: '', duration: 3000 });
    }, globalToast.duration);
    return () => clearTimeout(timer);
  }
}, [globalToast.show, globalToast.duration]);

// 💾 FILTER-EINSTELLUNGEN: Laden beim Start
useEffect(() => {
  get('settings-showFilters').then(saved => {
    try {
      const ls = localStorage.getItem('kiosk_show_filters');
      if (ls === '1' || ls === '0') { setShowFilters(ls === '1'); return; }
    } catch { /* */ }
    if (saved !== undefined) setShowFilters(Boolean(saved));
  });
  get('settings-showOld').then(saved => {
    if (saved !== undefined) setShowOld(saved);
  });
}, []);

// 💾 FILTER-EINSTELLUNGEN: Speichern bei Änderung (auch in localStorage für Konto-Profil-Sync)
useEffect(() => {
  set('settings-showFilters', showFilters);
  try { localStorage.setItem('kiosk_show_filters', showFilters ? '1' : '0'); } catch { /* */ }
}, [showFilters]);

useEffect(() => {
  try { localStorage.setItem('kiosk_show_details', showDetails ? '1' : '0'); } catch { /* */ }
}, [showDetails]);

useEffect(() => {
  try { localStorage.setItem('kiosk_show_images', showImages ? '1' : '0'); } catch { /* */ }
}, [showImages]);

useEffect(() => {
  try { localStorage.setItem('kiosk_show_sender', showSender ? '1' : '0'); } catch { /* */ }
}, [showSender]);

useEffect(() => {
  set('settings-showOld', showOld);
}, [showOld]);

// 📋 CHRONIK: Suchen nach 5 Sekunden ohne Eingabe loggen
useEffect(() => {
  // Lösche vorherigen Timeout
  if (searchLogTimeoutRef.current) {
    clearTimeout(searchLogTimeoutRef.current);
  }
  
  // Nur loggen wenn mindestens 2 Zeichen
  if (search.trim().length >= 2) {
    searchLogTimeoutRef.current = setTimeout(async () => {
      await logSuche(search);
      console.log('📋 Chronik: Suche geloggt:', search);
    }, 5000); // 5 Sekunden
  }
  
  return () => {
    if (searchLogTimeoutRef.current) {
      clearTimeout(searchLogTimeoutRef.current);
    }
  };
}, [search]);

// Effekt zur Deduplizierung der Pakete nach jeder Änderung
useEffect(() => {
  if (pakete.length === 0) return;
  
  // Sammle Pakete nach ID
  const uniquePackages = {};
  const duplicateIds = new Set();
  
  // Identifiziere Duplikate
  pakete.forEach(paket => {
    if (paket.id) {
      if (uniquePackages[paket.id]) {
        duplicateIds.add(paket.id);
      } else {
        uniquePackages[paket.id] = paket;
      }
    }
  });
  
  // Wenn Duplikate gefunden wurden
  if (duplicateIds.size > 0) {
    console.warn(`${duplicateIds.size} doppelte Paket-IDs gefunden. Führe Konsolidierung durch...`);
    
    // 📋 Zeige die Duplikate in der Konsole an (zum manuellen Prüfen)
    console.log('📋 Doppelte Paket-IDs:');
    duplicateIds.forEach(id => {
      const dupes = pakete.filter(p => p.id === id);
      console.log(`  ID: ${id}`);
      dupes.forEach((p, i) => {
        console.log(`    [${i+1}] Name: "${p.name}", Status: ${p.status}, Datum: ${p.datum}, Updated: ${p.updatedAt || p.lastUpdated || 'n/a'}`);
      });
    });
    
    const consolidated = Object.values(
      pakete.reduce((acc, paket) => {
        if (!paket.id) return acc;
        
        // Wenn noch kein Paket mit dieser ID existiert oder das aktuelle Paket neuer ist
        if (!acc[paket.id] || new Date(paket.updatedAt || 0) > new Date(acc[paket.id].updatedAt || 0)) {
          acc[paket.id] = paket;
        }
        return acc;
      }, {})
    );
    
    // Aktualisiere Pakete lokal + Hash + Firebase-Duplikate entfernen
    setPakete(consolidated);
    set('pakete', consolidated);
    console.log(`Konsolidierung abgeschlossen. ${pakete.length} -> ${consolidated.length} Pakete.`);
    
    // 🔧 FIX: Hash nach Konsolidierung aktualisieren!
    // Ohne diesen Fix divergiert der lokale Hash vom Firebase-Hash
    // → Full-Sync bei jedem App-Start
    (async () => {
      try {
        const newHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(consolidated));
        await set('packagesHash', newHash);
        console.log(`📦 Hash nach Konsolidierung aktualisiert: ${newHash.substring(0, 8)}...`);
        
        // Server: Duplikate löschen + Hash aktualisieren (nur wenige Writes!)
        if (getAuthUser() && navigator.onLine) {
          // Finde die entfernten Paket-IDs (die Duplikate die nicht im consolidated sind)
          const consolidatedIds = new Set(consolidated.map(p => p.id));
          const removedPakete = pakete.filter(p => !consolidatedIds.has(p.id) || 
            consolidated.find(c => c.id === p.id) !== p);
          
          // Nur die EXTRA Einträge (die nicht im consolidated Set sind) löschen
          // Da Duplikate die gleiche ID haben, müssen wir nichts aus Firebase löschen
          // (Firebase hat pro ID nur 1 Dokument). Nur den Hash updaten.
          await updatePackagesHash(consolidated);
          console.log(`📦 Firebase-Hash nach Konsolidierung aktualisiert (${consolidated.length} Pakete)`);
        }
      } catch (err) {
        console.warn('⚠️ Hash-Update nach Konsolidierung fehlgeschlagen:', err);
      }
    })();
  }
}, [pakete.length]); // Abhängigkeit nur von der Länge, um Endlosschleifen zu vermeiden

// ☁️ CLOUD-BACKUP TIMER: Prüfe alle 5 Minuten ob Backup-Zeit
// 🔧 v1.48 FIX: `pakete` aus Dependencies entfernt!
// Problem war: Jede Paket-Änderung (setPakete) re-triggerte diesen Effect,
// was den Timer ständig neu erstellte und checkScheduledBackup() aufruft.
// Pakete werden über IndexedDB geladen, nicht über den State.
useEffect(() => {
  // Lade letztes Backup-Datum beim Start
  get('lastCloudBackup').then(lastBackup => {
    if (lastBackup) {
      setLastCloudBackup(lastBackup);
    }
  });
  
  // Erste Prüfung nach kurzer Verzögerung (damit Pakete geladen sind)
  const initialTimer = setTimeout(() => {
    checkScheduledBackup();
  }, 10000); // 10s Verzögerung statt sofort
  
  // Timer: Prüfe alle 5 Minuten
  cloudBackupTimerRef.current = setInterval(() => {
    checkScheduledBackup();
  }, 5 * 60 * 1000); // 5 Minuten
  
  return () => {
    clearTimeout(initialTimer);
    if (cloudBackupTimerRef.current) {
      clearInterval(cloudBackupTimerRef.current);
    }
  };
}, [currentUser, isOnline]); // 🔧 v1.48 FIX: Nur stabile Dependencies!

// ============================================
// 💾 PAKET SPEICHERN & VALIDIEREN
// ============================================
// 🖨️ Auto-Etikettendruck nach dem Speichern eines NEUEN Pakets.
// Einzelpaket → still drucken; Mehrfach-Paket → Dialog mit Bestätigung je Etikett.
const triggerAutoPrintLabel = (paket) => {
  if (!isPrinterSupported() || !isAutoPrintEnabled() || !getPrinterConfig()?.address) return;
  if (getCopies(paket) > 1) {
    setPrintDialog({ paket, copies: getCopies(paket) });
  } else {
    printOne(paket).catch(e => {
      setInfoMessage('🖨️ ' + t('ui.main.printFailed') + ': ' + (e?.message || ''));
      setTimeout(() => isMountedRef.current && setInfoMessage(''), 5000);
      // Ins Fehlerprotokoll: die Meldung oben ist hinter offenen Dialogen (z. B. „Vinted erfassen") nicht zu sehen
      reportError('druck_fehler', String(e?.message || e || '').slice(0, 300), { auto: true });
    });
  }
};

// 🖨️ Manueller Nachdruck (Button in der Liste, z.B. wenn Papier leer war).
// Ignoriert den Auto-Druck-Schalter, braucht aber einen eingerichteten Drucker.
const triggerManualPrintLabel = (paket) => {
  if (!isPrinterSupported() || !getPrinterConfig()?.address) {
    setInfoMessage('🖨️ ' + t('ui.main.noPrinterSetup'));
    setTimeout(() => isMountedRef.current && setInfoMessage(''), 4000);
    return;
  }
  if (getCopies(paket) > 1) {
    setPrintDialog({ paket, copies: getCopies(paket) });
  } else {
    setInfoMessage('🖨️ ' + t('ui.main.printingLabel'));
    printOne(paket)
      .then(() => { setInfoMessage('🖨️ ' + t('ui.main.labelPrinted') + ' ✓'); setTimeout(() => isMountedRef.current && setInfoMessage(''), 3000); })
      .catch(e => { setInfoMessage('🖨️ ' + t('ui.main.printFailed') + ': ' + (e?.message || '')); setTimeout(() => isMountedRef.current && setInfoMessage(''), 5000); });
  }
};

// 🖨️ Nachdruck-Anforderung aus der Paket-Liste (Event von PackageListItem)
useEffect(() => {
  const handler = (e) => triggerManualPrintLabel(e.detail);
  window.addEventListener('kiosk:reprint-label', handler);
  return () => window.removeEventListener('kiosk:reprint-label', handler);
}, []);

const validateAndSavePaket = async (paket) => {
  // ⚠️ OFFLINE-CHECK: Warnung wenn User kürzlich eingeloggt war aber jetzt nicht
  if (!currentUser) {
    const lastAuth = await get('lastAuthenticatedAt');
    if (lastAuth && (Date.now() - lastAuth) < RECENT_AUTH_THRESHOLD) {
      const stundenSeit = Math.round((Date.now() - lastAuth) / (60 * 60 * 1000));
      const weiter = window.confirm(
        `⚠️ ${t('ui.main.notLoggedInWarn')}\n\n` +
        `${t('ui.main.wasLoggedInHoursAgo', { hours: stundenSeit })}\n` +
        `${t('ui.main.savedOnlyLocally')}\n\n` +
        `${t('ui.main.saveAnyway')}`
      );
      if (!weiter) return;
    }
  }

  // 🚦 Rate Limiting: Verhindere zu viele Firebase-Writes
  const now = Date.now();
  const timeSinceLastWrite = now - lastFirebaseWrite.current;
  
  if (timeSinceLastWrite < FIREBASE_WRITE_COOLDOWN) {
    const waitTime = FIREBASE_WRITE_COOLDOWN - timeSinceLastWrite;
    console.log(`⏸️ Rate Limit: Warte ${waitTime}ms vor nächstem Write`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  
  lastFirebaseWrite.current = Date.now();
  
  // Stelle sicher, dass die ID vorhanden ist
  if (!paket.id) {
    paket.id = window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  }
  
  // Generiere immer einen aktuellen Zeitstempel
  paket.updatedAt = new Date().toISOString();
  
  console.log(`📝 Speichere Paket: ${paket.name} (ID: ${paket.id})`);
  
  // � v1.48: Legacy-Read entfernt (users/{uid}.data ist seit Subcollection-Migration leer)
  // autoSyncChanges → syncSingleRequest macht Smart-Diffing gegen serverPakete-Cache
  const serverDaten = pakete; // Lokale Daten = Basis für Merge
  
  // Lokales Update durchführen
  let neuePakete;
  const existingIndex = pakete.findIndex(p => p.id === paket.id);
  
  if (existingIndex >= 0) {
    // Update: Ersetze das vorhandene Paket
    if (istOnlinePaket(pakete[existingIndex]) || istOnlinePaket(paket)) {
      paket.inCloud = true;
    }
    neuePakete = pakete.map(p => p.id === paket.id ? paket : p);
    console.log(`📝 Paket mit ID ${paket.id} aktualisiert (lokal).`);
  } else {
    // Neu: Füge das neue Paket hinzu
    neuePakete = [...pakete, paket];
    console.log(`✅ Neues Paket mit ID ${paket.id} hinzugefügt (lokal).`);
    // 🖨️ Etikett drucken (nur wenn Drucker konfiguriert + Auto-Druck an;
    // blockiert/bricht den Speichervorgang nie ab)
    triggerAutoPrintLabel(paket);
  }
  
  // 🔄 Offline: als "noch nicht hochgeladen" markieren, damit das Paket den
  // Full-Sync-Drop überlebt (packageSyncHelper.js:507) und der Reconnect-Flush es
  // später hochlädt. Online setzt autoSyncChanges/syncSingleRequest das Flag auf true.
  if (!isOnline) paket.uploaded = false;

  // Speichere die aktualisierten Pakete lokal
  setPakete(neuePakete);
  await set('pakete', neuePakete);
  console.log(`💾 Lokal gespeichert: ${neuePakete.length} Pakete`);
  
  // 🔄 SERVER = MASTER: Merge geändertes Paket in SERVER-Daten
  if (isOnline && currentUser) {
    try {
      // ⚠️ SICHERHEITSCHECK: Verhindere Datenverlust wenn Server-Daten unvollständig
      if (pakete.length > 50 && serverDaten.length < 10) {
        console.warn("🚨 UPLOAD ABGEBROCHEN: Server hat nur", serverDaten.length, "Pakete, aber lokal sind", pakete.length, "Pakete!");
        console.warn("🚨 Dies würde zu massivem Datenverlust führen - verwende lokale Daten als Master!");
        
        // Upload ALLE lokalen Pakete als Master
        const saveResult = await autoSyncChanges(neuePakete);
        if (saveResult) {
          console.log(`✅ SICHERHEITS-UPLOAD: Alle ${neuePakete.length} lokalen Pakete als Master hochgeladen`);
        }
        return neuePakete;
      }
      
      // ✅ OPTIMAL: Lade komplette Server-Daten, merge 1 Paket, upload NUR wenn unterschiedlich
      const serverIndex = serverDaten.findIndex(p => p.id === paket.id);
      let updatedServerDaten;
      let updateBeschreibung;
      
      if (serverIndex >= 0) {
        // Paket existiert auf Server - prüfe ob Änderung nötig
        const serverPaket = serverDaten[serverIndex];
        const hatSichGeaendert = JSON.stringify(serverPaket) !== JSON.stringify(paket);
        
        if (!hatSichGeaendert) {
          console.log(`ℹ️ Paket ${paket.id} ist bereits aktuell auf Server - kein Upload nötig`);
          return neuePakete;
        }
        
        // Update: Ersetze Paket in Server-Daten
        updatedServerDaten = serverDaten.map(p => p.id === paket.id ? paket : p);
        updateBeschreibung = `1 Paket aktualisiert in ${serverDaten.length} Server-Paketen`;
      } else {
        // Neu: Füge zu Server-Daten hinzu
        updatedServerDaten = [...serverDaten, paket];
        updateBeschreibung = `1 neues Paket hinzugefügt zu ${serverDaten.length} Server-Paketen`;
      }
      
      console.log(`📊 Server-Update: ${updateBeschreibung}`);
      console.log(`� Synchronisiere mit Server: ${updatedServerDaten.length} Pakete`);
      
      // Upload zu Server
      const saveResult = await autoSyncChanges(updatedServerDaten);
      
      if (saveResult) {
        console.log(`✅ SERVER-SYNC ERFOLGREICH: Paket ${paket.id} an Server übertragen`);
        
        // 🆕 SOFORT-VERIFIKATION: Lade NUR das einzelne Paket zurück (1 Read statt 100+!)
        console.log(`🔍 Verifiziere Upload: Einzelnes Paket ${paket.id} von Firebase laden...`);
        const verifyStartTime = Date.now();
        try {
          const verifiedPaket = await loadSinglePaket(paket.id); // ✅ 1 Read!
          const verifyDuration = Date.now() - verifyStartTime;
          
          if (verifiedPaket) {
            console.log(`✅ VERIFIZIERT in ${verifyDuration}ms: Paket ${paket.id} erfolgreich von Firebase geladen (1 Read)`);
            console.log(`   → Empfänger: ${verifiedPaket.empfaenger || 'N/A'}`);
            console.log(`   → Grüner Haken wird SOFORT angezeigt (kein Warten auf Polling)`);
            // 🔑 FIX: neuePakete statt pakete (stale closure!) verwenden,
            // und verifiedPaket vom Server nutzen (autoritativste Quelle)
            const updatedPakete = neuePakete.map(p => p.id === paket.id ? { ...verifiedPaket } : p);
            setPakete(updatedPakete);
            setServerPakete(updatedPakete);
            await set('pakete', updatedPakete);
            
            // Visuelles Feedback
            setInfoMessage(`✅ ${t('packages.savedAndVerified')}`);
            setTimeout(() => setInfoMessage(''), 2000);
          } else {
            console.warn(`⚠️ Paket ${paket.id} nicht in Firebase gefunden - verwende lokale Daten`);
            console.warn(`   → Polling wird es in max. 10 Sekunden abholen`);
          }
        } catch (verifyError) {
          console.warn(`⚠️ Verifikation fehlgeschlagen:`, verifyError.message);
          console.warn(`   → Polling wird es in max. 10 Sekunden abholen`);
          // Bei Fehler: Nutze lokale Daten (altes Verhalten)
        }
        
        return neuePakete;
      } else {
        console.warn(`⚠️ Server-Sync fehlgeschlagen, lokale Daten bleiben erhalten`);
        setInfoMessage(`⚠️ ${t('packages.savedLocallyOffline')}`);
        setTimeout(() => setInfoMessage(''), 5000);
        return neuePakete;
      }
      
    } catch (error) {
      console.error(`❌ SERVER-SYNC-FEHLER:`, error);
      
      // Bei Server-Fehlern: Warnung an Benutzer
      const errorMsg = error.message.includes('quota') || error.message.includes('size') ? 
        t('errors.databaseTooLarge') :
        t('errors.serverConnectionFailed');
        
      console.warn(`⚠️ ${errorMsg} - Paket nur lokal gespeichert`);
      
      // Lokale Daten bleiben erhalten, aber Warnung ausgeben
      setInfoMessage(`⚠️ ${t('packages.savedLocally')} - ${errorMsg}`);
      setTimeout(() => setInfoMessage(''), 8000);
      
      return neuePakete;
    }
  }
  
  return neuePakete;
};

const deepEqual = (obj1, obj2) => {
  // Handle null/undefined cases
  if (obj1 === null || obj2 === null || obj1 === undefined || obj2 === undefined) {
    return obj1 === obj2;
  }
  
  // Handle primitive types
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }
  
  // Compare object keys
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  // Skip comparison of updatedAt field as it often differs
  const filteredKeys1 = keys1.filter(key => key !== 'updatedAt');
  const filteredKeys2 = keys2.filter(key => key !== 'updatedAt');
  
  // If they have different number of keys (excluding updatedAt), they're not equal
  if (filteredKeys1.length !== filteredKeys2.length) {
    return false;
  }
  
  // Check each key
  for (const key of filteredKeys1) {
    if (key === 'updatedAt') continue;
    
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }
  
  return true;
};

// ============================================
// 🔐 AUTHENTICATION
// ============================================
// Wrapper für handleGoogleSignIn - Hook-Funktion mit Event-Handling
const handleGoogleSignIn = async (e) => {
  if (e) e.preventDefault();
  try {
    await signInWithGoogle();
  } catch (err) {
    // Früher ging der Fehler ins Leere → „Login-Knopf tut nichts". Jetzt sichtbar machen.
    const msg = String((err && (err.message || err.code)) || err || '');
    if (/cancel|abgebrochen/i.test(msg)) { reportError('login_abgebrochen', msg, { code: err && err.code ? String(err.code) : null }); return; } // kein Hinweis, aber zaehlen
    reportError('login_fehler', msg, { code: err && err.code ? String(err.code) : null });
    alert(t('auth.loginFailedDetail', { error: msg || '?' }));
  }
};

// Auth-Initialisierung wird jetzt vom useAuthentication Hook übernommen
// Echtzeit-Listener für Tablet-Suchen
// 🔧 v1.48 FIX: Dependencies reduziert! recentTabletSearches.length und showTabletSearchResults
// verursachten Endlos-Loop: Listener feuert → setzt State → Dependency ändert sich → 
// neuer Listener → feuert sofort → setzt State → ... → tausende Firebase Reads!
// Jetzt: Nur [currentUser, isOnline] als Dependencies (Listener einmal registrieren, fertig)
useEffect(() => {
  if (!currentUser || !isOnline) {
    sseSearchTriggerRef.current = null;
    return;
  }
  
  console.log("Starte Tablet-Suchen (SSE + Fallback-Polling)...");
  
  let cancelled = false;
  
  // Filter-Funktion: Nur Suchen der letzten Stunde
  const filterRecentSearches = (allSearches) => {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    return allSearches.filter(s => s.timestamp && s.timestamp > oneHourAgo);
  };
  
  // Suchen vom Server laden
  const loadSearches = async () => {
    try {
      const result = await serverDataService.getRecentSearches();
      if (cancelled) return;
      
      if (result?.searches) {
        const searches = filterRecentSearches(result.searches);
        setRecentTabletSearches(searches);
      }
    } catch (error) {
      if (!cancelled) console.error("Fehler beim Laden der Tablet-Suchen:", error);
    }
  };
  
  // 🔌 SSE-Trigger-Ref setzen
  sseSearchTriggerRef.current = loadSearches;
  
  // Initial laden
  loadSearches();
  
  // 🕐 Polling alle 10s
  const timer = setInterval(loadSearches, 10000);
  
  return () => {
    cancelled = true;
    sseSearchTriggerRef.current = null;
    clearInterval(timer);
  };
}, [currentUser, isOnline]);

// 🔴 LIVE-SUCHE: Echtzeit via SSE + Fallback-Polling
useEffect(() => {
  if (!currentUser) {
    console.log("🔴 Live-Suche NICHT gestartet - kein User angemeldet");
    sseLiveSearchTriggerRef.current = null;
    return;
  }
  
  // ⚠️ QUEST-FEATURE: Deaktiviert wenn nicht genutzt
  if (!QUEST_FEATURES.LIVE_SEARCH_ENABLED) {
    console.log("🔴 Live-Suche deaktiviert (QUEST_FEATURES.LIVE_SEARCH_ENABLED = false)");
    sseLiveSearchTriggerRef.current = null;
    return;
  }
  
  console.log("🔴 Starte Live-Suche (SSE + Fallback-Polling)...");
  
  let cancelled = false;
  let lastTimestamp = 0; // Epoch-ms, wird beim Empfang aktualisiert
  
  // Handler für Live-Suche (wird von SSE und Polling genutzt)
  const handleLiveSearch = (liveSearch) => {
    console.log('🔴 DEBUG handleLiveSearch aufgerufen:', JSON.stringify(liveSearch), 'cancelled:', cancelled);
    if (cancelled || !liveSearch) {
      console.log('🔴 DEBUG handleLiveSearch ABGEBROCHEN:', cancelled ? 'cancelled' : 'kein liveSearch');
      return;
    }
    
    // Timestamp normalisieren (Server sendet ISO-String)
    const eventTs = typeof liveSearch.timestamp === 'string'
      ? new Date(liveSearch.timestamp).getTime()
      : (liveSearch.timestamp || 0);
    
    console.log('🔴 DEBUG Timestamp-Check:', { eventTs, lastTimestamp, query: liveSearch.query, queryLen: liveSearch.query?.trim()?.length });
    
    if (liveSearch.query && 
        liveSearch.query.trim().length >= 2 && 
        eventTs > lastTimestamp) {
      
      console.log('🔴 ✅ NEUE Live-Suche:', liveSearch.query);
      lastTimestamp = eventTs;
      setSearch(liveSearch.query);
      setShowTabletSearchResults(true);
    } else {
      console.log('🔴 ⚠️ Live-Suche IGNORIERT:', !liveSearch.query ? 'kein query' : liveSearch.query.trim().length < 2 ? 'zu kurz' : 'alter Timestamp');
    }
  };
  
  // 🔌 SSE-Trigger-Ref setzen (bekommt liveSearch-Daten direkt vom SSE-Event)
  sseLiveSearchTriggerRef.current = handleLiveSearch;
  
  // SSE ist primär — kein Polling mehr nötig (GET /searches/live existiert nicht)
  
  return () => {
    cancelled = true;
    sseLiveSearchTriggerRef.current = null;
  };
}, [currentUser]);

  // 🎁 AUSGABE-OVERLAY: Countdown & Animationen
  useEffect(() => {
    if (!showIssueOverlay || issueCountdown <= 0) return;

    const timer = setTimeout(() => {
      setIssueCountdown(Math.max(0, issueCountdown - 0.5));
    }, 500);

    return () => clearTimeout(timer);
  }, [showIssueOverlay, issueCountdown]);

  // 🎁 Wenn Countdown auf 0, starte Verpackungs-Animation
  useEffect(() => {
    if (showIssueOverlay && issueCountdown === 0 && issueAnimationPhase === 'display') {
      console.log('🎁 Starte Verpackungs-Animation');
      setIssueAnimationPhase('pack');
      
      // Nach Pack-Animation: Fallen lassen (0,5 Sekunden)
      setTimeout(() => {
        setIssueAnimationPhase('drop');
      }, 500);
      
      // Nach Drop-Animation: Overlay schließen (0,5 Sekunden)
      setTimeout(() => {
        setShowIssueOverlay(false);
        setIssuedPackage(null);
        setIssuedBatchPackages([]);
        setIssueAnimationPhase('enter');
        setIssueCountdown(2.5);
      }, 1000);
    }
  }, [showIssueOverlay, issueCountdown, issueAnimationPhase]);
  
const findDifferences = (localPakete, remotePakete) => {
  // Ensure we have arrays to work with
  const local = Array.isArray(localPakete) ? localPakete : [];
  const remote = Array.isArray(remotePakete) ? remotePakete : [];
  
  // Create maps for faster lookups
  const localMap = new Map(local.map(paket => [paket.id, paket]));
  const remoteMap = new Map(remote.map(paket => [paket.id, paket]));
  
  // Find items only in local data
  const onlyInLocal = local.filter(paket => !remoteMap.has(paket.id))
    .map(paket => ({...paket}));
  
  // Find items only in remote data
  const onlyInRemote = remote.filter(paket => !localMap.has(paket.id))
    .map(paket => ({...paket}));
  
  // Find modified items (exist in both but with different content)
  const modified = local.filter(localPaket => {
    const remotePaket = remoteMap.get(localPaket.id);
    if (!remotePaket) return false;
    
    // Compare relevant fields, ignoring updatedAt which will often differ
    const relevantFields = ['name', 'streetAndNumber', 'postalAndCity', 'trackingNumber', 
                           'regalplatz', 'datum', 'ausgegebenAm', 'zurueckgeschickt'];
    
    return relevantFields.some(field => 
      JSON.stringify(localPaket[field]) !== JSON.stringify(remotePaket[field])
    );
  }).map(paket => ({
    id: paket.id,
    localItem: {...localMap.get(paket.id)},
    remoteItem: {...remoteMap.get(paket.id)}
  }));
  
  return {
    onlyInLocal,
    onlyInRemote,
    modified,
    hasDifferences: onlyInLocal.length > 0 || onlyInRemote.length > 0 || modified.length > 0
  };
};

useEffect(() => {
  // Vergleiche direkt pakete (State) mit serverPakete (State)
  // Kein IndexedDB-Laden nötig, da beide States synchron sind
  const differences = findDifferences(pakete, serverPakete);
  setSyncDifferences(differences);
  
  console.log(`🔍 Sync-Differences aktualisiert: ${differences.hasDifferences ? 'UNTERSCHIEDE gefunden' : 'KEINE Unterschiede'}`);
  if (differences.hasDifferences) {
    console.log(`   → Nur lokal: ${differences.onlyInLocal.length}, Nur remote: ${differences.onlyInRemote.length}, Geändert: ${differences.modified.length}`);
  }
}, [pakete, serverPakete]); // Aktualisiere, wenn sich `pakete` oder `serverPakete` ändern
// 🧹 Kontodaten im Arbeitsspeicher und in IndexedDB vollständig leeren (für Logout mit Cloud-Sync & Kontowechsel)
const IDB_ACCOUNT_KEYS = [
  'pakete', 'packagesHash', 'lastPackageSync', 'lastFullSyncTime',
  'chronik', 'chronik_last_sync', 'favoriten',
  'statistik', 'statistik_overview_cache',
  'hermes_abrechnungen', 'lastCleanupTimestamp', 'pendingPackageDeletions', 'pending_delete_ids',
];
const leereKontodatenImSpeicher = () => {
  if (speicherTimerRef.current) { clearTimeout(speicherTimerRef.current); speicherTimerRef.current = null; }
  if (hashTimerRef.current) { clearTimeout(hashTimerRef.current); hashTimerRef.current = null; }
  paketeRef.current = [];
  setInternalPakete([]);
  setServerPakete([]);
  syncQueueRef.current = [];
  lastWrittenHashRef.current = null;
  setSelectedIndex(null);
};
const leereKontodatenInIdb = async () => {
  for (const key of IDB_ACCOUNT_KEYS) {
    try { await del(key); } catch { /* ignore */ }
  }
};

// Logout-Funktion
// Wrapper für handleLogout - Hook-Funktion verwenden
const handleLogout = async () => {
  // 👷 Abmelden beendet immer auch den Mitarbeiter-Modus (Login muss erreichbar bleiben)
  try { disableStaffMode(); } catch { /* */ }
  // 🧹 OCR Worker terminieren um Speicher freizugeben
  if (ocrWorkerRef.current) {
    console.log('🧹 Terminiere OCR Worker...');
    await ocrWorkerRef.current.terminate();
    ocrWorkerRef.current = null;
  }

  // Merken, ob dieses Konto Cloud-Sync aktiv hatte (Premium / aktive Testphase / Backdoor),
  // BEVOR logout() den Status zurücksetzt.
  const cloudWarAktiv = isCloudSyncAllowed();
  await paketeJetztSpeichern();

  if (isOnline) {
    try {
      await flushPendingPackages({ anwenden: listeAnwenden });
    } catch { /* ignore */ }
  }

  // Falls noch ungesyncte Pakete (uploaded === false) übrig sind, vor dem Leeren lokal sichern
  const bestand = paketeRef.current || [];
  if (cloudWarAktiv && bestand.some((p) => p && p.uploaded === false)) {
    try { await createBackup(bestand, 'before_logout_unsynced'); } catch { /* */ }
  }

  await logout();

  if (cloudWarAktiv) {
    leereKontodatenImSpeicher();
    await leereKontodatenInIdb();
    console.log('🧹 Kontodaten nach Logout geleert (Cloud-Sync war aktiv)');
  } else {
    console.log('📱 Logout im kostenlosen Lokal-Modus — lokale Pakete bleiben auf dem Gerät erhalten');
  }
};

  // 📊 Statistik: komplett serverseitig (statistics_daily) — der Client hält
  // keinen eigenen Statistik-State mehr. Anzeige: Statistics.js via /overview.

  const videoRef = useRef(null);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const codeReaderRef = useRef(null);
  const [barcodeError, setBarcodeError] = useState(null);

  // NEU: State für OCR-Loading und -Fehler
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');

  // NEU: State für optionale Felder
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  // NEU: State für Bildrotation
  const [rotation, setRotation] = useState(0);

  // NEU: Regalplatz-Filter
  const [regalFilter, setRegalFilter] = useState('');

  const [selectedRegal, setSelectedRegal] = useState(''); // Standardmäßig kein Regal ausgewählt
  const [selectedRegalplatz, setSelectedRegalplatz] = useState(null);
  const [showRegale, setShowRegale] = useState(() => {
    try { return localStorage.getItem('kiosk_show_regale') === '1'; } catch { return false; }
  }); // Regal-Anzeige ein/aus — Standard: aus, per Filter einblendbar
  const [historicalDate, setHistoricalDate] = useState(null); // Datum für historische Regalansicht
  const [favoritenMessage, setFavoritenMessage] = useState(null);

  // 💾 showRegale: Laden beim Start
  // Schlüssel bewusst auf -v2 gebumpt: der alte Schlüssel hatte auf bestehenden Geräten
  // noch das frühere Default „true" gespeichert und überschrieb den neuen Default (aus).
  // Mit neuem Schlüssel startet jeder frisch bei „aus", kann aber im Filter einschalten.
  useEffect(() => {
    get('settings-showRegale-v2').then(saved => {
      try {
        const ls = localStorage.getItem('kiosk_show_regale');
        if (ls === '1' || ls === '0') { setShowRegale(ls === '1'); return; }
      } catch { /* */ }
      if (saved !== undefined) setShowRegale(Boolean(saved));
    });
  }, []);

  // 💾 showRegale: Speichern bei Änderung (auch in localStorage für Konto-Profil-Sync)
  useEffect(() => {
    set('settings-showRegale-v2', showRegale);
    try { localStorage.setItem('kiosk_show_regale', showRegale ? '1' : '0'); } catch { /* */ }
  }, [showRegale]);
  
function PackageListItem({ paket, idx, onSelect, onAusgeben, onZurueckschicken, onDelete, searchLower, formatDateTime, highlightMatch, texts, lang, colors, isOnline, isPaketSynced, handleTouchStart, handleTouchEnd, showOld, showPrintLabels, showImages, showDetails, showSender, t }) {
  // Prüfen, ob dieses Paket gerade animiert wird
  const isAnimating = animatingPaketIds.has(paket.id);
  const isButtonIssued = buttonIssuedPaketIds.has(paket.id); // 📌 Per Button ausgegeben
  const isDone = paket.ausgegebenAm || paket.zurueckgeschickt;
  const [swipeProgress, setSwipeProgress] = useState(0);
  
  // 💰 Hat dieses Paket einen Betrag (Nachnahme)?
  const hatBetrag = paket.betrag && parseFloat(paket.betrag) > 0;
  // 🔄 Wurde dieses Paket bei aktiver Suche NUR über den Absender gefunden?
  const senderOnlyMatch = isSenderOnlyMatch(paket, searchLower);
  // 👥 Wurde dieses Paket an einen Bevollmächtigten ausgegeben? -> Gelb markieren statt Grün!
  const isBevollmIssued = Boolean(
    paket.ausgegebenAm && (paket.abholArt === 'bevollmaechtigter' || paket.bevollmaechtigter?.nachname)
  );
  
  // Long-Press entfernt - nur noch Button zum Bearbeiten
  let background;

  if (paket.zurueckgeschickt) {
    background = '#b26a00';
  } else if (isBevollmIssued) {
    // 👥 An Bevollmächtigten ausgegeben → kräftig Gelb/Gold statt Grün
    background = '#ca8a04';
  } else if (isButtonIssued) {
    // 📌 Gerade per Button ausgegeben → leuchtend grün mit Rahmen
    background = '#2e7d32';
  } else if (paket.ausgegebenAm) {
    background = '#43a047';
  } else if (hatBetrag) {
    // 💰 ROT bei Nachnahme-Paketen!
    background = colors.darkMode ? '#5c2020' : '#ffebee';
  } else if (senderOnlyMatch) {
    // 🔄 Nur über den Absender gefunden → dezent-warme Amber-Hintergrundfarbe
    background = colors.darkMode ? '#332b1a' : '#fff8e6';
  } else {
    background = colors.darkMode ? '#2d323c' : '#fafafa';
  }

  // Berechnung von daysLeft (konfigurierbar über kiosk_delete_days, Standard 5 Tage)
  let daysLeft = null;
  if (paket.ausgegebenAm) {
    const ausgabeDatum = new Date(paket.ausgegebenAm);
    const now = new Date();
    const retentionDays = getConfiguredDeleteDays();
    const retentionMs = retentionDays === 0 ? (60 * 60 * 1000) : (retentionDays * 24 * 60 * 60 * 1000);
    const diffTime = ausgabeDatum.getTime() + retentionMs - now.getTime();
    daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // HIER wird der Hook außerhalb der Map-Funktion verwendet
  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      // ⛔ Blockiere Swipes während Animation läuft
      if (isAnimating) return;
      
      // Ignoriere Swipes, die mehr vertikal als horizontal sind (Scroll-Erkennung)
      // Verschärfte Bedingung: Vertikal darf max 50% der horizontalen Bewegung sein
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) * 0.5) {
        setSwipeProgress(0); // Sofort zurücksetzen bei vertikaler Bewegung
        return;
      }
      
      if (e.deltaX > 0) { // Nur für Swipes nach rechts
        const maxSwipeDistance = 200; // Erhöht für sanftere Bewegung
        const progress = Math.min(Math.max(e.deltaX / maxSwipeDistance, 0), 1);
        // Smooth Easing für den Progress
        const smoothProgress = progress * progress; // Quadratische Kurve
        setSwipeProgress(smoothProgress);
      }
    },
    onSwipedRight: (e) => {
      // ⛔ Blockiere Swipes während Animation läuft
      if (isAnimating) return;
      
      // Nur ausführen, wenn die horizontale Bewegung deutlich ist
      // und nicht hauptsächlich vertikal
      if (e.deltaX > 100 && Math.abs(e.deltaY) < Math.abs(e.deltaX) * 0.5) {
        e.event.stopPropagation();
        e.event.preventDefault();
        setSwipeProgress(1); // Auf 100% setzen für die Animation
        
        //  v1.51: Bei Lagerfrist-Filter → Zurückschicken statt Ausgeben
        if (showOld) {
          onZurueckschicken(paket);
          setTimeout(() => setSwipeProgress(0), 300);
        } else {
          // 📦 Bei Mehrfach-Paketen / Unterschrift / Vinted / DHL: Nur handleAusgeben aufrufen, Overlay wird später gestartet
          const anzahl = paket.paketAnzahl || 1;
          const sigRequired = (() => { try { return localStorage.getItem('kiosk_require_signature') === '1'; } catch { return false; } })();
          // 🧪 Vinted-/DHL-Anbindung oder Unterschrift: erst der Dialog (in handleAusgeben), kein „ausgegeben"-Overlay vorab
          if (anzahl > 1 || sigRequired || (ausgabeAktiv() && istVintedPaket(paket)) || (dhlAusgabeAktiv() && istDhlPaket(paket))) {
            // Mehrfach-Paket: Dialog wird in handleAusgeben gezeigt, Overlay später
            onAusgeben(paket);
            setTimeout(() => setSwipeProgress(0), 300);
          } else {
            // Einzelnes Paket: Normales Verhalten mit sofortigem Overlay
            // 🎁 NEUES OVERLAY-SYSTEM: Zeige Fullscreen-Overlay statt sofort auszugeben
            // 🎲 Wähle zufällige Animation aus
            const randomAnim = animations[Math.floor(Math.random() * animations.length)];
            setSelectedAnimation(randomAnim);
            console.log('🎨 Gewählte Animation:', randomAnim.label);
            
            setIssuedPackage(paket);
            setIssuedBatchPackages([]);
            setShowIssueOverlay(true);
            const hatWeitere = getWeitereOffenePaketeFuerAusgabe(paket).length > 0 || isBevollmaechtigterEnabled();
            setIssueCountdown(hatWeitere ? 5.0 : 2.5);
            setIssueAnimationPhase('enter');
            
            // Paket trotzdem ausgeben (kann mit Undo rückgängig gemacht werden)
            onAusgeben(paket);
            
            // Swipe-Progress nach Animation zurücksetzen
            setTimeout(() => {
              setSwipeProgress(0);
              setIssueAnimationPhase('display'); // Wechsel zu Display-Phase nach Enter-Animation
            }, 700);
          }
        }
      } else {
        // Bei zu kleiner oder zu vertikaler Bewegung: sanft zurückgleiten
        setSwipeProgress(0);
      }
    },
    onTouchEndOrOnMouseUp: () => {
      if (swipeProgress < 0.6) { // Wenn weniger als 60% geswiped wurde
        setSwipeProgress(0); // Zurücksetzen mit CSS-Transition
      }
    },
    preventDefaultTouchmoveEvent: false, // Ändere zu false für besseres Scrolling
    trackMouse: false,
    delta: 15,        // Reduziert für sensiblere Erkennung
    minDistance: 80,  // Erhöht: Nur eindeutige Swipes
    trackTouch: true,
    touchEventOptions: { passive: false } // Für preventDefault
  });

  // Neue Variablen für Doppeltipp + Einzel-Antippen (Details & Unterschrift per Lazy-Load)
  const [lastTap, setLastTap] = useState(0);
  const [itemDetailsOpen, setItemDetailsOpen] = useState(false);
  const [lazySig, setLazySig] = useState(null);
  const [sigLoading, setSigLoading] = useState(false);
  const [sigPreviewOpen, setSigPreviewOpen] = useState(false);
  const detailsVisible = Boolean(showDetails || itemDetailsOpen);
  const hatUnterschrift = Boolean(
    paket.signatureDataUrl || paket.signatureHatBild || paket.abgeholtVonName ||
    paket.vollmachtFoto || paket.vollmachtHatBild || paket.bevollmaechtigter ||
    lazySig?.signatureDataUrl || lazySig?.vollmachtFoto
  );

  useEffect(() => {
    // Nur beim gezielten Antippen eines Pakets (itemDetailsOpen) automatisch nachladen —
    // verhindert beim globalen Filter „Details anzeigen" (showDetails) hunderte parallele Abrufe.
    if (!itemDetailsOpen) return undefined;
    const needsLoad = (paket.signatureHatBild && !paket.signatureDataUrl) || (paket.vollmachtHatBild && !paket.vollmachtFoto);
    if (lazySig || !needsLoad || !paket.id || !isOnline) {
      setSigLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSigLoading(true);
    serverPackageService.loadPackageSignature(paket.id)
      .then((sig) => {
        if (!cancelled && sig) setLazySig(sig);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSigLoading(false);
      });
    return () => { cancelled = true; };
  }, [itemDetailsOpen, paket.id, paket.signatureDataUrl, paket.signatureHatBild, paket.vollmachtFoto, paket.vollmachtHatBild, lazySig, isOnline]);
  
  // Doppeltipp-Handler
  const handleDoubleTap = (e) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    if (now - lastTap < DOUBLE_TAP_DELAY) {
      // Doppeltipp erkannt - öffne Bearbeitungsmodal
      onSelect(paket, idx, true); // true signalisiert, dass das Modal geöffnet werden soll
      e.stopPropagation(); // Verhindere einfachen Klick
    }
    setLastTap(now);
  };

  return (
    <li
      className={`swipeable-item ${isAnimating ? 'package-issuing' : ''}`}
      data-testid={senderOnlyMatch ? 'paket-item-sender-match' : undefined}
      {...swipeHandlers}
      style={{
        cursor: 'pointer',
        background,
        borderRadius: 8,
        marginBottom: 10,
        padding: 12,
        border: isButtonIssued ? '3px solid #66bb6a' : senderOnlyMatch ? '2px solid #f59e0b' : 'none',
        boxShadow: isButtonIssued
          ? '0 0 12px rgba(76, 175, 80, 0.5), 0 2px 8px rgba(0,0,0,0.04)'
          : swipeProgress > 0 
            ? `0 4px 16px ${showOld ? `rgba(178, 106, 0, ${swipeProgress * 0.4})` : `rgba(67, 160, 71, ${swipeProgress * 0.4})`}` 
            : '0 2px 8px rgba(0,0,0,0.04)',
        display: 'flex',
        flexWrap: 'wrap', // 🖼️ Bild-Vorschau (flexBasis:100%) bricht in volle Zeile unter Text+Buttons um
        alignItems: 'center',
        justifyContent: 'space-between',
        color: isButtonIssued ? '#ffffff' : colors.text,
        position: 'relative',
        overflow: 'hidden',
        transform: `translateX(${swipeProgress * 100}px) scale(${1 - swipeProgress * 0.05})`,
        transition: swipeProgress === 0 ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease' : 'none',
        willChange: 'transform',
      }}
      onClick={(e) => {
        // ⛔ Blockiere Klicks während Animation läuft
        if (isAnimating) return;
        
        handleDoubleTap(e);
        // Nach einer kurzen Verzögerung normalen Klick ausführen,
        // falls kein Doppelklick erfolgt
        setTimeout(() => {
          if (Date.now() - lastTap > 300) {
            setItemDetailsOpen((prev) => !prev);
            onSelect(paket, idx);
          }
        }, 310);
      }}
      onTouchStart={(e) => {
        handleTouchStart(e, paket.regalplatz); // Für Regalplatz-Overlay
      }}
      onTouchEnd={(e) => {
        handleTouchEnd(e); // Für Regalplatz-Overlay
      }}
    >
      <div 
  className="swipe-indicator"
  style={{
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: `${swipeProgress * 100}%`,
    background: showOld ? '#b26a00' : '#43a047',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    opacity: swipeProgress > 0 ? 1 : 0,
    transition: swipeProgress === 0 ? 'width 0.3s, opacity 0.3s' : 'none',
    zIndex: 10,
    boxShadow: swipeProgress > 0 ? `0 0 10px ${showOld ? 'rgba(178, 106, 0, 0.5)' : 'rgba(67, 160, 71, 0.5)'}` : 'none',
  }}
>
  <span style={{ 
    fontWeight: 'bold',
    opacity: swipeProgress > 0.3 ? 1 : 0
  }}>{showOld ? (texts[lang]?.return || 'Zurückschicken') : (texts[lang]?.issue || 'Ausgeben')}</span>
</div>
      
      <span style={{ flex: '1 1 0', minWidth: 0 }}>
        {/* 🧪 DEMO-BADGE für Beispiel-Pakete beim Erststart */}
        {paket.isDemo && (
          <span
            data-testid="demo-package-badge"
            style={{
              display: 'inline-block',
              background: '#6366f1',
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: 6,
              padding: '2px 8px',
              marginRight: '8px',
              fontSize: '11px',
              letterSpacing: '0.4px',
            }}
          >
            🧪 DEMO
          </span>
        )}
        {/*  NACHNAHME-BADGE: Rot und auffällig wenn Betrag vorhanden */}
        {hatBetrag && (
          <span style={{
            display: 'inline-block',
            background: '#f44336',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: 6,
            padding: '2px 8px',
            marginRight: '8px',
            fontSize: '12px',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            💰 {parseFloat(paket.betrag).toFixed(2).replace('.', ',')} €
          </span>
        )}
        
        {/* 📦 MEHRFACH-PAKETE: Badge anzeigen wenn mehr als 1 */}
        {paket.paketAnzahl && paket.paketAnzahl > 1 && (
          <span style={{
            display: 'inline-block',
            background: '#3b82f6',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: 6,
            padding: '2px 8px',
            marginRight: '8px',
            fontSize: '12px'
          }}>
            📦×{paket.paketAnzahl}
          </span>
        )}
        
        {/* 🌍 INTERNATIONAL-BADGE: Blau wenn Paket international ist */}
        {paket.international && (
          <span style={{
            display: 'inline-block',
            background: '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: 6,
            padding: '2px 8px',
            marginRight: '8px',
            fontSize: '12px'
          }}>
            🌍 INT
          </span>
        )}
        {/* ✅ GERADE AUSGEGEBEN Badge (per Button) */}
        {isButtonIssued && (
          <span style={{
            display: 'inline-block',
            background: '#66bb6a',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: 6,
            padding: '2px 8px',
            marginRight: '8px',
            fontSize: '12px'
          }}>
            ✅ {t('ui.main.issuedBadge')}
          </span>
        )}
        {/* 🕶️ VR-BADGE: Wenn per Quest erfasst */}
        {paket.erfasstMit === 'quest3-voice' && (
          <span style={{
            display: 'inline-block',
            background: '#7c4dff',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: 6,
            padding: '2px 6px',
            marginRight: '8px',
            fontSize: '11px',
            letterSpacing: '0.5px'
          }} title={t('ui.main.capturedWithVr')}>
            🕶️ VR
          </span>
        )}
        {/* 🔄 ABSENDER-TREFFER BADGE: Wenn Suche nur im Absender getroffen hat */}
        {senderOnlyMatch && (
          <span
            data-testid="sender-match-badge"
            style={{
              display: 'inline-block',
              background: '#f59e0b',
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: 6,
              padding: '2px 8px',
              marginRight: '8px',
              fontSize: '12px',
            }}
            title={t('plzCheck.senderMatchHint', 'Suchtreffer im gespeicherten Absender (evtl. Absender & Empfänger auf dem Etikett vertauscht)')}
          >
            🔄 {t('plzCheck.senderMatchBadge', 'Über Absender gefunden')}
          </span>
        )}
        {/* 🔐 PIN-TREFFER BADGE: Wenn nach dem 4-stelligen Abhol-PIN gesucht wurde */}
        {(() => {
          const pin = getPackagePickupPin(paket);
          if (!pin || !searchLower) return null;
          const m = String(searchLower).trim().match(/^(?:pin[:\s#-]*)?(\d{4})$/i);
          if (!m || m[1] !== pin) return null;
          return (
            <span
              data-testid="pickup-pin-badge"
              style={{
                display: 'inline-block',
                background: '#2563eb',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: 6,
                padding: '2px 8px',
                marginRight: '8px',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}
              title={t('ui.main.pickupPinHint')}
            >
              🔐 PIN {pin}
            </span>
          );
        })()}
        {/* 📒 EMPFÄNGER-/STAMMKUNDEN-LISTE & MITARBEITER-HINWEIS BADGE BEI AUSGABE */}
        {(() => {
          const listContact = findBestAddressBookMatch(paket.name);
          if (!listContact) return null;
          return (
            <span
              data-testid="paket-addressbook-badge"
              style={{
                display: 'inline-block',
                background: listContact.note ? '#d97706' : '#2563eb',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: 6,
                padding: '2px 8px',
                marginRight: '8px',
                fontSize: '12px',
              }}
              title={listContact.note ? t('ui.main.staffNote', { note: listContact.note }) : t('ui.main.onRecipientList', { name: listContact.name })}
            >
              {listContact.note
                ? `📌 ${listContact.note}`
                : `⭐ ${t('ui.main.onList')}`}
            </span>
          );
        })()}
        {highlightMatch(paket.name, searchLower)} – {highlightMatch(paket.streetAndNumber, searchLower)}
        
        {/* 🎨 Farbe + Gewicht (Quest-Features) */}
        {(paket.paketFarbe || paket.gewicht) && (
          <span style={{ 
            display: 'inline-flex', 
            alignItems: 'baseline',  // ✅ Schrift-bündig statt center
            gap: '6px',
            margin: '0 8px'
          }}>
            {paket.paketFarbe && (
              <span style={{
                display: 'inline-block',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: getCssColor(paket.paketFarbe),
                border: getCssColor(paket.paketFarbe) === '#FFFFFF' ? '1px solid #ccc' : 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                position: 'relative',
                top: '2px'  // ✅ Leicht nach unten für optische Balance
              }} title={translatePackageColor(paket.paketFarbe, lang)} />
            )}
            {paket.gewicht && (() => {
              // Extrahiere nur die Zahl aus dem Gewicht (ohne "kg" und Tausendertrennzeichen)
              const gewichtStr = String(paket.gewicht);
              const nummer = gewichtStr.replace(/[^\d.,]/g, '').replace(',', '.');
              const gewichtZahl = parseFloat(nummer);
              // Zeige ganze Zahl wenn möglich, sonst mit einer Nachkommastelle
              const anzeige = gewichtZahl % 1 === 0 ? gewichtZahl.toFixed(0) : gewichtZahl.toFixed(1);
              // Rot wenn über 10kg, sonst normale Farbe
              const istSchwer = gewichtZahl > 10;
              
              return (
                <span style={{ 
                  fontWeight: istSchwer ? 'bold' : 'normal',
                  color: istSchwer ? '#ff0000' : colors.text
                }}>
                  {anzeige} kg
                </span>
              );
            })()}
          </span>
        )}
        
        <span style={{
          display: 'inline-block',
          background: '#ffe066',
          color: '#333',
          fontWeight: 'bold',
          borderRadius: 6,
          padding: '2px 10px',
          margin: '0 8px'
        }}>
          {texts[lang].shelf}: {highlightMatch(paket.regalplatz, searchLower)}
        </span>
        {/* 🚚 Paketdienst (Option im Setup, Standard aus — Wunsch Folkert 21.10.2026; bei Demo-Paketen immer zeigen) */}
        {(isListCarrierOn() || paket.isDemo) && (() => {
          const dienst = guessCarrier(paket.trackingNumber || paket.trackingNummer || '', paket.paketdienst || '');
          return dienst ? (
            <span data-testid="list-carrier" style={{ display: 'inline-block', background: colors.darkMode ? '#2d333b' : '#e8eaf6', color: colors.darkMode ? '#c5cae9' : '#283593',
              fontWeight: 600, borderRadius: 6, padding: '2px 8px', marginRight: 8, fontSize: '0.9em' }}>🚚 {dienst}</span>
          ) : null;
        })()}
        {/* 🪵 Paletten-/Gebinde-Typ als Badge (genauso gestaltet wie das Paketdienstleister-Badge) */}
        {paket.palettenTyp && (
          <span
            data-testid="list-palette-badge"
            style={{
              display: 'inline-block',
              background: colors.darkMode ? '#2d333b' : '#e8eaf6',
              color: colors.darkMode ? '#c5cae9' : '#283593',
              fontWeight: 600,
              borderRadius: 6,
              padding: '2px 8px',
              marginRight: 8,
              fontSize: '0.9em',
            }}
          >
            🪵 {paket.palettenTyp}
          </span>
        )}
        {' – '}{texts[lang].stored} {formatDateTime(paket.datum)}
        {paket.ausgegebenAm && (
          <>
            {' – '}{texts[lang].issued} {formatDateTime(paket.ausgegebenAm)}
            {isBevollmIssued && (
              <span
                data-testid="paket-bevollmaechtigter-badge"
                title="Ausgegeben an Bevollmächtigten (inkl. Vollmacht-Foto)"
                style={{
                  display: 'inline-block',
                  marginLeft: 6,
                  padding: '1px 7px',
                  borderRadius: 5,
                  fontSize: 12,
                  fontWeight: 800,
                  background: '#fef08a',
                  color: '#713f12',
                  border: '1px solid #a16207',
                }}
              >
                👥 Bevollm.: {[paket.bevollmaechtigter?.vorname, paket.bevollmaechtigter?.nachname].filter(Boolean).join(' ') || paket.abgeholtVonName || 'Ja'}
                {paket.vollmachtFoto ? ' 📷' : ''}
              </span>
            )}
            {hatUnterschrift && (
              <span
                data-testid="paket-has-signature-badge"
                title={t('ui.main.signature')}
                style={{
                  display: 'inline-block',
                  marginLeft: 6,
                  padding: '1px 6px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 700,
                  background: colors.darkMode ? '#1e3a8a' : '#dbeafe',
                  color: colors.darkMode ? '#93c5fd' : '#1e40af',
                }}
              >
                ✍️
              </span>
            )}
            {paket.isArchived && (
              <span
                data-testid="paket-archived-badge"
                style={{
                  display: 'inline-block',
                  marginLeft: 6,
                  padding: '1px 6px',
                  borderRadius: 4,
                  fontSize: 11.5,
                  fontWeight: 700,
                  background: colors.darkMode ? '#374151' : '#e2e8f0',
                  color: colors.darkMode ? '#d1d5db' : '#334155',
                }}
              >
                🗄️ Archiv
              </span>
            )}
            {daysLeft !== null && (
              <span style={{ 
                color: paket.zurueckgeschickt ? '#ffffff' : '#b26a00', 
                backgroundColor: paket.zurueckgeschickt ? 'rgba(0,0,0,0.3)' : 'transparent',
                marginLeft: 8, 
                fontWeight: 'bold', 
                fontSize: 13,
                padding: paket.zurueckgeschickt ? '2px 6px' : '0',
                borderRadius: 4
              }}>
                {t('packages.daysUntilDeletion', { days: daysLeft })}
              </span>
            )}
          </>
        )}
      </span>
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 4, minWidth: 120 }}>
        {showOld ? (
          /* 🔄 Lagerfrist aktiv → nur Zurückschicken-Button */
          <button
            type="button"
            disabled={isAnimating}
            style={{ opacity: isAnimating ? 0.5 : 1, width: '100%', padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }}
            onClick={async e => {
              e.stopPropagation();
              if (!isAnimating) {
                await onZurueckschicken(paket);
              }
            }}
          >
            {texts[lang].return}
          </button>
        ) : (
          /* Normal → Ausgeben-Button (+ optional Bevollmächtigter-Button für wiesn2022@) */
          <>
            <button
              type="button"
              disabled={isAnimating}
              onClick={async e => {
                e.stopPropagation();
                if (!isAnimating) {
                  await onAusgeben(paket, true);
                }
              }}
              style={{ opacity: isAnimating ? 0.5 : 1, width: '100%', padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }}
            >
              {texts[lang].issue}
            </button>
            {isBevollmaechtigterEnabled() && !paket.ausgegebenAm && !paket.zurueckgeschickt && (
              <button
                type="button"
                data-testid="paket-ausgeben-bevollm-btn"
                disabled={isAnimating}
                onClick={async e => {
                  e.stopPropagation();
                  if (!isAnimating) {
                    await onAusgeben(paket, true, false, false, 'bevollmaechtigter');
                  }
                }}
                style={{
                  opacity: isAnimating ? 0.5 : 1,
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  background: '#7c3aed',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 6,
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
                title="An Bevollmächtigten ausgeben (Name + Foto der Vollmacht + Unterschrift)"
              >
                👥 Bevollm.
              </button>
            )}
          </>
        )}
        <button
          type="button"
          style={{ width: '100%', padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }}
          onClick={e => {
            e.stopPropagation();
            onSelect(paket, idx, true); // true = Modal öffnen zum Bearbeiten
          }}
        >
          📝 {texts[lang].edit || t('common.edit')}
        </button>
        {/* 🖨️ Etikett nachdrucken — nur wenn Filter "Etiketten drucken" aktiv + Drucker eingerichtet */}
        {showPrintLabels && isPrinterSupported() && getPrinterConfig()?.address && (
          <button
            type="button"
            title={t('printer.reprintTitle')}
            style={{ width: '100%', padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }}
            onClick={e => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('kiosk:reprint-label', { detail: paket }));
            }}
          >
            🖨️ {t('printer.labelBtn')}
          </button>
        )}
      </div>

      {/* 📥 Absender — wenn Filter „Absender anzeigen" aktiv ODER wenn die Suche im Absender getroffen hat */}
      {(showSender || senderOnlyMatch) && !showDetails && (() => {
        const abs = getPaketAbsenderText(paket);
        if (!abs) return null;
        return (
          <div
            data-testid="paket-sender"
            style={{
              flexBasis: '100%',
              width: '100%',
              marginTop: 6,
              fontSize: 13,
              color: colors.text,
              textAlign: 'left',
              ...(senderOnlyMatch ? {
                padding: '4px 8px',
                borderRadius: 6,
                background: colors.darkMode ? 'rgba(245, 158, 11, 0.16)' : 'rgba(245, 158, 11, 0.14)',
                fontWeight: 600,
              } : {}),
            }}
          >
            <span style={{ opacity: senderOnlyMatch ? 0.9 : 0.65 }}>📥 {t('details.sender')}: </span>
            {highlightMatch(abs, searchLower)}
          </div>
        );
      })()}

      {/* ℹ️ Details — wenn Filter „Details anzeigen" aktiv ODER Paket einzeln angetippt wurde (lädt Unterschrift per Lazy-Load) */}
      {detailsVisible && (() => {
        const trk = String(paket.trackingNumber || paket.trackingNummer || '').trim();
        const ort = String(paket.postalAndCity || '').trim();
        const abs = getPaketAbsenderText(paket);
        const dienst = guessCarrier(trk);
        const pin = getPackagePickupPin(paket);
        const gpMeta = parseGrenzpaketMeta(paket);
        const gpInfoTeile = [];
        if (gpMeta) {
          if (gpMeta.info) gpInfoTeile.push(gpMeta.info);
          if (paket.regalplatz) gpInfoTeile.push(`Lager: ${paket.regalplatz}`);
          else if (!gpMeta.info && gpMeta.infoRaw) gpInfoTeile.push(gpMeta.infoRaw);
        }
        const gpInfoText = gpInfoTeile.join(' · ');
        const gpPaketCode = gpMeta?.paketCode || '';
        const trkIstNurGpCode = Boolean(gpPaketCode && trk && trk.toUpperCase() === gpPaketCode.toUpperCase());
        const echteTrk = trkIstNurGpCode ? '' : trk;
        // ⚠️ Schaden nur hier in den Details (bewusst unauffällig)
        const sch = paket.schaden && (paket.schaden.beschaedigt || paket.schaden.notiz || (paket.schaden.fotos || []).length) ? paket.schaden : null;
        // 👷 wer hat erfasst / ausgegeben (nur wenn gestempelt)
        const vonE = nameVon(paket.erfasstVon), vonA = nameVon(paket.ausgegebenVon);
        const sigName = String(paket.abgeholtVonName || lazySig?.abgeholtVonName || '').trim();
        const rawSig = paket.signatureDataUrl || lazySig?.signatureDataUrl || '';
        const sigImg = typeof rawSig === 'string' && rawSig.startsWith('data:image/') ? rawSig : '';
        const bevObj = paket.bevollmaechtigter || lazySig?.bevollmaechtigter || null;
        const bevName = bevObj ? [bevObj.vorname, bevObj.nachname].filter(Boolean).join(' ').trim() : '';
        const rawVollmacht = paket.vollmachtFoto || lazySig?.vollmachtFoto || '';
        const vollmachtImg = typeof rawVollmacht === 'string' && rawVollmacht.startsWith('data:image/') ? rawVollmacht : '';
        const hasSigSection = Boolean(sigName || sigImg || sigLoading || paket.signatureHatBild || bevName || vollmachtImg || paket.vollmachtHatBild);
        if (!trk && !ort && !abs && !sch && !vonE && !vonA && !pin && !hasSigSection && !gpInfoText && !gpPaketCode) return null;
        const zeile = { display: 'flex', gap: '6px', alignItems: 'baseline' };
        const label = { opacity: 0.65, minWidth: '88px', flexShrink: 0 };
        return (
          <div data-testid="paket-details" style={{ flexBasis: '100%', width: '100%', marginTop: 8, padding: '8px 10px', borderRadius: 8,
            fontSize: 13, lineHeight: 1.5, background: colors.darkMode ? '#1a1f27' : '#f6f7f9', color: colors.text, textAlign: 'left' }}>
            {pin && <div style={zeile}><span style={label}>🔐 PIN</span><span style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '1px' }}>{pin}</span></div>}
            {gpInfoText && (
              <div style={zeile} data-testid="paket-gp-info-lager">
                <span style={label}>📌 Info / Lager</span>
                <span style={{ fontWeight: 600 }}>{highlightMatch(gpInfoText, searchLower)}</span>
              </div>
            )}
            {ort && <div style={zeile}><span style={label}>{t('details.city')}</span><span>{ort}</span></div>}
            {(echteTrk || gpPaketCode) && (
              <div style={zeile} data-testid="paket-gp-tracking">
                <span style={label}>{t('details.tracking')}</span>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {echteTrk ? highlightMatch(echteTrk, searchLower) : 'Keine ID'}
                  {echteTrk && dienst ? ` · ${dienst}` : ''}
                  {gpPaketCode ? ` · Code: ${gpPaketCode}` : ''}
                </span>
              </div>
            )}
            {abs && <div style={zeile}><span style={label}>{t('details.sender')}</span><span>{highlightMatch(abs, searchLower)}</span></div>}
            {sch && <div style={zeile} data-testid="paket-schaden"><span style={label}>⚠️ {t('damage.short')}</span>
              <span>{[sch.beschaedigt ? t('damage.damaged') : '', sch.notiz || '', (sch.fotos || []).length ? t('damage.photos', { n: sch.fotos.length }) : ''].filter(Boolean).join(' · ')}</span></div>}
            {(vonE || vonA) && <div style={zeile} data-testid="paket-mitarbeiter"><span style={label}>👷 {t('staffs.who')}</span>
              <span>{[vonE ? t('staffs.capturedBy', { name: vonE }) : '', vonA ? t('staffs.issuedBy', { name: vonA }) : ''].filter(Boolean).join(' · ')}</span></div>}
            {(bevName || vollmachtImg || paket.vollmachtHatBild) && (
              <div style={{ ...zeile, alignItems: 'center', flexWrap: 'wrap' }} data-testid="paket-bevollmaechtigter">
                <span style={label}>👥 Vollmacht</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {bevName && <strong style={{ color: '#7c3aed' }}>Bevollmächtigter: {bevName}</strong>}
                  {vollmachtImg && (
                    <img
                      src={vollmachtImg}
                      alt="Vollmacht"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSigPreviewOpen(true);
                      }}
                      title="Vollmacht-Foto vergrößern"
                      style={{ height: 38, maxWidth: 90, objectFit: 'cover', background: '#fff', borderRadius: 4, border: '1px solid #7c3aed', cursor: 'zoom-in' }}
                    />
                  )}
                </span>
              </div>
            )}
            {hasSigSection && (
              <div style={{ ...zeile, alignItems: 'center', flexWrap: 'wrap' }} data-testid="paket-unterschrift">
                <span style={label}>✍️ {t('ui.main.signature')}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {sigName && <strong>{sigName}</strong>}
                  {sigLoading && !sigImg && (
                    <span style={{ fontSize: 12, opacity: 0.7 }}>⏳ {t('common.isLoading')}</span>
                  )}
                  {!sigLoading && !sigImg && (paket.signatureHatBild || paket.vollmachtHatBild) && !itemDetailsOpen && (
                    <button
                      type="button"
                      data-testid="load-signature-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemDetailsOpen(true);
                      }}
                      style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        border: `1px solid ${colors.border || '#cbd5e1'}`,
                        background: colors.darkMode ? '#2d3748' : '#ffffff',
                        color: colors.text,
                        cursor: 'pointer',
                      }}
                    >
                      ✍️ {lang === 'de' ? 'Unterschrift / Vollmacht laden' : lang === 'nl' ? 'Handtekening laden' : 'Load signature'}
                    </button>
                  )}
                  {sigImg && (
                    <img
                      src={sigImg}
                      alt={t('ui.main.signature')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSigPreviewOpen(true);
                      }}
                      title="Antippen zum Vergrößern"
                      style={{ height: 38, maxWidth: 160, objectFit: 'contain', background: '#fff', borderRadius: 4, padding: '2px 6px', border: '1px solid rgba(0,0,0,0.15)', cursor: 'zoom-in' }}
                    />
                  )}
                </span>
              </div>
            )}
            {sigPreviewOpen && (sigImg || vollmachtImg) && (
              <div
                data-testid="signature-preview-modal"
                onClick={(e) => { e.stopPropagation(); setSigPreviewOpen(false); }}
                style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 4000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: colors.darkMode ? '#1e242c' : '#ffffff',
                    color: colors.text,
                    borderRadius: 14,
                    padding: 18,
                    maxWidth: 460,
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ fontSize: 16 }}>✍️ {t('ui.main.signature')} — {paket.name}</strong>
                    <button
                      type="button"
                      onClick={() => setSigPreviewOpen(false)}
                      style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: colors.text }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ fontSize: 12.5, opacity: 0.8, marginBottom: 10 }}>
                    {[
                      bevName ? `Bevollmächtigter: ${bevName}` : (sigName && `Abgeholt von: ${sigName}`),
                      (paket.abholUnterschriftAm || paket.ausgegebenAm) && formatDateTime(paket.abholUnterschriftAm || paket.ausgegebenAm),
                      trk && `Sendung: ${trk}`,
                    ].filter(Boolean).join(' · ')}
                  </div>
                  {sigImg && (
                    <div style={{ background: '#ffffff', borderRadius: 10, padding: 12, border: '1px solid #cbd5e1', textAlign: 'center', marginBottom: vollmachtImg ? 10 : 0 }}>
                      <img src={sigImg} alt={t('ui.main.signature')} style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
                    </div>
                  )}
                  {vollmachtImg && (
                    <div style={{ background: '#f5f3ff', borderRadius: 10, padding: 10, border: '1px solid #c4b5fd', textAlign: 'center' }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#6d28d9', marginBottom: 6 }}>📷 Foto der Vollmacht</div>
                      <img src={vollmachtImg} alt="Vollmacht" style={{ maxWidth: '100%', maxHeight: 340, objectFit: 'contain', borderRadius: 6 }} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* 🖼️ Paket-Foto — nur wenn Filter "Bilder anzeigen" aktiv UND das Paket ein
          Bild hat (sonst kein Fetch/kein Zusammenklappen → keine Scroll-Sprünge) */}
      {showImages && (paket.imageUrl || paket.image_url) && <PackageThumbnail packageId={paket.id} />}

     <div style={{
  position: 'absolute',
  bottom: '2px',    // Noch weiter unten (von 4px auf 2px)
  right: '2px',     // Noch näher am Rand (von 4px auf 2px)
  zIndex: 20
}}>
  {isOnline ? (
    isPaketSynced(paket) ? (
      <span 
        style={{ 
          color: '#22c55e',           
          fontSize: '10px',           // Kleiner für dünnere Häkchen (von 12px auf 10px)
          fontWeight: 'normal',       // Normal statt bold für dünnere Häkchen
          textShadow: '0 0 3px rgba(34, 197, 94, 0.6), 0 1px 1px rgba(0,0,0,0.8)',  // Weniger intensiver Schatten
          filter: 'drop-shadow(0 0 1px rgba(34, 197, 94, 0.4))',  // Schwächerer Glow
          fontFamily: 'Arial, sans-serif'  // Explizite Schriftart für konsistente Darstellung
        }}
        title={t('sync.onlineSynced')}
      >
        ✓✓
      </span>
    ) : (
      <span 
        style={{ 
          color: '#f59e0b',
          fontSize: '10px',           // Kleiner
          fontWeight: 'normal',       // Normal statt bold
          textShadow: '0 0 3px rgba(245, 158, 11, 0.6), 0 1px 1px rgba(0,0,0,0.8)',
          filter: 'drop-shadow(0 0 1px rgba(245, 158, 11, 0.4))',
          fontFamily: 'Arial, sans-serif'
        }}
        title={t('sync.notYetSynced')}
      >
        ⏱️
      </span>
    )
  ) : (
    <span 
      style={{ 
        color: '#6b7280',
        fontSize: '10px',           // Kleiner
        fontWeight: 'normal',       // Normal statt bold
        textShadow: '0 1px 1px rgba(0,0,0,0.8)',
        fontFamily: 'Arial, sans-serif'
      }}
      title={t('sync.offlineMode')}
    >
      📴
    </span>
  )}
</div>
    </li>
  );
}
const forceFirebaseSync = async () => {
  // 🛡️ RACE CONDITION FIX
  if (!isMountedRef.current) return;
  
  try {
    setLoading(true);
    
    if (!currentUser) {
      alert(t('auth.notLoggedIn'));
      if (isMountedRef.current) setLoading(false);
      return;
    }
    
    console.log("=== FORCE SYNC STARTED ===");
    console.log("User ID:", currentUser.uid);
    
    // SCHRITT 1: Pakete vom Server laden
    const serverPaketeResult = await loadAllPackages();
    
    // 🛡️ Prüfung nach async
    if (!isMountedRef.current) return;
    
    console.log("Server-Pakete geladen:", serverPaketeResult?.length || 0);
    
    // Falls Server keine Pakete hat → lokale hochladen
    if (!serverPaketeResult || serverPaketeResult.length === 0) {
      console.log("KEINE PAKETE AUF SERVER - Lade lokale Daten hoch...");
      
      const lokalePakete = await get('pakete') || [];
      
      if (lokalePakete.length > 0) {
        let migrated = 0;
        for (const paket of lokalePakete) {
          try {
            await savePaket(paket);
            migrated++;
          } catch (error) {
            console.error(`Migration Fehler für ${paket.id}:`, error);
          }
        }
        await updatePackagesHash(lokalePakete);
        
        if (!isMountedRef.current) return;
        
        alert(t('sync.newDocCreated'));
        setServerPakete(lokalePakete);
        setLastUpdated(new Date().toISOString());
        setLoading(false);
        return;
      }
    }
    
    // Gefundene Pakete anwenden
    if (serverPaketeResult && serverPaketeResult.length > 0) {
      if (!isMountedRef.current) return;
      
      // Normalisiere alle Datumswerte
      const normalizedData = serverPaketeResult.map(normalizeDates);
      
      // Aktualisiere lokale Daten
      setPakete(normalizedData);
      await set('pakete', normalizedData);
      setServerPakete(normalizedData);
      
      console.log(`✅ ${normalizedData.length} PAKETE ERFOLGREICH SYNCHRONISIERT`);
      if (isMountedRef.current) {
        alert(`${normalizedData.length} ${t('packages.packages')} ${t('sync.synced').toLowerCase()}!`);
      }
    } else {
      console.log("❌ KEINE PAKETE GEFUNDEN");
      if (isMountedRef.current) alert(t('sync.noPackagesOnServer'));
    }
    
    console.log("=== FORCE SYNC COMPLETED ===");
  } catch (error) {
    console.error("SYNC ERROR:", error);
    if (isMountedRef.current) alert(t('sync.syncFailed') + ": " + error.message);
  } finally {
    if (isMountedRef.current) setLoading(false);
  }
};
  // Funktion zum Laden der Favoriten beim Fokussieren
  const handleStreetFocus = () => {
  get('favoriten')
    .then((gespeicherteFavoriten) => {
      if (gespeicherteFavoriten) {
        console.log('Favoriten beim Fokussieren geladen:', gespeicherteFavoriten); // Debugging
        setFavoriten(gespeicherteFavoriten);
      }
    })
    .catch(err => console.error('Fehler beim Laden der Favoriten:', err));
};
  const findShelfSuggestionsByStreet = (street) => {
  if (!street || street.trim().length < 3) return []; // Mindestens 3 Zeichen
  if (istFilialAdresse(street)) return []; // Keine Regalvorschläge anhand einer Paketshop-/Filialadresse

  // Extrahiere normalisierten Straßennamen ohne Hausnummern (Hemmstr. == Hemmstraße)
  const baseStreet = normStrasseBasis(street);
  if (!baseStreet || baseStreet.length < 3) return [];

  // Eigene Shop-Adresse (≥ 3 verschiedene Empfänger mit Straße + Hausnummer) ausschließen
  const shopAdressen = findeShopStrassen(pakete);
  const inHn = String(extractHausnummer(street) || '').toLowerCase();
  if (inHn && shopAdressen.has(`${baseStreet}|${inHn}`)) return [];
  
  // Finde alle Pakete mit dieser Straße (ohne Shop-Adressen)
  const matchingPackages = pakete.filter(paket => {
    if (!paket || !paket.regalplatz || paket.ausgegebenAm || paket.zurueckgeschickt || paket.deleted) return false;
    if (istFilialAdresse(paket.streetAndNumber)) return false;
    const paketStreet = normStrasseBasis(paket.streetAndNumber);
    if (paketStreet !== baseStreet) return false;
    const pHn = String(extractHausnummer(paket.streetAndNumber || '') || '').toLowerCase();
    if (pHn && shopAdressen.has(`${paketStreet}|${pHn}`)) return false;
    return true;
  });
  
  // Gruppiere nach normalisierten Regalplätzen und zähle
  const shelvesByFrequency = matchingPackages.reduce((acc, paket) => {
    const normShelf = normalizeRegalplatz(paket.regalplatz, pakete);
    acc[normShelf] = (acc[normShelf] || 0) + 1;
    return acc;
  }, {});
  
  // Sortiere nach Häufigkeit und formatiere
  return Object.entries(shelvesByFrequency)
    .sort((a, b) => b[1] - a[1])
    .map(([shelf, count]) => ({
      shelf,
      count,
      label: `${shelf} (${count}×)`
    }));
};
// Füge diese Funktionen vor dem useEffect-Block ein (ungefähr bei Zeile 295)

// Funktion zum Scrollen zum aktiven Eingabefeld
const scrollToActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    // 🔍 Suchfeld ist oben angedockt (sticky) — NIE bei Fokus/Tastenanschlag ins
    // Zentrum scrollen, sonst springt der Bildschirm bei jedem Buchstaben wild.
    if (activeElement.id === 'search-bar') return;
    // Spezielles Verhalten für das Regalplatz-Eingabefeld
    if (activeElement.id === 'regalplatz') {
      // Finde den Speichern-Button
      const saveButton = document.querySelector('form button[type="submit"]');
      if (saveButton) {
        // Warte kurz, bis die Tastatur sichtbar ist
        setTimeout(() => {
          // Berechne die optimale Scroll-Position
          const windowHeight = window.innerHeight;
          const keyboardHeight = windowHeight * 0.4; // Schätzung der Tastaturhöhe
          
          // Position des Buttons relativ zum Viewport
          const buttonRect = saveButton.getBoundingClientRect();
          
          // Scrolle so, dass der Button über der Tastatur liegt
          if (buttonRect.bottom > windowHeight - keyboardHeight) {
            const scrollNeeded = buttonRect.bottom - (windowHeight - keyboardHeight) + 20;
            window.scrollBy({
              top: scrollNeeded,
              behavior: 'smooth'
            });
          }
        }, 300);
      }
    } else {
      // Normales Verhalten für andere Eingabefelder
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
};

// Funktion für den Focus-Event
const handleFocus = () => {
  scrollToActiveInput();
};

// Funktion für den Input-Event
const handleInput = () => {
  scrollToActiveInput();
};


// Function to handle focus on input fields

useEffect(() => {
  // Event-Listener für Fokus auf Eingabefelder
  window.addEventListener('focusin', handleFocus);
  
  // NEUER Event-Listener für Tastatureingaben
  window.addEventListener('input', handleInput);
  
  // Spezifischer Event-Listener für das Regalplatz-Feld
  const regalplatzInput = document.getElementById('regalplatz');
  if (regalplatzInput) {
    regalplatzInput.addEventListener('focus', () => {
      // Warte kurz und scrolle dann zum Speichern-Button
      setTimeout(() => scrollToActiveInput(), 300);
    });
  }

  return () => {
    window.removeEventListener('focusin', handleFocus);
    window.removeEventListener('input', handleInput);
    if (regalplatzInput) {
      regalplatzInput.removeEventListener('focus', scrollToActiveInput);
    }
  };
}, []);
  // Funktion zur Filterung der Favoriten basierend auf der Eingabe
  const getFavoritenVorschlaege = (input) => {
  if (!input || input.length < 1) return []; // Nur Vorschläge anzeigen, wenn mindestens ein Zeichen eingegeben wurde
  return favoriten.filter(fav => fav.street.toLowerCase().startsWith(input.toLowerCase()));
};
  const [favoritenVorschlaege, setFavoritenVorschlaege] = useState([]); // NEU: State für Favoriten-Vorschläge
  const addFavorit = (streetAndNumber, postalAndCity) => {
  if (!streetAndNumber) {
    alert(t('favorites.enterStreet'));
    return;
  }

  // Entferne die Hausnummer aus der Straße
  const street = streetAndNumber.replace(/\s*\d+[a-zA-Z]?\s*.*$/, '').trim();
  
  // postalAndCity ist jetzt optional und kann ein leerer String sein
  const postalCityToSave = postalAndCity || ''; // Leerer String falls nicht angegeben

  // Prüfe nur auf Street-Duplikate, wenn postalAndCity leer ist
  const isDuplicate = favoriten.some(fav => {
    if (!postalCityToSave) {
      // Wenn keine PLZ angegeben, prüfe nur die Straße
      return fav.street === street;
    } else {
      // Wenn PLZ angegeben, prüfe beide Felder
      return fav.street === street && fav.postalAndCity === postalCityToSave;
    }
  });

  if (!isDuplicate) {
    const neueFavoriten = [...favoriten, { street, postalAndCity: postalCityToSave }];
    setFavoriten(neueFavoriten);

    set('favoriten', neueFavoriten)
      .then(() => {
        setFavoritenMessage(t('favorites.favoriteAdded'));
        setTimeout(() => setFavoritenMessage(null), 3000);
      })
      .catch(err => console.error('Fehler beim Speichern des Favoriten:', err));
  } else {
    alert(t('favorites.favoriteExists'));
  }
};
  const deleteFavorit = (street, postalAndCity) => {
  
    const gefilterteFavoriten = favoriten.filter(
    (fav) => fav.street !== street || fav.postalAndCity !== postalAndCity
  );
  setFavoriten(gefilterteFavoriten);

  set('favoriten', gefilterteFavoriten)
    .then(() => {
      setFavoritenMessage(t('favorites.favoriteDeleted'));
      setTimeout(() => setFavoritenMessage(null), 3000); // Nachricht nach 3 Sekunden ausblenden
    })
    .catch(err => console.error('Fehler beim Löschen des Favoriten:', err));
};
  // cleanupOldPackages entfernt — Cleanup läuft jetzt serverseitig per Cron-Job
  // Server löscht alte Pakete → berechnet neuen Hash → Client erkennt Mismatch → Full-Sync

  // 🛡️ DATENSCHUTZ: INTELLIGENTES Backup-System
  const createBackup = async (paketeArray, reason = 'auto') => {
    try {
      const now = Date.now();
      
      // 🚨 KRITISCHE Gründe - immer Backup erstellen
      const kritischeGruende = [
        'before_server_sync',
        'before_download_sync',
        'before_account_switch',
        'before_logout_unsynced',
        'before_cleanup', 
        'after_cleanup',
        'manual',
        'before_restore',
        'emergency'
      ];
      
      const istKritisch = kritischeGruende.some(k => reason.includes(k));
      
      // ⏱️ Prüfe letztes Backup (nur bei nicht-kritischen)
      if (!istKritisch) {
        const letzteBackupZeit = await get('last_backup_time') || 0;
        const stundeSeit = (now - letzteBackupZeit) / (1000 * 60 * 60);
        
        // Wenn letztes Backup < 1 Stunde her → überspringe
        if (stundeSeit < 1) {
          console.log(`⏸️ Backup übersprungen (letztes vor ${Math.round(stundeSeit * 60)} Minuten, Grund: ${reason})`);
          return null;
        }
      }
      
      const backup = {
        data: paketeArray,
        timestamp: new Date().toISOString(),
        reason: reason,
        version: APP_VERSION,
        count: paketeArray.length,
        critical: istKritisch
      };
      
      const backupKey = `backup_${now}`;
      await set(backupKey, backup);
      await set('last_backup_time', now);
      
      // 📋 Bereinige alte Backups intelligent
      const allKeys = await get('all_backup_keys') || [];
      allKeys.push(backupKey);
      
      // Lade alle Backups zum Analysieren
      const alleBackups = [];
      for (const key of allKeys) {
        const b = await get(key);
        if (b) {
          alleBackups.push({ key, ...b });
        }
      }
      
      // Sortiere nach Datum (älteste zuerst)
      alleBackups.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // Behalte-Logik:
      // - Alle kritischen Backups der letzten 7 Tage
      // - Die letzten 3 Backups (egal welcher Grund)
      // - 1 Backup pro Tag der letzten 7 Tage (nicht 30!)
      const siebenTageMs = 7 * 24 * 60 * 60 * 1000;
      const behalten = new Set();
      const tagesBackups = new Map(); // Tag → bestes Backup
      
      alleBackups.forEach((b, index) => {
        const alter = now - new Date(b.timestamp).getTime();
        const tag = new Date(b.timestamp).toDateString();
        
        // Regel 1: Kritische Backups < 7 Tage
        if (b.critical && alter < siebenTageMs) {
          behalten.add(b.key);
        }
        
        // Regel 2: Letzte 3 Backups (reduziert von 5)
        if (index >= alleBackups.length - 3) {
          behalten.add(b.key);
        }
        
        // Regel 3: 1 pro Tag (nur letzten 7 Tage statt 30!)
        if (alter < siebenTageMs) {
          if (!tagesBackups.has(tag) || b.count > tagesBackups.get(tag).count) {
            tagesBackups.set(tag, b);
          }
        }
      });
      
      // Füge Tages-Backups hinzu
      tagesBackups.forEach(b => behalten.add(b.key));
      
      // Lösche nicht-behaltene Backups
      const zuLoeschen = alleBackups.filter(b => !behalten.has(b.key));
      for (const b of zuLoeschen) {
        try {
          await del(b.key);
          console.log(`🗑️ Altes Backup gelöscht: ${b.key} (${b.count} Pakete, ${b.reason})`);
        } catch (err) {
          console.warn(`Konnte Backup nicht löschen: ${b.key}`, err);
        }
      }
      
      // Aktualisiere Key-Liste
      const neueKeys = Array.from(behalten);
      await set('all_backup_keys', neueKeys);
      
      console.log(`💾 Backup erstellt: ${backupKey} (${paketeArray.length} Pakete, ${reason}, kritisch: ${istKritisch}) - ${behalten.size} Backups behalten, ${zuLoeschen.length} gelöscht`);
      
      return backupKey;
    } catch (error) {
      console.error('❌ Fehler beim Backup erstellen:', error);
      return null;
    }
  };

  // ☁️ CLOUD-BACKUP: Paketdaten auf dem Server sichern
  const createCloudBackup = async (paketeArray, reason = 'app_start') => {
    if (!currentUser || !isOnline) {
      console.log('⏸️ Cloud-Backup übersprungen (nicht eingeloggt oder offline)');
      return null;
    }
    
    try {
      if (!paketeArray || paketeArray.length === 0) {
        console.log('⏸️ Cloud-Backup übersprungen (keine Pakete)');
        return null;
      }

      console.log(`💾 Erstelle Server-Backup für ${paketeArray.length} Pakete (${reason})...`);
      
      const now = new Date();
      const filename = `backup_${now.toISOString().replace(/[:.]/g, '-')}_${reason}.json`;
      
      // Backup-Daten mit Metadaten
      const backupData = {
        metadata: {
          timestamp: now.toISOString(),
          reason: reason,
          version: APP_VERSION,
          count: paketeArray.length
        },
        pakete: paketeArray
      };
      
      const result = await serverDataService.createBackup(backupData, filename);
      
      console.log(`✅ Server-Backup erstellt: ${result.filename} (${paketeArray.length} Pakete, ~${Math.round((result.size || 0) / 1024)} KB)`);
      
      // Aktualisiere lokalen Timestamp
      setLastCloudBackup(now.toISOString());
      await set('lastCloudBackup', now.toISOString());
      
      return result.filename;
    } catch (error) {
      // Backup-Fehler NICHT als kritisch behandeln — kein Logout auslösen
      console.warn('⚠️ Server-Backup fehlgeschlagen (nicht kritisch):', error.message);
      return null;
    }
  };
  
  // 🧹 Cloud-Backup Cleanup: Server behält max 20, ältere werden automatisch gelöscht
  const cleanupOldCloudBackups = async () => {
    console.log('🧹 Cloud-Backup Cleanup: Server limitiert automatisch auf 20');
  };
  
  // 📥 Liste alle Cloud-Backups vom Server
  const listCloudBackups = async () => {
    if (!currentUser) return [];
    
    try {
      const backups = await serverDataService.listBackups();
      // Server gibt Array mit { name, size, created, modified } zurück
      return (Array.isArray(backups) ? backups : []).sort((a, b) => new Date(b.modified) - new Date(a.modified));
    } catch (error) {
      console.error('❌ Fehler beim Laden der Cloud-Backups:', error);
      return [];
    }
  };
  
  // 📥 Lade Cloud-Backup-Metadaten UND lokale Backups  
  const loadCloudBackups = async () => {
    if (!currentUser) {
      alert('❌ ' + t('auth.notLoggedIn'));
      return [];
    }
    
    try {
      console.log(`📥 Lade Cloud-Backups vom Server...`);
      
      const cloudBackups = await listCloudBackups();
      console.log('☁️ Cloud-Backups gefunden:', cloudBackups.length);
      
      // Lade auch lokale Backups
      const localBackups = await loadAvailableBackups();
      console.log('💾 Lokale Backups gefunden:', localBackups.length);
      
      return {
        cloud: cloudBackups,
        local: localBackups
      };
    } catch (error) {
      console.error('❌ Fehler beim Laden der Backups:', error);
      return { cloud: [], local: [] };
    }
  };
  
  // 🔄 Stelle Cloud-Backup vom Server wieder her
  const restoreCloudBackup = async (backupFilename) => {
    if (!currentUser) {
      alert('❌ ' + t('auth.notLoggedIn'));
      return false;
    }
    
    try {
      setBackupProgress({ type: 'restore', step: t('ui.main.progressDownloading'), current: 0, total: 0, percent: 5, detail: backupFilename });
      
      console.log(`🔄 Lade Cloud-Backup: ${backupFilename}`);
      const backupData = await serverDataService.downloadBackup(backupFilename);
      
      if (!backupData.pakete || !Array.isArray(backupData.pakete)) {
        throw new Error('Ungültiges Backup-Format');
      }
      
      setBackupProgress({ type: 'restore', step: t('ui.main.progressSafetyBackup'), current: 0, total: 0, percent: 15, detail: t('ui.main.progressSaveCurrent', { count: pakete.length }) });
      
      // Erstelle lokales Backup vor Wiederherstellung
      await createBackup(pakete, 'before_cloud_restore');
      
      setBackupProgress({ type: 'restore', step: t('ui.main.progressValidating'), current: 0, total: backupData.pakete.length, percent: 25, detail: t('ui.main.progressCheckPackages', { count: backupData.pakete.length }) });
      
      // Validiere und migriere die Backup-Daten
      const restoredPakete = validateAndMigratePakete(backupData.pakete);
      
      setBackupProgress({ type: 'restore', step: t('ui.main.progressUpdatingLocal'), current: 0, total: restoredPakete.length, percent: 35, detail: t('ui.main.packagesCount', { count: restoredPakete.length }) });
      
      setPakete(restoredPakete);
      await set('pakete', restoredPakete);
      
      // Server-Sync: Lade wiederhergestellte Pakete auf Server hoch
      const restoreUser = getAuthUser();
      if (restoreUser && isOnline) {
        const total = restoredPakete.length;
        let uploaded = 0;
        let failed = 0;
        
        for (const paket of restoredPakete) {
          try {
            await savePaket(paket);
            uploaded++;
          } catch (error) {
            failed++;
            console.error(`Restore-Upload Fehler für ${paket.id}:`, error);
          }
          const percent = 40 + Math.round((uploaded + failed) / total * 55);
          setBackupProgress({ type: 'restore', step: t('ui.main.progressUploading'), current: uploaded + failed, total, percent, detail: `${t('ui.main.progressUploaded', { done: uploaded, total })}${failed > 0 ? ` (${t('ui.main.progressErrors', { count: failed })})` : ''}` });
        }
        
        await updatePackagesHash(restoredPakete);
        await set('lastPackageSync', Date.now());
      }
      
      const backupDate = new Date(backupData.metadata?.timestamp || backupData.timestamp);
      setBackupProgress({ type: 'restore', step: t('ui.main.progressDone'), current: restoredPakete.length, total: restoredPakete.length, percent: 100, detail: t('ui.main.packagesRestored', { count: restoredPakete.length }) });
      
      // Kurz anzeigen, dann schließen
      await new Promise(r => setTimeout(r, 1500));
      setBackupProgress(null);
      
      return true;
    } catch (error) {
      console.error('❌ Fehler beim Wiederherstellen:', error);
      setBackupProgress(null);
      alert('❌ ' + t('errors.couldNotLoad'));
      return false;
    }
  };
  
  // ⏰ Prüfe ob Cloud-Backup nötig (alle 6 Stunden beim App-Start)
  const checkScheduledBackup = async () => {
    if (!currentUser || !isOnline || !isCloudSyncAllowed() || !loginSyncCompletedRef.current) return;
    
    const aktuellePakete = paketeRef.current || [];
    // Warte kurz bis Pakete geladen sind
    if (aktuellePakete.length === 0) {
      console.log('⏸️ Cloud-Backup: Warte auf Pakete...');
      return;
    }
    
    // Prüfe ob letztes Backup mehr als 6 Stunden her ist
    const lastBackup = await get('lastCloudBackup');
    if (lastBackup) {
      const lastBackupDate = new Date(lastBackup);
      const now = new Date();
      const hoursSinceLastBackup = (now - lastBackupDate) / (1000 * 60 * 60);
      
      if (hoursSinceLastBackup < 6) {
        console.log(`⏸️ Cloud-Backup nicht nötig (letztes vor ${hoursSinceLastBackup.toFixed(1)}h)`);
        return;
      }
    }
    
    // Erstelle automatisches Backup mit aktuellem Bestand (nach abgeschlossenem Login-Sync!)
    console.log(`⏰ Cloud-Backup beim App-Start (${aktuellePakete.length} Pakete, letztes >6h her)`);
    await createCloudBackup(aktuellePakete, 'app_start');
  };

  // 🔄 NOTFALL-WIEDERHERSTELLUNG: Lade letztes Backup
  const restoreFromBackup = async () => {
    try {
      const allKeys = await get('all_backup_keys') || [];
      if (allKeys.length === 0) {
        console.warn('⚠️ Keine Backups gefunden');
        return false;
      }
      
      // Lade das neueste Backup
      const latestBackupKey = allKeys[allKeys.length - 1];
      const backup = await get(latestBackupKey);
      
      if (backup && backup.data) {
        console.log(`🔄 Stelle Backup wieder her: ${backup.count} Pakete vom ${backup.timestamp}`);
        
        // Validiere und migriere die Backup-Daten
        const restoredPakete = validateAndMigratePakete(backup.data);
        
        setPakete(restoredPakete);
        await set('pakete', restoredPakete);
        
        alert(`✅ ${restoredPakete.length} ${t('packages.packages')} ${t('backup.backupRestored').toLowerCase()}!`);
        return true;
      }
    } catch (error) {
      console.error('❌ Fehler beim Wiederherstellen:', error);
      alert('❌ ' + t('errors.couldNotLoad'));
    }
    
    return false;
  };

  // 🗑️ MIGRATION: Altes data-Feld gibt es auf dem Server nicht mehr
  const checkAndDeleteLegacyData = async () => {
    alert(`✅ ${t('backup.noOldBackups')}\n\n${t('backup.databaseClean')}`);
  };

  // �🕒 BACKUP-ÜBERSICHT: Zeige verfügbare Backups
  window.showBackups = async () => {
    const allKeys = await get('all_backup_keys') || [];
    console.log('📋 Verfügbare Backups:');
    
    for (const key of allKeys) {
      const backup = await get(key);
      if (backup) {
        console.log(`📄 ${key}: ${backup.count} Pakete, ${backup.timestamp} (${backup.reason})`);
      }
    }
    
    return allKeys;
  };

  // 🆘 NOTFALL-FUNKTION: Im Browser-Konsole aufrufbar
  window.emergencyRestore = restoreFromBackup;

  // 🔄 MIGRATION: Validiere und migriere Datenstrukturen
  const validateAndMigratePakete = (paketeArray) => {
    if (!Array.isArray(paketeArray)) {
      console.warn('⚠️ Pakete sind kein Array - konvertiere zu Array');
      return [];
    }
    
    return paketeArray.map((paket, index) => {
      // Stelle sicher, dass alle erforderlichen Felder existieren
      const migratedPaket = {
        id: paket.id || `migrated_${Date.now()}_${index}`,
        name: paket.name || 'Unbekannt',
        streetAndNumber: paket.streetAndNumber || paket.address || '',
        postalAndCity: paket.postalAndCity || '',
        trackingNumber: paket.trackingNumber || '',
        regalplatz: paket.regalplatz || '',
        datum: paket.datum || new Date().toISOString().slice(0, 10),
        ausgegebenAm: paket.ausgegebenAm || null,
        zurueckgeschicktAm: paket.zurueckgeschicktAm || null,
        zurueckgeschickt: paket.zurueckgeschickt || false,
        updatedAt: paket.updatedAt || new Date().toISOString(),
        // Behalte alle anderen Felder
        ...paket
      };
      
      return migratedPaket;
    });
  };

  // 🧹 OFFLINE-/LOKAL-CLEANUP: Lokales Aufräumen alter Pakete (ohne Account, offline oder im abgelaufenen Lokal-Modus)
  // Online + eingeloggt + aktiver Cloud-Sync → Server-Cron übernimmt → Hash-Mismatch → automatischer Sync
  // Offline / kein Account / Lokal-Modus (reason === 'expired') → Client räumt lokal in IDB selbst auf
  useEffect(() => {
    // Eingeloggt + online + nicht im abgelaufenen Lokal-Modus → Server-Cron übernimmt
    if (currentUser && isOnline && getCloudSyncStatus().reason !== 'expired') return;
    
    // Warte 3 Sekunden damit Auth + Premium-Status sich initialisieren können
    const timer = setTimeout(async () => {
      // Nochmal prüfen nach Delay (falls Cloud-Sync aktiv oder Status noch nicht 'expired' → Server-Cron übernimmt)
      if (getAuthUser() && navigator.onLine && getCloudSyncStatus().reason !== 'expired') return;
      
      try {
        const lastCleanupTime = await get('lastCleanupTimestamp') || 0;
        const now = Date.now();
        const sixHours = 6 * 60 * 60 * 1000;
        if (lastCleanupTime > 0 && (now - lastCleanupTime) < sixHours) {
          console.log('⏭️ Offline-Cleanup übersprungen (letzter < 6h her)');
          return;
        }

        const lokale = await get('pakete') || [];
        if (lokale.length === 0) return;

        const nowDate = new Date();
        const retentionDays = getConfiguredDeleteDays();
        const retentionLimitDays = retentionDays === 0 ? (1 / 24) : retentionDays;
        const cutoffMs = getActiveWindowCutoffMs(now);
        const behalten = (p) => {
          if (!p) return false;
          if (!p.ausgegeben && !p.zurueckgeschickt && !p.ausgegebenAm) return true;
          if (!p.ausgegebenAm) return true;
          // Online-Pakete, deren Ausgabe/Rücksendung noch nicht an den Server übertragen wurde (uploaded === false),
          // noch nicht lokal löschen — sonst erführe der Server nie von der Ausgabe.
          if (p.uploaded === false && istOnlinePaket(p)) return true;
          const d = new Date(p.ausgegebenAm);
          if (isNaN(d.getTime())) return true;
          // Reine Lokal-Pakete (ohne Cloud-Sync) liegen nicht im Server-Archiv → volle retentionLimitDays lokal behalten
          if (!isCloudSyncAllowed() && !istOnlinePaket(p)) {
            return (nowDate - d) / (1000 * 60 * 60 * 24) <= retentionLimitDays;
          }
          if (retentionLimitDays < ACTIVE_WINDOW_DAYS) {
            return (nowDate - d) / (1000 * 60 * 60 * 24) <= retentionLimitDays;
          }
          return isPackageInActiveWindow(p, cutoffMs);
        };
        const entferntIds = new Set(lokale.filter(p => !behalten(p)).map(p => p && p.id).filter(Boolean));

        if (entferntIds.size > 0) {
          console.log(`🧹 Offline-Cleanup: ${entferntIds.size} alte Pakete lokal entfernt`);
          await listeAnwenden((akt) => (akt || []).filter(p => !entferntIds.has(p.id)));
          const { calculatePackagesHash } = await import('./packageSyncHelper');
          const newHash = await calculatePackagesHash(paketeRef.current || []);
          await set('packagesHash', newHash);
        }
        await set('lastCleanupTimestamp', now);
      } catch (err) {
        console.warn('⚠️ Offline-Cleanup Fehler:', err);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [currentUser, isOnline]);

  // Initialisiere Favoriten beim App-Start
  // (Statistik wird nicht mehr clientseitig gehalten — Server zählt selbst)
  useEffect(() => {
    get('favoriten').then(gespeicherteFavoriten => {
      if (gespeicherteFavoriten && gespeicherteFavoriten.length > 0) {
        console.log('Favoriten beim Start geladen:', gespeicherteFavoriten);
        setFavoriten(gespeicherteFavoriten);
      }
    }).catch(err => console.error('Fehler beim Laden der Favoriten beim Start:', err));
  }, []);


// ============================================
// 🔄 LEGACY: syncDataAfterLogin wird nicht mehr aufgerufen
// ============================================
// Server-Modus: syncDataWithSubcollections ist die aktive Sync-Funktion
const syncDataAfterLogin = async () => {
  console.log("⚠️ syncDataAfterLogin ist veraltet - nutze syncDataWithSubcollections");
};
// ============================================
// 🆕 NEUE SUBCOLLECTION-SYNC FUNKTION
// ============================================
const syncDataWithSubcollections = async () => {
  console.log("📦 🆕 SUBCOLLECTION-SYNC startet...");
  
  // 🛡️ RACE CONDITION FIX: Prüfe ob Komponente noch mounted ist
  if (!isMountedRef.current) {
    console.log("⏹️ Sync abgebrochen - Komponente nicht mehr mounted");
    return false;
  }
  
  const user = getAuthUser();
  if (!user) {
    console.warn("Nicht eingeloggt");
    return false;
  }
  
  // 🔒 Premium/Trial-Check: Ohne aktives Premium/Trial keinen Cloud-Sync!
  if (!isCloudSyncAllowed()) {
    console.warn('🔒 Cloud-Sync blockiert – kein Premium oder Trial aktiv');
    return false;
  }
  
  if (syncInProgress || syncLockRef.current) {
    console.log("⏭️ Sync bereits aktiv (Lock aktiv)");
    return false;
  }
  
  try {
    syncLockRef.current = true; // 🔒 Synchroner Lock SOFORT setzen
    setSyncInProgress(true);
    setLoading(true);
    const userId = user.uid;

    // 📤 ZUERST offene Offline-Ausgaben/-Änderungen (uploaded === false) an den Server senden,
    // bevor wir den Server-Stand/Hash abfragen — verhindert, dass ein Pull sie überschreibt.
    try {
      await paketeJetztSpeichern();
      await flushPendingPackages({ anwenden: listeAnwenden });
    } catch (flushErr) {
      console.warn('⚠️ Pre-Sync-Flush Fehler:', flushErr?.message);
    }
    
    // ✅ Server-API: Ein einziger Aufruf für User-Daten (Version + Hash + Count)
    let serverVersion = null;
    let serverHash = null;
    let serverCount = 0;
    
    try {
      const userData = await serverDataService.getUser();
      if (userData) {
        serverVersion = userData.appVersion || userData.app_version;
        serverHash = userData.packagesHash || userData.packages_hash;
        serverCount = userData.packagesCount || userData.packages_count || 0;
      }
      console.log(`📦 User-Daten geladen: Version=${serverVersion}, Hash=${serverHash?.substring(0, 8) || 'keiner'}..., Count=${serverCount}`);
    } catch (docError) {
      console.warn("⚠️ User-Daten Fehler:", docError);
    }
    
    // 1️⃣ VERSION-CHECK
    try {
      if (serverVersion && serverVersion !== APP_VERSION) {
        const serverMajor = parseInt(serverVersion.split('.')[0]);
        const serverMinor = parseInt(serverVersion.split('.')[1]);
        const currentMajor = parseInt(APP_VERSION.split('.')[0]);
        const currentMinor = parseInt(APP_VERSION.split('.')[1]);
        
        if (serverMajor > currentMajor || (serverMajor === currentMajor && serverMinor > currentMinor)) {
          console.warn(`⚠️ VERALTETE APP! Server: ${serverVersion}, Aktuell: ${APP_VERSION}`);
          
          const openStore = confirm(`⚠️ ${t('ui.main.appUpdateAvailable')}\n\n${t('ui.main.yourVersion')}: ${APP_VERSION}\n${t('ui.main.newVersion')}: ${serverVersion}\n\n${t('ui.main.pressOkPlayStore')}`);
          if (openStore) {
            window.open('https://play.google.com/store/apps/details?id=com.paketverwaltung.app', '_blank');
          }
        }
      }
      
      // Version speichern wenn berechtigt
      const shouldUpdateVersion = !serverVersion || (() => {
        const serverMajor = parseInt(serverVersion.split('.')[0]);
        const serverMinor = parseInt(serverVersion.split('.')[1]);
        const currentMajor = parseInt(APP_VERSION.split('.')[0]);
        const currentMinor = parseInt(APP_VERSION.split('.')[1]);
        return currentMajor > serverMajor || (currentMajor === serverMajor && currentMinor >= serverMinor);
      })();
      
      await serverDataService.updateUser({
        ...(shouldUpdateVersion ? { appVersion: APP_VERSION } : {}),
        lastLogin: new Date().toISOString(),
        deviceInfo: navigator.userAgent
      });
      
    } catch (versionError) {
      console.warn("⚠️ Version-Check Fehler:", versionError);
    }
    
    // 2️⃣ PAKETE AUS SUBCOLLECTION LADEN (MIT HASH-OPTIMIERUNG!)
    // ✅ serverHash und serverCount sind bereits oben geladen!
    console.log("📦 Prüfe ob Sync notwendig...");
    
    const lokalePakete = await get('pakete') || [];
    const localHash = await get('packagesHash');
    const lastCleanup = await get('lastCleanupTimestamp') || 0;
    const lastSyncTime = await get('lastPackageSync') || 0;
    
    // ⚠️ STALE-SYNC WARNUNG: Nur wenn letzter Sync > 76h her UND der Pre-Sync-Flush oben
    // nicht alle ungesyncten Pakete hochladen konnte (z. B. nach Upgrade aus dem Lokal-Modus
    // sind die offenen Pakete bereits durch flushPendingPackages hochgeladen worden).
    const syncAge = Date.now() - lastSyncTime;
    const STALE_THRESHOLD = 76 * 60 * 60 * 1000; // 76 Stunden
    const hatNochNichtHochgeladene = lokalePakete.some(p => p && p.uploaded === false);
    if (lastSyncTime > 0 && syncAge > STALE_THRESHOLD && hatNochNichtHochgeladene && serverCount > 0) {
      const tageOffline = Math.floor(syncAge / (24 * 60 * 60 * 1000));
      const stunden = Math.floor((syncAge % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      console.warn(`⚠️ STALE-SYNC: Letzter Sync war vor ${tageOffline} Tagen und ${stunden} Stunden!`);
      console.warn(`   Lokal: ${lokalePakete.length} Pakete, Server: ${serverCount} Pakete`);
      
      // Server-Daten haben Vorrang — lokale Offline-Änderungen werden NICHT hochgeladen
      // Zeige dem User eine Warnung
      const warnung = `⚠️ ${tageOffline > 0 ? t('ui.main.offlineDays', { count: tageOffline }) : t('ui.main.offlineHours', { count: stunden })}\n\n` +
        `${t('ui.main.staleLocal', { count: lokalePakete.length })}\n` +
        `${t('ui.main.staleServer', { count: serverCount })}\n\n` +
        `${t('ui.main.staleLoadingServer')}\n` +
        `${t('ui.main.staleNotUploaded')}\n\n` +
        `${t('ui.main.staleHintRestore')}`;
      
      // Nicht-blockierender Hinweis
      setTimeout(() => {
        if (isMountedRef.current) alert(warnung);
      }, 1000);
    }
    console.log(`📌 Letzter Cleanup: ${lastCleanup ? new Date(lastCleanup).toLocaleString('de-DE') : 'nie'}`);
    console.log(`📌 Letzter Sync: ${lastSyncTime ? new Date(lastSyncTime).toLocaleString('de-DE') : 'nie'}`);
    console.log(`🔍 DEBUG HASH-VERGLEICH:`);
    console.log(`   📦 Lokal:  Hash="${localHash || 'NULL'}" (${lokalePakete.length} Pakete)`);
    console.log(`   📦 Server: Hash="${serverHash || 'NULL'}" (${serverCount} Pakete)`);
    console.log(`   📦 Match:  ${serverHash === localHash ? '✅ GLEICH' : '❌ UNTERSCHIEDLICH'}`);
    
    // 🔍 DEBUG: Warum kein lokaler Hash?
    if (!localHash && lokalePakete.length > 0) {
      console.warn(`⚠️ WARNUNG: Lokale Pakete vorhanden (${lokalePakete.length}) aber KEIN Hash!`);
      console.warn(`   → IndexedDB möglicherweise teilweise geleert (Browser-Cache-Bereinigung?)`);
    }    
    let firebasePakete = [];
    let needsFullSync = false;
    
    // 🔍 Entscheide ob Full-Sync nötig ist
    if (!serverHash) {
      // Kein Server-Hash → neuer User oder Migration nötig
      console.log("📦 Kein Server-Hash → prüfe ob Migration nötig...");
      needsFullSync = true;
    } else if (!localHash && lokalePakete.length > 0) {
      // 🔧 v1.43.6 FIX: Lokale Pakete vorhanden aber kein Hash
      // → Berechne Hash lokal und vergleiche mit Server!
      console.log("📦 Kein lokaler Hash, aber lokale Pakete vorhanden → berechne Hash...");
      const calculatedHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(lokalePakete));
      console.log(`📦 Berechneter Hash: ${calculatedHash.substring(0, 8)}... (Server: ${serverHash.substring(0, 8)}...)`);
      
      if (calculatedHash === serverHash) {
        // ✅ Lokale Daten sind korrekt! Nur Hash war weg (Cache-Bereinigung)
        console.log("✅ Berechneter Hash = Server-Hash → KEIN Full-Sync nötig!");
        console.log("   → Speichere Hash lokal und nutze lokale Daten");
        await set('packagesHash', calculatedHash);
        firebasePakete = lokalePakete;
        needsFullSync = false;
      } else {
        // ❌ Hash unterschiedlich → doch Full-Sync
        console.log("📦 Berechneter Hash ≠ Server-Hash → Full-Sync nötig");
        needsFullSync = true;
      }
    } else if (!localHash) {
      // Kein lokaler Hash UND keine lokalen Pakete → brauchen Server-Daten
      console.log("📦 Kein lokaler Hash und keine lokalen Pakete → Full-Sync nötig");
      needsFullSync = true;
    } else if (serverHash !== localHash) {
      // Hashes unterschiedlich → prüfe ob lokale Daten trotzdem korrekt sind
      // 🔧 FIX: Konsolidierung oder andere lokale Operationen können den gespeicherten Hash
      // veralten lassen, obwohl die Pakete identisch sind. Daher: Hash NEU berechnen!
      console.log("📦 Gespeicherte Hashes unterschiedlich → prüfe tatsächliche Daten...");
      const recalculatedHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(lokalePakete));
      if (recalculatedHash === serverHash) {
        console.log(`✅ Neuberechneter Hash = Server-Hash → KEIN Full-Sync nötig!`);
        console.log(`   → Gespeicherter Hash war veraltet, aktualisiere...`);
        await set('packagesHash', recalculatedHash);
        firebasePakete = lokalePakete;
        needsFullSync = false;
      } else {
        // 🔧 v1.47 FIX: Erst Chronik-Delta-Sync versuchen statt sofort Full-Sync!
        console.log(`📦 Hash unterschiedlich → versuche Chronik-Delta-Sync...`);
        console.log(`   Berechnet: ${recalculatedHash.substring(0, 8)}... vs Server: ${serverHash.substring(0, 8)}...`);
        
        try {
          const lastSyncTimestamp = await get('lastPackageSync') || 0;
          const { loadChronikSinceTimestamp } = await import('./chronikHelper');
          const chronikEntries = await loadChronikSinceTimestamp(lastSyncTimestamp);
          
          console.log(`📦 Chronik-Check: ${chronikEntries.length} Einträge seit ${new Date(lastSyncTimestamp).toLocaleTimeString()}`);
          
          // Nur SYNC-relevante Aktionen filtern
          const syncActions = ['paket_hinzugefuegt', 'paket_geaendert', 'paket_ausgegeben', 'paket_geloescht', 'paket_zurueckgeschickt', 'cleanup'];
          const relevantEntries = chronikEntries.filter(e => syncActions.includes(e.action));
          
          if (relevantEntries.length > 0 && relevantEntries.length <= 50) {
            // ✅ DELTA-SYNC: Wenige Änderungen → wende sie einzeln an
            console.log(`📦 ✅ Delta-Sync möglich: ${relevantEntries.length} Änderungen`);
            const { extractPaketIdsFromChronik } = await import('./chronikHelper');
            const { toLoad, toDelete } = extractPaketIdsFromChronik(relevantEntries);
            
            const paketMap = new Map(lokalePakete.map(p => [p.id, p]));
            
            // Löschungen anwenden
            toDelete.forEach(id => {
              paketMap.delete(id);
              console.log(`🗑️ Delta-Sync: Lokal gelöscht: ${id}`);
            });
            
            // Geänderte/neue Pakete einzeln laden
            if (toLoad.length > 0) {
              console.log(`📦 Delta-Sync: Lade ${toLoad.length} einzelne Pakete...`);
              const { loadSinglePaket } = await import('./packageSyncHelper');
              for (const paketId of toLoad) {
                try {
                  const existingLocal = paketMap.get(paketId);
                  if (existingLocal && existingLocal.uploaded === false) {
                    console.log(`📦 Delta-Sync: Behalte lokale ungesyncte Änderung für ${paketId}`);
                    continue;
                  }
                  const paket = await loadSinglePaket(paketId);
                  if (paket) {
                    paketMap.set(paketId, { ...paket, uploaded: true });
                  }
                } catch (e) {
                  console.warn(`⚠️ Delta-Sync: Paket ${paketId} konnte nicht geladen werden`);
                }
              }
            }
            
            firebasePakete = Array.from(paketMap.values());
            needsFullSync = false;
            console.log(`✅ Delta-Sync abgeschlossen: ${firebasePakete.length} Pakete (${toDelete.length} gelöscht, ${toLoad.length} geladen)`);
          } else if (relevantEntries.length === 0) {
            // Hashes weichen ab, aber keine Chronik-Einträge seit letztem Sync → Server-Wahrheit neu laden
            console.log(`📦 Keine Chronik-Einträge trotz Hash-Abweichung → Full-Sync erzwingen`);
            needsFullSync = true;
          } else {
            // Zu viele Änderungen → Full-Sync als Fallback
            console.log(`📦 ${relevantEntries.length} Änderungen (>50) → Full-Sync als Fallback`);
            needsFullSync = true;
          }
        } catch (chronikError) {
          console.warn(`⚠️ Chronik-Delta-Sync fehlgeschlagen:`, chronikError);
          needsFullSync = true;
        }
      }
    } else if (lokalePakete.length === 0 && serverCount > 0) {
      // Lokale Daten leer aber Server hat Daten
      console.log("📦 Lokale Daten leer, Server hat Daten → Full-Sync nötig");
      needsFullSync = true;
    } else if (serverHash === localHash && lokalePakete.length === 0) {
      // 🔧 v1.50 FIX: Hash identisch aber lokal LEER → Full-Sync erzwingen!
      // Kann passieren wenn Hash in IndexedDB gespeichert wurde aber Pakete gelöscht/cleared
      console.log("📦 Hash identisch aber lokal 0 Pakete → Full-Sync nötig (Hash-Anomalie)");
      needsFullSync = true;
    } else {
      // Selbstheilung (25.10.2026): Falls durch einen früheren Kontowechsel fremde Pakete
      // (uploaded !== false) in 'pakete' gelandet sind, während 'packagesHash' nur die
      // Server-Liste abbildete, stimmt der tatsächlich berechnete Hash der lokalen Liste
      // nicht mit serverHash überein → Full-Sync erzwingen (ohne vorheriges Leeren, damit
      // bei Netzausfall nichts verloren geht; gegenAktuelleListeSchuetzen verwirft alte
      // uploaded:true-Fremdpakete automatisch, weil sie bereits im Schnappschuss 'vorher' stehen).
      const echterLokalerHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(lokalePakete));
      const hatOffeneLokale = lokalePakete.some(p => p && p.uploaded === false);
      if (echterLokalerHash !== serverHash && !hatOffeneLokale) {
        console.warn(`📦 ⚠️ Lokale Liste (${lokalePakete.length}) weicht trotz gleichem gespeichertem Hash vom Server (${serverCount}) ab → Selbstheilung via Full-Sync!`);
        needsFullSync = true;
      } else {
        // ✅ Hashes identisch UND lokale Daten vorhanden → KEIN Full-Sync nötig!
        console.log("✅ Hash identisch → KEIN Full-Sync nötig! (spart hunderte Reads)");
        firebasePakete = lokalePakete; // Nutze lokale Daten
      }
    }
    
    if (needsFullSync) {
      console.log("📦 Lade Pakete aus Subcollection...");
      firebasePakete = await loadAllPackages(true);
    }
    
    // 🗑️ Lade gelöschte IDs NUR wenn Full-Sync läuft (sonst unnötige Reads!)
    let deletedIds = [];
    if (needsFullSync) {
      deletedIds = await loadDeletedIds();
    }
    try {
      const pendingDeletes = (await get('pending_delete_ids')) || [];
      if (Array.isArray(pendingDeletes) && pendingDeletes.length > 0) {
        deletedIds = [...deletedIds, ...pendingDeletes];
        const pendingSet = new Set(pendingDeletes);
        firebasePakete = firebasePakete.filter(p => !pendingSet.has(p.id));
      }
    } catch { /* */ }
    
    let filteredLokalePakete = lokalePakete;
    if (deletedIds.length > 0) {
      const deletedSet = new Set(deletedIds);
      const vorher = lokalePakete.length;
      filteredLokalePakete = lokalePakete.filter(p => !deletedSet.has(p.id));
      const entfernt = vorher - filteredLokalePakete.length;
      if (entfernt > 0) {
        console.log(`🗑️ ${entfernt} lokale Pakete entfernt (wurden auf anderem Gerät gelöscht)`);
        await set('pakete', filteredLokalePakete);
      }
    }
    
    console.log(`📦 Lokal: ${filteredLokalePakete.length}, Firebase: ${firebasePakete.length}`);
    
    // 🔍 DEBUG: Zeige Differenz zwischen lokal und Firebase
    if (firebasePakete.length !== filteredLokalePakete.length) {
      const diff = firebasePakete.length - filteredLokalePakete.length;
      console.log(`⚠️ DIFFERENZ: Firebase hat ${Math.abs(diff)} Pakete ${diff > 0 ? 'MEHR' : 'WENIGER'} als lokal`);
    }
    
    let serverListeFuerStatus = [];

    if (firebasePakete.length > 0) {
      // ✅ Server hat Daten → SERVER-FIRST (bis auf noch nicht hochgeladene, s.u.)
      console.log("📥 Server hat Daten → SERVER-FIRST Modus");
      
      // Backup vor Überschreiben (zur Not wieder einspielen)
      if (filteredLokalePakete.length > 0) {
        await createBackup(filteredLokalePakete, 'before_download_sync');
        console.log(`💾 Backup erstellt: ${filteredLokalePakete.length} lokale Pakete gesichert`);
      }
      
      // 🛡️ Prüfung vor State-Updates
      if (!isMountedRef.current) return;
      
      // 🔥 SERVER-FIRST: Der Server ist die einzige Wahrheit …
      // … MIT EINER AUSNAHME: Pakete, die offline ausgegeben/geändert oder angelegt
      // und noch nicht hochgeladen wurden (uploaded === false), dürfen nicht
      // von veralteten Server-Zeilen überschrieben werden!
      const serverPakete = firebasePakete;
      serverListeFuerStatus = serverPakete;
      const lokaleListe = gegenAktuelleListeSchuetzen(firebasePakete, filteredLokalePakete, new Set(deletedIds));

      const firebaseCount = firebasePakete.length;
      console.log(`📦 Server-First: ${serverPakete.length} Pakete vom Server übernommen (lokal aktiv: ${lokaleListe.length})`);
      
      // Angezeigt und gespeichert wird die Server-Wahrheit PLUS den noch
      // offenen lokalen Paketen/Ausgaben (siehe oben) — sonst verschwänden sie hier.
      setPakete(lokaleListe);
      await set('pakete', lokaleListe);
      // ✅ ServerPakete = Server-Daten (verhindert auto-upload alter lokaler Pakete)
      setServerPakete(serverPakete); // ← Wichtig für autoSyncChanges Vergleich!
      
      // 🔧 v1.47 FIX: Hash IMMER mit aktuellem Algorithmus berechnen + speichern!
      // Problem war: Alter Server-Hash nutzte lastUpdated (instabil) → bei jedem Start anders
      // Lösung: Hash wird jetzt IMMER deterministisch neu berechnet (ID + stabile Felder)
      // und SOWOHL lokal ALS AUCH auf Firebase gespeichert.
      const calculatedHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(serverPakete));
      await set('packagesHash', calculatedHash);
      lastWrittenHashRef.current = calculatedHash; // 🔧 v1.49: Self-Trigger-Schutz
      console.log(`📦 Hash berechnet und lokal gespeichert: ${calculatedHash.substring(0, 8)}... (${serverPakete.length} Pakete)`);
      
      // Prüfe ob Server-Hash identisch ist (dann kein Firebase-Write nötig)
      if (serverHash !== calculatedHash) {
        console.log(`📦 Server-Hash aktualisieren: ${(serverHash || 'NULL').substring(0, 8)}... → ${calculatedHash.substring(0, 8)}...`);
        await updatePackagesHash(serverPakete);
        console.log(`📦 ✅ Hash auf Firebase aktualisiert!`);
      } else {
        console.log(`📦 ✅ Server-Hash bereits aktuell, kein Update nötig`);
      }
      
      await set('lastPackageSync', Date.now());
      
      // Setze lastUpdated für UI-Anzeige
      const now = new Date().toISOString();
      if (isMountedRef.current) setLastUpdated(now);
      
      if (isMountedRef.current) {
        // 🔧 v1.46 FIX: Nur bei echtem Sync (needsFullSync) große Meldung zeigen
        if (needsFullSync) {
          setInfoMessage(`✅ ${t('sync.packagesSynced', { count: serverPakete.length })} (${t('ui.main.onlineCount', { count: firebaseCount })})`);
          setTimeout(() => isMountedRef.current && setInfoMessage(''), 5000);
        }
        // Bei Hash-Match (Tab-Wechsel) → KEINE Meldung (nichts hat sich geändert)
      }
      
    } else if (filteredLokalePakete.length > 0) {
      // Server leer, lokal voll → lokale Pakete direkt zum Server hochladen
      console.log(`📦 Server leer, ${filteredLokalePakete.length} lokale Pakete → Upload`);
      try {
        const CHUNK_SIZE = 50;
        for (let i = 0; i < filteredLokalePakete.length; i += CHUNK_SIZE) {
          if (!isMountedRef.current) break;
          const chunk = filteredLokalePakete.slice(i, i + CHUNK_SIZE);
          await serverPackageService.batchUpsertPackages(chunk);
        }
        if (isMountedRef.current) {
          setPakete(filteredLokalePakete);
          setServerPakete(filteredLokalePakete);
        }
        const { calculatePackagesHash } = await import('./packageSyncHelper');
        const newHash = await calculatePackagesHash(filteredLokalePakete);
        await set('packagesHash', newHash);
        lastWrittenHashRef.current = newHash;
        await serverPackageService.updateSyncHash(newHash);
        await set('lastPackageSync', Date.now());
        console.log(`📦 ✅ ${filteredLokalePakete.length} Pakete hochgeladen`);
      } catch (uploadErr) {
        console.warn('⚠️ Upload lokaler Pakete fehlgeschlagen:', uploadErr);
      }
    } else {
      // Server UND lokal leer → neuer User, nichts zu tun
      console.log("📦 Server und lokal leer → keine Pakete");
      if (isMountedRef.current) {
        setPakete([]);
        setServerPakete([]);
      }
    }
    
    // 🛡️ Prüfung vor nächstem Schritt
    if (!isMountedRef.current) return;
    
    // 3️⃣ CLEANUP: Wird jetzt serverseitig per Cron-Job erledigt (alle 6h)
    // Server löscht alte Pakete → berechnet neuen Hash → Client erkennt Hash-Mismatch → Full-Sync
    // Kein Client-Cleanup mehr nötig!
    console.log("🧹 Cleanup: Server-seitig per Cron-Job (alte Pakete > 5 Tage)");
    
    // 🛡️ Finale Prüfung
    if (!isMountedRef.current) return;
    
    // 4️⃣ SYNC-STATUS aktualisieren (für UI-Anzeige)
    const currentPaketeForStatus = await get('pakete') || [];
    const currentServerPakete = serverListeFuerStatus; // s. Deklaration oben
    const differences = findDifferences(currentPaketeForStatus, currentServerPakete);
    if (isMountedRef.current) setSyncDifferences(differences);
    console.log(`📊 Sync-Status: ${differences.onlyInLocal.length} nur lokal, ${differences.onlyInRemote.length} nur remote`);
    
    // 5️⃣ CHRONIK: Nicht beim Login laden! Nur bei Bedarf (on-demand)
    // Wird geladen wenn User auf Chronik-Tab klickt
    console.log("📋 Chronik: Wird bei Bedarf geladen (lazy loading)");
    
    // 6️⃣ STATISTIK: serverseitig — lädt Statistics.js direkt via /overview
    return true;

  } catch (error) {
    console.error("📦 SUBCOLLECTION-SYNC FEHLER:", error);
    if (isMountedRef.current) {
      alert(`${t('errors.syncError')}: ${error.message}`);
    }
    return false;
  } finally {
    // 🔓 Lock freigeben
    syncLockRef.current = false;
    // 🛡️ State-Updates nur wenn noch mounted
    if (isMountedRef.current) {
      setSyncInProgress(false);
      setLoading(false);
    }
    
    // 🔄 v1.44 FIX: Verarbeite Queue falls User während Login-Sync etwas gespeichert hat
    if (syncQueueRef.current.length > 0) {
      console.log(`📋 Login-Sync fertig → ${syncQueueRef.current.length} wartende Queue-Items werden verarbeitet`);
      setTimeout(() => processQueue(), 500);
    }
  }
};

// Function to check if a package is synced
  const isPaketSynced = (paket) => {
    // Einfache Prüfung: uploaded-Flag ist die Source of Truth
    return paket.uploaded === true;
  };

  // Function to update sync status
  const updateSyncStatus = async () => {
    if (currentUser && isOnline) {
      const lokalePakete = await get('pakete');
      const differences = findDifferences(lokalePakete, serverPakete);
      setSyncDifferences(differences);
    }
  };

  // 🆕 Background sync effect - NUR Login, kein Polling mehr hier!
  // 🔒 v1.44 FIX: Nutze localStorage für Cross-Tab-Schutz!
  // sessionStorage ist Tab-spezifisch → mehrere Tabs = mehrere Syncs!
  // localStorage ist für ALLE Tabs gleich → perfekt für Lock
  useEffect(() => {
    if (!currentUser) return;
    
    // 🔧 v1.50 FIX: Account-Wechsel erkennen → RAM + IndexedDB-Sync-Daten sofort clearen
    // Problem: IDB-Keys wie 'pakete', 'packagesHash' UND paketeRef.current sind NICHT per User → 
    // nach Account-Wechsel lagen noch Daten vom vorherigen User in IDB und im Arbeitsspeicher!
    const lastIdbUser = localStorage.getItem('kiosk_idb_user');
    const accountSwitched = Boolean(lastIdbUser && lastIdbUser !== currentUser.uid);
    if (accountSwitched) {
      console.log(`🔄 Account-Wechsel erkannt: ${lastIdbUser.substring(0,6)}... → ${currentUser.uid.substring(0,6)}...`);
      // Synchron SOFORT den Arbeitsspeicher leeren (auch offline), damit weder Reconnect-Flush noch
      // gegenAktuelleListeSchuetzen alte Pakete des vorherigen Kontos einmischen können!
      leereKontodatenImSpeicher();
      if (!isOnline) {
        (async () => {
          try {
            const alteIdbPakete = await get('pakete') || [];
            if (alteIdbPakete.length > 0) await createBackup(alteIdbPakete, 'before_account_switch');
          } catch { /* ignore */ }
          await leereKontodatenInIdb();
          localStorage.setItem('kiosk_idb_user', currentUser.uid);
        })();
      }
    } else {
      localStorage.setItem('kiosk_idb_user', currentUser.uid);
    }

    if (!isOnline) return;
    
    // ⚠️ WICHTIG: Cross-Tab-Lock mit localStorage!
    // Verhindert dass mehrere Browser-Tabs gleichzeitig synchen
    const lockKey = `syncLock_${currentUser.uid}`;
    const lastSyncKey = `lastLoginSync_${currentUser.uid}`;
    const SYNC_COOLDOWN = 30000; // 30 Sekunden Cooldown zwischen Syncs
    const LOCK_TIMEOUT = 60000; // Lock verfällt nach 60 Sekunden (falls Tab abstürzt)
    let idbBeiWechselGeleert = false;
    let eigenerTabSynct = false;

    const starteLoginSync = async (force = false) => {
      if (eigenerTabSynct) return;
      const now = Date.now();
      // 🔒 Prüfe ob ein anderer Tab gerade syncht (Lock aktiv)
      const lockData = localStorage.getItem(lockKey);
      if (lockData) {
        const lockTime = parseInt(lockData, 10);
        if (now - lockTime < LOCK_TIMEOUT) {
          const secondsAgo = Math.round((now - lockTime) / 1000);
          console.log(`📦 🔒 Cross-Tab-Lock aktiv (anderer Tab syncht seit ${secondsAgo}s)`);
          loginSyncCompletedRef.current = true;
          return;
        }
        console.log("📦 ⚠️ Abgelaufener Lock gefunden → wird ignoriert");
      }

      // 🕐 Prüfe ob kürzlich synchronisiert wurde (außer bei Kontowechsel oder expliziter Freigabe)
      const lastSyncTime = parseInt(localStorage.getItem(lastSyncKey) || '0', 10);
      if (!accountSwitched && !force && (now - lastSyncTime < SYNC_COOLDOWN)) {
        const secondsAgo = Math.round((now - lastSyncTime) / 1000);
        console.log(`📦 ⏭️ Login-Sync übersprungen (vor ${secondsAgo}s bereits synchronisiert)`);
        loginSyncCompletedRef.current = true;
        return;
      }

      // 🔒 Lock setzen (BEVOR wir starten!)
      eigenerTabSynct = true;
      localStorage.setItem(lockKey, now.toString());
      console.log("📦 🔒 Cross-Tab-Lock gesetzt");
    
    // � v1.46 FIX: Flag zurücksetzen - Login-Sync startet
      loginSyncCompletedRef.current = false;
      console.log("📦 🚀 Login-Sync starten...");

      try {
        // � v1.50 FIX: Bei Account-Wechsel IDB-Sync-Daten löschen
        if (accountSwitched && !idbBeiWechselGeleert) {
          console.log('🧹 Lösche RAM & IDB-Sync-Daten des vorherigen Users...');
          try {
            const alteIdbPakete = await get('pakete') || [];
            if (alteIdbPakete.length > 0) {
              await createBackup(alteIdbPakete, 'before_account_switch');
            }
          } catch { /* ignore */ }
          leereKontodatenImSpeicher();
          await leereKontodatenInIdb();
          localStorage.setItem('kiosk_idb_user', currentUser.uid);
          idbBeiWechselGeleert = true;
          console.log(`🧹 ✅ ${IDB_ACCOUNT_KEYS.length} IDB-Keys + RAM gelöscht — sauberer Start für neuen User`);
        }
        
        // Lokale-Pakete-Sync: Wenn Server leer ist aber lokal Daten existieren (z. B. erst ohne Konto
        // lokal getestet und dann erstmals mit Google angemeldet), einmalig hochladen.
        // NIEMALS bei einem Kontowechsel und NUR wenn Cloud-Sync aktiv ist!
        if (!accountSwitched && isCloudSyncAllowed()) {
          try {
            const userData = await serverDataService.getUser();
            const serverCount = parseInt(userData?.packages_count, 10) || 0;
            const lokalePakete = await get('pakete') || [];
            console.log(`📦 Pre-Sync-Check: Server=${serverCount}, Lokal=${lokalePakete.length}`);

            if (serverCount === 0 && lokalePakete.length > 0) {
              console.log(`📦 Server leer, ${lokalePakete.length} lokale Pakete → Upload`);
              const CHUNK_SIZE = 50;
              for (let i = 0; i < lokalePakete.length; i += CHUNK_SIZE) {
                const chunk = lokalePakete.slice(i, i + CHUNK_SIZE);
                await serverPackageService.batchUpsertPackages(chunk);
              }
              const { calculatePackagesHash } = await import('./packageSyncHelper');
              const newHash = await calculatePackagesHash(lokalePakete);
              await set('packagesHash', newHash);
              lastWrittenHashRef.current = newHash;
              await serverPackageService.updateSyncHash(newHash);
              await set('lastPackageSync', Date.now());
              console.log(`📦 ✅ ${lokalePakete.length} Pakete hochgeladen`);
            }
          } catch (preErr) {
            console.warn('⚠️ Pre-Sync-Check fehlgeschlagen:', preErr);
          }
        }

        const erfolg = await syncDataWithSubcollections();
        if (erfolg) {
          // Nur bei tatsächlich durchgeführtem Sync Zeitstempel + Fertig-Flag setzen!
          localStorage.setItem(lastSyncKey, Date.now().toString());
          loginSyncCompletedRef.current = true;
          console.log("📦 ✅ loginSyncCompletedRef = true (Echtzeit-Sync darf jetzt arbeiten)");
        }
      } finally {
        eigenerTabSynct = false;
        localStorage.removeItem(lockKey);
        console.log("📦 🔓 Cross-Tab-Lock freigegeben");
      }
    };

    starteLoginSync(false);

    // Falls der Premium/Trial-Status beim Login erst asynchron vom Server eintrifft,
    // den Login-Sync sofort nachholen, sobald 'kiosk:cloud-sync-allowed' feuert.
    const onCloudSyncAllowed = () => {
      if (!eigenerTabSynct && !syncLockRef.current && !syncInProgress && !loginSyncCompletedRef.current) {
        console.log('📦 🔓 Cloud-Sync freigegeben → hole Login-Sync nach...');
        starteLoginSync(true);
      }
    };
    window.addEventListener('kiosk:cloud-sync-allowed', onCloudSyncAllowed);
    return () => {
      window.removeEventListener('kiosk:cloud-sync-allowed', onCloudSyncAllowed);
    };
  }, [currentUser, isOnline]);
  
  // 🔄 Reset Sync-Marker wenn User sich ausloggt
  useEffect(() => {
    if (!currentUser) {
      // Alle Sync-bezogenen Keys löschen (localStorage UND sessionStorage)
      ['localStorage', 'sessionStorage'].forEach(storageType => {
        const storage = window[storageType];
        Object.keys(storage).forEach(key => {
          if (key.startsWith('lastLoginSync_') || key.startsWith('syncLock_')) {
            storage.removeItem(key);
          }
        });
      });
      console.log("📦 🔄 Sync-Marker gelöscht (Logout)");
    }
  }, [currentUser]);

// ============================================
// 🔥 ECHTZEIT-SYNC via SSE + Fallback-Polling (ersetzt onSnapshot)
// ============================================
useEffect(() => {
  if (!currentUser || !isOnline) {
    console.log("📦 Echtzeit-Sync nicht gestartet: kein User oder offline");
    sseSyncTriggerRef.current = null;
    return;
  }

  console.log("📦 🔥 Starte ECHTZEIT-Sync (SSE + Fallback-Polling)");

  let debounceTimer = null;

  // 🔄 Sync-Check-Funktion: Prüft Hash und synchronisiert bei Bedarf
  const checkAndSync = async () => {
    // Lock-Check & Cloud-Sync-Freigabe (im kostenlosen Lokal-Modus nach Testphase nicht abgleichen)
    if (syncLockRef.current || syncInProgress || !isCloudSyncAllowed()) return;

    try {
      const serverHashResult = await serverPackageService.getSyncHash();
      const serverHash = serverHashResult?.packagesHash || null;
      const serverCount = serverHashResult?.count || 0;
      
      // Self-Trigger-Schutz
      if (serverHash === lastWrittenHashRef.current) return;
      
      const localHash = await get('packagesHash');
      if (serverHash === localHash) return;

      console.log(`📦 Sync nötig:`);
      console.log(`   Server: ${serverHash?.substring(0, 8)}... (${serverCount} Pakete)`);
      console.log(`   Lokal:  ${localHash?.substring(0, 8) || 'NULL'}`);

      // Debounce (kurz, damit SSE-Events fast sofort wirken)
      if (debounceTimer) clearTimeout(debounceTimer);
      
      debounceTimer = setTimeout(async () => {
        const aktuellerLocalHash = await get('packagesHash');
        if (serverHash === aktuellerLocalHash) return;

        if (syncLockRef.current || syncInProgress) return;

        syncLockRef.current = true;
        console.log("📦 🔒 Sync-Lock gesetzt");

        try {
          // 📤 Zuerst evtl. offene Offline-Ausgaben (uploaded === false) hochladen
          try { await paketeJetztSpeichern(); await flushPendingPackages({ anwenden: listeAnwenden }); } catch { /* */ }

          const lokalePakete = serverPackageService.repariereRohePakete(await get('pakete') || []); // 🩹 alte rohe Zeilen
          
          // Chronik-basierter Delta-Sync
          const lastSyncTimestamp = await get('lastPackageSync') || 0;
          const { loadChronikSinceTimestamp } = await import('./chronikHelper');
          const chronikEntries = await loadChronikSinceTimestamp(lastSyncTimestamp);
          
          let firebasePakete;
          
          if (chronikEntries.length > 0 && chronikEntries.length <= 50) {
            // Delta-Sync
            const { toLoad, toDelete } = extractPaketIdsFromChronik(chronikEntries);
            const paketMap = new Map(lokalePakete.map(p => [p.id, p]));
            toDelete.forEach(id => paketMap.delete(id));
            if (toLoad.length > 0) {
              for (const paketId of toLoad) {
                const existingLocal = paketMap.get(paketId);
                if (existingLocal && existingLocal.uploaded === false) continue;
                const paket = await loadSinglePaket(paketId);
                if (paket) paketMap.set(paketId, { ...paket, uploaded: true });
              }
            }
            firebasePakete = Array.from(paketMap.values());
            console.log(`✅ Delta-Sync: ${firebasePakete.length} Pakete`);
          } else if (chronikEntries.length === 0) {
            const lokalerHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(lokalePakete));
            if (lokalerHash === serverHash) {
              await set('packagesHash', lokalerHash);
              await set('lastPackageSync', Date.now());
              firebasePakete = lokalePakete;
            } else {
              firebasePakete = await loadAllPackages(true, { speichern: false }); // forceBypass; gespeichert wird unten (geschützt)
            }
          } else {
            // Zu viele Chronik-Einträge = Full-Sync
            const lokalerHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(lokalePakete));
            if (lokalerHash === serverHash) {
              await set('packagesHash', lokalerHash);
              await set('lastPackageSync', Date.now());
              firebasePakete = lokalePakete;
            } else {
              firebasePakete = await loadAllPackages(true, { speichern: false }); // forceBypass; gespeichert wird unten (geschützt)
            }
            await set('lastPackageSync', Date.now());
          }

          // 🛡️ Ungesyncte lokale Änderungen (uploaded === false) niemals überschreiben
          firebasePakete = gegenAktuelleListeSchuetzen(firebasePakete, lokalePakete);

          // Server-First: Pakete übernehmen
          setPakete(firebasePakete);
          setServerPakete(firebasePakete);
          await set('pakete', firebasePakete);
          
          const hash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(firebasePakete));
          await set('packagesHash', hash);
          await set('lastPackageSync', Date.now());
          lastWrittenHashRef.current = hash;
          
          // Server-Hash ebenfalls aktualisieren (damit andere Clients korrekt vergleichen können)
          try {
            await serverPackageService.updateSyncHash(hash);
          } catch (hashErr) {
            console.warn('📦 Server-Hash-Update fehlgeschlagen (nicht kritisch):', hashErr.message);
          }
          
          const hatSichGeaendert = firebasePakete.length !== lokalePakete.length || hash !== aktuellerLocalHash;
          if (hatSichGeaendert) {
            const diff = firebasePakete.length - lokalePakete.length;
            const diffText = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : '';
            setInfoMessage(`🔄 ${t('ui.main.packagesSyncedCount', { count: firebasePakete.length })}${diffText ? ` (${diffText})` : ''}`);
            setTimeout(() => setInfoMessage(''), 3000);
          }
          
          console.log(`✅ Sync abgeschlossen: ${firebasePakete.length} Pakete, Hash: ${hash.substring(0, 8)}...`);
        } catch (error) {
          console.error("📦 Sync-Fehler:", error);
        } finally {
          syncLockRef.current = false;
          console.log("📦 🔓 Sync-Lock freigegeben");
        }
      }, 100);
    } catch (pollError) {
      // Fehler sind nicht kritisch
      console.debug("📦 Sync-Check-Fehler (wird ignoriert):", pollError.message);
    }
  };

  // 🔌 SSE-Trigger-Ref setzen (wird vom SSE-useEffect aufgerufen)
  sseSyncTriggerRef.current = checkAndSync;

  // 🕐 Polling alle 10s (+ SSE für sofortige Push-Events)
  const pollInterval = setInterval(checkAndSync, 10000);

  return () => {
    sseSyncTriggerRef.current = null;
    if (debounceTimer) clearTimeout(debounceTimer);
    clearInterval(pollInterval);
    console.log("📦 Echtzeit-Sync gestoppt");
  };
}, [currentUser, isOnline]); // Keine pakete Dependency - laden aus IndexedDB

// ============================================
// 🔌 SSE: Server-Sent Events für Echtzeit-Push
// ============================================
useEffect(() => {
  if (!currentUser || !isOnline) return;

  console.log("🔌 SSE: Verbinde mit Server für Echtzeit-Events...");

  const closeSSE = connectSSE({
    // 📦 Paket-Events → Direkt in lokale Liste einfügen/aktualisieren
    'package:created': async (data) => {
      console.log('🔌 SSE: package:created', data?.id || '');
      // 🩹 Der Server schickt die rohe Tabellenzeile (numerische id, empfaenger statt name) → erst ins App-Format
      const neu = data?.firebase_id ? serverPackageService.mapPackageFromServer(data) : null;
      if (neu?.id) {
        // Direkt einfügen statt Full-Sync (sofortige Anzeige)
        const lokalePakete = serverPackageService.repariereRohePakete(await get('pakete') || []);
        if (!lokalePakete.find(p => p.id === neu.id)) {
          const neuePakete = [{ ...neu, uploaded: true }, ...lokalePakete];
          setPakete(neuePakete);
          setServerPakete(neuePakete);
          await set('pakete', neuePakete);
          console.log('🔌 SSE: Paket direkt eingefügt:', data.id);
        }
      }
      sseSyncTriggerRef.current?.();
    },
    'package:updated': async (data) => {
      console.log('🔌 SSE: package:updated', data?.id || '');
      const neu = data?.firebase_id ? serverPackageService.mapPackageFromServer(data) : null; // 🩹 s. o.
      if (neu?.id) {
        // Direkt aktualisieren statt Full-Sync
        const lokalePakete = serverPackageService.repariereRohePakete(await get('pakete') || []);
        const idx = lokalePakete.findIndex(p => p.id === neu.id);
        if (idx !== -1) {
          lokalePakete[idx] = { ...neu, uploaded: true };
          setPakete([...lokalePakete]);
          setServerPakete([...lokalePakete]);
          await set('pakete', lokalePakete);
          console.log('🔌 SSE: Paket direkt aktualisiert:', data.id);
        } else {
          sseSyncTriggerRef.current?.();
        }
      } else {
        sseSyncTriggerRef.current?.();
      }
    },
    'package:deleted': async (data) => {
      console.log('🔌 SSE: package:deleted', data?.id || '');
      if (data?.id) {
        // Direkt entfernen statt Full-Sync
        const lokalePakete = await get('pakete') || [];
        const neuePakete = lokalePakete.filter(p => p.id !== data.id);
        if (neuePakete.length !== lokalePakete.length) {
          setPakete(neuePakete);
          setServerPakete(neuePakete);
          await set('pakete', neuePakete);
          console.log('🔌 SSE: Paket direkt entfernt:', data.id);
        }
      }
      sseSyncTriggerRef.current?.();
    },
    'packages:synced': (data) => {
      console.log('🔌 SSE: packages:synced', data?.count || '');
      sseSyncTriggerRef.current?.();
    },

    // 🔍 Such-Events → Tablet-Suchen neu laden
    'search:updated': (data) => {
      console.log('🔌 SSE: search:updated');
      sseSearchTriggerRef.current?.();
    },

    // 🔴 Live-Suche → Direkt verarbeiten (Push-Daten enthalten die Suche)
    'live-search': (data) => {
      console.log('🔌 SSE: live-search EVENT EMPFANGEN!', JSON.stringify(data));
      console.log('🔌 SSE: sseLiveSearchTriggerRef.current ist:', sseLiveSearchTriggerRef.current ? 'gesetzt ✅' : 'NULL ❌');
      sseLiveSearchTriggerRef.current?.(data);
    },

    // 📋 Chronik-Events (optional, für spätere Features)
    'chronik:new': (data) => {
      console.debug('🔌 SSE: chronik:new (Info)');
    },

    // 📋 Barcode-Stapel (pro Account über Fotostation/Tablet/Handy synchronisiert)
    'batch:updated': (data) => {
      applyServerBatch(data);
    },
  });

  return () => {
    closeSSE();
    console.log("🔌 SSE: Getrennt");
  };
}, [currentUser, isOnline]);// Hilfsfunktion zum Vergleichen von Arrays
const arraysEqual = (a, b) => {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  
  // Vergleiche IDs und updatedAt-Zeiten
  const aIds = new Set(a.map(item => `${item.id}-${item.updatedAt}`));
  const bIds = new Set(b.map(item => `${item.id}-${item.updatedAt}`));
  
  // Wenn die Anzahl der einzigartigen IDs unterschiedlich ist
  if (aIds.size !== bIds.size) return false;
  
  // Prüfe, ob alle IDs übereinstimmen
  for (const id of aIds) {
    if (!bIds.has(id)) return false;
  }
  
  return true;
};

const handleKeepLocal = async (item) => {
  try {
    // Aktualisiere Remote-Daten mit der lokalen Version via Server-API
    const user = getAuthUser();
    if (!user) throw new Error("Kein Benutzer eingeloggt");
    
    // Direkt das Paket auf Server aktualisieren
    await savePaket(item.localItem);
    await updatePackagesHash(pakete);
    
    setServerPakete(prev => prev.map(p => p.id === item.id ? item.localItem : p));
    console.log(`Lokale Version für ID ${item.id} auf Server überschrieben`);
    
    // Gehe zum nächsten Konflikt oder schließe Modal
    handleNextConflict();
  } catch (error) {
    console.error('Fehler beim Überschreiben der Remote-Daten:', error);
  }
};

const handleKeepRemote = async (item) => {
  try {
    // Aktualisiere lokale Daten mit der Remote-Version
    const lokalePakete = await get('pakete') || [];
    
    // Ersetze das konfliktbehaftete Element
    const updatedLocalData = lokalePakete.map(p => 
      p.id === item.id ? item.remoteItem : p
    );
    
    // Speichere lokal
    await set('pakete', updatedLocalData);
    setPakete(updatedLocalData);
    
    console.log(`Remote-Version für ID ${item.id} lokal übernommen`);
    
    // Gehe zum nächsten Konflikt oder schließe Modal
    handleNextConflict();
  } catch (error) {
    console.error('Fehler beim Aktualisieren der lokalen Daten:', error);
  }
};

// 🛟 BACKUP-WIEDERHERSTELLUNGS-FUNKTIONEN
const loadAvailableBackups = async () => {
  try {
    const { keys } = await import('idb-keyval');
    const allKeys = await keys();
    const backupKeys = allKeys.filter(k => k.startsWith('pakete_backup_'));
    
    const backupList = [];
    for (const key of backupKeys) {
      const backup = await get(key);
      if (backup && backup.data) {
        backupList.push({
          key: key,
          timestamp: backup.timestamp,
          reason: backup.reason,
          count: backup.count || backup.data.length,
          date: new Date(backup.timestamp),
          data: backup.data
        });
      }
    }
    
    // Sortiere nach Datum (neueste zuerst)
    backupList.sort((a, b) => b.date - a.date);
    
    setAvailableBackups(backupList);
    console.log(`📦 ${backupList.length} Backups gefunden`);
    
    return backupList;
  } catch (error) {
    console.error('Fehler beim Laden der Backups:', error);
    return [];
  }
};

const loadFirebaseWithoutCleanup = async () => {
  try {
    setLoading(true);
    const user = getAuthUser();
    
    if (!user) {
      alert('❌ ' + t('auth.notLoggedIn'));
      setLoading(false);
      return;
    }
    
    // Server-API: Alle Pakete laden
    const loadedPakete = await loadAllPackages();
    
    if (!loadedPakete || loadedPakete.length === 0) {
      alert('📦 ' + t('errors.noDataOnServer'));
      setLoading(false);
      return;
    }
    
    console.log(`📦 Server RAW: ${loadedPakete.length} Pakete (OHNE Cleanup)`);
    
    const confirmed = window.confirm(
      `☁️ ${t('sync.loadFirebaseData')}\n\n` +
      `📦 ${t('sync.packagesFound', { count: loadedPakete.length })}\n` +
      `📍 ${t('sync.packagesCurrently', { count: pakete.length })}\n\n` +
      `⚠️ ${t('sync.overwriteWarning')}\n\n` +
      `${t('sync.continueQuestion')}`
    );
    
    if (!confirmed) {
      setLoading(false);
      return;
    }
    
    // Backup vor Wiederherstellung
    await createBackup(pakete, 'before_firebase_restore');
    
    // Stelle Daten wieder her (OHNE Cleanup!)
    setPakete(loadedPakete);
    await set('pakete', loadedPakete);
    
    setInfoMessage(`✅ ${t('backup.packagesRestored', { count: loadedPakete.length })}`);
    setTimeout(() => setInfoMessage(''), 5000);
    
    console.log(`✅ Server-Daten wiederhergestellt: ${loadedPakete.length} Pakete`);
    setLoading(false);
    
  } catch (error) {
    console.error('❌ Fehler beim Laden vom Server:', error);
    alert(`❌ ${t('common.error')}: ${error.message}`);
    setLoading(false);
  }
};

const restoreBackup = async (backup) => {
  if (!backup || !backup.data) {
    alert('❌ ' + t('backup.invalidBackup'));
    return;
  }
  
  const confirmed = window.confirm(
    `🛟 ${t('sync.restoreBackupQuestion')}\n\n` +
    `📅 ${t('dates.storedOn')}: ${backup.date.toLocaleString('de-DE')}\n` +
    `📦 ${t('packages.packages')}: ${backup.count}\n` +
    `📝 ${t('backup.reason')}: ${backup.reason}\n\n` +
    `⚠️ ${t('sync.currentPackagesWillBeOverwritten', { count: pakete.length })}\n\n` +
    `${t('sync.continueQuestion')}`
  );
  
  if (!confirmed) return;
  
  try {
    setBackupProgress({ type: 'restore', step: t('ui.main.progressSafetyBackup'), current: 0, total: 0, percent: 5, detail: t('ui.main.progressSaveCurrent', { count: pakete.length }) });
    
    // Backup der aktuellen Daten erstellen
    await createBackup(pakete, 'before_restore');
    
    setBackupProgress({ type: 'restore', step: t('ui.main.progressUpdatingLocal'), current: 0, total: backup.data.length, percent: 15, detail: t('ui.main.packagesCount', { count: backup.data.length }) });
    
    // Stelle Backup wieder her
    setPakete(backup.data);
    await set('pakete', backup.data);
    
    // Server-Sync: Lade wiederhergestellte Pakete auf Server hoch
    const restoreUser = getAuthUser();
    if (restoreUser && isOnline) {
      const total = backup.data.length;
      let migrated = 0;
      let failed = 0;
      
      for (const paket of backup.data) {
        try {
          await savePaket(paket);
          migrated++;
        } catch (error) {
          failed++;
          console.error(`Restore-Upload Fehler für ${paket.id}:`, error);
        }
        const percent = 20 + Math.round((migrated + failed) / total * 75);
        setBackupProgress({ type: 'restore', step: t('ui.main.progressUploading'), current: migrated + failed, total, percent, detail: `${t('ui.main.progressUploaded', { done: migrated, total })}${failed > 0 ? ` (${t('ui.main.progressErrors', { count: failed })})` : ''}` });
      }
      
      // Hash updaten
      await updatePackagesHash(backup.data);
      await set('lastPackageSync', Date.now());
    }
    
    setBackupProgress({ type: 'restore', step: t('ui.main.progressDone'), current: backup.data.length, total: backup.data.length, percent: 100, detail: t('ui.main.packagesRestored', { count: backup.count }) });
    await new Promise(r => setTimeout(r, 1500));
    setBackupProgress(null);
    
    setShowBackupModal(false);
    setSelectedBackup(null);
    
    console.log(`✅ Backup wiederhergestellt: ${backup.count} Pakete`);
  } catch (error) {
    console.error('❌ Fehler beim Wiederherstellen:', error);
    setBackupProgress(null);
    alert(`❌ ${t('errors.restoreError')}: ${error.message}`);
  }
};

const deleteBackup = async (backupKey) => {
  const confirmed = window.confirm(
    `🗑️ ${t('backup.deleteConfirm')}`
  );
  
  if (!confirmed) return;
  
  try {
    await del(backupKey);
    console.log(`🗑️ Backup gelöscht: ${backupKey}`);
    
    // Liste aktualisieren
    await loadAvailableBackups();
    
    setInfoMessage(`🗑️ ${t('backup.deleted')}`);
    setTimeout(() => setInfoMessage(''), 3000);
  } catch (error) {
    console.error('Fehler beim Löschen des Backups:', error);
    alert(`❌ ${t('common.error')}: ${error.message}`);
  }
};

const handleNextConflict = () => {
  if (conflictIndex < conflictItems.length - 1) {
    // Es gibt weitere Konflikte
    setConflictIndex(prevIndex => prevIndex + 1);
  } else {
    // Alle Konflikte wurden bearbeitet
    setSyncModalVisible(false);
    setConflictItems([]);
    setConflictIndex(0);
    
    // 🆕 Nach der Konfliktlösung erneut synchronisieren (Subcollections)
    setTimeout(() => syncDataWithSubcollections(), 500);
  }
};

const saveSearchResults = async (query, results) => {
  try {
    const user = getAuthUser();
    if (!user || !isOnline) return;
    
    // Gerätetyp bestimmen
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const deviceType = isMobile ? 'Tablet' : 'Desktop';
    
    // Suchdaten vorbereiten
    const searchData = {
      query,
      timestamp: new Date().toISOString(),
      device: localStorage.getItem('deviceName') || deviceType,
      resultIds: results.map(paket => paket.id),
      resultCount: results.length,
      isCustomerSearch: deviceType === 'Tablet'
    };
    
    // Server-API: Lade bisherige Suchen
    let recentSearches = [];
    try {
      recentSearches = await serverDataService.getRecentSearches();
    } catch (e) {
      console.warn('Suchen konnten nicht geladen werden:', e);
    }
    
    // Prüfen, ob die gleiche Suche bereits vorhanden ist
    const existingIndex = recentSearches.findIndex(s => s.query === query);
    if (existingIndex !== -1) {
      recentSearches.splice(existingIndex, 1);
    }
    
    // Neue Suche an den Anfang stellen
    recentSearches.unshift(searchData);
    
    // Auf maximal 10 Einträge begrenzen
    if (recentSearches.length > 10) {
      recentSearches = recentSearches.slice(0, 10);
    }
    
    // Server-API: Suchen speichern
    await serverDataService.saveRecentSearches(recentSearches);
    
    // Aktualisiere lokalen State
    setRecentTabletSearches(recentSearches);
    setSelectedSearchIndex(0);
    
    console.log('Suchergebnisse gespeichert:', results.length);
  } catch (error) {
    console.error('Fehler beim Speichern der Suchergebnisse:', error);
  }
};
const loadTabletSearches = async () => {
  try {
    const user = getAuthUser();
    if (!user || !isOnline) return false;
    
    console.log("Lade Tablet-Suchen vom Server...");
    
    // Server-API: Suchen laden
    const recentSearches = await serverDataService.getRecentSearches();
    
    // WICHTIG: Alle Suchen anzeigen, nicht nur von Tablets
    if (recentSearches && recentSearches.length > 0) {
      console.log("Suchen gefunden:", recentSearches.length);
      setRecentTabletSearches(recentSearches);
      setSelectedSearchIndex(0);
      return true;
    } else {
      console.log("Keine Suchen gefunden");
      return false;
    }
  } catch (error) {
    console.error("Fehler beim Laden der Suchen:", error);
    return false;
  }
};
  const issueBatchCancelRef = useRef(0);

  // 📦 Weitere offene Pakete desselben Empfängers (unter Berücksichtigung von Unterschriftspflicht & Vinted-/DHL-Portalcode)
  const getWeitereOffenePaketeFuerAusgabe = (zielPaket, bereitsIds = null, allowWithSignature = false) => {
    if (!zielPaket || zielPaket.isDemo) return [];
    const sigRequired = (() => { try { return localStorage.getItem('kiosk_require_signature') === '1'; } catch { return false; } })();
    if (sigRequired && !allowWithSignature) return []; // Bei Unterschriftspflicht direkt im SignatureModal gebündelt unterschreiben lassen
    const ausgenommen = bereitsIds || new Set([zielPaket.id]);
    return findeWeitereOffenePakete(pakete, zielPaket).filter((p) => {
      if (!p || ausgenommen.has(p.id)) return false;
      // Vinted-/DHL-Live-Ausgabe braucht jeweils eigenen Portal-Abholcode
      if (ausgabeAktiv() && istVintedPaket(p)) return false;
      if (dhlAusgabeAktiv() && istDhlPaket(p)) return false;
      return true;
    });
  };

  // 🎁 UNDO-FUNKTION für Ausgabe-Overlay (nimmt Einzel- und Mehrfach-Ausgabe derselben Person zurück)
  const handleUndoIssue = async () => {
    issueBatchCancelRef.current += 1;
    if (!issuedPackage) return;
    if (issuedPackage.isDemo) {
      setDemoPakete(prev => prev.map(p => p.id === issuedPackage.id ? {
        ...p,
        ausgegebenAm: '',
        ausgegeben: false,
        paketAnzahl: p.id === 'demo-paket-2' ? 2 : 1,
        updatedAt: new Date().toISOString(),
      } : p));
      setButtonIssuedPaketIds(prev => { const next = new Set(prev); next.delete(issuedPackage.id); return next; });
      setShowIssueOverlay(false);
      setIssuedPackage(null);
      setIssuedBatchPackages([]);
      setIssueAnimationPhase('enter');
      setIssueCountdown(2.5);
      setInfoMessage(`✅ ${t('packages.undoIssueSuccess')}`);
      setTimeout(() => setInfoMessage(''), 3000);
      return;
    }
    
    const alleZurueck = [issuedPackage, ...(issuedBatchPackages || [])].filter(Boolean);
    console.log('🔄 Rückgängig: Pakete werden wieder eingecheckt', alleZurueck.map(p => p.id));
    
    try {
      const now = Date.now();
      const basis = paketeRef.current.length ? paketeRef.current : pakete;
      const zurueckMap = new Map(alleZurueck.map((pkg) => {
        const akt = basis.find((x) => x && x.id === pkg.id) || pkg;
        const updatedPackage = {
          ...akt,
          ...(istOnlinePaket(akt) ? { inCloud: true } : {}),
          ausgegebenAm: '', // Ausgabe zurücknehmen
          ausgegeben: false,
          status: 'vorhanden', // Status wieder auf vorhanden
          lastUpdated: now,
          uploaded: false // 🔄 Erst nach erfolgreichem Server-Write auf true setzen!
        };
        delete updatedPackage.ausgegebenVon; // 👷 Ausgabe-Stempel gehört zur zurückgenommenen Ausgabe
        delete updatedPackage.abgeholtVonName;
        delete updatedPackage.signatureDataUrl;
        delete updatedPackage.signatureHatBild;
        delete updatedPackage.abholUnterschriftAm;
        delete updatedPackage.abholArt;
        delete updatedPackage.bevollmaechtigter;
        delete updatedPackage.vollmachtFoto;
        delete updatedPackage.vollmachtHatBild;
        delete updatedPackage.ausgabeVorgangId;
        updatedPackage.unterschrift = null;
        return [pkg.id, updatedPackage];
      }));

      // Lokale Pakete aktualisieren
      const updatedPakete = basis.map(p => 
        zurueckMap.has(p.id) ? zurueckMap.get(p.id) : p
      );
      setPakete(updatedPakete);
      setButtonIssuedPaketIds(prev => {
        const next = new Set(prev);
        for (const id of zurueckMap.keys()) next.delete(id);
        return next;
      });
      await set('pakete', updatedPakete);
      
      // Server aktualisieren (falls online)
      const undoUser = getAuthUser();
      if (undoUser && isOnline) {
        try {
          const syncedIds = new Set();
          for (const pkg of zurueckMap.values()) {
            const ok = await savePaket(pkg);
            if (ok) syncedIds.add(pkg.id);
          }
          if (syncedIds.size > 0) {
            const aktuell = paketeRef.current.length ? paketeRef.current : updatedPakete;
            const syncedPakete = aktuell.map(p => syncedIds.has(p.id) ? { ...p, uploaded: true, inCloud: true } : p);
            setPakete(syncedPakete);
            setServerPakete(syncedPakete);
            await set('pakete', syncedPakete);
            await updatePackagesHash(syncedPakete);
            console.log('✅ Paket(e) erfolgreich wieder eingecheckt (Server)');
          }
        } catch (fbError) {
          console.error('❌ Server-Update fehlgeschlagen:', fbError);
        }
      }
      
      // 📊 Statistik: Der Server erkennt den Übergang ausgegeben→gelagert
      // beim savePaket() oben selbst und bucht die Ausgabe zurück.

      // Overlay sofort schließen
      setShowIssueOverlay(false);
      setIssuedPackage(null);
      setIssuedBatchPackages([]);
      setIssueAnimationPhase('enter');
      setIssueCountdown(2.5);
      
      // Erfolgs-Nachricht
      setInfoMessage(`✅ ${t('packages.undoIssueSuccess')}`);
      setTimeout(() => setInfoMessage(''), 3000);
      
    } catch (error) {
      console.error('❌ Fehler beim Rückgängig-Machen:', error);
      setInfoMessage(`❌ ${t('packages.undoIssueFailed')}`);
      setTimeout(() => setInfoMessage(''), 3000);
    }
  };

  // 📦 Weitere offene Pakete desselben Empfängers im Ausgabe-Overlay ermitteln
  const weitereOffenePakete = useMemo(() => {
    if (!issuedPackage || issuedPackage.isDemo) return [];
    const bereitsAusgegebenIds = new Set([issuedPackage.id, ...(issuedBatchPackages || []).map(p => p.id)]);
    return getWeitereOffenePaketeFuerAusgabe(issuedPackage, bereitsAusgegebenIds);
    // eslint-disable-next-line
  }, [pakete, issuedPackage, issuedBatchPackages]);

  const dismissIssueOverlay = () => {
    setShowIssueOverlay(false);
    setIssuedPackage(null);
    setIssuedBatchPackages([]);
    setIssueAnimationPhase('enter');
    setIssueCountdown(2.5);
  };

  const handleIssueRemainingPackages = async () => {
    if (!weitereOffenePakete.length) return;
    const zuAusgeben = [...weitereOffenePakete];
    const runId = ++issueBatchCancelRef.current;
    setIssueCountdown(2.0);
    for (const p of zuAusgeben) {
      if (issueBatchCancelRef.current !== runId) break;
      setIssuedBatchPackages((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, p]));
      await executeAusgabe(p, null, true);
    }
  };

  // State für Crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(2);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const [imageTimer, setImageTimer] = useState(null);
const handlePhotoUpload = async (e) => {
  const file = e.target.files[0];
  if (file) {
    try {
      // Bildschirmausrichtung sperren, falls die API verfügbar ist
      if (screen.orientation && screen.orientation.lock) {
        await screen.orientation.lock('portrait');
        console.log("Bildschirmorientierung wurde gesperrt");
      } else if (screen.lockOrientation) {
        // Ältere API für manche Browser
        screen.lockOrientation('portrait');
        console.log("Bildschirmorientierung wurde mit Legacy-API gesperrt");
      } else {
        console.log("Bildschirmsperrung nicht verfügbar");
      }
    } catch (error) {
      console.warn("Konnte Bildschirmausrichtung nicht sperren:", error);
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // 📸 AUTO-ROTATION: Querformat automatisch zu Hochformat drehen
        if (img.width > img.height) {
          console.log(`📱 Querformat erkannt (${img.width}x${img.height}) → Auto-Rotation um 90°`);
          setRotation(90); // Automatisch um 90° drehen
        } else {
          console.log(`📱 Hochformat erkannt (${img.width}x${img.height}) → Keine Rotation nötig`);
          setRotation(0); // Hochformat, keine Rotation
        }
        setImageUrl(reader.result);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }
};

// Orientierungssperre wieder freigeben, wenn das Bild geschlossen wird
const handleCloseImage = () => {
  setImageUrl(null);
  setPhoto(null);
  setRotation(0); // Rotation zurücksetzen
  if (paketPhotoInputRef.current) paketPhotoInputRef.current.value = '';
  
  // Bildschirmausrichtung entsperren
  try {
    if (screen.orientation && screen.orientation.unlock) {
      screen.orientation.unlock();
      console.log("Bildschirmorientierung wurde entsperrt");
    }
  } catch (error) {
    console.warn("Fehler beim Entsperren der Bildschirmausrichtung:", error);
  }
};

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

const getCroppedImg = async () => {
  const image = new window.Image();
  image.src = imageUrl;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Bestimme Canvas-Größe basierend auf der Rotation
  const isRotated90or270 = rotation % 180 !== 0;
  
  // Bei 90° oder 270° Rotation werden Höhe und Breite getauscht
  if (isRotated90or270) {
    canvas.width = croppedAreaPixels.height;
    canvas.height = croppedAreaPixels.width;
  } else {
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
  }

  // Verschiebe den Ursprung in die Mitte
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(1, 1); // Skalierung zurücksetzen (falls nötig)

  // Zeichne das Bild mit korrektem Offset für die Rotation
  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    -croppedAreaPixels.width / 2,  // Zentriere das Bild horizontal
    -croppedAreaPixels.height / 2, // Zentriere das Bild vertikal
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return canvas.toDataURL('image/jpeg');
};

// ============================================
// 🚀 OPTIMIERTE OCR-FUNKTION (ML-Service + Tesseract + Client-Fallback)
// ============================================
// Eingeloggt + Online → Server-OCR (ML-Service PaddleOCR + YOLO, ~1-3s)
//   ML nicht verfügbar → Tesseract C++ nativ (~0.5-2s)
// Nicht eingeloggt/Offline → Client-OCR (Tesseract.js im Browser)
// Bilder werden automatisch rotiert (EXIF + Querformat-Erkennung)
const performOCR = async (imageDataUrl) => {
  try {
    // 2️⃣ Bildgröße optimieren (max 1200px — reduziert Upload auf ~100-300 KB)
    const optimizedImage = await optimizeImageForOCR(imageDataUrl, 1200);
    
    // 🌐 SERVER-OCR: Wenn eingeloggt und online
    if (currentUser && isOnline) {
      try {
        console.log('🌐 Starte Server-OCR (ML-Service + Rotation)...');
        const startTime = performance.now();
        
        const response = await api.post('/ocr', { 
          image: optimizedImage,
          lang: 'deu+eng'
        });
        
        const duration = ((performance.now() - startTime) / 1000).toFixed(2);
        const source = response.source || 'server';
        const rotation = response.rotationApplied;
        console.log(`✅ Server-OCR (${source}) in ${duration}s: ${response.chars} Zeichen, ${response.confidence}% Konfidenz${rotation ? `, Rotation: ${rotation}` : ''}`);        
        
        // Ergebnis mit strukturierten Daten zurückgeben
        return { 
          data: { 
            text: response.text,
            structured: response.structured || null,
            imageId: response.imageId || null,
            source: source,
            rotationApplied: rotation,
          } 
        };
      } catch (serverError) {
        console.warn('⚠️ Server-OCR fehlgeschlagen, Fallback auf Client-OCR:', serverError.message);
        // Fallthrough zu Client-OCR
      }
    }
    
    // 📱 CLIENT-OCR: Fallback (nicht eingeloggt, offline oder Server-Fehler)
    console.log('📱 Starte Client-OCR (Tesseract.js)...');
    
    // Memory-Monitoring (nur in Chromium-Browsern verfügbar)
    if (performance.memory) {
      const usedMB = (performance.memory.usedJSHeapSize / 1048576).toFixed(0);
      const limitMB = (performance.memory.jsHeapSizeLimit / 1048576).toFixed(0);
      const percentage = ((performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100).toFixed(0);
      console.log(`💾 RAM: ${usedMB}MB / ${limitMB}MB (${percentage}%)`);
      
      if (percentage > 80) {
        console.warn(`⚠️ WARNUNG: RAM-Auslastung bei ${percentage}%! Empfehle App-Neustart.`);
      }
    }
    
    // 🔄 Periodischer Worker-Reset alle 50 OCR-Durchläufe (verhindert Memory-Leaks)
    ocrCounterRef.current++;
    if (ocrCounterRef.current >= 50 && ocrWorkerRef.current) {
      console.log('🔄 Resette OCR Worker nach 50 Durchläufen (Memory-Cleanup)...');
      await ocrWorkerRef.current.terminate();
      ocrWorkerRef.current = null;
      ocrCounterRef.current = 0;
    }
    
    // 1️⃣ Worker wiederverwenden oder neu erstellen
    if (!ocrWorkerRef.current) {
      console.log('🔧 Erstelle OCR Worker (einmalig)...');
      const worker = await Tesseract.createWorker('deu+eng', 1, {
        logger: m => {
          if (m.status === 'recognizing text') {
            console.log(`📝 OCR Progress: ${Math.round(m.progress * 100)}%`);
          }
        }
      });
      
      // 🚀 PERFORMANCE-OPTIMIERUNGEN
      await worker.setParameters({
        tessedit_pageseg_mode: Tesseract.PSM.SINGLE_BLOCK,
        tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzäöüÄÖÜß .-/',
      });
      
      ocrWorkerRef.current = worker;
      ocrCounterRef.current = 0;
      console.log('✅ OCR Worker bereit!');
    }
    
    // 3️⃣ OCR durchführen
    console.log(`🔍 Starte Client-OCR... (Durchlauf ${ocrCounterRef.current}/50)`);
    const startTime = performance.now();
    const result = await ocrWorkerRef.current.recognize(optimizedImage);
    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    console.log(`✅ Client-OCR abgeschlossen in ${duration}s`);
    
    return result;
  } catch (error) {
    console.error('❌ OCR Fehler:', error);
    // Bei Fehler: Worker zurücksetzen für nächsten Versuch
    if (ocrWorkerRef.current) {
      await ocrWorkerRef.current.terminate();
      ocrWorkerRef.current = null;
      ocrCounterRef.current = 0;
    }
    throw error;
  }
};

// 🖼️ Bild für OCR optimieren
const optimizeImageForOCR = async (imageDataUrl, maxWidth = 1200) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Wenn Bild bereits klein genug, direkt zurückgeben
        if (img.width <= maxWidth) {
          // ✅ Cleanup: Image-Referenz aufräumen
          img.onload = null;
          img.onerror = null;
          img.src = '';
          resolve(imageDataUrl);
          return;
        }
        
        // Größe anpassen mit korrektem Seitenverhältnis
        const canvas = document.createElement('canvas');
        const scaleFactor = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scaleFactor;
        
        const ctx = canvas.getContext('2d');
        
        // 🎨 Kontrast erhöhen für bessere OCR
        ctx.filter = 'contrast(1.2) brightness(1.1)';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const optimizedDataUrl = canvas.toDataURL('image/jpeg', 0.92);
        
        // ✅ CLEANUP: Speicher freigeben
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
        img.onload = null;
        img.onerror = null;
        img.src = '';
        
        resolve(optimizedDataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      reject(error);
    };
    
    img.src = imageDataUrl;
  });
};

  // OCR nur auf den Crop-Bereich anwenden
  const handleExtractText = async () => {
    setOcrError('');
    setOcrLoading(true);

    try {
      if (!croppedAreaPixels) return;

      // Hole das zugeschnittene Bild
      const croppedImage = await getCroppedImg();

      // 🚀 Verwende optimierte OCR-Funktion (ML-Service → Tesseract → Client)
      const result = await performOCR(croppedImage);
      const text = result.data.text;
      const structured = result.data.structured;
      const imageId = result.data.imageId;

      if ((!text || !text.trim()) && !structured) {
        setOcrError(
          t('ui.main.ocrNoText')
        );
        setOcrLoading(false);
        return;
      }

      // 🤖 ML-Service: Strukturierte Daten direkt verwenden (kein Regex nötig)
      if (structured && (structured.name || structured.street || structured.postal)) {
        console.log('🤖 ML-Ergebnis nutzen:', structured);
        setName(structured.name || '');
        setStreetAndNumber(structured.street || '');
        const plzOrt = [structured.postal, structured.city].filter(Boolean).join(' ');
        setPostalAndCity(plzOrt);
      } else {
        // 📝 Fallback: Regex-Parsing (Tesseract/Client-OCR ohne ML)
        const recipientSection = text.split(/\b(An|To|Empfänger)\b/i).pop();
        const nameMatch = recipientSection.match(/^[^\n]+/);
        setName(nameMatch ? nameMatch[0].trim() : '');

        const lines = recipientSection.split('\n').map(l => l.trim()).filter(Boolean);
        let streetLine = '';
        let plzOrtLine = '';
        for (let i = 0; i < lines.length; i++) {
          if (/\d{5}\s+[A-Za-zäöüÄÖÜß\s]+/.test(lines[i])) {
            plzOrtLine = lines[i];
            if (i > 0) streetLine = lines[i - 1];
            break;
          }
        }
        setStreetAndNumber(streetLine);
        setPostalAndCity(plzOrtLine);
      }

      // 📦 Tracking-Nummer aus Roh-Text extrahieren (beide Pfade)
      const trackingNumberMatch = text ? text.match(/\b\d{10,}\b/) : null;
      setTrackingNumber(trackingNumberMatch ? trackingNumberMatch[0] : '');

      // 🤖 OCR-Ergebnis für Auto-Feedback speichern
      if (imageId) {
        lastOcrResultRef.current = {
          imageId,
          ocrName: structured?.name || '',
          ocrStreet: structured?.street || '',
          ocrPostal: structured?.postal || '',
          ocrCity: structured?.city || '',
          source: result.data.source,
          timestamp: Date.now(),
        };
        console.log(`🤖 OCR-Ergebnis gespeichert für Feedback (imageId: ${imageId})`);
      }

      setOcrLoading(false);
    } catch (err) {
      setOcrError(t('ui.main.ocrFailed'));
      setOcrLoading(false);
    }
  };

  // Paket speichern oder bearbeiten
  const handleSave = async (e) => {
    e.preventDefault();
    const now = new Date();
    const timestamp = Date.now();
    // 🛡️ Fremd-Überschreibungs-Schutz (siehe isForeignOverwrite): veraltetes selectedIndex
    // darf kein fremdes Paket überschreiben → dann als neues Paket anlegen.
    let editId = selectedIndex;
    if (editId) {
      const zielPaket = pakete.find(p => p.id === editId);
      if (zielPaket && isForeignOverwrite(zielPaket, { name, trackingNumber })) {
        console.warn(`🛡️ Fremd-Überschreibung verhindert: „${zielPaket.name}" ≠ „${name}" — lege NEU an statt zu überschreiben.`);
        editId = null;
      }
    }
    const neuesPaket = {
      id: editId || (window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
      name,
      streetAndNumber,
      postalAndCity,
      trackingNumber,
      regalplatz: normalizeRegalplatz(regalplatz),
      datum: new Date(datum).toISOString(), 
      ausgegebenAm: ausgegebenAm ? new Date(ausgegebenAm).toISOString() : '',
      zurueckgeschickt,
      updatedAt: now.toISOString(),
      lastUpdated: timestamp  // ✅ FIX: Für Sync-Erkennung!
    };

    const neuePakete = [...pakete];
    const paketIndex = neuePakete.findIndex(p => p.id === neuesPaket.id);

    if (paketIndex !== -1) {
      // Paket aktualisieren
      neuePakete[paketIndex] = neuesPaket;
    } else {
      // Neues Paket hinzufügen
      neuePakete.push(neuesPaket);
    }

    // 1️⃣ Lokal sofort speichern (Offline-First!)
    setPakete(neuePakete);
    
    try {
      await set('pakete', neuePakete);
      console.log('✅ Paket lokal gespeichert');
    } catch (localError) {
      console.error('❌ KRITISCH: Lokale Speicherung fehlgeschlagen!', localError);
      alert(`⚠️ ${t('errors.saveErrorCritical')}`);
      return; // Abbrechen, nicht weitermachen
    }

    // 2️⃣ Server: Einzelnes Paket speichern
    const saveUser = getAuthUser();
    if (saveUser && navigator.onLine) {
      try {
        await savePaket(neuesPaket);
        await updatePackagesHash(neuePakete);
        console.log('📦 Paket auf Server gespeichert');
      } catch (error) {
        console.error('📦 Server-Speicherung fehlgeschlagen:', error);
        // Nicht kritisch - lokale Daten bleiben erhalten
        console.warn('⚠️ Server-Sync fehlgeschlagen, aber lokale Daten sind sicher!');
      }

      // 3️⃣ 🤖 Auto-Feedback: ML-Service über das Ergebnis informieren
      if (lastOcrResultRef.current && lastOcrResultRef.current.imageId) {
        try {
          const ocr = lastOcrResultRef.current;
          // Fuzzy-Vergleich: Normalisiert + Levenshtein-Toleranz (≤2 Zeichen = korrekt)
          const normalize = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
          const fuzzyMatch = (a, b) => {
            const na = normalize(a), nb = normalize(b);
            if (na === nb) return true;
            // Levenshtein-Distanz (≤2 = korrekt)
            if (Math.abs(na.length - nb.length) > 2) return false;
            let dist = 0;
            for (let i = 0; i < Math.max(na.length, nb.length); i++) {
              if (na[i] !== nb[i]) dist++;
              if (dist > 2) return false;
            }
            return true;
          };
          const wasCorrect = (
            fuzzyMatch(ocr.ocrName, name) &&
            fuzzyMatch(ocr.ocrStreet, streetAndNumber) &&
            fuzzyMatch(`${ocr.ocrPostal} ${ocr.ocrCity}`.trim(), postalAndCity)
          );
          
          await api.post('/ocr/feedback', {
            imageId: ocr.imageId,
            wasCorrect,
            correctedName: !wasCorrect ? name : undefined,
            correctedStreet: !wasCorrect ? streetAndNumber : undefined,
            correctedPostal: !wasCorrect ? postalAndCity.match(/^\d{5}/)?.[0] : undefined,
            correctedCity: !wasCorrect ? postalAndCity.replace(/^\d{5}\s*/, '') : undefined,
          });
          
          console.log(`🤖 Auto-Feedback gesendet: ${wasCorrect ? '✅ korrekt' : '📝 korrigiert'} (imageId: ${ocr.imageId})`);
          lastOcrResultRef.current = null; // Reset nach Feedback
        } catch (feedbackError) {
          console.warn('⚠️ Auto-Feedback fehlgeschlagen:', feedbackError.message);
          // Nicht kritisch - Paket ist bereits gespeichert
        }
      }
    }

    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      resetForm();
    }, 2000);
  };

  // Paket zum Bearbeiten auswählen
  const handleSelect = (paket, idx, openModal = false) => {
    console.log('=== PAKET AUSWAHL ===');
  console.log('Ausgewähltes Paket:', paket);
  console.log('Paket ID:', paket.id);
  console.log('Index:', idx);
  console.log('Modal öffnen:', openModal);

    setName(paket.name);
    setStreetAndNumber(paket.streetAndNumber);
    setPostalAndCity(paket.postalAndCity);
    setTrackingNumber(paket.trackingNumber);
    setRegalplatz(paket.regalplatz);
    setDatum(paket.datum ? paket.datum.slice(0, 10) : '');
    setAusgegebenAm(paket.ausgegebenAm ? paket.ausgegebenAm.slice(0, 10) : '');
    setZurueckgeschickt(!!paket.zurueckgeschickt);
    setSelectedIndex(paket.id);

    // NEU: Wenn openModal true ist, öffne das Modal zur Bearbeitung
    if (openModal) {
      setShowNewPackageModal(true);
    }
  };

  // Formular zurücksetzen
  const resetForm = () => {
  setName('');
  setStreetAndNumber('');
  setPostalAndCity('');
  setTrackingNumber('');
  setRegalplatz('');
  setDatum(new Date().toISOString().slice(0, 10));
  setAusgegebenAm('');
  setZurueckgeschickt(false);
  setSelectedIndex(null); // WICHTIG: Index zurücksetzen
  lastOcrResultRef.current = null; // 🤖 OCR-Feedback-Daten zurücksetzen
  if (paketPhotoInputRef.current) paketPhotoInputRef.current.value = '';
};

  // 🚀 PERFORMANCE: Alte Hilfsfunktionen wurden nach oben verschoben (außerhalb der Komponente)
  // normalizeForSearch, isSimilar, levenshteinDistance sind jetzt globale Funktionen

  // 🚀 PERFORMANCE: useMemo für gefilterte Pakete - läuft nur bei Änderung der Dependencies
  // 🚀 PERFORMANCE: Verwende debouncedSearch für die Filter-Logik
  const searchLower = debouncedSearch.trim().toLowerCase();
  const zeigeDemoPakete = setupAktiv && !staffMode && paketeGeladen && pakete.length === 0 && !demoHidden && demoPakete.length > 0 && (!currentUser || (!loading && !syncInProgress));
  const effektivePakete = useMemo(() => {
    if (gpDemoMode) {
      return (pakete || []).filter((p) => p && isDemoPackage(p));
    }
    if (zeigeDemoPakete) return demoPakete;
    return (pakete || []).filter((p) => !isDemoPackage(p));
  }, [gpDemoMode, zeigeDemoPakete, demoPakete, pakete]);

  // 🗄️ Server-Archiv (>5 Tage nach Ausgabe): Lazy-Load bei aktiver Suche (>= 2 Zeichen) ODER über Filter „Archiv (>5T)"
  const [showArchiv, setShowArchiv] = useState(false);
  const [archivPakete, setArchivPakete] = useState([]);
  const [archivLoading, setArchivLoading] = useState(false);
  const [archivHasMore, setArchivHasMore] = useState(false);
  const [archivOffset, setArchivOffset] = useState(0);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!isOnline || !currentUser || (!showArchiv && q.length < 2)) {
      setArchivPakete([]);
      setArchivHasMore(false);
      setArchivOffset(0);
      setArchivLoading(false);
      return undefined;
    }
    let cancelled = false;
    setArchivLoading(true);
    setArchivOffset(0);
    serverPackageService.searchArchivePackages({ q, limit: 50, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        setArchivPakete(res.packages || []);
        setArchivHasMore(!!res.hasMore);
      })
      .catch(() => {
        if (!cancelled) {
          setArchivPakete([]);
          setArchivHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setArchivLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedSearch, showArchiv, isOnline, currentUser]);

  const loadMoreArchiv = async () => {
    if (archivLoading || !archivHasMore || !isOnline || !currentUser) return;
    const nextOffset = archivOffset + 50;
    setArchivLoading(true);
    try {
      const res = await serverPackageService.searchArchivePackages({
        q: debouncedSearch.trim(),
        limit: 50,
        offset: nextOffset,
      });
      setArchivPakete((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const extra = (res.packages || []).filter((p) => p && p.id && !seen.has(p.id));
        return [...prev, ...extra];
      });
      setArchivHasMore(!!res.hasMore);
      setArchivOffset(nextOffset);
    } catch { /* */ } finally {
      setArchivLoading(false);
    }
  };

  const sichtbareArchivPakete = useMemo(() => {
    if (!archivPakete.length) return [];
    const activeIds = new Set((effektivePakete || []).map((p) => p && p.id));
    return archivPakete.filter((p) => p && p.id && !activeIds.has(p.id));
  }, [archivPakete, effektivePakete]);
  
  const filteredPakete = useMemo(() => {
    // Dedupliziere nach ID (behält nur das erste Vorkommen). Mit Set statt findIndex je Paket: das war O(n²) —
    // bei Folkerts 7.500 Paketen ~28 Mio. Vergleiche bei JEDER Listenänderung, im Profil 64 % der Rechenzeit (23.10.2026)
    const gesehen = new Set();
    return effektivePakete
    // Entferne undefined Pakete UND Pakete ohne ID
    .filter(paket => paket && paket.id)
    // Entferne gelöschte Pakete
    .filter(paket => !paket.deleted)
    .filter((paket) => {
      if (gesehen.has(paket.id)) return false;
      gesehen.add(paket.id);
      return true;
    })
    // Filterlogik für Tablet-Suchergebnisse
    .filter((paket) => {
       // Wenn Tablet-Suchergebnisse aktiv sind und es Ergebnisse gibt
      if (showTabletSearchResults && recentTabletSearches.length > 0) {
        const currentSearch = recentTabletSearches[selectedSearchIndex];
        
        // Für Live-Suchen (ohne resultIds): Verwende normale Suchlogik
        if (currentSearch?.isLive) {
          const liveSearchQuery = currentSearch.query?.toLowerCase() || '';
          const liveSearchNorm = normalizeForSearch(liveSearchQuery);
          const searchWords = liveSearchQuery.split(' ').filter(Boolean);
          const searchWordsNorm = liveSearchNorm.split(' ').filter(Boolean);
          
          // ✅ Normalisierte Suche (Umlaute + OCR-Fehler tolerant)
          const matchesStreetAndNumber = searchWords.every((word) =>
            paket.streetAndNumber?.toLowerCase().includes(word)
          );
          const matchesStreetAndNumberNorm = searchWordsNorm.every((word) =>
            normalizeForSearch(paket.streetAndNumber).includes(word)
          );
          
          return paket.name?.toLowerCase().includes(liveSearchQuery) ||
                 normalizeForSearch(paket.name).includes(liveSearchNorm) ||
                 matchesStreetAndNumber ||
                 matchesStreetAndNumberNorm ||
                 paket.regalplatz?.toLowerCase().includes(liveSearchQuery) ||
                 trackingTreffer(paket, liveSearchQuery);
        }
        
        // Für normale Tablet-Suchen (mit resultIds): Verwende resultIds
        return currentSearch?.resultIds?.includes(paket.id);
      }
      
      // Normale Filterlogik für die Suche mit Fuzzy-Matching
      const searchWords = searchLower.split(' ').filter(Boolean);
      const searchNorm = normalizeForSearch(search);
      const searchWordsNorm = searchNorm.split(' ').filter(Boolean);
      
      // Exakte Suche (schnell)
      const exactMatchName = paket.name?.toLowerCase().includes(searchLower);
      const exactMatchStreetAndNumber = searchWords.every((word) =>
        paket.streetAndNumber?.toLowerCase().includes(word)
      );
      const exactMatchRegalplatz = paket.regalplatz?.toLowerCase().includes(searchLower);
      const exactMatchTracking = trackingTreffer(paket, search); // 📦 Sendungsnr. / Vinted-Abholcode
      
      // ✅ NEU: Normalisierte Suche (Umlaute ö→o, ä→a, ü→u + OCR-Fehler 7↔/)
      const normMatchName = normalizeForSearch(paket.name).includes(searchNorm);
      const normMatchStreetAndNumber = searchWordsNorm.every((word) =>
        normalizeForSearch(paket.streetAndNumber).includes(word)
      );
      
      // 📅 DATUMSBEREICH-FILTER - Prüfe zuerst, ob Paket im Zeitraum liegt
      let matchesDateFilter = true;
      if (dateFilterFrom || dateFilterTo) {
        try {
          let paketDatum = null;
          if (paket.datum) {
            paketDatum = new Date(paket.datum);
          } else if (paket.updatedAt) {
            paketDatum = new Date(paket.updatedAt);
          } else if (paket.createdAt) {
            paketDatum = new Date(paket.createdAt);
          }
          
          if (paketDatum && !isNaN(paketDatum)) {
            paketDatum.setHours(0, 0, 0, 0);
            
            if (dateFilterFrom) {
              const fromDate = new Date(dateFilterFrom);
              fromDate.setHours(0, 0, 0, 0);
              if (paketDatum < fromDate) matchesDateFilter = false;
            }
            
            if (dateFilterTo) {
              const toDate = new Date(dateFilterTo);
              toDate.setHours(23, 59, 59, 999);
              if (paketDatum > toDate) matchesDateFilter = false;
            }
          }
        } catch (error) {
          matchesDateFilter = true;
        }
      }
      
      // Wenn Datumsfilter nicht passt, sofort ausschließen
      if (!matchesDateFilter) return false;
      
      // Wenn exakte ODER normalisierte Übereinstimmung (Umlaute/OCR-tolerant)
      if (exactMatchName || exactMatchStreetAndNumber || exactMatchRegalplatz ||
          normMatchName || normMatchStreetAndNumber || exactMatchTracking ||
          (paket.palettenTyp && String(paket.palettenTyp).toLowerCase().includes(searchLower)) ||
          matchesPaketSender(paket, searchLower) ||
          matchIntegrationSearch(paket, searchLower)) {
        const matchesSearch = true;
        const matchesRegalFilter = !regalFilter || paket.regalplatz?.startsWith(regalFilter);
        const matchesSelectedRegalplatz = !selectedRegalplatz || paket.regalplatz === selectedRegalplatz;
        
        if (showOld) {
          const now = new Date();
          now.setHours(0, 0, 0, 0); // ⏰ Uhrzeit auf Mitternacht setzen
          const paketDatumDate = new Date(paket.datum);
          paketDatumDate.setHours(0, 0, 0, 0); // ⏰ Uhrzeit auf Mitternacht setzen
          const businessDays = calculateBusinessDays(paketDatumDate, now, holidays);
          return matchesSearch && matchesRegalFilter && matchesSelectedRegalplatz && businessDays > filterDays;
        }
        return matchesSearch && matchesRegalFilter && matchesSelectedRegalplatz;
      }
      
      // Fuzzy-Suche (bei OCR-Fehlern wie /5 statt 75)
      const fuzzyMatchName = isSimilar(paket.name, searchLower, 0.75);
      const fuzzyMatchStreetAndNumber = searchWords.some(word => 
        isSimilar(paket.streetAndNumber, word, 0.75)
      );
      const fuzzyMatchRegalplatz = isSimilar(paket.regalplatz, searchLower, 0.85);

      const matchesSearch = fuzzyMatchName || fuzzyMatchStreetAndNumber || fuzzyMatchRegalplatz;

      const matchesRegalFilter = !regalFilter || paket.regalplatz?.startsWith(regalFilter);
      const matchesSelectedRegalplatz = !selectedRegalplatz || paket.regalplatz === selectedRegalplatz;

      // #15: Bei aktiver Namens-/Adress-Suche den Lagerfrist-Filter (showOld) NICHT anwenden —
      // sonst blendet ein zuvor aktivierter, während der Suche unsichtbarer Filter jüngere
      // Treffer aus und das Paket scheint „nicht da". Die Suche soll immer alles finden.
      return matchesSearch && matchesRegalFilter && matchesSelectedRegalplatz;
    });
  }, [effektivePakete, debouncedSearch, searchLower, regalFilter, selectedRegalplatz, showOld,
      dateFilterFrom, dateFilterTo, filterDays, holidays,
      showTabletSearchResults, recentTabletSearches, selectedSearchIndex]);
  
  function parseCSV(csv) {
  const [headerLine, ...lines] = csv.trim().split(/\r?\n/);
  // Splitte Header robust (auch mit Anführungszeichen)
  const headers = headerLine.match(/("([^"]|"")*"|[^,]*)/g).map(h => h.replace(/^"|"$/g, '').replace(/""/g, '"'));
  return lines
    .filter(line => line.trim().length > 0)
    .map(line => {
      // Splitte Felder robust (auch mit Kommas in Anführungszeichen)
      const fields = line.match(/("([^"]|"")*"|[^,]*)/g).map(f => f.replace(/^"|"$/g, '').replace(/""/g, '"'));
      // Mappe explizit auf die Felder, die du erwartest
      return {
        name: fields[headers.indexOf('name')] || '',
        streetAndNumber: fields[headers.indexOf('streetAndNumber')] || '',
        postalAndCity: fields[headers.indexOf('postalAndCity')] || '',
        trackingNumber: fields[headers.indexOf('trackingNumber')] || '',
        regalplatz: fields[headers.indexOf('regalplatz')] || '',
        datum: fields[headers.indexOf('datum')] || '',
        ausgegebenAm: fields[headers.indexOf('ausgegebenAm')] || '',
        zurueckgeschickt: (fields[headers.indexOf('zurueckgeschickt')] || '').toLowerCase() === 'true'
      };
    });
}

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = parseCSV(event.target.result);
        if (Array.isArray(imported)) {
          setPakete(imported);
          set('pakete', imported);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        } else {
          alert(t('errors.invalidCSV'));
        }
      } catch {
        alert(t('errors.importError'));
      }
    };
    reader.readAsText(file);
  };
function convertToCSV(arr) {
  if (!arr.length) return '';
  const header = [
    'name',
    'streetAndNumber',
    'postalAndCity',
    'trackingNumber',
    'regalplatz',
    'datum',
    'ausgegebenAm',
    'zurueckgeschickt'
  ];
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = arr.map(obj =>
    header.map(key =>
      key === 'zurueckgeschickt'
        ? escape(obj[key] ? 'true' : 'false')
        : escape(obj[key])
    ).join(',')
  );
  return [header.join(','), ...rows].join('\r\n');
}

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  }

  function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('de-DE') + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
// NEU: Verwendung der neuen Filterfunktion
// #40: Memoized — sonst wird die ganze Paketliste bei jedem Render neu gefiltert.
const filteredPaketeWithIndex = useMemo(
  () => filterPaketeWithIndex(effektivePakete, searchLower, regalFilter, selectedRegalplatz, showOld, dateFilterFrom, dateFilterTo, filterDays, historicalDate, filterDaysInternational),
  [effektivePakete, searchLower, regalFilter, selectedRegalplatz, showOld, dateFilterFrom, dateFilterTo, filterDays, historicalDate, filterDaysInternational]
);

// 🔄 WICHTIG: Verwende filteredPaketeWithIndex für konsistente Trefferanzeige
// Nur neu sortieren, wenn sich Liste/Filter ändern (vorher bei JEDEM Neuzeichnen alle ~7.500 Pakete, 23.10.2026)
const sortedPakete = useMemo(() => filteredPaketeWithIndex
  .map(item => item.paket) // Extrahiere nur das Paket-Objekt
  .sort((a, b) => {
    // ✨ Animierende Pakete bleiben an ihrer Position (nicht nach unten sortieren)
    const aAnimating = animatingPaketIds.has(a.id);
    const bAnimating = animatingPaketIds.has(b.id);
    if (aAnimating && !bAnimating) return -1; // a oben halten
    if (!aAnimating && bAnimating) return 1;  // b oben halten

    // 🔄 Pakete, die NUR über den Absender gefunden wurden, immer ganz nach unten sortieren
    if (searchLower && searchLower.trim().length >= 2) {
      const aSenderOnly = isSenderOnlyMatch(a, searchLower);
      const bSenderOnly = isSenderOnlyMatch(b, searchLower);
      if (aSenderOnly && !bSenderOnly) return 1;  // a (nur Absender) nach unten
      if (!aSenderOnly && bSenderOnly) return -1; // b (nur Absender) nach unten
    }
    
    // 📌 Button-ausgegebene Pakete: NICHT als "done" nach unten sortieren
    const aButtonIssued = buttonIssuedPaketIds.has(a.id);
    const bButtonIssued = buttonIssuedPaketIds.has(b.id);
    
    // "Offene" Pakete zuerst (aber button-ausgegebene bleiben als "offen" behandelt)
    const aDone = (a.ausgegebenAm || a.zurueckgeschickt) && !aButtonIssued;
    const bDone = (b.ausgegebenAm || b.zurueckgeschickt) && !bButtonIssued;
    if (aDone && !bDone) return 1;   // a nach unten
    if (!aDone && bDone) return -1;  // a nach oben
    
    // 📦 NEU: Bei Lagerfrist-Filter nach Regalplatz sortieren (A, B, C...)
    if (showOld && !aDone && !bDone) {
      const regalA = (a.regalplatz || '').toUpperCase();
      const regalB = (b.regalplatz || '').toUpperCase();
      const comparison = regalA.localeCompare(regalB);
      if (comparison !== 0) return comparison;
      // Wenn Regalplatz gleich → nach Datum sortieren
      return (b.updatedAt || b.ausgegebenAm || b.datum || '').localeCompare(
        a.updatedAt || a.ausgegebenAm || a.datum || ''
      );
    }
    
    // Innerhalb der anderen Gruppen: Zuletzt aktualisiert zuerst
    return (b.updatedAt || b.ausgegebenAm || b.datum || '').localeCompare(
      a.updatedAt || a.ausgegebenAm || a.datum || ''
    );
  }), [filteredPaketeWithIndex, animatingPaketIds, buttonIssuedPaketIds, searchLower, showOld]);

const combinedPakete = firebaseSearchResults.length > 0
  ? firebaseSearchResults // Zeige nur die Suchergebnisse, wenn vorhanden
  : filteredPakete; // Andernfalls zeige die gefilterten Pakete
// Prüfen, ob ein Paket auf dem Server vorhanden ist

const handleTouchStart = (e, platz) => {
  // Prüfe, ob ein Regal ausgewählt ist - wenn nicht, zeige kein Overlay
  if (!selectedRegal) {
    return;
  }
  
  // 🔄 WICHTIG: Filtere nur aktive Pakete (nicht ausgegeben, nicht zurückgeschickt, nicht gelöscht)
  // ⚡ Optimiert: Ein Filter statt 4 separate Durchläufe
  const paketeNames = effektivePakete
    .filter(paket => 
      regalGleich(paket.regalplatz, platz) &&
      !paket.ausgegebenAm &&
      !paket.zurueckgeschicktAm &&
      !paket.zurueckgeschickt &&
      !paket.deleted
    )
    .map(paket => paket.name);

  if (paketeNames.length > 0) {
    // Erkennung eines Doppeltipps
    const now = Date.now();
    const timeBetweenTaps = now - lastTapTimestamp;
    setLastTapTimestamp(now);

    // Wenn das Overlay eingerastet ist und ein Tipp erfolgt, entsperren
    if (overlayLocked) {
      setOverlayLocked(false);
      setOverlayData({ visible: false, names: [], position: { x: 0, y: 0 } });
      return;
    }

    // Wenn ein Doppeltipp erkannt wird (weniger als 300ms zwischen Tipps)
    if (timeBetweenTaps < 300) {
      // Overlay einrasten
      setOverlayLocked(true);
      clearTimeout(tapTimerRef.current);
    } 
    
    // Stelle sicher, dass wir das Overlay nur bei Paketen anzeigen, die zum ausgewählten Regal gehören
    if (platz && platz.startsWith(selectedRegal)) {
      setOverlayData({
        visible: true,
        names: paketeNames,
        position: {
          x: window.innerWidth / 2,
          y: 100
        },
        platz: platz
      });
    }
  }
};

const handleTouchEnd = () => {
  // Wenn das Overlay nicht eingerastet ist, nach einer kurzen Verzögerung ausblenden
  if (!overlayLocked) {
    tapTimerRef.current = setTimeout(() => {
      setOverlayData({ visible: false, names: [], position: { x: 0, y: 0 } });
    }, 100); // Kleine Verzögerung, damit der Nutzer die Info lesen kann
  }
};

// Ergänzen Sie auch einen Bereinigungseffekt, um den Timer zu löschen wenn die Komponente unmounted
useEffect(() => {
  return () => {
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
  };
}, []);

const loadDataForUser = async () => {
  const user = getAuthUser();
  if (user) {
    try {
      // Server-API: Alle Pakete laden
      const loadedPakete = await loadAllPackages();
      
      if (loadedPakete && loadedPakete.length > 0) {
        setPakete(loadedPakete);
        console.log('Daten für Nutzer geladen:', loadedPakete.length);
      } else {
        console.log('Keine Daten für diesen Nutzer gefunden.');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    }
  } else {
    console.log('Kein Nutzer eingeloggt.');
  }
};
const saveDataForUser = async (updatedPakete) => {
  if (!currentUser || !isOnline) return false;
  
  try {
    console.log("saveDataForUser: Speichere Pakete für Benutzer", currentUser.uid);
    
    // Use autoSyncChanges for the actual synchronization
    return await autoSyncChanges(updatedPakete);
  } catch (error) {
    console.error("Fehler beim Speichern der Daten:", error);
    return false;
  }
};
const saveSinglePackage = async (paket) => {
  const user = getAuthUser();
  if (user && isOnline) {
    try {
      // Paket normalisieren
      const normalizedPaket = normalizeDates(paket);
      
      // Server-API: Einzelnes Paket speichern
      await savePaket(normalizedPaket);
      
      console.log(`Paket ${normalizedPaket.id} einzeln synchronisiert`);
    } catch (error) {
      console.error('Fehler bei Einzel-Synchronisierung:', error);
    }
  }
};

// ============================================
// 🔄 AUTO-SYNC SYSTEM mit QUEUE
// ============================================
// 🆕 Queue-Verarbeitung: Verhindert Race Conditions bei schnellen Speichervorgängen
const processQueue = async () => {
  // 🔒 v1.44 FIX: Warte wenn Login-Sync noch läuft!
  // Verhindert Race Conditions zwischen Login-Sync und User-Aktionen
  if (syncLockRef.current || syncInProgress) {
    console.log("📋 Queue wartet auf Login-Sync... (wird nach Ende automatisch verarbeitet)");
    // Retry nach 1 Sekunde
    setTimeout(() => processQueue(), 1000);
    return;
  }
  
  // Verhindere parallele Queue-Verarbeitung
  if (processingQueueRef.current) {
    console.log("📋 Queue wird bereits verarbeitet...");
    return;
  }
  
  // Keine Queue-Items vorhanden
  if (syncQueueRef.current.length === 0) {
    return;
  }
  
  processingQueueRef.current = true;
  
  try {
    while (syncQueueRef.current.length > 0) {
      const updatedPakete = syncQueueRef.current.shift(); // Nächstes Item
      console.log(`📋 Queue: Verarbeite Item (${syncQueueRef.current.length} verbleibend)`);
      
      // Verarbeite das Queue-Item
      await syncSingleRequest(updatedPakete);
    }
  } finally {
    processingQueueRef.current = false;
  }
};

// 🔄 Einzelne Sync-Anfrage verarbeiten
const syncSingleRequest = async (updatedPakete) => {
  if (!currentUser || !isOnline) {
    console.warn("⚠️ Auto-Sync nicht möglich: Kein Benutzer oder offline");
    return false;
  }
  
  try {
    console.log("🔄 Auto-Sync (Subcollections): Änderungen werden synchronisiert...");
    
    // 🔍 GRÖßE-CHECK vor Speichern
    const dataSize = JSON.stringify(updatedPakete).length;
    const dataSizeMB = (dataSize / 1024 / 1024).toFixed(2);
    console.log(`📊 Zu synchronisierende Datengröße: ${dataSizeMB} MB`);
    
    // Warnung bei sehr großen Datenmengen
    if (dataSize > 8 * 1024 * 1024) { // 8MB Limit
      console.error(`❌ DATENBANK ZU GROß: ${dataSizeMB} MB überschreitet Limit!`);
      throw new Error(`Datenbank zu groß (${dataSizeMB} MB). Bitte bereinigen Sie alte Pakete.`);
    }
    
    // 🗑️ Lade gelöschte IDs um Re-Upload zu verhindern
    const deletedIds = await loadDeletedIds();
    const deletedSet = new Set(deletedIds);
    
    // Deduplizierung und Normalisierung
    const uniquePakete = deduplicatePakete(updatedPakete);
    // 🗑️ Filtere gelöschte Pakete VOR der Normalisierung
    const filteredPakete = uniquePakete.filter(p => !deletedSet.has(p.id));
    if (filteredPakete.length < uniquePakete.length) {
      console.log(`🗑️ ${uniquePakete.length - filteredPakete.length} gelöschte Pakete vom Sync ausgeschlossen`);
    }
    const normalizedPakete = filteredPakete.map(normalizeDates);
    
    // 🔄 Nutze CACHED Server-Daten für Vergleich (aus serverPaketeRef gegen Stale-Closures in processQueue)
    let currentServerPakete = serverPaketeRef.current.length > 0 ? serverPaketeRef.current : serverPakete;
    console.log(`📦 Nutze cached Server-Daten für Vergleich: ${currentServerPakete.length} Pakete`);
    
    // 🚨 SICHERHEITSNETZ: Verhindere Massen-Upload bei leerem Cache
    // (sollte nach dem Haupt-Fix nicht mehr passieren, aber sicherheitshalber)
    if (currentServerPakete.length === 0 && normalizedPakete.length > 10) {
      // 09.10.2026: Genau hier gingen bei Green Goat 24 Vinted-Pakete verloren — nach einem App-Neustart (Web-Update)
      // war der Cache 3,5 Minuten leer, jeder Scan wurde "erfolgreich" übersprungen und beim nächsten Voll-Sync vom
      // Server überschrieben. Darum: Pakete, die noch nie hochgeladen wurden (uploaded === false), trotzdem hochladen —
      // die sind sicher neu, ein Massen-Upload droht dadurch nicht.
      const neue = normalizedPakete.filter((p) => p.uploaded === false);
      console.warn(`⚠️ Sync übersprungen: serverPakete-Cache ist leer (${normalizedPakete.length} Pakete, ${neue.length} davon neu → werden hochgeladen)`);
      const okIds = new Set();
      for (const paket of neue) {
        try {
          if (await savePaket(paket)) okIds.add(paket.id);
        } catch (e) {
          console.warn('Upload neues Paket fehlgeschlagen:', e.message);
        }
      }
      if (okIds.size > 0) {
        const lokal = (await get('pakete')) || [];
        const synced = lokal.map((p) => (okIds.has(p.id) ? { ...p, uploaded: true } : p));
        setPakete(synced); await set('pakete', synced);
      }
      if (neue.length && okIds.size < neue.length) reportError('sync_uebersprungen', `${neue.length - okIds.size} neue Pakete nicht hochgeladen (Cache leer)`);
      return true;
    }
    
    // 🆕 NUR GEÄNDERTE ODER NEUE PAKETE HOCHLADEN (nicht alle!)
    // O(1)-Lookup per Map statt O(N²) Array.find über 1.800 Pakete
    const serverMap = new Map(currentServerPakete.map(sp => [sp.id, sp]));
    const changedPakete = normalizedPakete.filter(paket => {
      // 🗑️ Gelöschte Pakete NIE hochladen!
      if (deletedSet.has(paket.id)) {
        console.log(`🗑️ Überspringe gelöschtes Paket ${paket.id}`);
        return false;
      }
      
      const serverPaket = serverMap.get(paket.id);
      
      // ✅ NEUES Paket (nicht auf Server) → HOCHLADEN!
      if (!serverPaket) {
        console.log(`📦 Neues Paket ${paket.id} (${paket.name || 'unbekannt'}) → wird hochgeladen`);
        return true;
      }
      
      // ✅ GEÄNDERTES Paket → HOCHLADEN wenn neuer
      return paket.lastUpdated > (serverPaket.lastUpdated || 0);
    });
    
    console.log(`📦 ${changedPakete.length} von ${normalizedPakete.length} Paketen sind GEÄNDERT`);
    
    // Lade nur die geänderten Pakete hoch
    if (changedPakete.length > 0) {
      const succeededIds = new Set();
      const failedIds = new Set();
      for (const paket of changedPakete) {
        const ok = await savePaket(paket);
        if (ok) succeededIds.add(paket.id);
        else failedIds.add(paket.id);
      }
      
      // Aktualisiere Hash im Hauptdokument nur wenn mindestens 1 Paket hochgeladen wurde
      if (succeededIds.size > 0) {
        await updatePackagesHash(normalizedPakete);
      }
      
      console.log(`✅ ${succeededIds.size}/${changedPakete.length} Pakete hochgeladen`);
      
      // Nur tatsächlich bestätigte Pakete als uploaded:true markieren!
      // Fehlgeschlagene Writes bleiben uploaded:false, damit flushPendingPackages sie nachträgt.
      const updatedPakete = normalizedPakete.map(p => ({
        ...p,
        uploaded: failedIds.has(p.id) ? false : (succeededIds.has(p.id) ? true : p.uploaded !== false)
      }));
      
      // FIX: Aktuellen IndexedDB-Stand statt stale closure lesen
      const currentLocalPakete = await get('pakete') || [];
      const mergedPakete = mergePakete(currentLocalPakete, updatedPakete);
      setServerPakete(mergedPakete);
      setPakete(mergedPakete);
      await set('pakete', mergedPakete);
      
      // Hash merken für Self-Trigger-Schutz
      const newHash = await import('./packageSyncHelper').then(m => m.calculatePackagesHash(mergedPakete));
      lastWrittenHashRef.current = newHash;
      await set('packagesHash', newHash);
      
      console.log(`✅ Auto-Sync abgeschlossen: ${mergedPakete.length} Pakete (${succeededIds.size} hochgeladen, ${failedIds.size} offen)`);
      return failedIds.size === 0;
    } else {
      console.log(`✅ Keine Änderungen - nichts hochzuladen`);
    }
    
    // Lokalen Status aktualisieren (Fallback wenn Verifikation fehlschlägt)
    // FIX: Aktuellen IndexedDB-Stand statt stale closure lesen
    const currentLocalForFallback = await get('pakete') || [];
    const mergedPakete = mergePakete(currentLocalForFallback, normalizedPakete);
    setServerPakete(mergedPakete);
    setPakete(mergedPakete);
    await set('pakete', mergedPakete);
    
    console.log(`✅ Auto-Sync erfolgreich: ${mergedPakete.length} Pakete synchronisiert`);
    return true;
    
  } catch (error) {
    console.error("❌ Auto-Sync Fehler:", error);
    
    // Spezifische Fehlermeldungen
    if (error.message.includes('quota') || error.message.includes('size')) {
      console.error("💾 QUOTA/GRÖßE-FEHLER: Datenbank-Limit erreicht");
    } else if (error.message.includes('permission') || error.message.includes('unauthorized')) {
      console.error("🔒 BERECHTIGUNGS-FEHLER: Zugriff verweigert");
    } else if (error.message.includes('network') || error.message.includes('offline')) {
      console.error("🌐 NETZWERK-FEHLER: Verbindungsproblem");
    }
    
    return false;
  }
};

// 🆕 NEUE VERSION: Verwendet Queue statt Lock
const autoSyncChanges = async (updatedPakete) => {
  if (!currentUser || !isOnline) {
    console.warn("⚠️ Auto-Sync nicht möglich: Kein Benutzer oder offline");
    return false;
  }
  
  // � Füge zur Queue hinzu
  syncQueueRef.current.push(updatedPakete);
  console.log(`📋 Sync-Anfrage zur Queue hinzugefügt (${syncQueueRef.current.length} in Queue, ${updatedPakete?.length || 0} Pakete)`);
  
  // 🔄 Starte Queue-Verarbeitung (non-blocking)
  processQueue();
  
  return true;
};

// Neue Hilfsfunktion zum zuverlässigen Deduplizieren von Paketen
const deduplicatePakete = (pakete) => {
  // Nur gültige Pakete behalten
  const validPakete = pakete.filter(p => p && p.id);
  
  // Map mit ID als Schlüssel
  const uniqueMap = new Map();
  
  validPakete.forEach(paket => {
    const existingPaket = uniqueMap.get(paket.id);
    
    // Paket nur hinzufügen wenn:
    // 1. Es noch nicht existiert oder
    // 2. Es neuer ist als das vorhandene
    if (!existingPaket || 
        (paket.updatedAt && existingPaket.updatedAt && 
         new Date(paket.updatedAt) > new Date(existingPaket.updatedAt))) {
      uniqueMap.set(paket.id, paket);
    }
  });
  
  return Array.from(uniqueMap.values());
};
useEffect(() => {
  // Periodische Deduplizierung der lokalen Pakete
  const dedupTimer = setInterval(async () => {
    try {
      const currentPakete = await get('pakete') || [];
      
      // 🚨 SICHERHEITSCHECK: NIE mit leeren Daten syncen!
      if (currentPakete.length === 0) {
        console.log("⚠️ Deduplizierung übersprungen: Keine Pakete vorhanden");
        return;
      }
      
      // Nach Duplikaten suchen
      const uniquePakete = deduplicatePakete(currentPakete);
      
      // Wenn Duplikate gefunden wurden
      if (uniquePakete.length < currentPakete.length) {
        console.log(`🧹 Deduplizierung: ${currentPakete.length - uniquePakete.length} Duplikate entfernt`);
        
        // 🚨 SICHERHEITSCHECK: Mindestens 50% der Pakete müssen bleiben!
        if (uniquePakete.length < currentPakete.length * 0.5) {
          console.error("🚨 DEDUPLIZIERUNG ABGEBROCHEN: Zu viele Pakete würden verschwinden!");
          console.error(`🚨 Vorher: ${currentPakete.length}, Nachher: ${uniquePakete.length}`);
          return;
        }
        
        setPakete(uniquePakete);
        await set('pakete', uniquePakete);
        
        // Auch Server aktualisieren
        if (currentUser && isOnline) {
          await autoSyncChanges(uniquePakete);
        }
      }
    } catch (error) {
      console.error("Fehler bei periodischer Deduplizierung:", error);
    }
  }, 60000); // Alle 60 Sekunden prüfen
  
  return () => clearInterval(dedupTimer);
}, [currentUser, isOnline]);

// ============================================
// 🗑️ PAKET LÖSCHEN
// ============================================
const handleDelete = async (paket, idx, skipConfirm = false) => {
  console.log('Zu löschendes Paket:', paket);

  const deleteMessage = texts[lang]?.reallyDelete || 'Are you sure you want to delete this package?';

  // skipConfirm: Aufrufer (z.B. NewPackageModal) hat bereits bestätigt —
  // sonst käme die Sicherheitsabfrage doppelt.
  if (!skipConfirm && !window.confirm(deleteMessage)) {
    return;
  }

  if (paket?.isDemo) {
    setDemoPakete(prev => prev.filter(p => p.id !== paket.id));
    return;
  }

  try {
    // Speichere das ursprüngliche Paket für Undo
    setUndoInfo({ paket: { ...paket }, idx });
    
    // Starte Undo-Timer (5 Sekunden)
    if (undoTimeout.current) clearTimeout(undoTimeout.current);
    undoTimeout.current = setTimeout(() => {
      setUndoInfo(null);
    }, 5000);
    
    // 1️⃣ Lokal entfernen (auch falls aus Archiv-Ansicht gelöscht)
    const neuePakete = pakete.filter(p => p.id !== paket.id);
    setPakete(neuePakete);
    setArchivPakete(prev => prev.filter(p => p.id !== paket.id));
    await set('pakete', neuePakete);
    
    // 📋 CHRONIK + Server-Löschung: Im Hintergrund (blockiert UI nicht)
    logPaketGeloescht(paket.id, paket.name, paket.absender, paket.trackingNumber).catch(() => {});
    
    // 2️⃣ Server: Einzelnes Paket löschen (nicht-blockierend) — auch im Lokal-Modus für bestehende Online-Pakete!
    abgebrocheneLoeschungenRef.current.delete(paket.id);
    const mussVomServerGeloeschtWerden = Boolean(currentUser && (isCloudSyncAllowed() || istOnlinePaket(paket)));
    if (mussVomServerGeloeschtWerden) {
      if (isOnline) {
        console.log(`📦 Lösche Paket ${paket.id} vom Server...`);

        (async () => {
          try {
            const delOk = await deletePaket(paket.id, paket);
            // Wurde während des Netzwerkaufrufs auf „Rückgängig" geklickt?
            if (abgebrocheneLoeschungenRef.current.has(paket.id)) {
              abgebrocheneLoeschungenRef.current.delete(paket.id);
              if (delOk) {
                // Server-Löschung kam erst nach dem Undo an → Paket auf dem Server sofort wieder anlegen
                const aktuell = (paketeRef.current || []).find(p => p && p.id === paket.id);
                if (aktuell) await savePaket(aktuell);
              }
              return;
            }
            if (!delOk) {
              await update('pending_delete_ids', (alt) => ((alt || []).includes(paket.id) ? (alt || []) : [...(alt || []), paket.id]));
              console.warn(`📦 Server-Löschung von ${paket.id} fehlgeschlagen → für Reconnect-Flush vorgemerkt`);
            } else {
              await updatePackagesHash(neuePakete);
              await saveDeletedIds([paket.id]);
              invalidateDeletedIdsCache();
              console.log(`📦 Paket ${paket.id} erfolgreich gelöscht`);
            }
          } catch (error) {
            console.error('📦 Server-Löschung fehlgeschlagen:', error);
          }
        })();
      } else {
        // Offline gelöscht → für späteren Reconnect-Flush vormerken
        await update('pending_delete_ids', (alt) => ((alt || []).includes(paket.id) ? (alt || []) : [...(alt || []), paket.id]));
      }
    }
    
    console.log('Paket lokal gelöscht:', paket.id);
    
  } catch (error) {
    console.error('Fehler beim Löschen des Pakets:', error);
    alert(`${t('errors.deleteError')}: ${error.message}`);
  }
};
// Funktion zum Normalisieren aller Datumsfelder in einem Paket

    const abgebrocheneLoeschungenRef = useRef(new Set());

    // Undo-Funktion
  const handleUndo = async () => {
    if (!undoInfo) return;

    const wiederhergestellt = {
      ...undoInfo.paket,
      ...(istOnlinePaket(undoInfo.paket) ? { inCloud: true, uploaded: false } : {}),
    };
    abgebrocheneLoeschungenRef.current.add(wiederhergestellt.id);
    try {
      await update('pending_delete_ids', (alt) => (alt || []).filter((id) => id !== wiederhergestellt.id));
    } catch { /* */ }

    // Paket wiederherstellen (Archiv-Paket → zurück in archivPakete, sonst in aktive pakete)
    if (wiederhergestellt.isArchived) {
      setArchivPakete((prev) => {
        const next = [...prev];
        const idx = typeof undoInfo.idx === 'number' && undoInfo.idx >= 0 ? undoInfo.idx : 0;
        next.splice(idx, 0, wiederhergestellt);
        return next;
      });
    } else {
      const neuePakete = [...pakete];
      neuePakete.splice(undoInfo.idx, 0, wiederhergestellt);
      setPakete(neuePakete);
      await set('pakete', neuePakete);
    }

    // Undo-Information zurücksetzen
    setUndoInfo(null);
    if (undoTimeout.current) clearTimeout(undoTimeout.current);

    if (currentUser && isOnline && (isCloudSyncAllowed() || istOnlinePaket(wiederhergestellt))) {
      try {
        const ok = await savePaket(wiederhergestellt);
        if (ok && !wiederhergestellt.isArchived) {
          const frisch = (await get('pakete')) || pakete;
          const synced = frisch.map(p => p.id === wiederhergestellt.id ? { ...p, uploaded: true, inCloud: true } : p);
          setPakete(synced);
          setServerPakete(synced);
          await set('pakete', synced);
          await updatePackagesHash(synced);
        }
      } catch { /* */ }
    }
  };

  // Statistik-Berechnung
  function getStats(pakete) {
    const now = new Date();
    let day = 0, week = 0, month = 0, year = 0;
    pakete.forEach(p => {
      if (!p.datum) return;
      const d = new Date(p.datum);
      const diffTage = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      if (diffTage === 0) day++;
      if (diffTage < 7) week++;
      if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) month++;
      if (d.getFullYear() === now.getFullYear()) year++;
    });
    return {
      day,
      week,
      month,
      year,
      total: pakete.length
    };
  }

  const stats = getStats(pakete);

  // Standard-Freikontingent
const FREE_LIMIT = Infinity; // Für Testphase: unbegrenzt speichern
const [currentLimit, setCurrentLimit] = useState(FREE_LIMIT);
// Später: setCurrentLimit(100), setCurrentLimit(350), setCurrentLimit(Infinity) je nach Abo

  // Hilfsfunktion: Gibt alle belegten Nummern für ein Präfix zurück
  function getBelegteNummern(pakete, prefix) {
    if (!prefix) return [];
    // Gib ALLE Nummern zurück (inkl. Duplikate)
    return pakete
      .map(p => p.regalplatz)
      .filter(rp => rp && rp.toUpperCase().startsWith(prefix.toUpperCase()) && rp.length > prefix.length)
      .map(rp => rp.slice(prefix.length));
  }

  // Hilfsfunktion: Gibt belegte Nummern + Zähler für ein Präfix zurück
  function getBelegteNummernMitZaehler(pakete, prefix) {
    if (!prefix) return [];
    const nummern = {};
    pakete.forEach(p => {
      if (
        p.regalplatz &&
        p.regalplatz.toUpperCase().startsWith(prefix.toUpperCase()) &&
        !p.ausgegebenAm && // Nur Pakete, die nicht ausgegeben wurden
        !p.zurueckgeschickt // Und nicht zurückgeschickt wurden
      ) {
        const normSlot = normalizeRegalplatz(p.regalplatz, pakete);
        const rest = normSlot.toUpperCase().startsWith(prefix.toUpperCase())
          ? normSlot.slice(prefix.length)
          : p.regalplatz.slice(prefix.length);
        if (rest) {
          nummern[rest] = (nummern[rest] || 0) + 1;
        }
      }
    });
    // Format: ["12", "13 (2x)", ...]
    return Object.entries(nummern).map(([nr, count]) => count > 1 ? `${nr} (${count}x)` : nr);
  }

  // Prefix berechnen (z.B. "A", "AA", "AA1", "AA12" usw.)
  const regalplatzPrefix = regalplatz.match(/^[A-Za-z]{1,2}/)
    ? regalplatz.match(/^[A-Za-z]{1,2}/)[0]
    : '';
  const belegteNummern = getBelegteNummernMitZaehler(pakete, regalplatzPrefix);

  const handleExportCSV = async () => {
    const csv = convertToCSV(pakete);
    
    // 🔒 Capacitor/Native: nutze Filesystem + Share Plugin
    if (window.Capacitor?.isNativePlatform()) {
      try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');
        
        const fileName = `pakete-export-${Date.now()}.csv`;
        const result = await Filesystem.writeFile({
          path: fileName,
          data: csv,
          directory: Directory.Cache,
          encoding: 'utf8'
        });
        
        await Share.share({
          title: t('ui.main.exportTitle'),
          text: t('ui.main.exportText'),
          url: result.uri,
          dialogTitle: t('ui.main.exportDialogTitle')
        });
      } catch (err) {
        console.error('CSV-Export fehlgeschlagen:', err);
        alert(t('ui.main.exportFailed') + ': ' + err.message);
      }
      return;
    }
    
    // Web: Standard-Download via a.click()
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pakete-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTrackingPhotoUpload = (e) => {
    const file = e.target.files[0];
    setTrackingPhoto(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTrackingImageUrl(reader.result);
        setTrackingImageTimer(20);
        let t = 20;
        const interval = setInterval(() => {

          t--;
          setTrackingImageTimer(t);
          if (t <= 0) {

            setTrackingImageUrl(null);
            setTrackingPhoto(null);
            setTrackingNumberCandidates([]);
            if (trackingPhotoInputRef.current) trackingPhotoInputRef.current.value = '';
            clearInterval(interval);
          }
        }, 1000);
      };
      reader.readAsDataURL(file);
    }
  };
 
const SectionHeader = ({ title, section, isVisible }) => (
  <div 
    onClick={() => handleSectionHeaderTap(section)}
    onDoubleClick={() => {
      // Direkt beim Doppelklick umschalten (für Desktop)
      setVisibleSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    }}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isVisible ? '12px' : '4px',
      borderBottom: `1px solid ${colors.border}`,
      paddingBottom: '8px',
      cursor: 'pointer'
    }}
  >
    <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
      {title}
    </h3>
    <span style={{ 
  fontSize: '18px', 
  transform: isVisible ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: 'transform 0.3s ease' 
}}>
      {isVisible ? '▼' : '▶'}
    </span>
  </div>
);
const handleSectionHeaderTap = (section) => {
  const now = Date.now();
  const lastTap = lastTapTimestamp[section] || 0;
  
  if (now - lastTap < 300) {
    // Double tap detected
    setVisibleSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }
  
  // Update timestamp for this section
  setLastTapTimestamp(prev => ({
    ...prev,
    [section]: now
  }));
};
  // NEU: Crop-Bereich für das Tracking-Bild setzen
  const onTrackingCropComplete = (croppedArea, croppedAreaPixels) => {
    setTrackingCroppedAreaPixels(croppedAreaPixels);
  };

  // NEU: Funktion zum Zuschneiden des Tracking-Bilds
  const getTrackingCroppedImg = async () => {
    const image = new window.Image();
    image.src = trackingImageUrl;
    await new Promise((resolve) => { image.onload = resolve; });

    const canvas = document.createElement('canvas');
    canvas.width = trackingCroppedAreaPixels.width;
    canvas.height = trackingCroppedAreaPixels.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      trackingCroppedAreaPixels.x,
      trackingCroppedAreaPixels.y,
      trackingCroppedAreaPixels.width,
      trackingCroppedAreaPixels.height,
           0,
      0,
      trackingCroppedAreaPixels.width,
      trackingCroppedAreaPixels.height
    );

    return canvas.toDataURL('image/jpeg');
  };

  const handleExtractTrackingNumber = async () => {
    if (!trackingImageUrl || !trackingCroppedAreaPixels) return;
    const croppedImage = await getTrackingCroppedImg();
    // 🚀 Verwende optimierte OCR-Funktion
    const result = await performOCR(croppedImage);
    // Nur Zeilen mit ausschließlich mindestens 10 Ziffern (keine Sonderzeichen)
    const candidates = Array.from(
      result.data.text.matchAll(/^\s*(\d{10,})\s*$/gm),
      m => m[1]
    ).filter(num => /^[0-9]{10,}$/.test(num));
    if (candidates.length > 0) {
         setTrackingNumberCandidates(candidates);
      setTrackingNumber(candidates[0]);
    } else {
      setTrackingNumberCandidates([]);
    }
  };
const handleSearchChange = (e) => {
  const value = e.target.value;
  const warLeer = search.trim().length === 0; // Zustand VOR dieser Eingabe
  setSearch(value);

  if (value.trim().length > 0) {
    // 🔍 NUR beim ERSTEN Zeichen einmalig nach unten scrollen, damit das oben
    // angedockte Suchfeld an den Rand rückt und darunter die Treffer erscheinen.
    // Bei jedem weiteren Zeichen NICHT erneut scrollen — sonst zappelt der Bildschirm.
    if (warLeer) {
      setTimeout(() => {
        searchBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }

    // Bei mind. 3 Zeichen und auf mobilem Gerät: Suchergebnisse speichern
    if (value.trim().length >= 3 && navigator.userAgent.includes('Mobile')) {
      const results = filterPakete(pakete, value.toLowerCase(), regalFilter, selectedRegalplatz);
      if (results.length > 0) {
        saveSearchResults(value, results);
      }
    }
  }
};
// Hilfsfunktion zum Filtern, ohne showOld-Parameter:
function filterPakete(pakete, searchLower, regalFilter, selectedRegalplatz) {
  // ✅ Normalisierungsfunktion für Umlaute + OCR-Fehler
  const normalizeStr = (str) => {
    if (!str) return '';
    return str.toLowerCase().trim()
      .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
      .replace(/\//g, '7').replace(/l/g, '1').replace(/i/g, '1')
      .replace(/o/g, '0').replace(/s/g, '5').replace(/z/g, '2').replace(/b/g, '8');
  };
  
  return pakete.filter((paket) => {
    if (paket.deleted) return false;
    
    const searchWords = searchLower.split(' ').filter(Boolean);
    const matchesStreetAndNumber = searchWords.every(word =>
      paket.streetAndNumber?.toLowerCase().includes(word)
    );
    
    // ✅ NEU: Normalisierte Suche (Umlaute + OCR-Fehler tolerant)
    const searchNorm = normalizeStr(searchLower);
    const searchWordsNorm = searchNorm.split(' ').filter(Boolean);
    const matchesStreetAndNumberNorm = searchWordsNorm.every(word =>
      normalizeStr(paket.streetAndNumber).includes(word)
    );

    const matchesSearch =
      paket.name?.toLowerCase().includes(searchLower) ||
      normalizeStr(paket.name).includes(searchNorm) ||
      matchesStreetAndNumber ||
      matchesStreetAndNumberNorm ||
      paket.regalplatz?.toLowerCase().includes(searchLower) ||
      trackingTreffer(paket, searchLower);

    const matchesRegalFilter = !regalFilter || paket.regalplatz?.startsWith(regalFilter);

    // Nur filtern, wenn ein Regalplatz ausgewählt ist
    const matchesSelectedRegalplatz = !selectedRegalplatz || paket.regalplatz === selectedRegalplatz;

    return matchesSearch && matchesRegalFilter && matchesSelectedRegalplatz;
  });
}
  // NEU: Tracking-Nummer aus dem zugeschnittenen Bild extrahieren
  const handleExtractTrackingNumberFromCrop = async () => {
    if (!trackingCroppedAreaPixels) return;
    const croppedImage = await getTrackingCroppedImg();
    // 🚀 Verwende optimierte OCR-Funktion
    const result = await performOCR(croppedImage);
    // Suche nach einer langen Zahl (10+ Ziffern)
    const match = result.data.text.match(/\b\d{10,}\b/);
    if (match) setTrackingNumber(match[0]);
  };
const startBarcodeScanner = () => {
  setBarcodeError(null);
  setShowBarcodeScanner(true);
  setTimeout(() => {
    if (!videoRef.current) return;
    if (
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      setBarcodeError(t('ui.main.cameraUnavailable'));
      setShowBarcodeScanner(false);
      return;
    }
    if (!codeReaderRef.current) {
      codeReaderRef.current = new BrowserMultiFormatReader();
    }
    codeReaderRef.current.decodeFromVideoDevice(null, videoRef.current, (result, err) => {
      if (result) {
        setTrackingNumber(result.getText());
        setShowBarcodeScanner(false);
        codeReaderRef.current.reset();
      }
      if (err && err.name === 'NotAllowedError') {
        setBarcodeError(t('ui.main.cameraDenied'));
        setShowBarcodeScanner(false);
      }
    });
  }, 200);
};

const stopBarcodeScanner = () => {
  setShowBarcodeScanner(false);
  if (codeReaderRef.current) {
    codeReaderRef.current.reset();
  }
};

// ============================================
// 📬 BENACHRICHTIGUNGEN SENDEN
// ============================================
const handleSendNotification = async (customer, paket) => {
  try {
    console.log('📬 Sende Benachrichtigung an:', customer.name, '(', customer.kontakt, ')');
    const pickupPin = getPackagePickupPin(paket);
    let branche = 'paketshop';
    try { branche = localStorage.getItem('kiosk_branche') || 'paketshop'; } catch { /* */ }
    
    // Über eigenen Server senden (statt Firebase Cloud Function)
    const result = await serverDataService.sendNotification(
      {
        name: customer.name,
        kontakt: customer.kontakt,
        strasse: customer.strasse,
        hausnummer: customer.hausnummer
      },
      {
        id: paket.id,
        name: paket.name,
        streetAndNumber: paket.streetAndNumber,
        regalplatz: paket.regalplatz || 'Nicht zugewiesen',
        pickupPin
      },
      customer.kontaktTyp || 'email',
      { pickupPin, branche, lang }
    );
    
    if (result.success) {
      console.log('✅ Benachrichtigung erfolgreich gesendet:', result);
      if (result.simulated) {
        console.warn('⚠️ Email wurde nur simuliert (Gmail nicht konfiguriert)');
      }
      return true;
    } else {
      console.error('❌ Benachrichtigung fehlgeschlagen:', result);
      return false;
    }
  } catch (error) {
    console.error('📬 Fehler beim Senden der Benachrichtigung:', error);
    return false;
  }
};

// ============================================
// ✅ PAKET AUSGEBEN & ZURÜCKSCHICKEN
// ============================================

// 📦 MEHRFACH-PAKET: Interne Ausgabe-Funktion (für alle Fälle)
const executeAusgabe = async (paket, anzahlAusgeben = null, fromButton = false) => {
  // ✅ Animation SOFORT starten - füge ID zum Set hinzu
  setAnimatingPaketIds(prev => new Set([...prev, paket.id]));
  
  // 📌 Bei Button-Klick: Paket an Position halten (nicht nach unten sortieren)
  if (fromButton) {
    setButtonIssuedPaketIds(prev => new Set([...prev, paket.id]));
  }

  if (paket?.isDemo) {
    const aktuelleAnzahl = paket.paketAnzahl || 1;
    const ausgebenAnzahl = anzahlAusgeben || aktuelleAnzahl;
    const verbleibendeAnzahl = aktuelleAnzahl - ausgebenAnzahl;
    const nowIso = new Date().toISOString();
    setDemoPakete(prev => prev.map(p => p.id === paket.id ? (
      verbleibendeAnzahl <= 0
        ? { ...p, ausgegeben: true, ausgegebenAm: nowIso, updatedAt: nowIso }
        : { ...p, paketAnzahl: verbleibendeAnzahl, updatedAt: nowIso }
    ) : p));
    setTimeout(() => {
      setAnimatingPaketIds(prev => { const next = new Set(prev); next.delete(paket.id); return next; });
    }, 2600);
    return;
  }
  
  try {
    const now = Date.now();
    const aktuelleAnzahl = paket.paketAnzahl || 1;
    const ausgebenAnzahl = anzahlAusgeben || aktuelleAnzahl;
    const verbleibendeAnzahl = aktuelleAnzahl - ausgebenAnzahl;
    
    let updatedPaket;
    
    if (verbleibendeAnzahl <= 0) {
      // Alle Pakete ausgeben - normales Verhalten
      updatedPaket = stempele({ // 👷 wer gibt aus
        ...paket,
        ausgegeben: true,
        ausgegebenAm: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastUpdated: now
      }, 'ausgegebenVon');
    } else {
      // Nur teilweise ausgeben - Anzahl reduzieren, NICHT als ausgegeben markieren.
      // Die Unterschrift gehört zur Ausgabe, nicht zum Rest im Regal (Review 21.10.2026)
      const { signatureDataUrl: _sig, abgeholtVonName: _abholer, abholUnterschriftAm: _sigAm, ...restPaket } = paket;
      updatedPaket = {
        ...restPaket,
        paketAnzahl: verbleibendeAnzahl,
        updatedAt: new Date().toISOString(),
        lastUpdated: now
      };
    }
    
    const normalizedPaket = normalizeDates(updatedPaket);
    // 🔄 IMMER zuerst als "noch nicht hochgeladen" markieren! Erst wenn savePaket() true
    // liefert, wird uploaded=true gesetzt. So landen Ausgaben auch bei plötzlichem
    // Verbindungsabbruch garantiert in der Offline-Warteschlange (flushPendingPackages).
    if (istOnlinePaket(paket)) normalizedPaket.inCloud = true;
    normalizedPaket.uploaded = false;

    // ✅ SOFORT: Alle Speicheroperationen parallel
    // Lokale Liste aktualisieren - immer gegen paketeRef.current rechnen, damit Mehrfach-Ausgaben in Serie sich nicht überschreiben!
    const basis = paketeRef.current.length ? paketeRef.current : pakete;
    const neuePakete = basis.map(p => p.id === paket.id ? normalizedPaket : p);
    setPakete(neuePakete);
    
    await Promise.all([
      // 📋 Chronik — zentrale Quelle, identisch zur Bench-Station
      // (Statistik bucht der Server beim Paket-Write selbst — keine
      //  Client-Zählung mehr, dadurch keine Doppelzählung Modal vs. Bench)
      recordPackageHandedOut(normalizedPaket, ausgebenAnzahl),
      // Lokale Liste in IndexedDB speichern (verwendet neuePakete statt altes pakete!)
      set('pakete', neuePakete),
      // Server synchronisieren (wenn online)
      (async () => {
        if (verbleibendeAnzahl <= 0 && dpdAusgabeAktiv() && istDpdPaket(paket)) {
          dpdAusgabe(paket.trackingNumber || '', {
            pickerName: normalizedPaket.abgeholtVonName || paket.name || '',
            signature: normalizedPaket.signatureDataUrl || '',
          }).catch(() => {});
        }
        if (isOnline && currentUser) {
          console.log("Ausgabe wird SOFORT an Server gesendet:", normalizedPaket.id);
          const ok = await savePaket(normalizedPaket);
          if (ok) {
            const frisch = (paketeRef.current && paketeRef.current.length) ? paketeRef.current : ((await get('pakete')) || neuePakete);
            const synced = frisch.map(p => (p.id === normalizedPaket.id && Boolean(p.ausgegebenAm) === Boolean(normalizedPaket.ausgegebenAm)) ? { ...p, uploaded: true, inCloud: true } : p);
            setPakete(synced);
            setServerPakete(synced);
            await set('pakete', synced);
            await updatePackagesHash(synced);
          } else {
            console.warn("⚠️ Ausgabe konnte nicht sofort gesendet werden → bleibt als Offline-Ausgabe vorgemerkt (uploaded:false)");
          }
        }
      })()
    ]);

    // 🎭 Animation läuft visuell weiter dank animation-aware sorting
    // Das Paket ist bereits als ausgegeben markiert, wird aber durch
    // animatingPaketIds.has(id) noch an seiner Position gehalten
    
  } catch (error) {
    console.error("Fehler beim Ausgeben:", error);
    // Animation sofort abbrechen bei Fehler - entferne ID aus Set
    setAnimatingPaketIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(paket.id);
      return newSet;
    });
  } finally {
    // Animation-State nach 2600ms aufräumen (Animation: 2500ms + Buffer: 100ms)
    setTimeout(() => {
      setAnimatingPaketIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(paket.id);
        return newSet;
      });
    }, 2600);
  }
};

// 📦 MEHRFACH-PAKET: Haupt-Handler - zeigt Dialog wenn nötig
const handleAusgeben = async (paket, fromButton = false, ohneVinted = false, ohneUnterschrift = false, initialMode = 'empfaenger') => {
  const anzahl = paket.paketAnzahl || 1;
  // 🧪 Vinted-Anbindung (Experiment): Vinted gibt nur gegen den Abholcode des Kunden heraus → erst der
  // Dialog; er bestätigt in der Vinted-App und ruft danach handleAusgeben(paket, true, true) auf.
  // Mehrfach-Pakete laufen wie bisher (eigener Dialog).
  if (!paket.isDemo && !ohneVinted && anzahl === 1 && ausgabeAktiv() && istVintedPaket(paket)) {
    setVintedAusgabePaket(paket);
    return;
  }
  if (!paket.isDemo && !ohneVinted && anzahl === 1 && dhlAusgabeAktiv() && istDhlPaket(paket)) {
    setDhlAusgabePaket(paket);
    return;
  }
  // ✍️ Unterschrift bei Abholung (optional über Setup aktivierbar, ODER gezielt über „👥 Bevollm.")
  const sigRequired = (() => { try { return localStorage.getItem('kiosk_require_signature') === '1'; } catch { return false; } })();
  if (!ohneUnterschrift && (sigRequired || initialMode === 'bevollmaechtigter')) {
    setSignatureRequest({ paket, fromButton, initialMode });
    return;
  }
  // ⚠️ Verwechslungs-Check VOR der Aktion (Bestand noch unverändert): liegt im Nachbarfach
  // dieselbe Hausnummer mit ANDEREM Namen? Bremst nichts — der Hinweis kommt danach.
  const verwechslungsRisiko = findeHausnummerKonflikt(pakete, paket);

  if (anzahl > 1) {
    // Zeige Dialog für Mehrfach-Pakete
    setMultiPackagePaket(paket);
    setMultiPackageCountdown(5);
    setShowMultiPackageDialog(true);
    
    // Auto-Confirm Timer starten (alle ausgeben nach 2 Sekunden)
    if (multiPackageTimerRef.current) {
      clearInterval(multiPackageTimerRef.current);
    }
    
    multiPackageTimerRef.current = setInterval(() => {
      setMultiPackageCountdown(prev => {
        if (prev <= 0.1) {
          clearInterval(multiPackageTimerRef.current);
          setShowMultiPackageDialog(false);
          
          // 🎁 Issue-Overlay starten NACH Auto-Confirm
          const randomAnim = animations[Math.floor(Math.random() * animations.length)];
          setSelectedAnimation(randomAnim);
          setIssuedPackage(paket);
          setIssuedBatchPackages([]);
          setShowIssueOverlay(true);
          const hatWeitere = getWeitereOffenePaketeFuerAusgabe(paket).length > 0 || isBevollmaechtigterEnabled();
          setIssueCountdown(hatWeitere ? 5.0 : 2.5);
          setIssueAnimationPhase('enter');
          setTimeout(() => setIssueAnimationPhase('display'), 700);
          
          // Alle ausgeben
          executeAusgabe(paket, anzahl, fromButton);
          if (verwechslungsRisiko) setVerwechslung({ ...verwechslungsRisiko, eigen: paket.name, aktion: 'ausgegeben' });
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);
    
    return; // Warte auf User-Entscheidung oder Auto-Confirm
  }
  
  // Einzelnes Paket - ausgeben
  // #26: Der Swipe-Pfad baut das Undo-Overlay selbst auf (2,5s Rückgängig), der Button-Pfad bisher
  // nicht → gleiche Aktion, unterschiedliches Sicherheitsnetz. Hier für den Button nachziehen.
  if (fromButton) {
    const randomAnim = animations[Math.floor(Math.random() * animations.length)];
    setSelectedAnimation(randomAnim);
    setIssuedPackage(paket);
    setIssuedBatchPackages([]);
    setShowIssueOverlay(true);
    const hatWeitere = getWeitereOffenePaketeFuerAusgabe(paket).length > 0 || isBevollmaechtigterEnabled();
    setIssueCountdown(hatWeitere ? 5.0 : 2.5);
    setIssueAnimationPhase('enter');
    setTimeout(() => setIssueAnimationPhase('display'), 700);
  }
  await executeAusgabe(paket, null, fromButton);
  if (verwechslungsRisiko) setVerwechslung({ ...verwechslungsRisiko, eigen: paket.name, aktion: 'ausgegeben' });
};

// 📦 Vinted-Namen auf Platzhalter-Pakete schreiben (Screenshot-Import und 🧪 Abgleich der Vinted-Anbindung).
// Gleicher Weg wie eine normale Bearbeitung: lokal → Chronik → Server → Sync-Hash
// updates: [{ paket, code, name, regalplatz? }] — regalplatz nur, wenn angegeben (DHL: Fach erst mit dem Namen, 13.10.2026).
// Liefert die Anzahl; die aktualisierten Pakete stehen danach in `pakete` (für einen Etikettdruck: Paket neu aus der Liste holen).
// imageUrl? (⚡ Turbo, 21.10.2026): Foto-ID, die erst nach dem Anlegen im Hintergrund fertig wurde.
const vintedNamenAnwenden = async (updates) => {
  const namen = new Map(updates.map((u) => [u.paket.id, u.name]));
  const faecher = new Map(updates.filter((u) => u.regalplatz).map((u) => [u.paket.id, u.regalplatz]));
  const bilder = new Map(updates.filter((u) => u.imageUrl).map((u) => [u.paket.id, u.imageUrl]));
  if (!namen.size) return 0;
  const jetzt = new Date().toISOString();
  const ts = Date.now();
  const basis = paketeRef.current.length ? paketeRef.current : pakete;
  const neuePakete = basis.map((p) => (namen.has(p.id)
    ? { ...p, ...(istOnlinePaket(p) ? { inCloud: true } : {}), name: namen.get(p.id), ...(faecher.has(p.id) ? { regalplatz: faecher.get(p.id) } : {}), ...(bilder.has(p.id) ? { imageUrl: bilder.get(p.id) } : {}), updatedAt: jetzt, lastUpdated: ts, uploaded: false }
    : p));
  paketeRef.current = neuePakete;
  imHintergrund(() => setPakete(neuePakete));
  paketeSpaeterSpeichern();
  for (const p of neuePakete) {
    // Nur das Foto nachgetragen (Turbo): kein Chronik-Eintrag „geändert" je Paket
    const nurFoto = bilder.has(p.id) && updates.some((u) => u.paket.id === p.id && u.nurFoto);
    if (namen.has(p.id) && !nurFoto) { try { await logPaketGeaendert(p.id, p.name, p.absender, p.trackingNumber); } catch { /* */ } }
  }
  if (getAuthUser() && navigator.onLine) {
    const hoch = new Set();
    for (const p of neuePakete) {
      if (!namen.has(p.id)) continue;
      try { if (await savePaket(p)) hoch.add(p.id); } catch (e) { console.error('📦 Vinted-Name: Server-Update fehlgeschlagen', e); }
    }
    hashSpaeter();
    if (hoch.size) {
      const aktuell = paketeRef.current.length ? paketeRef.current : neuePakete;
      const synced = aktuell.map((p) => (hoch.has(p.id) ? { ...p, uploaded: true, inCloud: true } : p));
      paketeRef.current = synced;
      imHintergrund(() => { setPakete(synced); setServerPakete(synced); });
      paketeSpaeterSpeichern();
    } else {
      await paketeJetztSpeichern();
    }
  } else {
    await paketeJetztSpeichern();
  }
  return namen.size;
};

// 🧪 Vinted-Anbindung: Pakete, die laut Abgleich in Vinted nicht mehr im Bestand stehen (dort ausgegeben), auch in
// PaketPilot als ausgegeben markieren — nur nach Rückfrage im Panel, nie von selbst. Ein Durchgang für alle
// (kein Overlay je Paket, kein Mitarbeiter-Stempel: ausgegeben wurde ja in der Vinted-App).
// Weg wie bei der Ausgabe: lokal → Chronik → Server → Sync-Hash.
const vintedAbgeholtUebernehmen = async (ids) => {
  const menge = new Set(ids);
  if (!menge.size) return 0;
  const jetzt = new Date().toISOString();
  const ts = Date.now();
  const betroffen = [];
  const basis = paketeRef.current.length ? paketeRef.current : pakete;
  const neuePakete = basis.map((p) => {
    if (!menge.has(p.id) || p.ausgegebenAm) return p;
    const neu = normalizeDates({ ...p, ...(istOnlinePaket(p) ? { inCloud: true } : {}), ausgegeben: true, ausgegebenAm: jetzt, updatedAt: jetzt, lastUpdated: ts, uploaded: false });
    betroffen.push(neu);
    return neu;
  });
  if (!betroffen.length) return 0;
  setPakete(neuePakete);
  await set('pakete', neuePakete);
  for (const p of betroffen) await recordPackageHandedOut(p, p.paketAnzahl || 1);
  if (getAuthUser() && navigator.onLine) {
    const hoch = new Set();
    for (const p of betroffen) {
      try { if (await savePaket(p)) hoch.add(p.id); } catch (e) { console.error('📦 Vinted-Übernahme: Server-Update fehlgeschlagen', e); }
    }
    if (hoch.size) {
      const aktuell = paketeRef.current.length ? paketeRef.current : neuePakete;
      const synced = aktuell.map((p) => (hoch.has(p.id) ? { ...p, uploaded: true, inCloud: true } : p));
      setPakete(synced);
      setServerPakete(synced);
      await set('pakete', synced);
      try { await updatePackagesHash(synced); } catch { /* */ }
    }
  }
  return betroffen.length;
};

// 🧪 Vinted-Anbindung: Paket aus „Vinted erfassen" anlegen — derselbe Weg wie ein neues Paket aus dem Formular
// (Liste → IndexedDB → Einzel-Upload → Etikett → Chronik/Wartelisten-Abgleich), ohne Adresse; imageUrl = Bild-ID des Scan-Fotos (optional).
// drucken=false: Etikett NICHT sofort (DHL „Etikett erst mit Name", 13.10.2026) — der Aufrufer druckt später über onDrucken.
// extra: weitere Paketfelder (Turbo PaketPilot: Adresse, Absender)
const vintedPaketAnlegen = async ({ tracking, name, regalplatz, dienst = 'Vinted', imageUrl = '', drucken = true, extra = {} }) => {
  const jetzt = new Date().toISOString();
  const ts = Date.now();
  const neuesPaket = {
    id: window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    name, streetAndNumber: '', postalAndCity: '', trackingNumber: tracking,
    regalplatz: regalplatz ? regalplatz.replace(/\s+/g, '').replace(/^RG:?/i, '') : '',
    paketAnzahl: 1, betrag: null, paketFarbe: '', gewicht: '', international: false,
    datum: jetzt, ausgegebenAm: '', zurueckgeschickt: false, imageUrl: imageUrl || '', paketdienst: dienst,
    ...extra,
    updatedAt: jetzt, lastUpdated: ts,
    uploaded: false, // bis der Upload bestätigt ist
  };
  const basis = paketeRef.current.length ? paketeRef.current : pakete;
  const neuePakete = [...basis, neuesPaket];
  paketeRef.current = neuePakete;
  imHintergrund(() => setPakete(neuePakete));
  // Neues Paket SOFORT auf dem Gerät sichern (wie bisher): bricht die App direkt nach dem Scan ab, bevor der Upload
  // durch ist, darf es nicht fehlen (bei Vinted ist es dann schon gebucht). Gesammelt werden nur die Folgeänderungen.
  if (speicherTimerRef.current) { clearTimeout(speicherTimerRef.current); speicherTimerRef.current = null; }
  set('pakete', neuePakete).catch((e) => console.warn('📦 Speichern neues Paket fehlgeschlagen:', e?.message));
  if (drucken) triggerAutoPrintLabel(neuesPaket);
  decrementCounter();

  // 🚀 Einzel-Upload + Chronik im Hintergrund, ohne die Scan-Kette durch 1.800-Pakete-Sync zu blockieren
  (async () => {
    try {
      if (getAuthUser() && navigator.onLine) {
        const ok = await savePaket(neuesPaket);
        if (ok) {
          const aktuell = paketeRef.current.length ? paketeRef.current : neuePakete;
          const synced = aktuell.map((p) => (p.id === neuesPaket.id ? { ...p, uploaded: true, inCloud: true } : p));
          paketeRef.current = synced;
          imHintergrund(() => {
            setPakete(synced);
            setServerPakete((prev) => [...prev.filter((p) => p.id !== neuesPaket.id), { ...neuesPaket, uploaded: true, inCloud: true }]);
          });
          paketeSpaeterSpeichern();
          hashSpaeter();
        } else {
          await paketeJetztSpeichern(); // Upload gescheitert → Paket (uploaded:false) sofort sicher auf dem Gerät
        }
      }
    } catch (e) {
      console.warn('📦 Vinted Einzel-Upload Fehler:', e?.message);
    }
    try {
      await recordPackageCreated(neuesPaket);
    } catch (e) {
      console.warn('📋 Vinted Chronik Fehler:', e?.message);
    }
  })();

  return neuesPaket;
};

// 🇨🇭 Grenzpaket-Anbindung: Offene Pakete aus dem Grenzpaket-Rapport gesammelt nach PaketPilot übernehmen
const grenzpaketImportAnlegen = async (items = []) => {
  if (!Array.isArray(items) || !items.length) return 0;
  const jetzt = new Date().toISOString();
  const ts = Date.now();
  const basis = paketeRef.current.length ? paketeRef.current : pakete;
  const angelegt = items.map((it) => ({
    id: window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2),
    name: it.name || '',
    streetAndNumber: it.streetAndNumber || '',
    postalAndCity: it.postalAndCity || '',
    trackingNumber: it.trackingNumber || '',
    regalplatz: it.regalplatz ? normalizeRegalplatz(it.regalplatz, basis) : '',
    paketAnzahl: 1,
    betrag: null,
    paketFarbe: '',
    gewicht: '',
    international: false,
    datum: it.datum || jetzt,
    ausgegebenAm: '',
    zurueckgeschickt: false,
    imageUrl: '',
    paketdienst: it.paketdienst || 'Grenzpaket',
    notizen: it.notizen || '',
    updatedAt: jetzt,
    lastUpdated: ts,
    uploaded: false,
  }));

  const neuePakete = [...angelegt, ...basis];
  paketeRef.current = neuePakete;
  setPakete(neuePakete);
  await set('pakete', neuePakete);

  if (getAuthUser() && navigator.onLine) {
    try {
      const CHUNK_SIZE = 50;
      for (let i = 0; i < angelegt.length; i += CHUNK_SIZE) {
        await serverPackageService.batchUpsertPackages(angelegt.slice(i, i + CHUNK_SIZE));
      }
      const angelegtIds = new Set(angelegt.map((p) => p.id));
      const aktuell = paketeRef.current.length ? paketeRef.current : neuePakete;
      const synced = aktuell.map((p) => (angelegtIds.has(p.id) ? { ...p, uploaded: true, inCloud: true } : p));
      paketeRef.current = synced;
      setPakete(synced);
      setServerPakete(synced);
      await set('pakete', synced);
      await updatePackagesHash(synced);
    } catch (e) {
      console.warn('📦 Grenzpaket Batch-Upload Fehler (bleibt lokal vorgemerkt):', e?.message);
    }
  }
  return angelegt.length;
};



  const handleZurueckschicken = async (paket) => {
  // ⚠️ Folgenreiche, nur über die Chronik umkehrbare Aktion → kurz bestätigen (anders als
  // Löschen/Ausgeben hatte die Retour bisher kein Sicherheitsnetz).
  if (!window.confirm(t('packages.reallyReturn', { name: paket?.name || t('packages.package') }))) return;
  if (paket?.isDemo) {
    const nowIso = new Date().toISOString();
    setDemoPakete(prev => prev.map(p => p.id === paket.id ? { ...p, zurueckgeschickt: true, ausgegebenAm: nowIso, updatedAt: nowIso } : p));
    return;
  }
  // ⚠️ Verwechslungs-Check vor der Aktion (Bestand noch unverändert)
  const verwechslungsRisiko = findeHausnummerKonflikt(pakete, paket);
  if (verwechslungsRisiko) setVerwechslung({ ...verwechslungsRisiko, eigen: paket.name, aktion: 'zurückgeschickt' });
  // Animation starten - füge ID zum Set hinzu
  setAnimatingPaketIds(prev => new Set([...prev, paket.id]));
  setTimeout(async () => {
    // Erstelle das aktualisierte Paket
    const now = Date.now();
    const updatedPaket = stempele({ // 👷 wer schickt zurück
      ...paket, 
      zurueckgeschickt: true, // ✅ Markiere als zurückgeschickt
      ausgegebenAm: new Date().toISOString(), // ✅ Setze Ausgabe-Datum für Cleanup
      updatedAt: new Date().toISOString(),
      lastUpdated: now
    }, 'ausgegebenVon');
    
    // Normalisiere das Paket vor der Speicherung
    const normalizedPaket = normalizeDates(updatedPaket);
    // 🔄 IMMER zuerst als "nicht hochgeladen" markieren, bis savePaket() true liefert
    if (istOnlinePaket(paket)) normalizedPaket.inCloud = true;
    normalizedPaket.uploaded = false;

    // Aktualisiere lokale Pakete
    const neuePakete = pakete.map(p => p.id === paket.id ? normalizedPaket : p);
    setPakete(neuePakete);
    await set('pakete', neuePakete);
    
    // 📋 CHRONIK: Paket zurückgeschickt
    await logPaketZurueckgeschickt(normalizedPaket.id, normalizedPaket.name, normalizedPaket.absender, normalizedPaket.trackingNumber);
    
    // 🔄 Server-Synchronisierung
    if (isOnline && currentUser) {
      const ok = await savePaket(normalizedPaket);
      if (ok) {
        const frisch = (await get('pakete')) || neuePakete;
        const synced = frisch.map(p => p.id === normalizedPaket.id ? { ...p, uploaded: true, inCloud: true } : p);
        setPakete(synced);
        setServerPakete(synced);
        await set('pakete', synced);
        await updatePackagesHash(synced);
      } else {
        console.warn("⚠️ Rücksendung konnte nicht sofort gesendet werden → bleibt als Offline-Änderung vorgemerkt (uploaded:false)");
      }
    }
    
    // 📊 Statistik: Der Server erkennt den Übergang „zurückgeschickt"
    // beim Paket-Write oben selbst — keine Client-Zählung mehr.

    // Animation zurücksetzen - entferne ID aus Set
    setAnimatingPaketIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(paket.id);
      return newSet;
    });
  }, 500); // Dauer der Animation (500ms)
};

  // ============================================
  // 📊 STATISTIK — komplett serverseitig
  // ============================================
  // Alle früheren Client-Zählpfade (loadStatistik/saveStatistik,
  // handleAusgebenStatistik/handleZurueckStatistik, loadMonthlyStats)
  // sind entfernt. Der Server bucht atomar an den Paket-Zustands-
  // übergängen (server/lib/statsTracker.js → statistics_daily);
  // die Statistik-Seite liest ausschließlich /api/statistics/overview.

// Direkt nach dem Laden der Pakete aus dem Speicher (ca. Zeile 676)
useEffect(() => {
  const startUser = getAuthUser();
  const lastIdbUser = localStorage.getItem('kiosk_idb_user');
  if (startUser && lastIdbUser && lastIdbUser !== startUser.uid) {
    setDatum(new Date().toISOString().slice(0, 10));
    setPaketeGeladen(true);
    return;
  }
  get('pakete').then(gespeichertePakete => {
    const aktUser = getAuthUser();
    const aktIdbUser = localStorage.getItem('kiosk_idb_user');
    if (aktUser && aktIdbUser && aktIdbUser !== aktUser.uid) {
      setPaketeGeladen(true);
      return;
    }
    // 🩹 rohe Server-Zeilen aus der alten Live-Aktualisierung reparieren (Pakete ohne Namen)
    const arr = serverPackageService.repariereRohePakete(Array.isArray(gespeichertePakete) ? gespeichertePakete : []);
    
    // Migration: Alle Datums- und Uhrzeitfelder ins ISO-Format konvertieren
    const migratedPakete = arr.map(paket => {
      try {
        // Kopie des Pakets erstellen
        const newPaket = {...paket};
        // Pakete mit serverId oder bereits bei einem eingeloggten Konto bestätigtem Upload dauerhaft als Online-Paket markieren
        if (newPaket.serverId != null || (aktUser && newPaket.uploaded === true)) {
          newPaket.inCloud = true;
        }
        
        // datum-Feld migrieren
        if (newPaket.datum) {
          const datumDate = new Date(newPaket.datum);
          if (!isNaN(datumDate)) {
            newPaket.datum = datumDate.toISOString();
          }
        }
        
        // ausgegebenAm-Feld migrieren
        if (newPaket.ausgegebenAm) {
          const ausgegebenDate = new Date(newPaket.ausgegebenAm);
          if (!isNaN(ausgegebenDate)) {
            newPaket.ausgegebenAm = ausgegebenDate.toISOString();
          }
        }
        
        // Andere Datumsfelder nach Bedarf migrieren...
        
        return newPaket;
      } catch (error) {
        console.warn("Fehler bei Migration des Pakets:", paket.id, error);
        return paket; // Ursprüngliches Paket zurückgeben, wenn Migration fehlschlägt
      }
    });
    
    // ✅ KEIN CLEANUP HIER - wird nur beim Server-Sync durchgeführt
    // Migration durchführen, aber NICHT filtern
    
    // Speichern, wenn Änderungen durch Migration (oder die Reparatur roher Zeilen) vorgenommen wurden
    if (arr !== gespeichertePakete || JSON.stringify(migratedPakete) !== JSON.stringify(arr)) {
      console.log("✅ Pakete migriert und gespeichert:", migratedPakete.length);
      set('pakete', migratedPakete);
    }
    
    setPakete(migratedPakete);
    setDatum(new Date().toISOString().slice(0, 10));
    setPaketeGeladen(true);
  }).catch(() => setPaketeGeladen(true));
  // Rest des useEffect wie gehabt...
}, []);


// Hilfsfunktion zum Hervorheben von Suchtreffern
  function highlightMatch(text, search) {
    if (!search) return text;

    const searchWords = search.split(' ').filter(Boolean); // Zerlege den Suchbegriff in Wörter
    const regex = new RegExp(`(${searchWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');

    return String(text).split(regex).map((part, i) =>
      regex.test(part)
        ? <mark key={i} style={{ background: '#a7ffeb', color: '#00695c', padding: 0 }}>{part}</mark>
        : part
    );
  }

  // Berechnung von availableRegale
  const availableRegale = effektivePakete && effektivePakete.length > 0
  ? Array.from(
      new Set(
        effektivePakete
          .filter(p => {
            // Bei historischem Datum: Pakete einbeziehen, die zu diesem Zeitpunkt im Regal waren
            if (historicalDate) {
              const historicalDateObj = new Date(historicalDate + 'T00:00:00');
              historicalDateObj.setHours(23, 59, 59, 999);
              
              const einlagerungDate = p.datum ? new Date(p.datum) : null;
              if (einlagerungDate) einlagerungDate.setHours(0, 0, 0, 0);
              
              const ausgabeDate = p.ausgegebenAm ? new Date(p.ausgegebenAm) : null;
              if (ausgabeDate) ausgabeDate.setHours(0, 0, 0, 0);
              
              const zurueckDate = p.zurueckgeschicktAm ? new Date(p.zurueckgeschicktAm) : null;
              if (zurueckDate) zurueckDate.setHours(0, 0, 0, 0);
              
              // Paket war im Regal, wenn:
              // - Es eingelagert wurde VOR oder AM historischen Datum
              // - UND (noch nicht ausgegeben ODER ausgegeben NACH dem historischen Datum)
              // - UND (noch nicht zurückgeschickt ODER zurückgeschickt NACH dem historischen Datum)
              const wasEingelagert = einlagerungDate && einlagerungDate <= historicalDateObj;
              const warNochNichtAusgegeben = !ausgabeDate || ausgabeDate > historicalDateObj;
              const warNochNichtZurueck = !zurueckDate || zurueckDate > historicalDateObj;
              
              return wasEingelagert && warNochNichtAusgegeben && warNochNichtZurueck && !p.deleted;
            }
            
            // Normal: Nur aktive Pakete berücksichtigen
            return !p.ausgegebenAm && !p.zurueckgeschickt && !p.deleted;
          })
          .map(p => p.regalplatz?.match(/^[A-Za-z]+/)?.[0])
          .filter(Boolean)
      )
    ).sort()
  : [];
// Diese Funktion irgendwo in deiner Komponente hinzufügen
const debugSyncStatus = async () => {
  try {
    const lokalePakete = await get('pakete') || [];
    console.log("==== DEBUG SYNC STATUS ====");
    console.log("Lokale Pakete:", lokalePakete.length);
    // ...
  } catch (error) {
    console.error("Debug-Fehler:", error);
  }
};

// Füge den Button in der Nähe des Login-Bereichs ein:
{currentUser && (
  <button 
    onClick={debugSyncStatus}
    style={{
      marginTop: '10px', 
      padding: '4px 8px', 
      background: colors.darkMode ? '#333' : '#f0f0f0',
      color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: '4px'
    }}
  >
    Debug Sync
  </button>
)}
  // NEU: Zähler für belegte Plätze pro Regalplatz
const belegungsZaehler = effektivePakete.reduce((acc, paket) => {
  if (!paket.regalplatz) return acc;
  
  // Bei historischem Datum: Pakete einbeziehen, die zu diesem Zeitpunkt im Regal waren
  let shouldCount = false;
  
  if (historicalDate) {
    const historicalDateObj = new Date(historicalDate + 'T00:00:00');
    historicalDateObj.setHours(23, 59, 59, 999);
    
    const einlagerungDate = paket.datum ? new Date(paket.datum) : null;
    if (einlagerungDate) einlagerungDate.setHours(0, 0, 0, 0);
    
    const ausgabeDate = paket.ausgegebenAm ? new Date(paket.ausgegebenAm) : null;
    if (ausgabeDate) ausgabeDate.setHours(0, 0, 0, 0);
    
    const zurueckDate = paket.zurueckgeschicktAm ? new Date(paket.zurueckgeschicktAm) : null;
    if (zurueckDate) zurueckDate.setHours(0, 0, 0, 0);
    
    const wasEingelagert = einlagerungDate && einlagerungDate <= historicalDateObj;
    const warNochNichtAusgegeben = !ausgabeDate || ausgabeDate > historicalDateObj;
    const warNochNichtZurueck = !zurueckDate || zurueckDate > historicalDateObj;
    
    shouldCount = wasEingelagert && warNochNichtAusgegeben && warNochNichtZurueck;
  } else {
    // Normal: Nur aktive Pakete
    shouldCount = !paket.ausgegebenAm && !paket.zurueckgeschickt;
  }
  
  if (shouldCount) {
    // Normalisiere den Regalplatz zur konsistenten Verarbeitung
    const normalizedRegalplatz = normalizeRegalplatz(paket.regalplatz, effektivePakete);
    acc[normalizedRegalplatz] = (acc[normalizedRegalplatz] || 0) + 1;
  }
  
  return acc;
}, {});

const belegtePlaetze = Object.keys(belegungsZaehler)
  .map(key => {
    // Extrahiere nur numerische Teile aus dem Regalplatz
    const match = key.match(/[A-Za-z]+(\d+)/);
    return match && match[1] ? parseInt(match[1], 10) : NaN;
  })
  .filter(num => !isNaN(num));

// Sichere Berechnung von min/max mit Fallbacks
const minPlatz = belegtePlaetze.length > 0 ? 
  Math.floor(Math.min(...belegtePlaetze) / 10) * 10 : 0;
  
const maxPlatz = belegtePlaetze.length > 0 ? 
  Math.ceil(Math.max(...belegtePlaetze) / 10) * 10 : 10;

  // 🕶️ Quest Mode: DEAKTIVIERT - Quest bekommt eigene App
  // if (QUEST_FEATURES.enabled && deviceType === 'quest' && QUEST_FEATURES.ui.simplifiedLayout) {
  //   return (
  //     <>
  //       <QuestInputLayout 
  //         user={currentUser}
  //         pakete={pakete}
  //         onPackageSaved={async (paketData) => {
  //           await validateAndSavePaket(paketData);
  //         }}
  //       />
  //     </>
  //   );
  // }

  return (
    <PremiumProvider userEmail={currentUser?.email}>
    <div className="kioskapp-container" style={{ background: colors.box, color: colors.text }}>
      
      {/* � BACKUP/RESTORE FORTSCHRITTS-OVERLAY — blockiert gesamte UI */}
      {backupProgress && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: colors.darkMode ? '#1e2330' : '#ffffff',
            borderRadius: 16,
            padding: '32px 40px',
            maxWidth: 420,
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            textAlign: 'center',
          }}>
            {/* Icon */}
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {backupProgress.percent >= 100 ? '✅' : backupProgress.type === 'backup' ? '💾' : '🔄'}
            </div>
            
            {/* Titel */}
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: 18,
              fontWeight: 700,
              color: colors.darkMode ? '#e5e7eb' : '#111827',
            }}>
              {backupProgress.type === 'backup' ? t('ui.main.backupCreating') : t('ui.main.backupRestoring')}
            </h3>
            
            {/* Schritt-Beschreibung */}
            <p style={{
              margin: '0 0 20px 0',
              fontSize: 14,
              color: colors.darkMode ? '#9ca3af' : '#6b7280',
            }}>
              {backupProgress.step}
            </p>
            
            {/* Fortschrittsbalken */}
            <div style={{
              width: '100%',
              height: 12,
              background: colors.darkMode ? '#374151' : '#e5e7eb',
              borderRadius: 6,
              overflow: 'hidden',
              marginBottom: 12,
            }}>
              <div style={{
                width: `${backupProgress.percent}%`,
                height: '100%',
                background: backupProgress.percent >= 100
                  ? 'linear-gradient(90deg, #10b981, #34d399)'
                  : 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                borderRadius: 6,
                transition: 'width 0.3s ease',
              }} />
            </div>
            
            {/* Prozent + Detail */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 13,
              color: colors.darkMode ? '#9ca3af' : '#6b7280',
            }}>
              <span>{backupProgress.detail}</span>
              <span style={{ fontWeight: 600, fontSize: 15, color: colors.darkMode ? '#e5e7eb' : '#111827' }}>
                {backupProgress.percent}%
              </span>
            </div>
            
            {/* Hinweis */}
            <p style={{
              margin: '16px 0 0 0',
              fontSize: 12,
              color: colors.darkMode ? '#6b7280' : '#9ca3af',
              fontStyle: 'italic',
            }}>
              ⚠️ {t('ui.main.doNotClose')}
            </p>
          </div>
        </div>
      )}
      
      {/* 🏷️ VERSION BADGE - Fixed bottom-right corner */}
      <div
        style={{
          position: 'fixed',
          bottom: '12px',
          right: '12px',
          background: colors.darkMode ? 'rgba(30, 35, 45, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          color: colors.darkMode ? '#9ca3af' : '#6b7280',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '500',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(8px)',
          border: colors.darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontFamily: 'monospace',
          userSelect: 'none',
        }}
      >
        <span style={{ opacity: 0.7 }}>v</span>
        <span style={{ fontWeight: 'bold' }}>{APP_VERSION}</span>
        {currentUser && <span style={{ opacity: 0.5, marginLeft: '4px' }}>✓</span>}
        {!currentUser && <span style={{ opacity: 0.7, marginLeft: '4px', color: '#f59e0b' }}>⚠</span>}
      </div>
      
      {/* �🔄 UPDATE BANNER */}
      {false && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10001,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <span style={{ fontSize: '24px' }}>🚀</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {t('ui.main.updateAvailable')}: {APP_VERSION} → {updateInfo?.version}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {hasUnsyncedData 
                  ? t('ui.main.updateNextStart')
                  : t('ui.main.updateClickInstall')
                }
              </div>
            </div>
          </div>
          {!hasUnsyncedData && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  // Dismiss: Speichere Version in localStorage
                  localStorage.setItem('dismissedUpdate', updateInfo?.version);
                  window.location.reload(); // Reload um Meldung auszublenden
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.5)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('ui.main.later')}
              </button>
              <button
                onClick={() => {
                  // Zeige Lade-Screen
                  const loadingDiv = document.createElement('div');
                  loadingDiv.id = 'update-loading';
                  loadingDiv.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(102, 126, 234, 0.95);
                    color: white;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 99999;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                  `;
                  loadingDiv.innerHTML = `
                    <div style="font-size: 48px; margin-bottom: 24px;">🔄</div>
                    <div style="font-size: 24px; font-weight: bold; margin-bottom: 12px;">${t('ui.main.updateInstalling')}</div>
                    <div style="font-size: 16px; opacity: 0.9; margin-bottom: 24px;">${t('ui.main.updatePleaseWait')}</div>
                    <div id="update-status" style="font-size: 14px; opacity: 0.8; text-align: center; max-width: 400px;"></div>
                  `;
                  document.body.appendChild(loadingDiv);
                  
                  // Update installieren
                  installUpdate();
                }}
                style={{
                  background: '#fff',
                  color: '#667eea',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                {t('ui.main.installNow')}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* 💡 HILFE-CENTER MODAL */}
      <HelpCenter 
        isOpen={showHelpCenter} 
        onClose={() => setShowHelpCenter(false)} 
        darkMode={colors.darkMode}
      />
      
      {/* ⭐ PAYWALL MODAL */}
      <Paywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        darkMode={colors.darkMode}
      />
      
      {/* ⚠️ NICHT-EINGELOGGT WARNUNG — wenn User mal eingeloggt war aber jetzt nicht mehr */}
      {showNotLoggedInWarning && !currentUser && (
        <div style={{
          background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
          border: '2px solid #ef4444',
          borderRadius: '10px',
          padding: '12px 16px',
          marginBottom: '10px',
          boxShadow: '0 2px 12px rgba(239,68,68,0.3)',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 700, 
            color: '#991b1b',
            marginBottom: '4px',
          }}>
            🚨 {t('sync.notLoggedInTitle')}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#b91c1c',
            lineHeight: '1.4',
          }}>
            {t('sync.notLoggedInText')}
          </div>
        </div>
      )}
      
      {/* 🔒 TRIAL BANNER - Prominent oben in der App */}
      {!staffMode && <TrialBanner 
        t={t} 
        colors={colors} 
        setShowPaywall={setShowPaywall} 
        isAuthenticated={!!currentUser} 
      />}
      
      {/* 🔌 VERBINDUNGSSTATUS-INDIKATOR */}
      {currentUser && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '4px 12px',
          marginBottom: '4px',
          fontSize: '11px',
          color: connectionStatus.status === 'connected' ? (colors.darkMode ? '#81c784' : '#388e3c')
               : connectionStatus.status === 'unstable' ? (colors.darkMode ? '#ffb74d' : '#e65100')
               : (colors.darkMode ? '#ef5350' : '#c62828'),
          opacity: 0.85,
          transition: 'all 0.3s ease'
        }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: connectionStatus.status === 'connected' ? '#4CAF50'
                           : connectionStatus.status === 'unstable' ? '#FF9800'
                           : '#f44336',
            display: 'inline-block',
            boxShadow: connectionStatus.status === 'connected' ? '0 0 4px #4CAF50'
                      : connectionStatus.status === 'unstable' ? '0 0 4px #FF9800'
                      : '0 0 4px #f44336',
            animation: connectionStatus.status === 'disconnected' ? 'pulse 1.5s ease-in-out infinite' : 'none'
          }} />
          <span>{connectionStatus.status === 'connected' ? t('sync.connected') : connectionStatus.status === 'unstable' ? t('sync.unstable') : t('sync.noConnection')}{connectionStatus.status !== 'disconnected' && connectionStatus.latency ? ` (${connectionStatus.latency}ms)` : ''}</span>
        </div>
      )}

      {/* 📡 OFFLINE-MODUS-BANNER: Erklärt klar, was offline geht (Suche + Ausgabe) und was gesperrt ist (Neu-Annahme) */}
      {currentUser && !isOnline && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          padding: '8px 12px', marginBottom: '8px', borderRadius: '8px',
          fontSize: '12px', fontWeight: 600, lineHeight: 1.35,
          background: colors.darkMode ? 'rgba(230, 81, 0, 0.22)' : '#fff3e0',
          border: `1px solid ${colors.darkMode ? '#ff9800' : '#ffb74d'}`,
          color: colors.darkMode ? '#ffcc80' : '#bf360c',
        }}>
          <span style={{ fontSize: '16px' }}>📡</span>
          <span>
            <strong>{t('sync.offlineMode')}:</strong> {t('sync.offlineBanner')}
          </span>
        </div>
      )}

      {/* ⏳ NICHT-SYNCHRONISIERT-HINWEIS: offline angelegte/geänderte Pakete, die noch
          auf den Upload warten (uploaded === false). Verschwindet nach dem Flush. */}
      {currentUser && (() => {
        const pending = (pakete || []).filter(p => p && p.uploaded === false).length;
        if (!pending) return null;
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '4px 12px', marginBottom: '4px', fontSize: '11px', fontWeight: 600,
            color: colors.darkMode ? '#ffb74d' : '#e65100', opacity: 0.9,
          }}>
            <span>⏳</span>
            <span>{t('sync.pending', { count: pending })}</span>
          </div>
        );
      })()}

      {/* Header mit Buttons für neues Paket */}
<div style={{ 
  marginTop: '0',
  marginBottom: '16px',
  padding: '12px',
  background: colors.darkMode ? '#1a1f27' : '#f0f0f0',
  borderRadius: '8px',
  position: 'relative'
}}>
  {/* 💡 Hilfe-Button oben rechts (im klassischen Modus absolut; mit Setup sauber in der Button-Zeile ohne Überlappung) */}
  {!staffMode && !setupAktiv && <button
    onClick={() => setShowHelpCenter(true)}
    style={{
      position: 'absolute',
      top: '8px',
      right: '8px',
      background: 'none',
      border: 'none',
      fontSize: '22px',
      cursor: 'pointer',
      opacity: 0.7,
      transition: 'opacity 0.2s ease',
      padding: '4px',
      zIndex: 10
    }}
    title={t('common.help')}
  >
    💡
  </button>}
  
  {/* 📦 Package Counter Bereich (inkl. 📸 Einlieferungsbeleg-Kamera mit „+ Weiteres Foto hinzufügen") */}
  <input
    ref={receiptCameraInputRef}
    type="file"
    accept="image/*"
    capture="environment"
    style={{ display: 'none' }}
    onChange={(e) => {
      if (e.target.files && e.target.files.length > 0) {
        scanDeliveryReceiptFiles(e.target.files, pakete);
      }
      e.target.value = '';
    }}
  />
  <input
    ref={receiptFileInputRef}
    type="file"
    accept="image/*,application/pdf"
    multiple
    style={{ display: 'none' }}
    onChange={(e) => {
      if (e.target.files && e.target.files.length > 0) {
        scanDeliveryReceiptFiles(e.target.files, pakete);
      }
      e.target.value = '';
    }}
  />
  {!staffMode && (packageCounter > 0 || showCounterInput || receiptScanning || (receiptCodes && receiptCodes.length > 0) || (receiptPages && receiptPages.length > 0)) && (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ 
        padding: '8px 12px',
        background: getCounterColor(),
        borderRadius: (showReceiptChecklist && receiptCodes?.length > 0) || (receiptPages && receiptPages.length > 0) ? '6px 6px 0 0' : '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '6px'
      }}>
        {showCounterInput ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>📦 {t('packages.packagesReceived')}:</span>
            <input
              type="number"
              value={counterInputValue}
              onChange={(e) => handleCounterInputChange(e.target.value)}
              placeholder={t('common.quantity')}
              style={{
                width: '74px',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={confirmCounterInput}
              style={{
                padding: '4px 8px',
                background: '#48bb78',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✓
            </button>
            <button
              type="button"
              onClick={() => receiptCameraInputRef.current?.click()}
              style={{
                padding: '5px 10px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Kamera öffnen und Einlieferungsbeleg fotografieren"
            >
              📸 Einlieferungsbeleg fotografieren
            </button>
            <button
              type="button"
              onClick={() => receiptFileInputRef.current?.click()}
              style={{
                padding: '5px 8px',
                background: 'rgba(255,255,255,0.22)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              title="Vorhandenes Foto oder PDF aus Dateien/Galerie auswählen"
            >
              📁 PDF/Datei
            </button>
            <button
              type="button"
              onClick={toggleCounterInput}
              style={{
                padding: '4px 8px',
                background: '#e53e3e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ✗
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                📦 {packageCounter > 0 ? t('counter.remaining', { count: packageCounter }) : (receiptScanning ? '⏳ Beleg wird ausgewertet...' : 'Alle gezählten Pakete erfasst!')}
              </span>
              {packageCounter <= 5 && packageCounter > 0 && (
                <span style={{ fontSize: '12px', color: 'white', opacity: 0.9 }}>
                  ({t('counter.almostDone')})
                </span>
              )}
              {receiptCodes && receiptCodes.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowReceiptChecklist((v) => !v)}
                  style={{
                    padding: '3px 8px',
                    background: 'rgba(0,0,0,0.28)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.45)',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  📋 Beleg: {receiptCodes.filter((r) => r.matched).length}/{receiptCodes.length} abgeglichen {showReceiptChecklist ? '▲' : '▼'}
                </button>
              )}
              {receiptDocsStatus === 'saving' && (
                <span style={{
                  padding: '3px 8px',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: 700
                }}>
                  ⏳ Speichere in Docs...
                </span>
              )}
              {receiptDocsStatus === 'saved' && (
                <span
                  style={{
                    padding: '3px 8px',
                    background: 'rgba(22,163,74,0.45)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.5)',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 700
                  }}
                  title={`Automatisch in Google Drive / Docs unter „DHL Einlieferungsbeleg" gespeichert: ${receiptDocsFileName}`}
                >
                  📄 In Docs gespeichert ✓
                </span>
              )}
              {(receiptDocsStatus === 'needs_setup' || receiptDocsStatus === 'error') && (
                <button
                  type="button"
                  onClick={connectAndSaveReceiptToDocs}
                  style={{
                    padding: '3px 9px',
                    background: '#f59e0b',
                    color: '#111827',
                    border: 'none',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                  title='Einmalig Google Drive (Docs) verbinden und diesen Einlieferungsbeleg automatisch unter „DHL Einlieferungsbeleg" speichern'
                >
                  {receiptDocsStatus === 'needs_setup' ? '🔗 Docs verbinden & Beleg speichern' : '🔄 In Docs speichern'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => receiptCameraInputRef.current?.click()}
                style={{
                  padding: '5px 10px',
                  background: '#2563eb',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.55)',
                  borderRadius: '5px',
                  fontSize: '11.5px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
                title="Kamera öffnen und weiteres Foto (z. B. 2. Seite vom Einlieferungsbeleg) hinzufügen"
              >
                📸 + Weiteres Foto
              </button>
              <button
                type="button"
                onClick={() => receiptFileInputRef.current?.click()}
                style={{
                  padding: '4px 7px',
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
                title="Vorhandenes PDF oder Foto hinzufügen"
              >
                📁
              </button>
              <button
                type="button"
                onClick={resetCounter}
                style={{
                  padding: '4px 8px',
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
                title={t('counter.resetCounter')}
              >
                🗑️
              </button>
            </div>
          </>
        )}
      </div>

      {/* 📸 Seiten-Vorschau der fotografierten Beleg-Seiten + direkter Button „+ Weiteres Foto hinzufügen" */}
      {receiptPages && receiptPages.length > 0 && (
        <div style={{
          background: colors.darkMode ? '#0f172a' : '#f1f5f9',
          border: `1px solid ${colors.border}`,
          borderTop: 'none',
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          overflowX: 'auto',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {receiptPages.map((pg, idx) => (
              <div
                key={pg.id}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px 4px 4px',
                  borderRadius: '8px',
                  background: colors.darkMode ? '#1e293b' : '#ffffff',
                  border: `1.5px solid ${pg.status === 'scanning' ? '#3b82f6' : pg.status === 'error' ? '#ef4444' : '#10b981'}`,
                  fontSize: '11px',
                  fontWeight: 700,
                  color: colors.text
                }}
              >
                {pg.previewUrl ? (
                  <img
                    src={pg.previewUrl}
                    alt={`Seite ${idx + 1}`}
                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 5 }}
                  />
                ) : (
                  <span style={{ fontSize: 18 }}>📄</span>
                )}
                <div style={{ lineHeight: 1.25 }}>
                  <div>Seite {idx + 1}</div>
                  <div style={{ fontSize: '10px', color: pg.status === 'scanning' ? '#3b82f6' : '#10b981' }}>
                    {pg.status === 'scanning' ? '⏳ Liest...' : pg.status === 'error' ? '⚠️ Fehler' : `✓ ${pg.codesCount || 0} Nr.`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeReceiptPage(pg.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    fontWeight: 800,
                    fontSize: '12px',
                    cursor: 'pointer',
                    padding: '0 2px'
                  }}
                  title="Diese Seite entfernen"
                >
                  ✕
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => receiptCameraInputRef.current?.click()}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1.5px dashed #2563eb',
                background: colors.darkMode ? 'rgba(37,99,235,0.18)' : '#dbeafe',
                color: colors.darkMode ? '#93c5fd' : '#1d4ed8',
                fontWeight: 800,
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              📸 + Weiteres Foto hinzufügen (Seite {receiptPages.length + 1})
            </button>
          </div>
        </div>
      )}

      {/* ⚠️ Warnung falls bei "Blatt X von Y" ein Bon-Streifen vergessen wurde (z. B. Blatt 4 & 5 von 5) */}
      {missingReceiptSheets && missingReceiptSheets.length > 0 && (
        <div style={{
          background: '#fef3c7',
          color: '#92400e',
          border: '1px solid #f59e0b',
          borderTop: 'none',
          padding: '6px 10px',
          fontSize: '12px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <span>⚠️ Achtung: Laut Nummerierung fehlen noch Bon-Streifen: <strong>{missingReceiptSheets.join(', ')}</strong></span>
          <button
            type="button"
            onClick={() => receiptCameraInputRef.current?.click()}
            style={{
              padding: '4px 9px',
              background: '#d97706',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            📸 Fehlende Seite fotografieren
          </button>
        </div>
      )}

      {/* 📋 Aufklappbare Checkliste der Sendungsnummern vom Einlieferungsbeleg */}
      {showReceiptChecklist && receiptCodes && receiptCodes.length > 0 && (
        <div style={{
          background: colors.darkMode ? '#1e293b' : '#f8fafc',
          border: `1px solid ${colors.border}`,
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          padding: '8px 10px',
          maxHeight: '190px',
          overflowY: 'auto'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.subtext, marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
            <span>📋 Sendungsnummern vom Einlieferungsbeleg ({receiptCodes.filter((r) => r.matched).length} von {receiptCodes.length} erfasst)</span>
            <span>{receiptCodes.filter((r) => !r.matched).length} offen</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {receiptCodes.map((item) => (
              <div
                key={item.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  borderRadius: '5px',
                  fontSize: '12px',
                  background: item.matched
                    ? (colors.darkMode ? 'rgba(22,163,74,0.18)' : '#dcfce7')
                    : (colors.darkMode ? 'rgba(234,179,8,0.14)' : '#fef9c3'),
                  border: `1px solid ${item.matched ? '#16a34a' : '#ca8a04'}`
                }}
              >
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: colors.text }}>
                  {item.matched ? '✅' : '⏳'} {item.code}
                </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: item.matched ? '#15803d' : '#a16207' }}>
                  {item.matched ? `Erfasst${item.matchedName ? ` (${item.matchedName})` : ''}` : 'Noch offen'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )}
  
  {/* 👷 Angemeldeter Mitarbeiter (nur wenn das Konto Mitarbeiter hat) — antippen: wechseln / Schicht beenden */}
  {mitarbeiterAktiv() && (() => {
    const wer = aktuellerMitarbeiter();
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
        <button type="button" data-testid="staffs-chip" onClick={() => setShowAnmeldung(true)}
          style={{ padding: '4px 10px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${wer ? colors.border : '#d9822b'}`, background: wer ? 'transparent' : (colors.darkMode ? '#3a2a12' : '#fff4e5'),
            color: wer ? colors.text : '#d9822b' }}>
          👷 {wer ? wer.name : t('staffs.notLoggedIn')}
        </button>
      </div>
    );
  })()}
  <div style={{ 
    display: 'flex',
    gap: '6px'
  }}>
    {/* Button für neues Paket (60% Breite) */}
    <button
      onClick={() => {
        // 🐛 FIX 30.09.2026: Ein einfacher Tipp auf ein Paket setzt Name/Fach im Hauptzustand (handleSelect).
        // Nur selectedIndex zu leeren reichte nicht — das neue Formular startete mit dem FACH des zuletzt
        // angetippten Pakets (Green Goat: 4× „Lieshout" auf S statt L). Jetzt alles zurücksetzen.
        resetForm();
        if (turboPpAktiv()) { setShowTurboPp(true); return; } // ⚡ Turbo PaketPilot: Foto auf Foto
        setShowNewPackageModal(true);
      }}
      style={{
        flex: '0.6', // 60% Breite
        background: colors.button,
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 16px',
        fontWeight: 'bold',
        fontSize: '16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        position: 'relative'
      }}
    >
      <span style={{ fontSize: '20px' }}>{turboPpAn && turboPpAktiv() ? '⚡' : '+'}</span>
      {t('packages.newPackage')}
      
      {/* Kleiner Entwurf-Indikator am Hauptbutton */}
      {hasDraft && (
        <span style={{
          position: 'absolute',
          top: '-6px',
          right: '-6px',
          background: '#f6ad55',
          color: '#fff',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          📝
        </span>
      )}
    </button>

    {/* 🧪 Vinted erfassen (Experiment): Etiketten scannen, die Annahme läuft im Hintergrund in die Vinted-App */}
    {/* Nur mit dem ausdrücklich eingeschalteten Live-Schalter „Annahme" — der rote Punkt heißt: bucht echt in Vinted */}
    {vintedAnnahmeAn && (
      <button data-testid="vinted-erfassen-open" onClick={() => {
        if (!isOnline) {
          if (window.showToast) window.showToast('📡 ' + t('ui.main.offlineVintedIntake'), 'warning', 4000);
          return;
        }
        setShowVintedErfassen(true);
      }}
        style={{ flex: '0.4', background: !isOnline ? '#718096' : '#007782', color: '#fff', border: 'none', borderRadius: '8px',
          padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', cursor: !isOnline ? 'not-allowed' : 'pointer', opacity: !isOnline ? 0.65 : 1 }}>
        🔴 {vt(lang)('erfassen')}
      </button>
    )}
    {/* 🧪 DHL erfassen (Experiment): Barcodes scannen, die Annahme läuft im Hintergrund ins DHL-Portal — nur mit Live-Schalter */}
    {dhlAnnahmeAn && (
      <button data-testid="dhl-erfassen-open" onClick={() => {
        if (!isOnline) {
          if (window.showToast) window.showToast('📡 ' + t('ui.main.offlineDhlIntake'), 'warning', 4000);
          return;
        }
        setShowDhlErfassen(true);
      }}
        style={{ flex: '0.4', background: !isOnline ? '#718096' : '#d40511', color: '#fff', border: 'none', borderRadius: '8px',
          padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', cursor: !isOnline ? 'not-allowed' : 'pointer', opacity: !isOnline ? 0.65 : 1 }}>
        🔴 {dt(lang)('erfassen')}
      </button>
    )}

    {/* 📝 DRAFT BUTTON - Floating Draft Indicator */}
    {hasDraft && (
      <button
        onClick={() => setShowNewPackageModal(true)}
        style={{
          flex: '0.35', // Etwas kleiner als normal
          background: '#f6ad55', // Orange für Entwurf
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 8px',
          fontWeight: 'bold',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          position: 'relative',
          boxShadow: '0 2px 8px rgba(246, 173, 85, 0.3)'
        }}
        title={t('draft.continueDraft')}
      >
        <span style={{ fontSize: '16px' }}>📝</span>
        <span style={{ fontSize: '12px' }}>{t('draft.draft')}</span>
        {/* Badge für Entwurf-Indikator */}
        <span style={{
          position: 'absolute',
          top: '-5px',
          right: '-5px',
          background: '#e53e3e',
          color: 'white',
          borderRadius: '50%',
          width: '12px',
          height: '12px',
          fontSize: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          1
        </span>
      </button>
    )}
    

    {/* 🧪 Visueller Demo-Modus (blendet echte Pakete aus & zeigt nur Testpakete) */}
    {(grenzpaketFreigeschaltet() || gpDemoMode) && (
      <button
        type="button"
        data-testid="toggle-gp-demo-btn"
        onClick={() => setDemoModeActive(!gpDemoMode)}
        style={{
          flex: '0.24',
          background: gpDemoMode ? '#f59e0b' : (colors.darkMode ? '#2d2417' : '#fffbeb'),
          color: gpDemoMode ? '#ffffff' : (colors.darkMode ? '#fcd34d' : '#b45309'),
          border: `1.5px solid ${gpDemoMode ? '#d97706' : '#f59e0b'}`,
          borderRadius: '8px',
          padding: '10px 8px',
          fontWeight: 'bold',
          fontSize: '12.5px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
        }}
        title={gpDemoMode ? 'Demo-Modus beenden (echte Pakete wieder anzeigen)' : 'Demo-Modus starten (echte Pakete ausblenden & A–Z testen)'}
      >
        <span style={{ fontSize: '15px' }}>🧪</span>
        <span style={{ lineHeight: 1.15 }}>{gpDemoMode ? 'Demo AN' : 'Demo'}</span>
      </button>
    )}

    {/* 🔔 Paket-Benachrichtigung (QR-Code fürs Kunden-Handy oder direkt eintragen) */}
    <button
      type="button"
      data-testid="open-notify-signup-btn"
      onClick={() => setShowNotifySignupModal(true)}
      style={{
        flex: '0.25',
        background: colors.darkMode ? '#2b4c7e' : '#ebf8ff',
        color: colors.darkMode ? '#90cdf4' : '#2b6cb0',
        border: `1.5px solid ${colors.darkMode ? '#4299e1' : '#90cdf4'}`,
        borderRadius: '8px',
        padding: '10px 8px',
        fontWeight: 'bold',
        fontSize: '13px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        position: 'relative',
      }}
      title={t('ui.main.notifyButtonTitle')}
    >
      <span style={{ fontSize: '16px' }}>🔔</span>
      <span style={{ fontSize: '12px', lineHeight: 1.15 }}>{t('ui.main.notifyButton')}</span>
      {waitingNotifyCount > 0 && (
        <span style={{
          position: 'absolute',
          top: '-6px',
          right: '-6px',
          background: '#3182ce',
          color: '#fff',
          borderRadius: '999px',
          minWidth: '18px',
          height: '18px',
          padding: '0 4px',
          fontSize: '10px',
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.25)'
        }}>
          {waitingNotifyCount}
        </span>
      )}
    </button>

    {/* 📦 Package Counter Button - nur anzeigen wenn kein Counter aktiv ist */}
    {!staffMode && packageCounter === 0 && !showCounterInput && (
      <button
        onClick={toggleCounterInput}
        style={{
          flex: '0.18',
          background: colors.darkMode ? '#4a5568' : '#a0aec0',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 8px',
          fontWeight: 'bold',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px'
        }}
        title={t('counter.setCounter')}
      >
        <span style={{ fontSize: '16px' }}>📦</span>
        <span style={{ fontSize: '12px' }}>{t('counter.counter')}</span>
      </button>
    )}
    {!staffMode && setupAktiv && (
      <button
        type="button"
        onClick={() => setShowHelpCenter(true)}
        style={{
          background: colors.darkMode ? '#2d3748' : '#e2e8f0',
          color: colors.text,
          border: `1px solid ${colors.darkMode ? '#4a5568' : '#cbd5e1'}`,
          borderRadius: '8px',
          padding: '10px',
          fontSize: '18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        title={t('common.help')}
      >
        💡
      </button>
    )}
  </div>
</div>

      {/* 🥨 EXKLUSIVES WIESN-MODUL (nur für wiesn2022@gmail.com sichtbar — alle anderen Konten sehen nichts) */}
      {!staffMode && <WiesnShopModule colors={colors} onOpenDhlCockpit={() => setShowDhlBridge(true)} />}

      {/* 👋 ERSTSTART-WILLKOMMENSKARTE (nur bei 0 Paketen) — interaktive Feature-Slides,
          bringt Anmeldung, Setup (Sprache/Sortierung/Drucker) und Hilfe direkt nach oben ins Sichtfeld */}
      {setupAktiv && !staffMode && paketeGeladen && pakete.length === 0 && (() => {
        const rawSections = t('tutorialPage.sections');
        const slides = Array.isArray(rawSections) && rawSections.length > 0
          ? rawSections.slice(0, 5)
          : [];
        const slideIdx = slides.length > 0 ? ((welcomeSlide % slides.length) + slides.length) % slides.length : 0;
        const activeSlide = slides[slideIdx] || null;
        const slideHighlights = activeSlide && Array.isArray(activeSlide.highlights)
          ? activeSlide.highlights.slice(0, 3)
          : [];
        return (
          <div
            data-testid="first-start-card"
            style={{
              marginBottom: '12px',
              padding: '12px 14px',
              borderRadius: '14px',
              background: colors.darkMode
                ? 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)'
                : 'linear-gradient(145deg, #eff6ff 0%, #ffffff 100%)',
              border: `1.5px solid ${colors.darkMode ? '#334155' : '#bfdbfe'}`,
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.06)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <span style={{ fontSize: '20px' }}>📦</span>
                <strong style={{ fontSize: '16px', color: colors.text }}>PaketPilot</strong>
              </div>
              {slides.length > 1 && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '999px',
                  background: colors.darkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                  color: colors.darkMode ? '#93c5fd' : '#1d4ed8',
                }}>
                  {slideIdx + 1} / {slides.length}
                </span>
              )}
            </div>
            <p style={{ margin: '0 0 8px 0', fontSize: '12.5px', lineHeight: 1.35, color: colors.darkMode ? '#cbd5e1' : '#334155' }}>
              {t('tutorialPage.subtitle')}
            </p>

            {/* 🎠 Interaktive Feature-Slides (Karussell) */}
            {activeSlide && (
              <div
                data-testid="first-start-slides"
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  borderRadius: '10px',
                  background: colors.darkMode ? 'rgba(15, 23, 42, 0.65)' : '#f8fafc',
                  border: `1px solid ${colors.darkMode ? '#334155' : '#cbd5e1'}`,
                }}
              >
                {/* Slide-Tabs oben zum schnellen Durchklicken */}
                <div style={{ display: 'flex', gap: '5px', marginBottom: '8px', overflowX: 'auto' }}>
                  {slides.map((s, idx) => {
                    const active = idx === slideIdx;
                    return (
                      <button
                        key={s.id || idx}
                        type="button"
                        data-testid={`welcome-slide-tab-${idx}`}
                        onClick={() => setWelcomeSlide(idx)}
                        style={{
                          flex: '1 1 0',
                          padding: '4px 6px',
                          borderRadius: '999px',
                          border: active
                            ? `1.5px solid ${colors.darkMode ? '#60a5fa' : '#2563eb'}`
                            : `1px solid ${colors.darkMode ? '#334155' : '#cbd5e1'}`,
                          background: active
                            ? (colors.darkMode ? '#1e3a8a' : '#2563eb')
                            : (colors.darkMode ? '#1e293b' : '#ffffff'),
                          color: active ? '#ffffff' : colors.text,
                          fontSize: '12px',
                          fontWeight: active ? 700 : 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '3px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>{s.icon}</span>
                        <span>{idx + 1}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Aktiver Slide-Inhalt */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '14px', color: colors.text }}>
                    <span style={{ fontSize: '17px' }}>{activeSlide.icon}</span>
                    <span>{activeSlide.title}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button
                      type="button"
                      data-testid="welcome-slide-prev"
                      onClick={() => setWelcomeSlide((slideIdx - 1 + slides.length) % slides.length)}
                      aria-label={t('common.back')}
                      style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.darkMode ? '#475569' : '#cbd5e1'}`,
                        background: colors.darkMode ? '#1e293b' : '#ffffff',
                        color: colors.text,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '14px',
                      }}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      data-testid="welcome-slide-next"
                      onClick={() => setWelcomeSlide((slideIdx + 1) % slides.length)}
                      aria-label={t('common.next')}
                      style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.darkMode ? '#475569' : '#cbd5e1'}`,
                        background: colors.darkMode ? '#1e293b' : '#ffffff',
                        color: colors.text,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '14px',
                      }}
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div style={{ fontSize: '12px', lineHeight: 1.35, color: colors.darkMode ? '#cbd5e1' : '#475569', marginBottom: slideHighlights.length > 0 ? '7px' : 0 }}>
                  {activeSlide.intro}
                </div>

                {slideHighlights.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {slideHighlights.map((h, i) => (
                      <span
                        key={i}
                        title={h.desc}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '3px 8px',
                          borderRadius: '999px',
                          background: colors.darkMode ? '#1e293b' : '#ffffff',
                          border: `1px solid ${colors.darkMode ? '#334155' : '#cbd5e1'}`,
                          fontSize: '11.5px',
                          fontWeight: 600,
                          color: colors.darkMode ? '#e2e8f0' : '#1e293b',
                        }}
                      >
                        <span>{h.icon}</span>
                        <span>{h.title}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!currentUser && (
              <div
                style={{
                  padding: '9px 11px',
                  borderRadius: '10px',
                  background: colors.darkMode ? 'rgba(30, 64, 175, 0.25)' : '#dbeafe',
                  border: `1px solid ${colors.darkMode ? '#3b82f6' : '#93c5fd'}`,
                  marginBottom: '8px',
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 700, color: colors.darkMode ? '#93c5fd' : '#1e40af', marginBottom: '2px' }}>
                  🎁 {t('premium.trialTitle')}
                </div>
                <div style={{ fontSize: '11.5px', color: colors.darkMode ? '#bfdbfe' : '#1e3a8a', marginBottom: '7px', lineHeight: 1.3 }}>
                  {t('premium.trialDescription')}
                </div>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  style={{
                    padding: '9px 14px',
                    background: '#ffffff',
                    color: '#1f2937',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                    <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" />
                  </svg>
                  {loading ? t('common.isLoading') : t('auth.loginWithGoogle')}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              {currentUser ? (
                <button
                  type="button"
                  data-testid="welcome-setup"
                  onClick={() => {
                    setShowSetup(true);
                    setSetupGesehen(true);
                    try { localStorage.setItem('kiosk_setup_seen', '1'); } catch { /* */ }
                  }}
                  style={{
                    flex: '1 1 180px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.darkMode ? '#475569' : '#cbd5e1'}`,
                    background: colors.darkMode ? '#1e293b' : '#ffffff',
                    color: colors.text,
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>🧭</span>
                  <span style={{ flex: 1 }}>{t('setup.button')}: {t('setup.buttonHint')}</span>
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>🌐 {t('settings.language')}:</span>
                  <LanguageSelector variant="hybrid" />
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {demoHidden && (
                  <button
                    type="button"
                    data-testid="demo-packages-restore"
                    onClick={() => {
                      setDemoPakete(createInitialDemoPakete());
                      setDemoHidden(false);
                      try { localStorage.removeItem('kiosk_demo_hidden'); } catch { /* */ }
                    }}
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.darkMode ? '#059669' : '#6ee7b7'}`,
                      background: colors.darkMode ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                      color: colors.darkMode ? '#6ee7b7' : '#047857',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <span>🧪</span>
                    <span>Demo</span>
                  </button>
                )}
                <button
                  type="button"
                  data-testid="welcome-help"
                  onClick={() => setShowHelpCenter(true)}
                  style={{
                    padding: '7px 11px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.darkMode ? '#475569' : '#cbd5e1'}`,
                    background: colors.darkMode ? '#1e293b' : '#ffffff',
                    color: colors.text,
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <span>💡</span>
                  <span>{t('whatsnewPopup.openHelp')}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Regal-Auswahl */}
      {!staffMode && showRegale && (
      <div style={{ marginBottom: 16 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 8 
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}>
            {availableRegale.map(regal => (
    <button
      key={regal}
      onClick={() => {
        // Wenn das gleiche Regal nochmal angeklickt wird (abwählen)
        if (selectedRegal === regal) {
          setSelectedRegal(''); // Regal abwählen
          setSelectedRegalplatz(null); // WICHTIG: Auch Regalplatz-Selektion zurücksetzen
        } else {
          // Anderes Regal auswählen
          setSelectedRegal(regal);
          // Optional: Wenn der ausgewählte Regalplatz nicht mit dem neuen Regal beginnt, zurücksetzen
          if (selectedRegalplatz && !selectedRegalplatz.startsWith(regal)) {
            setSelectedRegalplatz(null);
          }
        }
      }}
      style={{
        background: selectedRegal === regal ? colors.button : '#ccc',
        color: selectedRegal === regal ? '#fff' : '#000',
        border: 'none',
        borderRadius: 4,
        padding: '8px 16px',
        cursor: 'pointer'
      }}
    >
      {regal}
    </button>
  ))}
          </div>
          
          {/* Ausblenden-Button (Auge-Symbol) */}
          <button
            onClick={() => setShowRegale(false)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              opacity: 0.6,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.6'}
            title={t('filters.hideShelves')}
          >
            👁️
          </button>
        </div>
      </div>
      )}

      {/* Platzübersicht Grid, nur anzeigen wenn ein Regal ausgewählt ist UND Regale sichtbar sind */}
{!staffMode && showRegale && selectedRegal && (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
    gap: 8,
    marginBottom: 16,
    backgroundColor: colors.darkMode ? '#20232a' : '#f5f5f5',
    padding: 10,
    borderRadius: 8
  }}>
    {/* Spezielle Regalplätze ohne Nummern (Vorne, Kasse, etc.) */}
    {Object.entries(belegungsZaehler)
      .filter(([platz]) => {
        // Filtere Plätze, die mit dem ausgewählten Regal beginnen und keine Zahlen enthalten
        return platz.startsWith(selectedRegal) && !/\d/.test(platz);
      })
      .map(([platz, count]) => (
        <div
          key={platz}
          style={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#4caf50', // Grün, da immer mindestens ein Paket vorhanden ist
            color: '#fff',
            borderRadius: 4,
            cursor: 'pointer',
            position: 'relative',
            fontSize: '11px', // Kleinere Schrift für längere Namen
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0 4px',
            gridColumn: '1 / -1', // Ganze Zeile einnehmen
            marginTop: 8
          }}
          onClick={() => setSelectedRegalplatz(selectedRegalplatz === platz ? null : platz)}
          onTouchStart={(e) => handleTouchStart(e, platz)}
          onTouchEnd={handleTouchEnd}
        >
          {platz.replace(selectedRegal, '')} {/* Nur den Teil nach dem Regalprefix anzeigen */}
          <span style={{ 
            position: 'absolute', 
            top: -5, 
            right: -5,
            background: '#ff5722',
            color: '#fff',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 'bold'
          }}>
            {count}
          </span>
        </div>
      ))}
    
    {/* Bestehender Code für nummerierte Regalplätze */}
    {Array.from({ length: Math.min(Math.max(0, maxPlatz - minPlatz + 10), 100) }, (_, i) => {
      const num = minPlatz + i;
      const platzId = `${selectedRegal}${num}`;
      const count = belegungsZaehler[platzId] || 0;
      
      return (
        <div
          key={platzId}
          style={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: count > 0 ? '#4caf50' : colors.darkMode ? '#1e2127' : '#e0e0e0',
            color: count > 0 ? '#fff' : colors.text,
            borderRadius: 4,
            cursor: count > 0 ? 'pointer' : 'default',
            position: 'relative'
          }}
          onClick={() => count > 0 && setSelectedRegalplatz(selectedRegalplatz === platzId ? null : platzId)}
          onTouchStart={(e) => count > 0 && handleTouchStart(e, platzId)}
          onTouchEnd={handleTouchEnd}
        >
          {num}
          {count > 0 && (
            <span style={{ 
              position: 'absolute', 
              top: -5, 
              right: -5,
              background: '#ff5722',
              color: '#fff',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 'bold'
            }}>
              {count}
            </span>
          )}
        </div>
      );
    })}
  </div>
)}

      {/* Historische Regalansicht - Datumsfeld */}
      {showRegale && (
        <div style={{
          padding: '12px 16px',
          background: colors.darkMode ? '#2d333b' : '#f5f5f5',
          borderRadius: '8px',
          marginBottom: '16px',
          border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>🕐</span>
              <span>{t('filters.shelfStatusOnDay')}</span>
            </label>
            <input
              type="date"
              value={historicalDate || ''}
              onChange={(e) => setHistoricalDate(e.target.value || null)}
              max={new Date().toISOString().split('T')[0]}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: `1px solid ${colors.darkMode ? '#3a444e' : '#ccc'}`,
                background: colors.darkMode ? '#1a1f28' : '#fff',
                color: colors.text,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            />
            {historicalDate && (
              <button
                onClick={() => setHistoricalDate(null)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: 'none',
                  background: '#f44336',
                  color: '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                ✕ {t('common.reset')}
              </button>
            )}
          </div>
          {historicalDate && (() => {
            const selectedDate = new Date(historicalDate + 'T12:00:00');
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysDiff = Math.floor((today - selectedDate) / (1000 * 60 * 60 * 24));
            const isOlderThan5Days = daysDiff > 5;
            const dateLocale = lang === 'nl' ? 'nl-NL' : lang === 'en' ? 'en-GB' : 'de-DE';
            
            return (
              <>
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: colors.darkMode ? '#8b949e' : '#666',
                  fontStyle: 'italic'
                }}>
                  ℹ️ {t('filters.shelfSnapshotInfo', { date: selectedDate.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) })}
                </div>
                {isOlderThan5Days && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: '#ff9800',
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                    <span>
                      {t('filters.dateWarning')}
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* 📋 Barcode-Stapel nach Kurierlieferung — bewusst AUSSERHALB des sticky Suchkastens,
          damit der Stapel bei der Suche nicht oben angepinnt bleibt, sondern beim Scrollen mit nach oben verschwindet. */}
      {hasCustomFeature('batchBarcodes') && batchCount > 0 && (() => {
        const allDone = batchScannedCount >= batchCount;
        const times = (batchItems || []).map((x) => x.at).filter(Boolean);
        const minAt = times.length ? Math.min(...times) : 0;
        const maxAt = times.length ? Math.max(...times) : 0;
        const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
        const timeRange = minAt ? (fmtTime(minAt) === fmtTime(maxAt) ? t('ui.main.clockTime', { time: fmtTime(minAt) }) : t('ui.main.clockTime', { time: `${fmtTime(minAt)}–${fmtTime(maxAt)}` })) : '';
        return (
          <div
            style={{
              width: '100%', marginBottom: '10px', padding: '10px 12px', borderRadius: '8px',
              border: `1px solid ${allDone ? '#22c55e' : '#7b61ff'}`,
              background: allDone ? (colors.darkMode ? '#142e1f' : '#ecfdf5') : (colors.darkMode ? '#241f3a' : '#f1eeff'),
              color: colors.text,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              data-testid="batch-banner"
              onClick={() => setShowBatch(true)}
              style={{
                flex: 1, minWidth: 200, background: 'transparent', border: 'none', color: 'inherit',
                padding: 0, cursor: 'pointer', fontSize: '14px', fontWeight: 600, textAlign: 'left',
                display: 'flex', flexDirection: 'column', gap: '2px',
              }}
            >
              <span>
                {allDone
                  ? `✅ ${t('batch.bannerAllDone', { count: batchCount }) || `Stapel: Alle ${batchCount} Barcode(s) bereits gescannt ✓`}`
                  : `📋 ${t('batch.banner', { count: batchCount })}`}
              </span>
              <span style={{ fontSize: '12px', opacity: 0.85, fontWeight: 500 }}>
                {timeRange ? `🕐 ${t('batch.currentDelivery', { time: timeRange })}` : ''}
                {batchExpected > 0
                  ? ` · 🚚 ${t('batch.courierCheck', { n: batchCount, total: batchExpected })} ${batchCount === batchExpected ? '✓' : ''}`
                  : ''}
                {!allDone && (batchPos > 0 || batchScannedCount > 0)
                  ? ` · ⏸️ ${t('batch.progress', { pos: Math.min(batchPos + 1, batchCount), total: batchCount, scanned: batchScannedCount })}`
                  : ''}
              </span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                data-testid="batch-banner-open"
                onClick={() => setShowBatch(true)}
                style={{
                  fontSize: '12px', fontWeight: 700, background: '#7b61ff', color: '#fff',
                  border: 'none', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                ▶ {t('batch.open')} ({batchScannedCount}/{batchCount})
              </button>
              <button
                type="button"
                data-testid="batch-banner-complete"
                onClick={(ev) => {
                  ev.stopPropagation();
                  completeCurrentBatch();
                  if (window.showToast) {
                    window.showToast(`✅ ${t('batch.doneToast', { count: batchCount, time: new Date().toLocaleTimeString(lang || undefined, { hour: '2-digit', minute: '2-digit' }) })}`, 'success', 3500);
                  }
                }}
                title={t('batch.doneTitle')}
                style={{
                  fontSize: '12px', fontWeight: 700,
                  background: colors.darkMode ? '#14532d' : '#dcfce7',
                  color: colors.darkMode ? '#86efac' : '#166534',
                  border: '1px solid #22c55e',
                  padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                ✅ {t('batch.done')}
              </button>
            </div>
          </div>
        );
      })()}

      {/* 🧪 Visueller Demo-Modus Banner (wenn aktiv: echte Pakete ausgeblendet, nur Demo-Pakete sichtbar) */}
      {gpDemoMode && (
        <div
          data-testid="gp-demo-mode-banner"
          style={{
            marginBottom: '12px',
            padding: '12px 14px',
            borderRadius: '10px',
            border: '2px solid #f59e0b',
            background: colors.darkMode ? 'rgba(245, 158, 11, 0.14)' : '#fffbeb',
            color: colors.text,
            boxShadow: '0 4px 14px rgba(245, 158, 11, 0.18)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 800, fontSize: '14.5px', color: colors.darkMode ? '#fcd34d' : '#b45309' }}>
              🧪 DEMO-MODUS AKTIV — Echte Pakete sind sicher ausgeblendet
            </div>
            <span style={{
              fontSize: '11.5px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '999px',
              background: '#f59e0b',
              color: '#fff',
            }}>
              {effektivePakete.filter((p) => p && !p.deleted).length} Demo-Paket(e)
            </span>
          </div>
          <div style={{ fontSize: '12.5px', marginTop: '4px', lineHeight: 1.45, opacity: 0.92 }}>
            Hier siehst du <b>nur deine Demo-Testpakete</b>. Du kannst den gesamten Ablauf gefahrlos von A bis Z testen:{' '}
            <b>1. Paket erfassen & Etikett drucken</b> → <b>2. Im Lager mit Scanner Paket + Regal scannen</b> → <b>3. Mit Unterschrift ausgeben</b>.
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
            {grenzpaketFreigeschaltet() && (
              <button
                type="button"
                data-testid="gp-demo-open-logs"
                onClick={() => setShowGrenzpaketPanel(true)}
                style={{
                  padding: '7px 12px',
                  borderRadius: '7px',
                  border: '1px solid #d97706',
                  background: colors.darkMode ? '#1f2937' : '#ffffff',
                  color: colors.darkMode ? '#fcd34d' : '#b45309',
                  fontWeight: 700,
                  fontSize: '12.5px',
                  cursor: 'pointer',
                }}
              >
                📋 Buchungs-Log prüfen (A–Z)
              </button>
            )}
            {effektivePakete.filter((p) => p && !p.deleted).length > 0 && (
              <button
                type="button"
                data-testid="gp-demo-clear-packages"
                onClick={async () => {
                  const demoList = (pakete || []).filter((p) => p && !p.deleted && isDemoPackage(p));
                  if (!demoList.length) return;
                  const demoIds = new Set(demoList.map((p) => p.id));
                  const verbleibend = (pakete || []).filter((p) => !demoIds.has(p.id));
                  setPakete(verbleibend);
                  await set('pakete', verbleibend);
                  for (const dp of demoList) {
                    try { await serverPackageService.deletePackage(dp.id); } catch { /* ignore */ }
                  }
                  if (window.showToast) window.showToast(`🗑️ ${demoList.length} Demo-Paket(e) aufgeräumt`, 'info', 2800);
                }}
                style={{
                  padding: '7px 12px',
                  borderRadius: '7px',
                  border: '1px solid #ef4444',
                  background: colors.darkMode ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                  color: colors.darkMode ? '#fca5a5' : '#b91c1c',
                  fontWeight: 700,
                  fontSize: '12.5px',
                  cursor: 'pointer',
                }}
              >
                🗑️ Demo-Pakete löschen
              </button>
            )}
            <button
              type="button"
              data-testid="gp-demo-exit"
              onClick={() => setDemoModeActive(false)}
              style={{
                marginLeft: 'auto',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                background: '#d97706',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '12.5px',
                cursor: 'pointer',
              }}
            >
              ✕ Demo-Modus beenden (Echte Pakete zeigen)
            </button>
          </div>
        </div>
      )}

      {/* Suchbereich - oben angedockt (sticky), damit man beim Tippen immer sieht,
          wonach man sucht, und der Bildschirm nicht bei jedem Buchstaben springt. */}
      <div ref={searchBoxRef} style={{
        // NUR beim aktiven Tippen oben andocken (dann ist der Kasten kompakt und die
        // Filter sind ausgeblendet). Bei reinem Filtern (leere Suche) NICHT andocken —
        // sonst bleiben die Werktage-Regler oben kleben und man sieht kaum Treffer.
        // So wandern die Filtereinstellungen beim Scrollen mit nach oben weg.
        position: search.trim() ? 'sticky' : 'static',
        top: 0,
        zIndex: 30,
        // Während der Suche kompakter (weniger Rand) → mehr Platz für Treffer.
        padding: search.trim() ? '8px 12px' : '16px',
        background: colors.darkMode ? '#2d333b' : '#ffffff',
        borderRadius: '8px',
        marginBottom: search.trim() ? '6px' : '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: search.trim() ? '4px' : '8px' }}>
          {!search.trim() && <h2 style={{ margin: 0 }}>{texts[lang].search}</h2>}
          {!staffMode && <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            color: colors.text
          }}>
            <input
              type="checkbox"
              checked={showFilters}
              onChange={(e) => {
                const newState = e.target.checked;
                setShowFilters(newState);
                // Wenn Filter deaktiviert wird, auch Regalansicht ausblenden
                if (!newState) {
                  setShowRegale(false);
                }
              }}
              style={{ cursor: 'pointer', margin: 0, width: '16px', height: '16px' }}
            />
            <span>{t('common.filter')}</span>
          </label>}
        </div>
        {/* 📦 Vinted-Pakete ohne Namen → per Screenshot füllen (nur Sonderfunktion, nur wenn nötig) */}
        {hasCustomFeature('vintedImport') && offenePlatzhalter(pakete).length > 0 && (
          <button type="button" data-testid="vinted-banner" onClick={() => setShowVintedImport(true)}
            style={{ width: '100%', marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid #3b9fd1', background: colors.darkMode ? '#12303a' : '#e6f4fb', color: colors.text,
              fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
            📦 {t('vinted.banner', { count: offenePlatzhalter(pakete).length })}
          </button>
        )}
        {/* 🧪 Vinted-Anbindung gestört (abgemeldet) → Hinweis; Tippen öffnet das Panel mit der Anmeldung */}
        {vintedAn && vintedStoerung && (
          <button type="button" data-testid="vinted-stoerung" data-art={vintedStoerung} onClick={() => setShowVintedPanel(true)}
            style={{ width: '100%', marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid #d19a3b', background: colors.darkMode ? '#3a2f12' : '#fdf3dc', color: colors.text,
              fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
            ⚠️ {vt(lang)(vintedStoerung === 'login' ? 'bannerLogin' : 'bannerDown')}
          </button>
        )}
        {/* 🧪 DHL-Anbindung gestört (abgemeldet oder nicht erreichbar) → Hinweis; Tippen öffnet das Panel mit der Anmeldung */}
        {dhlBridgeAn && dhlStoerung && (
          <button type="button" data-testid="dhl-stoerung" data-art={dhlStoerung} onClick={() => setShowDhlBridge(true)}
            style={{ width: '100%', marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid #d19a3b', background: colors.darkMode ? '#3a2f12' : '#fdf3dc', color: colors.text,
              fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
            ⚠️ {dt(lang)(dhlStoerung === 'login' ? 'bannerLogin' : 'bannerDown')}
          </button>
        )}
        {/* 🧪 Was bei Vinted ansteht: abgelaufene Pakete (zurück an den Fahrer) und Nachrichten — Tippen öffnet die Liste mit Fach */}
        {vintedAn && !vintedStoerung && vintedHinweis && (vintedHinweis.rueck > 0 || vintedHinweis.post) && (
          <button type="button" data-testid="vinted-hinweis" onClick={() => setShowVintedPanel(true)}
            style={{ width: '100%', marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid #007782', background: colors.darkMode ? '#0f2e31' : '#e3f4f5', color: colors.text,
              fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
            🚚 {[vintedHinweis.rueck > 0 ? vt(lang)('bannerRueck', { n: vintedHinweis.rueck }) : '', vintedHinweis.post ? vt(lang)('bannerPost') : ''].filter(Boolean).join(' · ')}
          </button>
        )}
        {/* 🧪 Laut Vinted-Sendungsverfolgung abgeholt, hier noch offen — Tippen öffnet die Rückfrage „übernehmen?" */}
        {vintedAn && vintedAbgeholtZahl > 0 && (
          <button type="button" data-testid="vinted-abgeholt" onClick={() => setShowVintedPanel(true)}
            style={{ width: '100%', marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid #007782', background: colors.darkMode ? '#0f2e31' : '#e3f4f5', color: colors.text,
              fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
            📦 {vt(lang)('bannerAbgeholt', { n: vintedAbgeholtZahl })}
          </button>
        )}
        <div style={{ 
  position: 'relative', 
  marginBottom: '12px' 
}}>
  {/* 📷 Abholcode scannen (z. B. Vinted-QR aus der Kunden-App) → Sendungsnummer suchen */}
  <ShelfQrScanner
    active={searchScanOn}
    formats={SUCH_SCAN_FORMATE}
    onCode={(code, alle) => {
      // Ein Label trägt oft mehrere Codes → den nehmen, der zu einem Paket im Bestand passt
      const kandidaten = (alle && alle.length ? alle : [code]).map(scanZuSuchtext).filter(Boolean);
      if (!kandidaten.length) return;
      let paketTreffer = null;
      for (const k of kandidaten) {
        paketTreffer = pakete.find((p) => p && !p.deleted && trackingTreffer(p, k));
        if (paketTreffer) break;
      }
      setSearch(paketTreffer ? String(paketTreffer.trackingNumber) : kandidaten[0]);
      setSearchScanOn(false);
    }}
  />
  <input
    id="search-bar"
    type="text"
    value={search}
    onChange={handleSearchChange}
    // Beim Tippen die untere Navigationsleiste ausblenden — sonst schwebt sie
    // über der Tastatur und verdeckt Trefferplätze. Bei Verlassen wieder zeigen.
    onFocus={() => {
      // Beim Fokus EINMALIG nach oben andocken — Header/„Neues Paket erfassen" wegscrollen.
      setTimeout(() => searchBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 320);
    }}
    placeholder={texts[lang].searchPlaceholder || 'Name, Straße, Regalplatz...'}
    style={{
      width: '100%',
      padding: '12px',
      paddingRight: '80px', // Platz für Scan- und X-Button
      borderRadius: '6px',
      border: `1px solid ${colors.border}`,
      background: colors.inputBg,
      color: colors.text,
      fontSize: '16px'
    }}
  />
  <button
    type="button"
    onClick={() => setSearchScanOn((v) => !v)}
    title={t('search.scanCode')}
    aria-label={t('search.scanCode')}
    style={{
      position: 'absolute', right: search.length > 0 ? '40px' : '8px', top: '50%',
      transform: 'translateY(-50%)', border: 'none', borderRadius: '6px', cursor: 'pointer',
      padding: '4px 8px', fontSize: '18px',
      background: searchScanOn ? '#4caf50' : 'transparent',
    }}
  >
    📷
  </button>
  {search.length > 0 && (
    <button
      onClick={() => {
        setSearch('');
        // Optionaler Fokus zurück aufs Suchfeld
        document.getElementById('search-bar')?.focus();
      }}
      style={{
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'transparent',
        border: 'none',
        color: colors.darkMode ? '#fff' : '#333',
        fontSize: '18px',
        cursor: 'pointer',
        padding: '4px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      aria-label={t('search.clearSearch')}
    >
      ✕
    </button>
  )}
</div>

        {/* Checkboxen in 2-Spalten Layout - optimiert */}
        {/* Beim Tippen die Filter-Checkboxen ausblenden → mehr Platz für Treffer.
            Die Filter bleiben aktiv; bei leerer Suche sind sie wieder da.
            Beim allerersten Öffnen ohne Anmeldung und ohne Pakete ausblenden, damit keine 5 leeren Filter über einer leeren Liste stehen. */}
        {(staffMode || showFilters) && !search.trim() && !(setupAktiv && !currentUser && paketeGeladen && pakete.length === 0) && (
        <div data-testid="filter-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '6px',
          marginBottom: '12px'
        }}>
          {/* Checkbox 1: Regale anzeigen — immer als Umschalter sichtbar (ein/aus); nicht im Mitarbeiter-Modus */}
          {!staffMode && <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: colors.text,
              padding: '4px 8px',
              background: colors.darkMode ? '#1a2332' : '#f0f0f0',
              border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
              borderRadius: '4px',
              minHeight: '28px'
            }}>
              <input
                type="checkbox"
                checked={showRegale}
                onChange={(e) => setShowRegale(e.target.checked)}
                style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
              />
              <span>{t('filters.showShelves')}</span>
            </label>}

          {/* Checkbox 2: Lagerfrist überprüfen */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: colors.text,
            padding: '4px 8px',
            background: showOld ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
            border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
            borderRadius: '4px',
            minHeight: '28px'
          }}>
            <input
              type="checkbox"
              id="show-old"
              checked={showOld}
              onChange={() => setShowOld(!showOld)}
              style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
            />
            <span>{t('filters.storagePeriod')}</span>
          </label>

          {/* Checkbox 3: Nach Datum filtern */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: colors.text,
            padding: '4px 8px',
            background: showDateFilter ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
            border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
            borderRadius: '4px',
            minHeight: '28px'
          }}>
            <input
              type="checkbox"
              id="show-date-filter"
              checked={showDateFilter}
              onChange={() => {
                const next = !showDateFilter;
                setShowDateFilter(next);
                // Abwählen leert den Datumsfilter — sonst filtert er (dateFilterFrom/To bleiben
                // gesetzt) still weiter und man findet bei der Suche „keine Pakete".
                if (!next) { setDateFilterFrom(''); setDateFilterTo(''); }
              }}
              style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
            />
            <span>📅 {t('search.date')}</span>
          </label>

          {/* 🖨️ Etiketten-Druck-Buttons in der Liste anzeigen — nur wenn Drucker eingerichtet */}
          {!staffMode && isPrinterSupported() && getPrinterConfig()?.address && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              fontSize: '14px', color: colors.text, padding: '4px 8px',
              background: showPrintLabels ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
              border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
              borderRadius: '4px', minHeight: '28px'
            }}>
              <input
                type="checkbox"
                id="show-print-labels"
                checked={showPrintLabels}
                onChange={() => setShowPrintLabels(!showPrintLabels)}
                style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
              />
              <span>🖨️ {t('printer.listToggle')}</span>
            </label>
          )}

          {/* 🖼️ Paket-Fotos in der Liste anzeigen (Standard aus — streckt die Ansicht) */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
            fontSize: '14px', color: colors.text, padding: '4px 8px',
            background: showImages ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
            border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
            borderRadius: '4px', minHeight: '28px'
          }}>
            <input
              type="checkbox"
              id="show-images"
              checked={showImages}
              onChange={() => setShowImages(!showImages)}
              style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
            />
            <span>🖼️ {t('filters.showImages')}</span>
          </label>

          {/* ℹ️ Details in der Liste anzeigen (Sendungsnr., PLZ/Ort, Paketdienst) — Standard aus */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
            fontSize: '14px', color: colors.text, padding: '4px 8px',
            background: showDetails ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
            border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
            borderRadius: '4px', minHeight: '28px'
          }}>
            <input
              type="checkbox"
              id="show-details"
              checked={showDetails}
              onChange={() => setShowDetails(!showDetails)}
              style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
            />
            <span>ℹ️ {t('filters.showDetails')}</span>
          </label>

          {/* 📥 Absender in der Liste — nur wenn „Absender mit speichern" eingeschaltet ist */}
          {isCaptureSenderOn() && (
            <label data-testid="filter-sender" style={{
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              fontSize: '14px', color: colors.text, padding: '4px 8px',
              background: showSender ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
              border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
              borderRadius: '4px', minHeight: '28px'
            }}>
              <input
                type="checkbox"
                id="show-sender"
                checked={showSender}
                onChange={() => setShowSender(!showSender)}
                style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
              />
              <span>📥 {t('filters.showSender')}</span>
            </label>
          )}

          {/* 🗄️ Archiv (>5 Tage nach Ausgabe) einblenden */}
          {currentUser && (
            <label data-testid="filter-archiv" style={{
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              fontSize: '14px', color: colors.text, padding: '4px 8px',
              background: showArchiv ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
              border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
              borderRadius: '4px', minHeight: '28px'
            }}>
              <input
                type="checkbox"
                id="show-archiv"
                checked={showArchiv}
                onChange={() => setShowArchiv(!showArchiv)}
                style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
              />
              <span>🗄️ {lang === 'de' ? 'Archiv (>5T)' : lang === 'nl' ? 'Archief (>5d)' : 'Archive (>5d)'}</span>
            </label>
          )}

          {/* Checkbox 4: Suchtreffer vom Tablet */}
          {!staffMode && (recentTabletSearches.length > 0 || (isOnline && currentUser)) && (
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: colors.text,
              padding: '4px 8px',
              background: showTabletSearchResults ? (colors.darkMode ? '#1a2332' : '#e3f2fd') : 'transparent',
              border: `1px solid ${colors.darkMode ? '#3a444e' : '#ddd'}`,
              borderRadius: '4px',
              minHeight: '28px'
            }}>
              <input
                type="checkbox"
                id="show-tablet-results"
                checked={showTabletSearchResults}
                onChange={async () => {
                  const newState = !showTabletSearchResults;
                  setShowTabletSearchResults(newState);
                  if (newState) {
                    const found = await loadTabletSearches();
                    if (found && recentTabletSearches.length > 0) {
                      setTimeout(() => searchResultsRef.current?.scrollIntoView({behavior: 'smooth'}), 100);
                    } else {
                      alert(t('search.noTabletSearches'));
                      setShowTabletSearchResults(false);
                    }
                  }
                }}
                style={{ cursor: 'pointer', margin: 0, flexShrink: 0, width: '16px', height: '16px' }}
              />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Tablet
              </span>
              {recentTabletSearches.length > 0 && (
                <span style={{
                  background: colors.button,
                  color: 'white',
                  padding: '1px 5px',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {recentTabletSearches.length}
                </span>
              )}
              {showTabletSearchResults && search && (
                <span style={{
                  background: '#ff5722',
                  color: 'white',
                  padding: '2px 4px',
                  borderRadius: '8px',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  🔴
                </span>
              )}
            </label>
          )}
        </div>
        )}

        {/* 📊 TREFFERANZEIGE */}
        {(search || showOld || showDateFilter || regalFilter || selectedRegalplatz) && (() => {
          const ausgegeben = filteredPaketeWithIndex.filter(item => item.paket.ausgegebenAm).length;
          const zurueckgeschickt = filteredPaketeWithIndex.filter(item => item.paket.zurueckgeschicktAm || item.paket.zurueckgeschickt).length;
          
          return (
            <div style={{
              padding: search.trim() ? '6px 10px' : '10px 12px',
              background: colors.darkMode ? '#1a2332' : '#e3f2fd',
              borderRadius: '6px',
              marginBottom: search.trim() ? '6px' : '12px',
              fontSize: '14px',
              color: colors.darkMode ? '#64b5f6' : '#1976d2',
              fontWeight: '500'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span>🔍</span>
                <span>{filteredPaketeWithIndex.length} {t('search.hitCount')}</span>
                {pakete.length > 0 && (
                  <span style={{ color: colors.darkMode ? '#888' : '#666' }}>
                    / {pakete.filter(p => !p.deleted).length} {t('search.total')}
                  </span>
                )}
              </div>
              {(ausgegeben > 0 || zurueckgeschickt > 0) && (
                <div style={{ 
                  fontSize: '12px', 
                  color: colors.darkMode ? '#999' : '#666',
                  marginLeft: '28px',
                  display: 'flex',
                  gap: '12px'
                }}>
                  {ausgegeben > 0 && (
                    <span>📤 {ausgegeben} {t('search.issuedCount')}</span>
                  )}
                  {zurueckgeschickt > 0 && (
                    <span>📦 {zurueckgeschickt} {t('search.returnedCount')}</span>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Erweiterte Optionen unter den Checkboxen — leer erzeugt sonst eine große
            leere Lücke; während der Suche daher ohne Rand (Kinder bringen eigenen Abstand). */}
        <div style={{ marginBottom: search.trim() ? '0' : '16px' }}>
  {showOld && !search.trim() && (
    <>
      {/* 📦 Nationaler Balken */}
      <div style={{
        fontSize: '13px',
        color: colors.darkMode ? '#aaa' : '#666',
        marginLeft: '24px',
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span>📦</span>
        <span>{t('filters.national')}: {t('search.showOld')} <strong>{filterDays}</strong> {t('search.workdays')}</span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: '24px',
        marginBottom: '12px'
      }}>
        <input
          type="range"
          min="1"
          max="30"
          value={filterDays}
          onChange={(e) => setFilterDays(parseInt(e.target.value, 10))}
          style={{
            flex: 1,
            marginRight: '12px',
            accentColor: '#43a047'
          }}
        />
        <span style={{
          minWidth: '60px',
          textAlign: 'center',
          padding: '4px 8px',
          background: colors.darkMode ? '#2e4034' : '#e8f5e9',
          borderRadius: '4px',
          color: '#43a047',
          fontWeight: 'bold',
          fontSize: '13px'
        }}>
          {filterDays} {t('dates.days')}
        </span>
      </div>

      {/* 🌍 Internationaler Balken */}
      <div style={{
        fontSize: '13px',
        color: colors.darkMode ? '#aaa' : '#666',
        marginLeft: '24px',
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span>🌍</span>
        <span>{t('filters.international')}: {t('search.showOld')} <strong>{filterDaysInternational}</strong> {t('search.workdays')}</span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: '24px',
        marginBottom: '8px'
      }}>
        <input
          type="range"
          min="1"
          max="30"
          value={filterDaysInternational}
          onChange={(e) => setFilterDaysInternational(parseInt(e.target.value, 10))}
          style={{
            flex: 1,
            marginRight: '12px',
            accentColor: '#1976d2'
          }}
        />
        <span style={{
          minWidth: '60px',
          textAlign: 'center',
          padding: '4px 8px',
          background: colors.darkMode ? '#1a2332' : '#e3f2fd',
          borderRadius: '4px',
          color: '#1976d2',
          fontWeight: 'bold',
          fontSize: '13px'
        }}>
          {filterDaysInternational} {t('dates.days')}
        </span>
      </div>
    </>
  )}

  {showDateFilter && !search.trim() && (
    <div style={{
      marginLeft: '24px',
      marginBottom: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <label style={{
          fontSize: '14px',
          color: colors.text,
          minWidth: '40px'
        }}>{t('common.from')}:</label>
        <input
          type="date"
          value={dateFilterFrom}
          onChange={(e) => setDateFilterFrom(e.target.value)}
          style={{
            flex: 1,
            padding: '6px 10px',
            borderRadius: '6px',
            border: `1px solid ${colors.darkMode ? '#555' : '#ddd'}`,
            background: colors.darkMode ? '#2a2a2a' : 'white',
            color: colors.text,
            fontSize: '14px'
          }}
        />
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <label style={{
          fontSize: '14px',
          color: colors.text,
          minWidth: '40px'
        }}>{t('common.to')}:</label>
        <input
          type="date"
          value={dateFilterTo}
          onChange={(e) => setDateFilterTo(e.target.value)}
          style={{
            flex: 1,
            padding: '6px 10px',
            borderRadius: '6px',
            border: `1px solid ${colors.darkMode ? '#555' : '#ddd'}`,
            background: colors.darkMode ? '#2a2a2a' : 'white',
            color: colors.text,
            fontSize: '14px'
          }}
        />
      </div>
      {(dateFilterFrom || dateFilterTo) && (
        <button
          onClick={() => {
            setDateFilterFrom('');
            setDateFilterTo('');
          }}
          style={{
            padding: '6px 12px',
            background: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            alignSelf: 'flex-start'
          }}
        >
          🗑️ {t('filters.clearFilters')}
        </button>
      )}
    </div>
  )}

{infoMessage && (
  <div style={{
    position: 'fixed',
    bottom: 30,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#43a047',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    zIndex: 2000,
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  }}>
    {infoMessage}
  </div>
)}

{showTabletSearchResults && recentTabletSearches.length > 0 && (
  <div style={{
    marginLeft: '24px',
    marginBottom: '12px'
  }}>
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginBottom: '10px'
    }}>
      {recentTabletSearches.map((search, index) => (
        <button
          key={index}
          onClick={() => setSelectedSearchIndex(index)}
          style={{
            padding: '6px 12px',
            fontSize: '14px',
            border: 'none',
            borderRadius: '20px',
            background: selectedSearchIndex === index 
              ? '#007bff' 
              : colors.darkMode ? '#3a444e' : '#e6e6e6',
            color: selectedSearchIndex === index 
              ? 'white' 
              : colors.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span>
            {search.query.length > 15 
              ? search.query.substring(0, 15) + '...' 
              : search.query}
          </span>
          <span style={{
            background: '#ff5722',
            color: 'white',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 'bold'
          }}>
            {search.resultCount}
          </span>
        </button>
      ))}
    </div>
    
    <div style={{
      fontSize: '14px',
      color: colors.darkMode ? '#aaa' : '#666',
      background: colors.darkMode ? '#3a444e' : '#f0f0f0',
      padding: '4px 8px',
      borderRadius: '4px'
    }}>
      <div><strong>{t('search.searchQuery')}:</strong> "{recentTabletSearches[selectedSearchIndex].query}"</div>
      <div><strong>{t('search.fromDevice')}:</strong> {recentTabletSearches[selectedSearchIndex].device}</div>
      <div><strong>{t('search.time')}:</strong> {
        new Date(recentTabletSearches[selectedSearchIndex].timestamp).toLocaleString()
      }</div>
      <div><strong>{t('search.hitCount')}:</strong> {recentTabletSearches[selectedSearchIndex].resultCount}</div>
    </div>
  </div>
)}

</div>
      </div>

      {/* 🧪 Demo-Pakete Info-Bar (nur bei 0 echten Paketen sichtbar) */}
      {zeigeDemoPakete && (
        <div
          data-testid="demo-packages-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '6px',
            padding: '7px 10px',
            marginBottom: '8px',
            borderRadius: '8px',
            background: colors.darkMode ? 'rgba(16, 185, 129, 0.14)' : '#ecfdf5',
            border: `1px dashed ${colors.darkMode ? '#10b981' : '#6ee7b7'}`,
            fontSize: '12px',
            color: colors.darkMode ? '#a7f3d0' : '#065f46',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600 }}>
            <span>🧪</span>
            <span>Demo ({demoPakete.filter(p => !p.deleted && !p.ausgegebenAm).length}/2 {t('packages.packages')})</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="demo-packages-regale"
              onClick={() => setShowRegale(v => !v)}
              style={{
                padding: '3px 7px',
                borderRadius: '6px',
                border: `1px solid ${showRegale ? '#2563eb' : (colors.darkMode ? '#059669' : '#6ee7b7')}`,
                background: showRegale ? '#2563eb' : (colors.darkMode ? '#064e3b' : '#ffffff'),
                color: showRegale ? '#ffffff' : (colors.darkMode ? '#a7f3d0' : '#047857'),
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              🗄️ {t('filters.showShelves')}
            </button>
            <button
              type="button"
              data-testid="demo-packages-reset"
              onClick={() => setDemoPakete(createInitialDemoPakete())}
              title={t('common.reset')}
              style={{
                padding: '3px 7px',
                borderRadius: '6px',
                border: `1px solid ${colors.darkMode ? '#059669' : '#6ee7b7'}`,
                background: colors.darkMode ? '#064e3b' : '#ffffff',
                color: colors.darkMode ? '#a7f3d0' : '#047857',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              🔄 {t('common.reset')}
            </button>
            <button
              type="button"
              data-testid="demo-packages-hide"
              onClick={() => {
                setDemoHidden(true);
                try { localStorage.setItem('kiosk_demo_hidden', '1'); } catch { /* */ }
              }}
              title={t('common.close')}
              style={{
                padding: '3px 7px',
                borderRadius: '6px',
                border: `1px solid ${colors.darkMode ? '#475569' : '#cbd5e1'}`,
                background: colors.darkMode ? '#1e293b' : '#ffffff',
                color: colors.text,
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Paketliste - direkt sichtbar */}
      <ul ref={searchResultsRef} style={{ 
        listStyle: 'none', 
        padding: 0,
        background: colors.darkMode ? '#2d333b' : '#ffffff',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {sortedPakete.slice(0, visibleCount).map((paket, idx) => (
          <PackageListItem
            key={paket.id || idx}
            paket={paket}
            idx={idx}
            onSelect={handleSelect}
            onAusgeben={handleAusgeben}
            onZurueckschicken={handleZurueckschicken}
            onDelete={handleDelete}
            searchLower={searchLower}
            formatDateTime={formatDateTime}
            highlightMatch={highlightMatch}
            texts={texts}
            lang={lang}
            colors={colors}
            isOnline={isOnline}
            isPaketSynced={isPaketSynced}
            handleTouchStart={handleTouchStart}
            handleTouchEnd={handleTouchEnd}
            showOld={showOld}
            showPrintLabels={showPrintLabels || staffMode} // 👷 Mitarbeiter-Modus: Nachdruck immer möglich (Filter ist dort ausgeblendet)
            showImages={showImages}
            showDetails={showDetails}
            showSender={showSender}
            t={t}
          />
        ))}
      </ul>
      
      {/* "Mehr anzeigen" Button */}
      {sortedPakete.length > visibleCount && (
        <button
          onClick={() => setVisibleCount(prev => prev + 10)}
          style={{
            display: 'block',
            margin: '12px auto',
            padding: '8px 16px',
            background: colors.button,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {t('common.loadMore')}
        </button>
      )}

      {/* 🗄️ Lazy-Load Archiv-Treffer (> 5 Tage nach Ausgabe) */}
      {(sichtbareArchivPakete.length > 0 || (archivLoading && showArchiv)) && (
        <div
          data-testid="archiv-results-section"
          style={{
            marginTop: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: colors.darkMode ? '#1e242c' : '#f8fafc',
            border: `1px dashed ${colors.darkMode ? '#374151' : '#cbd5e1'}`,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13, fontWeight: 700, color: colors.text }}>
            <span>
              🗄️ {lang === 'de'
                ? `Archiv (> 5 Tage ausgegeben) — ${sichtbareArchivPakete.length} Treffer`
                : lang === 'nl'
                  ? `Archief (> 5 dagen uitgegeven) — ${sichtbareArchivPakete.length} treffers`
                  : `Archive (> 5 days handed out) — ${sichtbareArchivPakete.length} matches`}
            </span>
            {archivLoading && <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75 }}>⏳ {t('common.isLoading')}</span>}
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {sichtbareArchivPakete.map((paket, idx) => (
              <PackageListItem
                key={`archiv-${paket.id || idx}`}
                paket={paket}
                idx={idx}
                onSelect={handleSelect}
                onAusgeben={handleAusgeben}
                onZurueckschicken={handleZurueckschicken}
                onDelete={handleDelete}
                searchLower={searchLower}
                formatDateTime={formatDateTime}
                highlightMatch={highlightMatch}
                texts={texts}
                lang={lang}
                colors={colors}
                isOnline={isOnline}
                isPaketSynced={isPaketSynced}
                handleTouchStart={handleTouchStart}
                handleTouchEnd={handleTouchEnd}
                showOld={false}
                showPrintLabels={showPrintLabels || staffMode}
                showImages={showImages}
                showDetails={showDetails}
                showSender={showSender}
                t={t}
              />
            ))}
          </ul>
          {archivHasMore && (
            <button
              type="button"
              data-testid="archiv-load-more"
              disabled={archivLoading}
              onClick={loadMoreArchiv}
              style={{
                display: 'block',
                margin: '10px auto 2px',
                padding: '7px 14px',
                background: colors.darkMode ? '#374151' : '#e2e8f0',
                color: colors.text,
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 13,
                cursor: archivLoading ? 'wait' : 'pointer',
              }}
            >
              🗄️ {t('common.loadMore')}
            </button>
          )}
        </div>
      )}

      {/* Login-Bereich — im Mitarbeiter-Modus nur eine schmale Leiste mit Schloss */}
      {staffMode ? (
        <div data-testid="staff-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
          padding: '12px 14px', marginTop: '16px', marginBottom: '40px', borderRadius: '8px',
          border: `1px dashed ${colors.border}`, fontSize: '13px', color: colors.darkMode ? '#aaa' : '#666' }}>
          <span>👷 {t('staff.active')}</span>
          <button type="button" data-testid="staff-exit" onClick={() => setStaffDialog('exit')}
            style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', border: `1px solid ${colors.border}`,
              background: 'transparent', color: colors.text, fontSize: '13px' }}>🔒 {t('staff.exit')}</button>
        </div>
      ) : (
      <div style={{ 
        padding: '16px', 
        paddingBottom: '120px', // ✨ Extra Platz für Android-Navigationsleiste!
        border: '1px solid #ddd', 
        borderRadius: '8px', 
        marginTop: '16px',
        marginBottom: '80px' // ✨ Extra Abstand am Ende
      }}>
  <h3>{texts[lang]?.loginTitle || 'Login'}</h3>
  {/* 🔁 Anmeldung liegt am Gerät, der Server war beim Start nicht erreichbar → die App verbindet sich von selbst neu */}
  {authReconnecting && (
    <div data-testid="auth-reconnecting" style={{ background: colors.darkMode ? '#3a3320' : '#fff8e1', border: '1px solid #f0b429', borderRadius: '10px',
      padding: '12px 14px', marginBottom: '14px', color: colors.darkMode ? '#fff' : '#5c4400', fontSize: '14px', lineHeight: 1.5 }}>
      ⏳ {t('auth.reconnecting')}
      <div>
        <button type="button" data-testid="auth-retry" onClick={retryConnection}
          style={{ marginTop: '8px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #f0b429', background: 'transparent',
            color: 'inherit', fontWeight: 600, cursor: 'pointer' }}>
          🔄 {t('auth.retryNow')}
        </button>
      </div>
    </div>
  )}
  
  {!currentUser ? (
  <>
    {/* 🎁 Trial-Hinweis (nur wenn nicht schon oben in der Erststart-Willkommenskarte angezeigt) */}
    {!(setupAktiv && paketeGeladen && pakete.length === 0) && (
    <div style={{
      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
      border: '1px solid #3b82f6',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px'
    }}>
      <div style={{ fontSize: '20px', marginBottom: '8px', color: '#1e40af' }}>
        🎁 {t('premium.trialTitle')}
      </div>
      <p style={{ margin: '0 0 12px 0', color: '#1e40af', fontSize: '14px' }}>
        {t('premium.trialDescription')}
      </p>
      <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px', color: '#1e40af', fontSize: '13px' }}>
        <li>☁️ {t('premium.cloudBenefitShort')}</li>
        <li>📱 {t('premium.multiBenefitShort')}</li>
        <li>💾 {t('premium.backupBenefitShort')}</li>
      </ul>
    </div>
    )}
    
    {/* 🖨️ Drucker AUCH ohne Login einrichten — man kann lokal ohne Konto arbeiten,
        dann braucht man den Etikettendruck genauso. Kopplung liegt gerätelokal (localStorage). */}
    {isPrinterSupported() && (
      <div style={{ marginBottom: '16px' }}>
        <button
          type="button"
          onClick={() => setShowPrinterSettings(true)}
          style={{
            padding: '12px 20px',
            background: colors.darkMode ? '#2d333b' : '#f5f5f5',
            color: colors.darkMode ? '#fff' : '#1a202c',
            border: `1px solid ${colors.darkMode ? '#444' : '#ddd'}`,
            borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            width: '100%', cursor: 'pointer', fontSize: '15px', fontWeight: 600,
          }}
        >
          {t('printer.setup')}
        </button>
        <p style={{ fontSize: '12px', color: colors.darkMode ? '#999' : '#666', textAlign: 'center', marginTop: '6px' }}>
          {t('printer.noAccountHint')}
        </p>
      </div>
    )}

    <p style={{ marginBottom: '12px', color: colors.darkMode ? '#ccc' : '#555' }}>
      {texts[lang]?.loginDescription || 'Melde dich an, um deine Pakete zu synchronisieren:'}
    </p>
    <button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={loading}
      style={{ 
        padding: '12px 20px', 
        background: '#fff', 
        color: '#333', 
        border: '1px solid #ddd', 
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        fontSize: '16px',
        fontWeight: '500'
      }}
    >
      {loading ? (
        t('common.isLoading')
      ) : (
        <>
          <svg width="20" height="20" viewBox="0 0 24 24" style={{marginRight: '10px'}}>
            <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" />
          </svg>
          {t('auth.loginWithGoogle')}
        </>
      )}
    </button>
    
    <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '12px' }}>
      ✅ {t('premium.freeNote')}
    </p>

    {/* 🖨️ Drucker-Dialog auch im ausgeloggten Zweig rendern — der Modal-Render im
        eingeloggten Zweig greift hier nicht. Nur ein Zweig ist gemountet, kein Doppel. */}
    {showPrinterSettings && (setupAktiv && !druckerKlassisch
      ? <PrinterWizard colors={colors} onClose={() => setShowPrinterSettings(false)} onClassic={() => setDruckerKlassisch(true)} />
      : <PrinterSettings colors={colors} onClose={() => { setShowPrinterSettings(false); setDruckerKlassisch(false); }} />
    )}
  </>
) : (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
        {t('auth.loggedInAs')}: {currentUser.displayName || currentUser.email}
      </p>
      
      {/* 🎁 Premium-Status Badge (Trial/Expired) — im Setup-Modus steht TrialBanner bereits ganz oben */}
      {!setupAktiv && <PremiumStatusBadge t={t} colors={colors} setShowPaywall={setShowPaywall} />}
      
      {lastCloudBackup && (
        <p style={{ fontSize: '12px', color: colors.darkMode ? '#888' : '#666', marginBottom: '8px' }}>
          ☁️ {t('backup.lastCloudBackup')}: {new Date(lastCloudBackup).toLocaleString(lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-US' : lang)}
        </p>
      )}
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', width: '100%', flexWrap: 'wrap' }}>
        <button
          onClick={() => syncDataWithSubcollections()}
          style={{ 
            padding: '8px 16px', 
            background: '#4CAF50', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer',
            flex: '1',
            minWidth: '120px'
          }}
        >
          {t('sync.syncing').replace('...', '')}
        </button>
        
        {/* 🛠️ WARTUNGS-BUTTON */}
        <button
          onClick={() => setShowMaintenanceModal(true)}
          style={{ 
            padding: '8px 16px', 
            background: colors.darkMode ? '#5e4a8f' : '#9C27B0', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer',
            flex: '1',
            minWidth: '120px'
          }}
        >
          🛠️ {t('settings.maintenance')}
        </button>
        
        {/* ⭐ PREMIUM-BUTTON */}
        <button
          onClick={() => setShowPaywall(true)}
          style={{ 
            padding: '8px 16px', 
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', 
            color: '#1a1a1a', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer',
            flex: '1',
            minWidth: '120px',
            fontWeight: 'bold'
          }}
        >
          ⭐ Premium
        </button>
        
        <button
          onClick={handleLogout}
          style={{ 
            padding: '8px 16px', 
            background: '#f44336', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer',
            flex: '1',
            minWidth: '120px'
          }}
        >
          {t('common.logout')}
        </button>
      </div>
      
      {/* 👷 Mitarbeiter anlegen (Name + PIN) — für „wer hat was gemacht"; ohne Mitarbeiter ändert sich nichts */}
      <button type="button" data-testid="staffs-open" onClick={() => setShowMitarbeiter(true)}
        style={{ width: '100%', marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
          border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f5f5', color: colors.text,
          fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
        👥 {t('staffs.menuEntry')}
      </button>
      {/* 🏪 Netzwerk: mehrere Shops mit eigenen Konten, Inhaber sieht nur Zahlen */}
      <button type="button" data-testid="network-open" onClick={() => setShowNetzwerk(true)}
        style={{ width: '100%', marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
          border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f5f5', color: colors.text,
          fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
        🏪 {t('network.menuEntry')}
      </button>
      {/* 📒 Empfänger-Adressbuch (CSV-Import für Büro, Wohnheim, Stammkunden — bei Paketshop standardmäßig ausgeblendet, im Setup einschaltbar) */}
      {isAddressBookEnabled() && (
        <button type="button" data-testid="addressbook-open" onClick={() => setShowAddressBook(true)}
          style={{ width: '100%', marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
            border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f5f5', color: colors.text,
            fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
          📒 {t('ui.main.recipientListButton')}
        </button>
      )}
      {/* 👷 Mitarbeiter-Modus einschalten (nur Sonderfunktion) */}
      {hasCustomFeature('staffMode') && (
        <button type="button" data-testid="staff-enable" onClick={() => setStaffDialog('setup')}
          style={{ width: '100%', marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
            border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f5f5', color: colors.text,
            fontSize: '14px', fontWeight: 600 }}>
          👷 {t('staff.menuEntry')}
        </button>
      )}
      {/* 🌐 SPRACHAUSWAHL & DUNKELMODUS */}
      <div style={{ 
        marginTop: '16px', 
        padding: '12px',
        background: colors.darkMode ? '#2d333b' : '#f5f5f5',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {/* Sprachauswahl — mit Setup steckt sie dort im ersten Schritt */}
        {setupAktiv ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold' }}>🧭 {t('setup.button')}:</span>
            <button
              data-testid="setup-open"
              onClick={() => { setSetupStartStep(0); setShowSetup(true); setSetupGesehen(true); try { localStorage.setItem('kiosk_setup_seen', '1'); } catch { /* */ } }}
              style={{
                background: colors.darkMode ? '#4a5568' : '#e2e8f0',
                color: colors.darkMode ? '#fff' : '#1a202c',
                border: 'none', padding: '8px 16px', borderRadius: '20px',
                cursor: 'pointer', fontWeight: 'bold', textAlign: 'left',
              }}
            >
              {t('setup.buttonHint')}
            </button>
            {!setupGesehen && (
              <span data-testid="setup-new" style={{ background: '#e53935', color: '#fff', fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>{t('setup.newBadge')}</span>
            )}
          </div>
        ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'bold' }}>🌐 {t('settings.language')}:</span>
          <LanguageSelector variant="hybrid" />
        </div>
        )}
        
        {/* Dunkelmodus Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontWeight: 'bold' }}>{darkMode ? '🌙' : '☀️'} {t('settings.darkMode')}:</span>
          <button
            onClick={() => setDarkMode && setDarkMode(d => !d)}
            style={{
              background: darkMode ? '#4a5568' : '#e2e8f0',
              color: darkMode ? '#fff' : '#1a202c',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {lang === 'de' ? (darkMode ? '☀️ Hell' : '🌙 Dunkel') : (darkMode ? `☀️ ${t('settings.lightMode')}` : `🌙 ${t('settings.darkMode')}`)}
          </button>
        </div>

        {/* 📱 Meine Geräte — nur anzeigen, wenn mind. 2 Geräte im Konto aktiv sind (z. B. 2 Handys, Handy + Drucker oder Handy + Scanner) */}
        {activeDeviceCount >= 2 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold' }}>📱 {lang === 'nl' ? 'Mijn apparaten' : lang === 'en' ? 'My Devices' : 'Meine Geräte'}:</span>
            <button
              type="button"
              data-testid="devices-open"
              onClick={() => setShowDevicesModal(true)}
              style={{
                background: colors.darkMode ? '#4a5568' : '#e2e8f0',
                color: colors.darkMode ? '#fff' : '#1a202c',
                border: 'none', padding: '8px 16px', borderRadius: '20px',
                cursor: 'pointer', fontWeight: 'bold',
              }}
            >
              {lang === 'nl' ? `Live-overzicht (${activeDeviceCount} actief)` : lang === 'en' ? `Live Overview (${activeDeviceCount} active)` : `Live-Übersicht (${activeDeviceCount} aktiv)`}
            </button>
          </div>
        )}
      </div>

      {/* 🧪 Experimentierfunktionen — nur für freigeschaltete Konten, bewusst ganz unten: je Anbindung ein Eintrag */}
      {(vintedFreigeschaltet() || dhlFreigeschaltet() || grenzpaketFreigeschaltet() || turboPpVerfuegbar()) && (
        <div data-testid="experimente" style={{ marginTop: '14px', padding: '10px 12px', borderRadius: '8px', border: `1px dashed ${colors.border}` }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.text, opacity: 0.8, marginBottom: '6px' }}>🧪 {t('setup.experiments') || 'Experimentierfunktionen'}</div>
          {turboPpVerfuegbar() && (
            <label data-testid="turbo-pp-schalter" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: `1px solid ${turboPpAn ? '#2e7d32' : colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text }}>
              <input type="checkbox" checked={turboPpAn} onChange={(e) => {
                const an = e.target.checked;
                if (an && !isAutoRegalLetter()) { window.alert(tpt(lang)('nurNachname')); return; }
                if (an && !window.confirm(tpt(lang)('warnung'))) return;
                setTurboPpOn(an); setTurboPpAn(an);
              }} style={{ width: 20, height: 20, marginTop: 2 }} />
              <span>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>{tpt(lang)('schalter')}</span>
                <span style={{ display: 'block', fontSize: '12px', opacity: 0.75, marginTop: 2, lineHeight: 1.4 }}>{tpt(lang)('hinweis')}</span>
              </span>
            </label>
          )}
          {turboPpVerfuegbar() && turboPpAn && (
            <div data-testid="turbo-pp-optionen" style={{ marginTop: '6px', marginLeft: '14px', padding: '8px 12px', borderRadius: '8px',
              borderLeft: '3px solid #2e7d32', background: colors.darkMode ? '#252a31' : '#eef2f5', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                {
                  id: 'turbo-opt-hud', checked: turboHudAn, label: tpt(lang)('opt_hud'), hint: tpt(lang)('opt_hud_kurz'),
                  onChange: (an) => { setTurboHudOn(an); setTurboHudAn(an); if (!an) setTurboAutoFotoAn(false); },
                },
                {
                  id: 'turbo-opt-auto', checked: turboAutoFotoAn, label: tpt(lang)('opt_auto'), hint: tpt(lang)('opt_auto_kurz'),
                  onChange: (an) => { setTurboAutoFotoOn(an); setTurboAutoFotoAn(an); if (an) setTurboHudAn(true); },
                },
                {
                  id: 'turbo-opt-regal', checked: turboRegalAn, label: tpt(lang)('opt_regal'), hint: tpt(lang)('opt_regal_kurz'),
                  onChange: (an) => { setTurboRegalOn(an); setTurboRegalAn(an); },
                },
                {
                  id: 'turbo-opt-ton', checked: turboTonAn, label: tpt(lang)('opt_ton'), hint: tpt(lang)('opt_ton_kurz'),
                  onChange: (an) => { setTurboTonOn(an); setTurboTonAn(an); },
                },
              ].map((o) => (
                <label key={o.id} data-testid={o.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', color: colors.text }}>
                  <input type="checkbox" checked={o.checked} onChange={(e) => o.onChange(e.target.checked)} style={{ width: 17, height: 17, marginTop: 2 }} />
                  <span>
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>{o.label}</span>
                    <span style={{ display: 'block', fontSize: '11.5px', opacity: 0.72, lineHeight: 1.35 }}>{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          {turboPpVerfuegbar() && (
            <button type="button" data-testid="turbo-demo-open" onClick={() => setShowTurboDemo(true)}
              style={{ width: '100%', marginTop: '6px', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', border: `1px dashed ${colors.border}`,
                background: 'transparent', color: colors.text, fontSize: '13px', textAlign: 'left' }}>
              👁 {tpt(lang)('demo')}
            </button>
          )}
          {(vintedFreigeschaltet() || dhlFreigeschaltet()) && (
            <label data-testid="turbo-schalter" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: `1px solid ${turboAn ? '#2e7d32' : colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text }}>
              <input type="checkbox" checked={turboAn} onChange={(e) => { setTurboOn(e.target.checked); setTurboAn(e.target.checked); }} style={{ width: 20, height: 20, marginTop: 2 }} />
              <span>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>{turboT(lang)('schalter')}</span>
                <span style={{ display: 'block', fontSize: '12px', opacity: 0.75, marginTop: 2, lineHeight: 1.4 }}>{turboT(lang)('hinweis')}</span>
              </span>
            </label>
          )}
          {(vintedFreigeschaltet() || dhlFreigeschaltet()) && (
            <label data-testid="station-autofach-schalter" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
              border: `1px solid ${stationAutoFach ? '#2e7d32' : colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text }}>
              <input type="checkbox" checked={stationAutoFach} onChange={(e) => { setStationAutoFachOn(e.target.checked); setStationAutoFach(e.target.checked); }} style={{ width: 20, height: 20, marginTop: 2 }} />
              <span>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>📸 {t('ui.main.stationAutoShelf')}</span>
                <span style={{ display: 'block', fontSize: '12px', opacity: 0.75, marginTop: 2, lineHeight: 1.4 }}>{t('ui.main.stationAutoShelfHint')}</span>
              </span>
            </label>
          )}
          {vintedFreigeschaltet() && (
            <button type="button" data-testid="vinted-open" onClick={() => setShowVintedPanel(true)}
              style={{ width: '100%', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text,
                fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
              {vt(lang)('titel')} →
            </button>
          )}
          {dhlFreigeschaltet() && (
            <button type="button" data-testid="dhl-bridge-open" onClick={() => setShowDhlBridge(true)}
              style={{ width: '100%', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text,
                fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
              {dt(lang)('titel')} →
            </button>
          )}
          {dpdFreigeschaltet() && (
            <button type="button" data-testid="dpd-open" onClick={() => setShowDpdPanel(true)}
              style={{ width: '100%', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text,
                fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
              📦 {dpdT(lang).titel} →
            </button>
          )}
          {grenzpaketFreigeschaltet() && (
            <button type="button" data-testid="grenzpaket-open" onClick={() => setShowGrenzpaketPanel(true)}
              style={{ width: '100%', marginTop: '6px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                border: `1px solid ${colors.border}`, background: colors.darkMode ? '#2d333b' : '#f5f6f8', color: colors.text,
                fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
              {gt(lang)('menu')} →
            </button>
          )}
        </div>
      )}

      {/* 🖨️ ETIKETTENDRUCKER-EINSTELLUNGEN */}
      {showPrinterSettings && (setupAktiv && !druckerKlassisch
        ? <PrinterWizard colors={colors} onClose={() => setShowPrinterSettings(false)} onClassic={() => setDruckerKlassisch(true)} />
        : <PrinterSettings colors={colors} onClose={() => { setShowPrinterSettings(false); setDruckerKlassisch(false); }} />
      )}

      {/* ✨ Einmaliger Hinweis nach dem Update: es bleibt alles, wie es ist — mit Option auf mehr */}
      <WhatsNewHint
        aktiv={setupAktiv && !!currentUser && !staffMode && pakete.length > 0 && !showSetup && !showNewPackageModal}
        colors={colors}
        onOpenSetup={() => { setShowSetup(true); setSetupGesehen(true); try { localStorage.setItem('kiosk_setup_seen', '1'); } catch { /* */ } }}
        onOpenHelp={() => setShowHelpCenter(true)}
      />

      {/* 🧭 SETUP — Schritt für Schritt, speichert das Konto-Profil für alle Geräte */}
      {showSetup && (
        <SetupWizard
          initialStep={setupStartStep}
          colors={colors}
          darkMode={darkMode}
          onDarkMode={setDarkMode}
          printerOpen={showPrinterSettings}
          onOpenPrinter={() => setShowPrinterSettings(true)}
          pakete={pakete}
          onClose={(gespeichert) => { setShowSetup(false); setSetupStartStep(0); if (gespeichert) alert(`✅ ${t('setup.saved')}`); }}
        />
      )}


      {/* 🛠️ WARTUNGS-MODAL */}
      {showMaintenanceModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }} 
          onClick={() => setShowMaintenanceModal(false)}
        >
          <div 
            style={{
              width: '100%',
              maxWidth: '500px',
              backgroundColor: colors.darkMode ? '#2d333b' : '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: `2px solid ${colors.darkMode ? '#444' : '#eee'}`,
              paddingBottom: '12px'
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '20px' }}>🛠️ {t('ui.main.dbMaintenance')}</h2>
              <button
                onClick={() => setShowMaintenanceModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: colors.text,
                  lineHeight: '1',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              
              {/* 🎓 SCHULUNGSMODUS & FERNHILFE */}
              <div style={{
                padding: '14px 16px',
                borderRadius: '10px',
                background: trainingModeActive
                  ? (colors.darkMode ? 'rgba(16, 185, 129, 0.16)' : '#ecfdf5')
                  : (colors.darkMode ? '#1e242c' : '#f8fafc'),
                border: `2px solid ${trainingModeActive ? '#10b981' : (colors.darkMode ? '#374151' : '#cbd5e1')}`,
                marginBottom: '4px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ fontWeight: 800, fontSize: '15px', color: colors.text }}>
                    🎓 Schulungsmodus & Fernhilfe
                  </div>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 800,
                    padding: '3px 9px',
                    borderRadius: '999px',
                    background: trainingModeActive ? '#10b981' : (colors.darkMode ? '#374151' : '#e2e8f0'),
                    color: trainingModeActive ? '#ffffff' : (colors.darkMode ? '#d1d5db' : '#475569')
                  }}>
                    {trainingModeActive ? '🟢 Aktiv' : 'Bereit'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: colors.textSecondary || (colors.darkMode ? '#9ca3af' : '#64748b'), marginBottom: '10px', lineHeight: 1.45 }}>
                  Aktiviert die Live-Begleitung für Schulungen oder das Einrichten neuer Geräte (Drucker, Ringscanner, Zebra) aus der Ferne.
                </div>
                <button
                  onClick={() => {
                    const next = !trainingModeActive;
                    setTrainingModeRequested(next);
                    setTrainingModeActive(next);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: '14px',
                    background: trainingModeActive ? '#ef4444' : '#0284c7',
                    color: '#ffffff',
                    boxShadow: trainingModeActive ? '0 3px 10px rgba(239,68,68,0.3)' : '0 3px 10px rgba(2,132,199,0.3)'
                  }}
                >
                  {trainingModeActive ? '⏹️ Schulungsmodus beenden' : '🎓 Schulungsmodus starten'}
                </button>
              </div>

              {/* 🚨 NOTFALL-RESTORE BUTTON */}
              <button
                onClick={() => {
                  setShowMaintenanceModal(false);
                  notfallRestore();
                }}
                style={{
                  padding: '14px 16px',
                  background: '#ff0000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(255,0,0,0.4)'
                }}
              >
                🚨 {t('ui.main.emergencyRestoreButton', { count: pakete.length })}
              </button>
              
              {/* Cloud-Backup wiederherstellen */}
              <button
                onClick={async () => {
                  setLoading(true);
                  try {
                    const cloudBackups = await listCloudBackups();
                    if (cloudBackups.length > 0) {
                      const backupList = cloudBackups.map((b, i) => 
                        `${i + 1}. ${new Date(b.modified).toLocaleString('de-DE')} — ${b.name} (${Math.round(b.size / 1024)} KB)`
                      ).join('\n');
                      
                      const choice = window.prompt(
                        `☁️ ${t('ui.main.serverBackupsFound', { count: cloudBackups.length })}\n\n${backupList}\n\n${t('ui.main.enterBackupNumber')}`,
                        '0'
                      );
                      
                      const index = parseInt(choice) - 1;
                      if (index >= 0 && index < cloudBackups.length) {
                        const success = await restoreCloudBackup(cloudBackups[index].name);
                        if (success) {
                          alert(`✅ ${t('ui.main.backupRestored')}`);
                        }
                      }
                    } else {
                      alert(`📦 ${t('backup.noCloudBackups')}`);
                    }
                  } catch (err) {
                    alert('❌ ' + t('ui.main.error') + ': ' + err.message);
                  }
                  setLoading(false);
                }}
                disabled={loading}
                style={{
                  padding: '14px 16px',
                  background: colors.darkMode ? '#1a4d6d' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  opacity: loading ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {loading ? '⏳ ' + t('ui.main.loading') : '☁️ ' + t('ui.main.restoreCloudBackup')}
              </button>
              
              {/* Jetzt Backup erstellen */}
              <button
                onClick={async () => {
                  setLoading(true);
                  await createCloudBackup(pakete, 'manual');
                  alert(`✅ ${t('backup.createdWithHint', { count: pakete.length })}`);
                  setLoading(false);
                }}
                disabled={loading}
                style={{
                  padding: '14px 16px',
                  background: colors.darkMode ? '#1a5a66' : '#00BCD4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  opacity: loading ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {loading ? '⏳ ' + t('ui.main.loading') : '💾 ' + t('ui.main.createBackupNow')}
              </button>
              
              {/* Schließen-Button */}
              <button
                onClick={() => setShowMaintenanceModal(false)}
                style={{
                  padding: '14px 16px',
                  background: colors.darkMode ? '#444' : '#e0e0e0',
                  color: colors.darkMode ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginTop: '8px',
                  transition: 'all 0.2s'
                }}              >
                {t('ui.main.close')}
              </button>

              {/* 🥽 VR TOOLS */}
              <div style={{
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: `2px solid ${colors.darkMode ? '#444' : '#ddd'}`
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: colors.text, fontSize: '16px' }}>🥽 VR Tools</h3>
                
                <Link
                  to="/vr/qr-generator"
                  onClick={() => setShowMaintenanceModal(false)}
                  style={{
                    display: 'block',
                    background: '#4CAF50',
                    color: '#fff',
                    border: 'none',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  📋 Regal QR-Codes generieren & drucken
                </Link>
                
                <Link
                  to="/vr-packages"
                  onClick={() => setShowMaintenanceModal(false)}
                  style={{
                    display: 'block',
                    background: '#2196F3',
                    color: '#fff',
                    border: 'none',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  🥽 VR Paket-Scanner starten
                </Link>
              </div>
              
              {/* 🧪 TEST-BEREICH */}
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `2px solid ${colors.darkMode ? '#444' : '#ddd'}`
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: colors.text, fontSize: '16px' }}>🧪 Test-Bereich</h3>
                
                <button
                  onClick={async () => {
                    let info = '📱 Device Info:\n\n';
                    info += 'Platform: ' + navigator.platform + '\n';
                    info += 'Screen: ' + window.screen.width + 'x' + window.screen.height + '\n';
                    info += 'Language: ' + navigator.language + '\n\n';
                    
                    if ('getBattery' in navigator) {
                      try {
                        const battery = await navigator.getBattery();
                        info += '🔋 Akku: ' + Math.round(battery.level * 100) + '%' + 
                               (battery.charging ? ' (lädt)' : '') + '\n';
                      } catch (e) {
                        info += '🔋 Akku: Nicht verfügbar\n';
                      }
                    }
                    
                    if (performance.memory) {
                      const usedMB = Math.round(performance.memory.usedJSHeapSize / 1048576);
                      const limitMB = Math.round(performance.memory.jsHeapSizeLimit / 1048576);
                      info += '💾 RAM: ' + usedMB + 'MB / ' + limitMB + 'MB\n';
                    }
                    
                    alert(info);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: '#34a853',
                    color: '#fff',
                    border: 'none',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  📱 Device Info anzeigen
                </button>
                
                <button
                  onClick={async () => {
                    try {
                      // 🔒 Prüfe ob Kamera-API verfügbar ist (Android Capacitor Kompatibilität)
                      if (!navigator.mediaDevices || typeof navigator.mediaDevices.enumerateDevices !== 'function') {
                        throw new Error('mediaDevices API nicht verfügbar auf diesem Gerät');
                      }
                      
                      const devices = await navigator.mediaDevices.enumerateDevices();
                      const videoDevices = devices.filter(d => d.kind === 'videoinput');
                      
                      let info = '📷 Verfügbare Kameras:\n\n';
                      videoDevices.forEach((device, i) => {
                        info += (i + 1) + '. ' + (device.label || 'Kamera ' + (i + 1)) + '\n';
                        info += '   ID: ' + device.deviceId.substring(0, 20) + '...\n\n';
                      });
                      
                      if (videoDevices.length === 0) {
                        info = '❌ Keine Kameras gefunden!\n\nBitte Kamera-Berechtigung erteilen.';
                      }
                      
                      alert(info);
                    } catch (error) {
                      alert('❌ Fehler beim Abrufen der Kameras:\n\n' + error.message);
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: '#ea4335',
                    color: '#fff',
                    border: 'none',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  📷 Verfügbare Kameras anzeigen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      
      <div style={{ 
        fontSize: '12px', 
        color: colors.darkMode ? '#aaa' : '#666',
        textAlign: 'center'
      }}>
        {t('sync.lastSync')}: {lastUpdated ? new Date(lastUpdated).toLocaleString(lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-US' : lang) : t('sync.lastSyncNever')}
        {syncDifferences && 
          (syncDifferences.onlyInLocal.length > 0 || 
           syncDifferences.onlyInRemote.length > 0) && 
          ` • ${t('sync.unsyncedChanges', { count: syncDifferences.onlyInLocal.length + syncDifferences.onlyInRemote.length })}`}
      </div>
    </div>
    
  )}
</div>
      )}

      {/* 🖨️ MEHRFACH-ETIKETTENDRUCK (mit Bestätigung je Etikett) — AUSSERHALB des Einstellungsbereichs,
          damit er auch im Mitarbeiter-Modus erscheint */}
      {printDialog && (
        <PrintLabelDialog
          paket={printDialog.paket}
          copies={printDialog.copies}
          colors={colors}
          onClose={() => setPrintDialog(null)}
        />
      )}

      {showBatch && <BatchView t={t} colors={colors} pakete={pakete} onClose={() => setShowBatch(false)} />}

      {showAnmeldung && <MitarbeiterAnmeldung t={t} colors={colors} onClose={() => setShowAnmeldung(false)} />}
      {showMitarbeiter && <MitarbeiterVerwalten t={t} colors={colors} onClose={() => setShowMitarbeiter(false)} />}
      {showNetzwerk && <NetzwerkDialog t={t} colors={colors} onClose={() => setShowNetzwerk(false)} />}
      {showAddressBook && <AddressBookModal isOpen={showAddressBook} onClose={() => setShowAddressBook(false)} colors={colors} lang={lang} />}
      {staffDialog && (
        <StaffModeDialog mode={staffDialog} t={t} colors={colors}
          onClose={() => { setStaffDialog(null); setStaffModeState(isStaffMode()); }}
          onForgot={async () => { setStaffDialog(null); disableStaffMode(); try { await handleLogout(); } catch { /* */ } }} />
      )}
      
      {/* Platz-Overlay */}
      {overlayData.visible && (
        <div style={{
          position: 'fixed',
          top: overlayData.position.y,
          left: '50%',
          transform: 'translateX(-50%)',
          background: colors.darkMode ? '#2d333c' : '#fff',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          maxWidth: '300px',
          width: '90%'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <strong>{t('packages.shelfLocation')}: {overlayData.platz}</strong>
            <button 
              onClick={() => {
                setOverlayLocked(false);
                setOverlayData({ visible: false, names: [], position: { x: 0, y: 0 } });
              }}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0',
                lineHeight: 1
              }}
            >
              &times;
            </button>
          </div>
          <ul style={{ margin: 0, paddingLeft: '16px' }}>
            {overlayData.names.map((name, i) => (
              <li key={i}>{name}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* ⚠️ Verwechslungs-Hinweis nach Ausgabe/Rücksendung — muss quittiert werden.
          Die Aktion ist bereits ausgeführt; der Hinweis dient der Kontrolle, solange
          der Kunde noch da ist. */}
      {verwechslung && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div style={{
            background: colors.box, color: colors.text, borderRadius: '14px', padding: '20px',
            maxWidth: '420px', width: '100%', border: '2px solid #f5a623',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>⚠️ {t('packages.mixupTitle')}</div>
            <div style={{ fontSize: '15px', lineHeight: 1.5 }}>
              <b>{verwechslung.eigen || t('packages.package')}</b>{' '}
              {verwechslung.aktion === 'zurückgeschickt' ? t('packages.returned') : t('packages.issued')}.
              <div style={{ marginTop: '10px' }}>
                {t('packages.mixupBody', {
                  shelf: verwechslung.regalplatz,
                  houseNr: verwechslung.hausnummer,
                  name: verwechslung.name,
                })}
              </div>
              <div style={{ marginTop: '10px', fontWeight: 600 }}>
                {t('packages.mixupCheck')}
              </div>
            </div>
            <button
              onClick={() => setVerwechslung(null)}
              style={{
                marginTop: '18px', width: '100%', padding: '14px', fontSize: '16px', fontWeight: 700,
                background: '#f5a623', color: '#1a1a1a', border: 'none', borderRadius: '10px', cursor: 'pointer',
              }}
            >
              {t('packages.mixupOk')}
            </button>
          </div>
        </div>
      )}

      {/* Modal für neue Paketerfassung oder Bearbeitung */}
      {showVintedImport && (
        <VintedImport
          pakete={pakete}
          t={t}
          colors={colors}
          onClose={() => { setShowVintedImport(false); window.dispatchEvent(new CustomEvent('kiosk:vinted-names')); }}
          onApply={vintedNamenAnwenden}
        />
      )}
      {/* 🧪 Vinted-Anbindung */}
      {showVintedPanel && (
        <VintedPanel colors={colors} language={lang} pakete={pakete} onNamen={vintedNamenAnwenden} onAbgeholt={vintedAbgeholtUebernehmen} onAnlegen={vintedPaketAnlegen}
          onToggle={() => { setVintedAn(vintedAnzeigeAktiv()); setVintedAnnahmeAn(annahmeAktiv()); }} onClose={() => setShowVintedPanel(false)} />
      )}
      {showVintedErfassen && (
        <VintedErfassen pakete={pakete} colors={colors} language={lang} onAnlegen={vintedPaketAnlegen} onNamen={vintedNamenAnwenden}
          onClose={() => setShowVintedErfassen(false)} />
      )}
      {/* 🧪 DHL-Anbindung (Experiment) */}
      {showDhlBridge && (
        <DhlBridgePanel colors={colors} language={lang} onToggle={() => { setDhlBridgeAn(dhlAktiv()); setDhlAnnahmeAn(dhlAnnahmeAktiv()); }} onClose={() => setShowDhlBridge(false)} />
      )}
      {/* 📦 DPD Pickup-Anbindung (Konto 166 / erweiterbar) */}
      {showDpdPanel && (
        <DpdPanel
          colors={colors}
          language={lang}
          pakete={pakete}
          onNamen={vintedNamenAnwenden}
          onAbgeholt={vintedAbgeholtUebernehmen}
          onAnlegen={vintedPaketAnlegen}
          onToggle={() => setFeatureTick((n) => n + 1)}
          onClose={() => setShowDpdPanel(false)}
        />
      )}
      {/* 🇨🇭 Grenzpaket-Anbindung (Konto 1186) */}
      {showGrenzpaketPanel && (
        <GrenzpaketPanel
          colors={colors}
          language={lang}
          pakete={pakete}
          onImport={grenzpaketImportAnlegen}
          onToggle={() => setFeatureTick((n) => n + 1)}
          onClose={() => setShowGrenzpaketPanel(false)}
        />
      )}
      {/* 📱 Meine Geräte — Live-Übersicht pro Konto (Handys, verbundene Drucker & Scanner) */}
      {showDevicesModal && (
        <DevicesModal
          setupMode={false}
          colors={colors}
          language={lang}
          onOpenPrinter={() => { setShowDevicesModal(false); setShowPrinterSettings(true); }}
          onOpenSetup={() => {
            setShowDevicesModal(false);
            setSetupStartStep(3);
            setShowSetup(true);
            setSetupGesehen(true);
            try { localStorage.setItem('kiosk_setup_seen', '1'); } catch { /* */ }
          }}
          onClose={() => setShowDevicesModal(false)}
        />
      )}
      {/* 👁 Turbo-Vorschau: erfundene Pakete, kein Server, kein Paket-Speichern, kein Druck */}
      {showTurboDemo && (
        <TurboPaketPilot demo language={lang} t={t} holePakete={() => []} onAnlegen={async () => null} onBearbeiten={() => {}}
          onClose={() => {
            setShowTurboDemo(false);
            ladeTurboOptionen();
            try { const u = new URL(window.location.href); if (u.searchParams.has('turbodemo')) { u.searchParams.delete('turbodemo'); window.history.replaceState(null, '', u.toString()); } } catch { /* */ }
          }} />
      )}
      {/* ⚡ Turbo PaketPilot */}
      {showTurboPp && (
        <TurboPaketPilot language={lang} t={t}
          holePakete={() => (paketeRef.current.length ? paketeRef.current : pakete)}
          onAnlegen={(f) => vintedPaketAnlegen({
            tracking: f.trackingNumber, name: f.name, regalplatz: f.regalplatz, dienst: f.paketdienst || '', imageUrl: f.imageUrl, drucken: false,
            extra: { streetAndNumber: f.streetAndNumber || '', postalAndCity: f.postalAndCity || '', paketFarbe: f.paketFarbe || '', gewicht: f.gewicht || '', ...(f.absender ? { absender: f.absender } : {}) },
          })}
          onBearbeiten={(p) => {
            setShowTurboPp(false);
            ladeTurboOptionen();
            const liste = paketeRef.current.length ? paketeRef.current : pakete;
            const aktuell = liste.find((x) => x && x.id === p.id) || p;
            handleSelect(aktuell, liste.indexOf(aktuell), true);
          }}
          onClose={() => { setShowTurboPp(false); ladeTurboOptionen(); }} />
      )}
      {showDhlErfassen && (
        <DhlErfassen pakete={pakete} colors={colors} language={lang} onAnlegen={vintedPaketAnlegen} onNamen={vintedNamenAnwenden}
          onDrucken={triggerAutoPrintLabel} onClose={() => setShowDhlErfassen(false)} />
      )}
      {vintedAusgabePaket && (
        <VintedAusgabe paket={vintedAusgabePaket} pakete={pakete} colors={colors} language={lang}
          onPaketAusgeben={(p) => handleAusgeben(p, true, true)} onClose={() => setVintedAusgabePaket(null)} />
      )}
      {dhlAusgabePaket && (
        <DhlAusgabe paket={dhlAusgabePaket} pakete={pakete} colors={colors} language={lang}
          onPaketAusgeben={(p) => handleAusgeben(p, true, true)} onClose={() => setDhlAusgabePaket(null)} />
      )}
      {signatureRequest && (
        <SignatureModal
          isOpen={!!signatureRequest}
          paket={signatureRequest.paket}
          weiterePakete={getWeitereOffenePaketeFuerAusgabe(signatureRequest.paket, null, true)}
          initialMode={signatureRequest.initialMode || 'empfaenger'}
          colors={colors}
          lang={lang}
          onConfirm={async (sigData) => {
            const req = signatureRequest;
            setSignatureRequest(null);
            if (!req?.paket) return;
            const ausgabeVorgangId = 'ausg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
            const sigPatch = {
              ...(sigData?.abholerName ? { abgeholtVonName: sigData.abholerName } : {}),
              ...(sigData?.signatureDataUrl ? { signatureDataUrl: sigData.signatureDataUrl, signatureHatBild: true } : {}),
              abholArt: sigData?.abholArt === 'bevollmaechtigter' ? 'bevollmaechtigter' : 'empfaenger',
              ...(sigData?.bevollmaechtigter ? { bevollmaechtigter: sigData.bevollmaechtigter } : {}),
              ...(sigData?.vollmachtFoto ? { vollmachtFoto: sigData.vollmachtFoto, vollmachtHatBild: true } : {}),
              ausgabeVorgangId,
              abholUnterschriftAm: sigData?.timestamp || new Date().toISOString(),
            };
            const pWithSig = { ...req.paket, ...sigPatch };
            await handleAusgeben(pWithSig, true, true, true);

            // 📦 Weitere im SignatureModal angehakte Pakete mit derselben Unterschrift + Vollmacht ausgeben!
            const selectedSet = new Set(Array.isArray(sigData?.selectedPaketIds) ? sigData.selectedPaketIds : [req.paket.id]);
            selectedSet.delete(req.paket.id);
            const weitere = getWeitereOffenePaketeFuerAusgabe(req.paket, null, true).filter((w) => selectedSet.has(w.id));
            const ausgegebeneWeitere = [];
            for (const w of weitere) {
              const wWithSig = { ...w, ...sigPatch };
              ausgegebeneWeitere.push(wWithSig);
              setIssuedBatchPackages((prev) => (prev.some((x) => x.id === wWithSig.id) ? prev : [...prev, wWithSig]));
              await executeAusgabe(wWithSig, null, true);
            }

            // 🔌 DHL USB-Stick: Nur DHL-Sendungen in einem Batch mit der 1 Unterschrift an den Scanner senden
            if (isDhlStickEnabled()) {
              const alleAusgegebenen = [pWithSig, ...ausgegebeneWeitere];
              const barcodes = alleAusgegebenen
                .filter((p) => istDhlPaket(p))
                .map((p) => String(p.trackingNumber || p.trackingNummer || '').trim())
                .filter((code) => code.length >= 6);
              if (barcodes.length > 0) {
                enqueueDhlStickAusgabe({
                  barcodes,
                  mode: sigData?.abholArt === 'bevollmaechtigter' ? 'bevollmaechtigter' : 'empfaenger',
                  firstName: sigData?.bevollmaechtigter?.vorname || '',
                  lastName: sigData?.bevollmaechtigter?.nachname || '',
                  fullName: req.paket.name || '',
                  strokes: Array.isArray(sigData?.strokes) ? sigData.strokes : [],
                  submitFinal: false,
                }).then((res) => {
                  if (!window.showToast) return;
                  if (res?.queuedForReview || res?.pendingReview) {
                    window.showToast('🛡️ Ausgabe in Stick-Warteschlange (Prüf-Modus) gelegt', 'info', 3500);
                  } else if (res?.ok) {
                    window.showToast(`🔌 DHL-Stick: ${barcodes.length} Sendung(en) + Unterschrift an Scanner übertragen`, 'success', 3500);
                  } else {
                    window.showToast(`⚠️ DHL-Stick nicht erreichbar — Auftrag unter „🔌 DHL-Stick" zum Wiederholen gespeichert`, 'warning', 4500);
                  }
                }).catch(() => {});
              }
            }
          }}
          onSkip={() => {
            const req = signatureRequest;
            setSignatureRequest(null);
            if (req?.paket) handleAusgeben(req.paket, true, true, true);
          }}
          onClose={() => setSignatureRequest(null)}
        />
      )}
      {showNewPackageModal && (
        <NewPackageModal 
          key="new-package-modal" // 🛡️ FIX: Stabiler Key verhindert Re-Mount bei Parent-Render
          forceNewPackage={selectedIndex === null} // 🆕 Nur wenn kein Paket ausgewählt
          onNeedPremium={() => setShowPaywall(true)} // ⭐ KI-Tageslimit erreicht → Paywall
          // ⚠️ Verwechslungs-Schutz: prüft gegen den aktuellen Bestand (bleibt hier beim Parent)
          checkRegalConflict={(vals) => findeHausnummerKonflikt(pakete, vals)}
          findSameRecipient={(vals) => findeGleichenEmpfaenger(pakete, vals)}
          findSameTracking={(vals) => findeGleicheSendung(pakete, vals)}
          onClose={async () => {
            // 📝 DRAFT SYSTEM - Die Logik ist jetzt in NewPackageModal.js
            setShowNewPackageModal(false);
            resetForm(); // ✅ FIX: Formular komplett zurücksetzen (inkl. selectedIndex)
            
            // 🛡️ FIX: Lade Pakete aus IndexedDB nach, falls während Modal-Öffnung synchronisiert wurde
            try {
              const cachedPakete = await get('pakete');
              if (cachedPakete && cachedPakete.length > 0) {
                console.log(`📦 Modal geschlossen - lade ${cachedPakete.length} Pakete aus IndexedDB nach`);
                setPakete(cachedPakete);
                setServerPakete(cachedPakete);
              }
            } catch (e) {
              console.warn('⚠️ Fehler beim Nachladen der Pakete:', e);
            }
          }}
          onDraftChange={(hasDraftValue, draftDataValue) => {
            console.log('📝 Draft-Änderung erhalten:', hasDraftValue);
            setHasDraft(hasDraftValue);
            if (draftDataValue) {
              setDraftData(draftDataValue);
            }
          }}
          onSave={async (paket, keepModalOpen) => {
  // 🛡️ FIX: Session-ID beim Start speichern, um Race Condition zu erkennen
  const saveSessionId = getCurrentModalSession();
  console.log(`💾 [SAVE] Speichervorgang startet (Session #${saveSessionId})`);
  
  // Aktuelles Datum für Updated-At
  const now = new Date();
  
  // 🛡️ Fremd-Überschreibungs-Schutz: zeigt selectedIndex (evtl. veraltet) auf ein Paket mit
  // komplett anderer Identität, NICHT überschreiben, sondern als neues Paket anlegen.
  let editId = selectedIndex;
  if (editId && String(editId).startsWith('demo-paket-')) {
    setDemoPakete(prev => prev.map(p => p.id === editId ? { ...p, ...paket, id: editId, isDemo: true, updatedAt: now.toISOString() } : p));
    if (!keepModalOpen) {
      setShowNewPackageModal(false);
      resetForm();
    }
    return;
  }
  if (editId) {
    const zielPaket = pakete.find(p => p.id === editId);
    if (zielPaket && isForeignOverwrite(zielPaket, paket)) {
      console.warn(`🛡️ Fremd-Überschreibung verhindert: „${zielPaket.name}" ≠ „${paket.name}" — lege NEU an statt zu überschreiben.`);
      editId = null;
    }
  }

  // WICHTIG: Verwende editId als ID wenn vorhanden (nach Schutz-Prüfung).
  // 🛡️ Datenverlust-Schutz: Bei Bearbeitung das BESTEHENDE Paket als Basis mergen, sonst
  // gehen Felder, die das Modal nicht ausgibt (absender, Foto-Verknüpfung, Server-Felder),
  // beim reinen Regalplatz-Edit still verloren. Modal-Werte gewinnen (auch bewusste Leerungen).
  const basisPaket = editId ? (pakete.find(p => p.id === editId) || {}) : {};
  const finalPaket = {
    ...basisPaket,
    ...paket,
    ...(editId && istOnlinePaket(basisPaket) ? { inCloud: true } : {}),
    id: editId || (window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
    updatedAt: now.toISOString(),
    lastUpdated: now.getTime(),
    uploaded: false // Immer erst als nicht-synchronisiert speichern; erst nach erfolgreichem Server-Upload auf true setzen
  };
  // 📷 Das Modal setzt imageUrl='' wenn kein NEUES Foto — dann alte Verknüpfung behalten.
  if (editId) {
    if (!paket.imageUrl && basisPaket.imageUrl) finalPaket.imageUrl = basisPaket.imageUrl;
    if (!paket.image_url && basisPaket.image_url) finalPaket.image_url = basisPaket.image_url;
    if (!paket.imageId && basisPaket.imageId) finalPaket.imageId = basisPaket.imageId;
    // 🚚 Paketdienst kommt nur aus einer Foto-Analyse — beim reinen Bearbeiten den bekannten behalten
    if (!paket.paketdienst && basisPaket.paketdienst) finalPaket.paketdienst = basisPaket.paketdienst;
  }

  // Prüfe ob es ein Update oder ein neues Paket ist
  if (editId) {
    // BEARBEITUNG: Ersetze das vorhandene Paket
    console.log('✏️ Bearbeite bestehendes Paket mit ID:', editId);
    const basis = paketeRef.current.length ? paketeRef.current : pakete;
    const neuePakete = basis.map(p =>
      p.id === editId ? finalPaket : p
    );
    setPakete(neuePakete);
    await set('pakete', neuePakete); // ✅ Speichere in IndexedDB

    // 🎨 Sofortiges Farb-Update: Wurde die Ausgabe/Rücksendung hier zurückgenommen,
    // den „per Button ausgegeben"-Marker entfernen — sonst bleibt die Kachel grün
    // (der Marker überschreibt in der Farb-Logik das leere ausgegebenAm) bis zum Refresh.
    if (!finalPaket.ausgegebenAm && !finalPaket.zurueckgeschickt) {
      setButtonIssuedPaketIds(prev => {
        if (!prev.has(editId)) return prev;
        const next = new Set(prev); next.delete(editId); return next;
      });
    }
    
    // 📋 CHRONIK: Paket geändert (mit Änderungsdetails)
    await logPaketGeaendert(finalPaket.id, finalPaket.name, finalPaket.absender, finalPaket.trackingNumber);
    
    // 🔄 Server Sync - NUR das geänderte Paket!
    const editUser = getAuthUser();
    if (editUser && isOnline) {
      try {
        const ok = await savePaket(finalPaket); // ✅ Nur 1 Paket updaten!
        if (ok) {
          const aktuell = paketeRef.current.length ? paketeRef.current : neuePakete;
          const syncedPakete = aktuell.map(p => 
            p.id === finalPaket.id ? { ...p, uploaded: true, inCloud: true } : p
          );
          setPakete(syncedPakete);
          setServerPakete(syncedPakete);
          await set('pakete', syncedPakete);
          await updatePackagesHash(syncedPakete); // Hash aktualisieren
          
          console.log('📦 Paket auf Server aktualisiert:', finalPaket.id);
        } else {
          console.warn('📴 Server-Update fehlgeschlagen (uploaded bleibt false für Reconnect-Flush):', finalPaket.id);
        }
      } catch (error) {
        console.error('📦 Server-Update fehlgeschlagen:', error);
      }
    }
  } else {
    // NEUES PAKET: Erstelle neue ID
    console.log('Erstelle neues Paket');
    const neuesPaket = {
      ...finalPaket,
      id: window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)
    };
    const basis = paketeRef.current.length ? paketeRef.current : pakete;
    const neuePakete = [...basis, neuesPaket];
    setPakete(neuePakete);
    set('pakete', neuePakete);

    // 🖨️ Etikett automatisch drucken (nur wenn Drucker konfiguriert + Auto-Druck an)
    triggerAutoPrintLabel(neuesPaket);

    // 📦 Counter bei erfolgreichem Speichern dekrementieren
    decrementCounter(neuesPaket);

    // 🚀 Einzel-Upload + Chronik + Wartelisten-Matching im Hintergrund ausführen,
    // damit das Modal sofort (ohne 3s Freeze nach dem Serienmodus-Countdown) schließt!
    (async () => {
      try {
        if (getAuthUser() && navigator.onLine) {
          const ok = await savePaket(neuesPaket);
          if (ok) {
            const aktuell = paketeRef.current.length ? paketeRef.current : neuePakete;
            const synced = aktuell.map(p => p.id === neuesPaket.id ? { ...p, uploaded: true, inCloud: true } : p);
            setPakete(synced);
            setServerPakete(prev => [...prev.filter(p => p.id !== neuesPaket.id), { ...neuesPaket, uploaded: true, inCloud: true }]);
            await set('pakete', synced);
            updatePackagesHash(synced).catch(() => {});
          }
        }
      } catch (err) {
        console.warn('📦 Einzel-Upload neues Paket fehlgeschlagen:', err?.message);
      }
      try {
        const matches = await recordPackageCreated(neuesPaket);
        if (matches && matches.length > 0 && isMountedRef.current) {
          console.log(`📬 [Matching] ${matches.length} passende Kunden gefunden!`);
          const listMode = getListNotifyMode(); // 'staff' | 'ask' | 'auto'
          const waitingAutoNotify = (() => { try { return localStorage.getItem('kiosk_auto_notify') === '1'; } catch { return false; } })();

          const modalCandidates = [];

          for (const target of matches) {
            const isListMatch = !!target.isAddressBook;
            const hasEmail = Boolean(target.kontakt && target.kontakt.includes('@'));
            const isStrongMatch = (target.nameSimilarity || 0) >= 80 || (target.matchScore || 0) >= 75;

            if (isListMatch) {
              // 📒 Treffer auf der Empfänger- / Stammkunden-Liste (CSV)
              if (listMode === 'staff' || !hasEmail) {
                // (a) Nur Mitarbeiter am Bildschirm informieren (oder Kontakt hat keine E-Mail hinterlegt)
                if (window.showToast) {
                  const infoParts = [`⭐ ${t('ui.main.onList')}: ${target.name}`];
                  if (target.note) infoParts.push(`📌 ${target.note}`);
                  if (target.shelf) infoParts.push(`🗄️ ${t('ui.main.shelfLabel', { shelf: target.shelf })}`);
                  window.showToast(infoParts.join(' · '), 'info', 6000);
                }
              } else if (listMode === 'auto' && navigator.onLine && isStrongMatch) {
                // (c) Vollautomatisch E-Mail senden + Mitarbeiter informieren
                try {
                  const sent = await handleSendNotification(target, neuesPaket);
                  if (sent) {
                    await markAsNotified(target.id, neuesPaket.id);
                    if (window.showToast) {
                      const noteSuffix = target.note ? ` · 📌 ${target.note}` : '';
                      window.showToast(`📧 ${t('ui.main.emailAutoSent', { name: target.name, contact: target.kontakt })}${noteSuffix}`, 'success', 5000);
                    }
                  } else {
                    modalCandidates.push(target);
                  }
                } catch {
                  modalCandidates.push(target);
                }
              } else {
                // (b) 'ask' (oder unsicherer Name bei 'auto') → Dialog für Mitarbeiter öffnen
                modalCandidates.push(target);
              }
            } else {
              // 🔔 Treffer auf der einmaligen Warteliste (QR-Code / Benachrichtigungs-Button)
              if (waitingAutoNotify && navigator.onLine && hasEmail && isStrongMatch) {
                try {
                  const sent = await handleSendNotification(target, neuesPaket);
                  if (sent) {
                    await markAsNotified(target.id, neuesPaket.id);
                    if (window.showToast) {
                      window.showToast(`📧 ${t('ui.main.emailAutoSent', { name: target.name, contact: target.kontakt })}`, 'success', 4000);
                    }
                  } else {
                    modalCandidates.push(target);
                  }
                } catch {
                  modalCandidates.push(target);
                }
              } else {
                modalCandidates.push(target);
              }
            }
          }

          if (modalCandidates.length > 0 && isMountedRef.current) {
            setMatchingPaket(neuesPaket);
            setMatchingResults(modalCandidates);
            setShowMatchingModal(true);
          }
        }
      } catch (err) {
        console.warn('📋 Chronik/Matching Fehler:', err?.message);
      }
    })();
  }
  
  // ✅ FIX: resetForm() NUR wenn Modal geschlossen wird!
  // Sonst ändert sich forceNewPackage während der Benutzer noch Fotos macht
  if (!keepModalOpen) {
    // 🛡️ FIX: Race Condition Check - Schließe NUR wenn noch dieselbe Session!
    const currentSession = getCurrentModalSession();
    if (currentSession !== saveSessionId) {
      console.warn(`⚠️ [RACE CONDITION] Session geändert! Speicherung war #${saveSessionId}, aktuell #${currentSession}`);
      console.warn('⚠️ [RACE CONDITION] Modal wird NICHT geschlossen, da bereits ein neues geöffnet wurde!');
      // NUR resetForm aufrufen, aber Modal NICHT schließen!
      resetForm();
    } else {
      console.log(`✅ [SAVE] Session #${saveSessionId} abgeschlossen - Modal wird geschlossen`);
      resetForm();
      setShowNewPackageModal(false);
    }
  }
}}
          colors={colors}
          lang={lang}
          texts={texts}
          belegteNummern={belegteNummern}
          favoriten={favoriten}
          setFavoriten={setFavoriten}
          shelfSuggestions={shelfSuggestions}
          pakete={pakete}
          // Übergebe die Werte des aktuell ausgewählten Pakets
          initialName={name}
          initialStreetAndNumber={streetAndNumber}
          initialPostalAndCity={postalAndCity}
          initialRegalplatz={regalplatz}
          initialTrackingNumber={trackingNumber}
          initialSchaden={selectedIndex ? ((pakete.find(p => p.id === selectedIndex) || {}).schaden || null) : null}
          initialDatum={datum}
          initialAusgegebenAm={ausgegebenAm}
          initialZurueckgeschickt={zurueckgeschickt}
          initialPaketAnzahl={selectedIndex ? (pakete.find(p => p.id === selectedIndex)?.paketAnzahl || 1) : 1}
          // 💰🎨⚖️ EXTRAS (wie bei Quest VR)
          initialBetrag={selectedIndex ? (pakete.find(p => p.id === selectedIndex)?.betrag || '') : ''}
          initialFarbe={selectedIndex ? (pakete.find(p => p.id === selectedIndex)?.paketFarbe || '') : ''}
          initialGewicht={selectedIndex ? (pakete.find(p => p.id === selectedIndex)?.gewicht || '') : ''}
          initialInternational={selectedIndex ? (pakete.find(p => p.id === selectedIndex)?.international || false) : false}
          paketId={selectedIndex} // Übergebe die ID des ausgewählten Pakets (FIX: paketId statt selectedPaketId)
          onDelete={handleDelete} // 🗑️ Löschen-Funktion übergeben
        />
      )}

      {/* 📬 WARTELISTEN-MATCHING MODAL */}
      {showMatchingModal && matchingPaket && matchingResults.length > 0 && (
        <MatchingModal
          matches={matchingResults}
          paket={matchingPaket}
          onClose={() => {
            setShowMatchingModal(false);
            setMatchingResults([]);
            setMatchingPaket(null);
          }}
          onNotify={handleSendNotification}
          colors={colors}
        />
      )}

      {/* 🔔 BENACHRICHTIGUNG HINZUFÜGEN & QR-CODE MODAL */}
      <NotificationSignupModal
        isOpen={showNotifySignupModal}
        onClose={() => setShowNotifySignupModal(false)}
        colors={colors}
        onCountChange={setWaitingNotifyCount}
      />

      {/* 📦 MEHRFACH-PAKET DIALOG - Alle oder nur 1 ausgeben? */}
      {showMultiPackageDialog && multiPackagePaket && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 2000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            background: colors.darkMode ? '#2d333b' : '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
            <h2 style={{ 
              margin: '0 0 8px 0', 
              color: colors.text 
            }}>
              {t('packages.packagesForPerson', { count: multiPackagePaket.paketAnzahl })}
            </h2>
            <p style={{ 
              margin: '0 0 24px 0', 
              color: colors.darkMode ? '#aaa' : '#666',
              fontSize: '14px'
            }}>
              {multiPackagePaket.name}
            </p>
            
            {/* Countdown-Anzeige */}
            <div style={{
              background: colors.darkMode ? '#1e3a5f' : '#e0f2fe',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ 
                margin: 0, 
                color: colors.darkMode ? '#93c5fd' : '#1e40af',
                fontSize: '14px'
              }}>
                ⏱️ {t('packages.issueAllIn', { seconds: multiPackageCountdown.toFixed(1) })}
              </p>
            </div>
            
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <button
                onClick={() => {
                  if (multiPackageTimerRef.current) clearInterval(multiPackageTimerRef.current);
                  setShowMultiPackageDialog(false);
                  
                  // 🎁 Issue-Overlay starten NACH Dialog-Schließung
                  const randomAnim = animations[Math.floor(Math.random() * animations.length)];
                  setSelectedAnimation(randomAnim);
                  setIssuedPackage(multiPackagePaket);
                  setShowIssueOverlay(true);
                  setIssueCountdown(2.5);
                  setIssueAnimationPhase('enter');
                  setTimeout(() => setIssueAnimationPhase('display'), 700);
                  
                  executeAusgabe(multiPackagePaket, multiPackagePaket.paketAnzahl);
                }}
                style={{
                  padding: '14px 20px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                ✅ {t('packages.issueAllPackages', { count: multiPackagePaket.paketAnzahl })}
              </button>
              
              <button
                onClick={() => {
                  if (multiPackageTimerRef.current) clearInterval(multiPackageTimerRef.current);
                  setShowMultiPackageDialog(false);
                  
                  // 🎁 Issue-Overlay starten NACH Dialog-Schließung
                  const randomAnim = animations[Math.floor(Math.random() * animations.length)];
                  setSelectedAnimation(randomAnim);
                  setIssuedPackage(multiPackagePaket);
                  setShowIssueOverlay(true);
                  setIssueCountdown(2.5);
                  setIssueAnimationPhase('enter');
                  setTimeout(() => setIssueAnimationPhase('display'), 700);
                  
                  executeAusgabe(multiPackagePaket, 1);
                }}
                style={{
                  padding: '14px 20px',
                  background: colors.darkMode ? '#4a5568' : '#e2e8f0',
                  color: colors.text,
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                📦 {t('packages.issueOnePackage')}
              </button>
              
              <button
                onClick={() => {
                  if (multiPackageTimerRef.current) clearInterval(multiPackageTimerRef.current);
                  setShowMultiPackageDialog(false);
                  setMultiPackagePaket(null);
                }}
                style={{
                  padding: '10px',
                  background: 'transparent',
                  color: colors.darkMode ? '#888' : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Synchronisierungs-Konflikt-Modal */}
{syncModalVisible && conflictItems.length > 0 && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <div style={{
      width: '90%',
      maxWidth: '600px',
      backgroundColor: colors.darkMode ? '#2d333b' : '#ffffff',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      maxHeight: '90vh',
      overflow: 'auto'
    }}>
      <h2 style={{ marginTop: 0 }}>{t('ui.main.syncConflict')}</h2>
      <p>
        {t('ui.main.syncConflictText')}
        {' '}({conflictIndex + 1}/{conflictItems.length})
      </p>

      <div style={{ 
        display: 'flex', 
        flexDirection: window.innerWidth > 600 ? 'row' : 'column',
        gap: '15px' 
      }}>
        {/* Lokale Version */}
        <div style={{
          flex: 1,
          padding: '15px',
          border: `2px solid ${colors.darkMode ? '#4a6da7' : '#2196f3'}`,
          borderRadius: '8px'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: colors.darkMode ? '#4a6da7' : '#2196f3' }}>
            {t('ui.main.localVersion')}
          </h3>
          <div>
            <p><strong>{t('ui.main.labelName')}:</strong> {conflictItems[conflictIndex]?.localItem?.name}</p>
            <p><strong>{t('ui.main.labelStreet')}:</strong> {conflictItems[conflictIndex]?.localItem?.streetAndNumber}</p>
            <p><strong>{t('ui.main.labelPostalCity')}:</strong> {conflictItems[conflictIndex]?.localItem?.postalAndCity}</p>
            <p><strong>{t('ui.main.labelShelf')}:</strong> {conflictItems[conflictIndex]?.localItem?.regalplatz}</p>
            <p><strong>{t('ui.main.labelLastChanged')}:</strong> {
              conflictItems[conflictIndex]?.localItem?.updatedAt 
              ? new Date(conflictItems[conflictIndex].localItem.updatedAt).toLocaleString()
              : t('ui.main.unknown')
            }</p>
          </div>
          <button
            onClick={() => handleKeepLocal(conflictItems[conflictIndex])}
            style={{
              marginTop: '10px',
              width: '100%',
              padding: '10px',
              background: colors.darkMode ? '#4a6da7' : '#2196f3',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {t('ui.main.keepLocal')}
          </button>
        </div>

        {/* Remote Version */}
        <div style={{
          flex: 1,
          padding: '15px',
          border: `2px solid ${colors.darkMode ? '#43a047' : '#4CAF50'}`,
          borderRadius: '8px'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: colors.darkMode ? '#43a047' : '#4CAF50' }}>
            {t('ui.main.onlineVersion')}
          </h3>
          <div>
            <p><strong>{t('ui.main.labelName')}:</strong> {conflictItems[conflictIndex]?.remoteItem?.name}</p>
            <p><strong>{t('ui.main.labelStreet')}:</strong> {conflictItems[conflictIndex]?.remoteItem?.streetAndNumber}</p>
            <p><strong>{t('ui.main.labelPostalCity')}:</strong> {conflictItems[conflictIndex]?.remoteItem?.postalAndCity}</p>
            <p><strong>{t('ui.main.labelShelf')}:</strong> {conflictItems[conflictIndex]?.remoteItem?.regalplatz}</p>
            <p><strong>{t('ui.main.labelLastChanged')}:</strong> {
              conflictItems[conflictIndex]?.remoteItem?.updatedAt 
              ? new Date(conflictItems[conflictIndex].remoteItem.updatedAt).toLocaleString()
              : t('ui.main.unknown')
            }</p>
          </div>
          <button
            onClick={() => handleKeepRemote(conflictItems[conflictIndex])}
            style={{
              marginTop: '10px',
              width: '100%',
              padding: '10px',
              background: colors.darkMode ? '#43a047' : '#4CAF50',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {t('ui.main.keepOnline')}
          </button>
        </div>
      </div>

      {/* Änderungen hervorheben */}
      <div style={{ marginTop: '20px' }}>
        <h3>{t('ui.main.differences')}</h3>
        <div style={{ 
          maxHeight: '200px', 
          overflowY: 'auto',
          padding: '10px',
          background: colors.darkMode ? '#1e2127' : '#f5f5f5',
          borderRadius: '4px'
        }}>
          {Object.keys(conflictItems[conflictIndex]?.localItem || {})
            .filter(key => 
              conflictItems[conflictIndex]?.localItem[key] !== conflictItems[conflictIndex]?.remoteItem[key] &&
              key !== 'updatedAt'
            )
            .map(key => (
              <div key={key} style={{ marginBottom: '5px' }}>
                <strong>{key}:</strong>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ color: colors.darkMode ? '#4a6da7' : '#2196f3' }}>
                    {t('ui.main.labelLocal')}: {String(conflictItems[conflictIndex]?.localItem[key])}
                  </span>
                  <span style={{ color: colors.darkMode ? '#43a047' : '#4CAF50' }}>
                    {t('ui.main.labelOnline')}: {String(conflictItems[conflictIndex]?.remoteItem[key])}
                  </span>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  </div>
)}
{undoInfo && (
  <div style={{
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: colors.darkMode ? '#2d333b' : '#323232',
    color: 'white',
    padding: '10px 16px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1000,
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
    maxWidth: '90%'
  }}>
    <span>{t('vrMenu.packageDeleted')}</span>
    <button 
      onClick={handleUndo}
      style={{
        marginLeft: '10px',
        background: '#4CAF50',
        color: 'white',
        border: 'none',
        padding: '5px 10px',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      {t('actions.undo')}
    </button>
  </div>
)}
      {/* 🎁 AUSGABE-OVERLAY mit Verpackungs-Animation */}
      {showIssueOverlay && issuedPackage && (
        <div 
          className={`issue-overlay issue-overlay-${issueAnimationPhase}`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: issueAnimationPhase === 'enter' 
              ? 'slideInFromRight 0.5s cubic-bezier(0.4, 0, 0.2, 1)' 
              : 'none'
          }}
        >
          <div 
            className={`overlay-content ${issueAnimationPhase}`}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '90%',
              width: '400px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              animation: issueAnimationPhase === 'pack' && selectedAnimation
                ? selectedAnimation.pack
                : issueAnimationPhase === 'drop' && selectedAnimation
                ? selectedAnimation.drop
                : 'none'
            }}
          >
            {/* Großes Check-Icon */}
            <div style={{
              fontSize: '80px',
              marginBottom: '20px',
              animation: 'bounce 0.6s ease-out'
            }}>
              ✅
            </div>
            
            {/* Titel */}
            <h2 style={{
              color: '#333',
              fontSize: '28px',
              marginBottom: '20px',
              fontWeight: 'bold'
            }}>
              {t('packages.packageIssued')}
            </h2>
            
            {/* Paket-Details */}
            <div style={{
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: '2px solid rgba(102, 126, 234, 0.3)'
            }}>
              {issuedPackage.name && (
                <div style={{
                  fontSize: '20px',
                  marginBottom: '12px',
                  color: '#333',
                  fontWeight: '600'
                }}>
                  📦 {issuedPackage.name}
                </div>
              )}
              {issuedPackage.streetAndNumber && (
                <div style={{
                  fontSize: '16px',
                  marginBottom: '8px',
                  color: '#555'
                }}>
                  📍 {issuedPackage.streetAndNumber}
                </div>
              )}
              {issuedPackage.regalplatz && (
                <div style={{
                  fontSize: '18px',
                  color: '#667eea',
                  fontWeight: '600'
                }}>
                  🗄️ {t('packages.shelfLocation')}: {issuedPackage.regalplatz}
                </div>
              )}
            </div>
            
            {/* Countdown & Undo Button */}
            {issueCountdown > 0 && issueAnimationPhase === 'display' && (
              <div style={{ marginTop: '20px' }}>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 'bold',
                  color: '#667eea',
                  marginBottom: '16px',
                  animation: 'pulse 1s infinite'
                }}>
                  {issueCountdown}
                </div>
                <button
                  onClick={handleUndoIssue}
                  style={{
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '16px 32px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 6px 16px rgba(244, 67, 54, 0.6)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(244, 67, 54, 0.4)';
                  }}
                >
                  ↩️ {t('packages.undoAction')}
                </button>
              </div>
            )}
            
            {/* Verpackungs-Phase Text mit Animation-Label */}
            {issueAnimationPhase === 'pack' && selectedAnimation && (
              <div style={{
                fontSize: '24px',
                color: '#667eea',
                fontWeight: 'bold',
                marginTop: '20px'
              }}>
                {selectedAnimation.label.split(' ')[0]} {t('ui.main.anim' + selectedAnimation.name.charAt(0).toUpperCase() + selectedAnimation.name.slice(1))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* 🛟 BACKUP-WIEDERHERSTELLUNGS-MODAL */}
      {showBackupModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.box,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: `2px solid ${colors.darkMode ? '#444' : '#ddd'}`,
              paddingBottom: '12px'
            }}>
              <h2 style={{ margin: 0, color: colors.text }}>
                🛟 {t('ui.main.restoreBackupTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowBackupModal(false);
                  setSelectedBackup(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: colors.text
                }}
              >
                ×
              </button>
            </div>
            
            <p style={{ color: colors.text, marginBottom: '20px' }}>
              📦 {t('ui.main.found')}: <strong>{t('ui.main.backupsCount', { count: availableBackups.length })}</strong>
            </p>
            
            {availableBackups.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: colors.text
              }}>
                <p>{t('ui.main.noBackupsFound')}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {availableBackups.map((backup, index) => (
                  <div
                    key={backup.key}
                    style={{
                      background: colors.darkMode ? '#2d333b' : '#f5f5f5',
                      border: selectedBackup?.key === backup.key 
                        ? '2px solid #FF9800' 
                        : `1px solid ${colors.darkMode ? '#444' : '#ddd'}`,
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => setSelectedBackup(backup)}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: colors.text,
                          marginBottom: '8px'
                        }}>
                          📅 {backup.date.toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: colors.darkMode ? '#aaa' : '#666',
                          marginBottom: '4px'
                        }}>
                          📦 <strong>{t('ui.main.packagesCount', { count: backup.count })}</strong>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: colors.darkMode ? '#888' : '#999'
                        }}>
                          📝 {t('ui.main.reason')}: {backup.reason === 'before_server_sync' ? t('ui.main.reasonBeforeServerSync') :
                                     backup.reason === 'before_cleanup' ? t('ui.main.reasonBeforeCleanup') :
                                     backup.reason === 'after_cleanup' ? t('ui.main.reasonAfterCleanup') :
                                     backup.reason === 'before_server_update' ? t('ui.main.reasonBeforeServerUpdate') :
                                     backup.reason === 'before_restore' ? t('ui.main.reasonBeforeRestore') :
                                     backup.reason}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            restoreBackup(backup);
                          }}
                          style={{
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          ✅ {t('ui.main.restore')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBackup(backup.key);
                          }}
                          style={{
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    {selectedBackup?.key === backup.key && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: colors.darkMode ? '#1a1f27' : '#fff',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: colors.text
                      }}>
                        <strong>⚠️ {t('ui.main.note')}:</strong> {t('ui.main.restoreReplaceHint', { current: pakete.length, count: backup.count })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: colors.darkMode ? '#2d333b' : '#e3f2fd',
              borderRadius: '8px',
              fontSize: '14px',
              color: colors.text
            }}>
              <strong>💡 {t('ui.main.tip')}:</strong> {t('ui.main.autoBackupTip')}
            </div>
          </div>
        </div>
      )}
      
      {/* 🔔 Globale Toast-Benachrichtigung */}
      {globalToast.show && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '15px 30px',
          borderRadius: '8px',
          backgroundColor: globalToast.type === 'success' ? '#4caf50' : 
                          globalToast.type === 'error' ? '#f44336' : 
                          globalToast.type === 'warning' ? '#ff9800' : '#2196f3',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          animation: 'slideUp 0.3s ease-out',
          maxWidth: '90%',
          textAlign: 'center'
        }}>
          {globalToast.message}
        </div>
      )}

      {/* 🎯 Device-Type CSS Class für conditional styling */}
      <style>{`
        body {
          --device-type: ${deviceType};
        }
        body.device-type-quest {
          /* Quest-spezifische CSS-Variablen */
          --button-size: 60px;
          --font-size-base: 18px;
          --touch-target-min: 48px;
        }
        body.device-type-android {
          --button-size: 48px;
          --font-size-base: 16px;
          --touch-target-min: 44px;
        }
        body.device-type-desktop {
          --button-size: 40px;
          --font-size-base: 14px;
          --touch-target-min: 32px;
        }
      `}</style>
    </div>
    </PremiumProvider>
  );
}


export default AddPackageWithAI;

// Funktion zur Berechnung der Werktage zwischen zwei Daten
function calculateBusinessDays(startDate, endDate, holidays = []) {
  let count = 0;
  // 📅 Nur Tage zählen, Uhrzeit ignorieren (sonst fehlen Pakete je nach Tageszeit)
  let currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);
  
  let end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (currentDate <= end) {
    const dayOfWeek = currentDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sonntag (0) oder Samstag (6)
    const isHoliday = holidays.some(holiday => {
      const holidayDate = new Date(holiday);
      return (
        holidayDate.getFullYear() === currentDate.getFullYear() &&
        holidayDate.getMonth() === currentDate.getMonth() &&
        holidayDate.getDate() === currentDate.getDate()
      );
    });

    if (!isWeekend && !isHoliday) {
      count++;
    }

    currentDate.setDate(currentDate.getDate() + 1); // Zum nächsten Tag wechseln
  }

  return count;
}
function normalizeRegalplatz(regalplatz) {
  // Leerzeichen entfernen + evtl. gescanntes "RG:"-Präfix abschneiden.
  // Regal-QR-Codes codieren "RG:K28" → gespeichert wird nur "K28".
  return regalplatz ? regalplatz.replace(/\s+/g, '').replace(/^RG:?/i, '') : regalplatz;
}

// Feiertage in Deutschland (2025–2029)
const holidays = [
  // 2025
  '2025-01-01', // Neujahr
  '2025-04-18', // Karfreitag
  '2025-04-21', // Ostermontag
  '2025-05-01', // Tag der Arbeit
  '2025-05-29', // Christi Himmelfahrt
  '2025-06-09', // Pfingstmontag
  '2025-10-03', // Tag der Deutschen Einheit
  '2025-12-25', // 1. Weihnachtstag
  '2025-12-26', // 2. Weihnachtstag

  // 2026
  '2026-01-01', // Neujahr
  '2026-04-03', // Karfreitag
  '2026-04-06', // Ostermontag
  '2026-05-01', // Tag der Arbeit
  '2026-05-14', // Christi Himmelfahrt
  '2026-05-25', // Pfingstmontag
  '2026-10-03', // Tag der Deutschen Einheit
  '2026-12-25', // 1. Weihnachtstag
  '2026-12-26', // 2. Weihnachtstag

  // 2027
  '2027-01-01', // Neujahr
  '2027-03-26', // Karfreitag
  '2027-03-29', // Ostermontag
  '2027-05-01', // Tag der Arbeit
  '2027-05-06', // Christi Himmelfahrt
  '2027-05-17', // Pfingstmontag
  '2027-10-03', // Tag der Deutschen Einheit
  '2027-12-25', // 1. Weihnachtstag
  '2027-12-26', // 2. Weihnachtstag

  // 2028 (fehlte komplett → Lagerfrist-/Werktagsberechnung driftete um diese Tage)
  '2028-01-01', // Neujahr
  '2028-04-14', // Karfreitag
  '2028-04-17', // Ostermontag
  '2028-05-01', // Tag der Arbeit
  '2028-05-25', // Christi Himmelfahrt
  '2028-06-05', // Pfingstmontag
  '2028-10-03', // Tag der Deutschen Einheit
  '2028-12-25', // 1. Weihnachtstag
  '2028-12-26', // 2. Weihnachtstag

  // 2029
  '2029-01-01', // Neujahr
  '2029-03-30', // Karfreitag
  '2029-04-02', // Ostermontag
  '2029-05-01', // Tag der Arbeit
  '2029-05-10', // Christi Himmelfahrt
  '2029-05-21', // Pfingstmontag
  '2029-10-03', // Tag der Deutschen Einheit
 
  '2029-12-25', // 1. Weihnachtstag
  '2029-12-26', // 2. Weihnachtstag
];

// NEU: Funktion zum Finden des ursprünglichen Index eines Pakets
function findOriginalIndex(paket, pakete) {
  const index = pakete.findIndex(p => p.id === paket.id);
 
  return index !== -1 ? index : null;
}

// 📥 Hilfsfunktionen für Absender-Text & Absender-Suche (Sicherheitsnetz bei KI-Dreher)
function normalizeSearchStr(str) {
  if (!str) return '';
  return String(str).toLowerCase().trim()
    .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
    .replace(/\//g, '7').replace(/l/g, '1').replace(/i/g, '1')
    .replace(/o/g, '0').replace(/s/g, '5').replace(/z/g, '2').replace(/b/g, '8');
}

function getPaketAbsenderText(paket) {
  if (!paket || !paket.absender) return '';
  if (typeof paket.absender === 'string') return paket.absender.trim();
  const ort = [paket.absender.plz, paket.absender.ort].filter(Boolean).join(' ').trim();
  return [paket.absender.name, paket.absender.strasse, ort].map(x => String(x || '').trim()).filter(Boolean).join(' · ');
}

function matchesPaketPrimary(paket, searchLower) {
  const q = String(searchLower || '').toLowerCase().trim();
  if (!q) return true;
  const searchWords = q.split(' ').filter(Boolean);
  const matchesStreetAndNumber = searchWords.length > 0 && searchWords.every(word =>
    paket.streetAndNumber?.toLowerCase().includes(word)
  );
  const searchNorm = normalizeSearchStr(q);
  const searchWordsNorm = searchNorm.split(' ').filter(Boolean);
  const matchesStreetAndNumberNorm = searchWordsNorm.length > 0 && searchWordsNorm.every(word =>
    normalizeSearchStr(paket.streetAndNumber).includes(word)
  );
  return Boolean(
    paket.name?.toLowerCase().includes(q) ||
    normalizeSearchStr(paket.name).includes(searchNorm) ||
    matchesStreetAndNumber ||
    matchesStreetAndNumberNorm ||
    paket.regalplatz?.toLowerCase().includes(q) ||
    trackingTreffer(paket, q)
  );
}

function matchesPaketSender(paket, searchLower) {
  const q = String(searchLower || '').toLowerCase().trim();
  if (!q || q.length < 2) return false;
  const abs = getPaketAbsenderText(paket);
  if (!abs) return false;
  const absLower = abs.toLowerCase();
  const searchWords = q.split(' ').filter(Boolean);
  if (searchWords.length > 0 && searchWords.every(word => absLower.includes(word))) {
    return true;
  }
  const absNorm = normalizeSearchStr(abs);
  const searchWordsNorm = normalizeSearchStr(q).split(' ').filter(Boolean);
  return searchWordsNorm.length > 0 && searchWordsNorm.every(word => absNorm.includes(word));
}

function isSenderOnlyMatch(paket, searchLower) {
  const q = String(searchLower || '').toLowerCase().trim();
  if (!q || q.length < 2) return false;
  return !matchesPaketPrimary(paket, q) && matchesPaketSender(paket, q);
}

// NEU: Funktion zum Filtern und Beibehalten des Index
function filterPaketeWithIndex(pakete, searchLower, regalFilter, selectedRegalplatz, showOld, dateFilterFrom, dateFilterTo, filterDays = 7, historicalDate = null, filterDaysInternational = 14) {
  return pakete
    .map((paket, idx) => ({ paket, originalIndex: idx })) // Ursprünglichen Index beibehalten
    .filter(({ paket }) => {
      // 🕐 HISTORISCHE ANSICHT: Filtere Pakete, die am historischen Datum im Regal waren
      if (historicalDate) {
        // Normalisiere historisches Datum auf Ende des Tages (23:59:59)
        const historicalDateObj = new Date(historicalDate + 'T00:00:00');
        historicalDateObj.setHours(23, 59, 59, 999);
        
        // Normalisiere alle Paket-Daten auf Mitternacht für faire Vergleiche
        const einlagerungDate = paket.datum ? new Date(paket.datum) : null;
        if (einlagerungDate) {
          einlagerungDate.setHours(0, 0, 0, 0);
        }
        
        const ausgabeDate = paket.ausgegebenAm ? new Date(paket.ausgegebenAm) : null;
        if (ausgabeDate) {
          ausgabeDate.setHours(0, 0, 0, 0);
        }
        
        const zurueckDate = paket.zurueckgeschicktAm ? new Date(paket.zurueckgeschicktAm) : null;
        if (zurueckDate) {
          zurueckDate.setHours(0, 0, 0, 0);
        }
        
        // Paket war am historischen Datum im Regal wenn:
        // 1. Es wurde VOR oder AM historischen Datum eingelagert
        const wasEingelagert = einlagerungDate && einlagerungDate <= historicalDateObj;
        
        // 2. UND es wurde noch NICHT ausgegeben ODER NACH dem historischen Datum ausgegeben
        const warNochNichtAusgegeben = !ausgabeDate || ausgabeDate > historicalDateObj;
        
        // 3. UND es wurde noch NICHT zurückgeschickt ODER NACH dem historischen Datum zurückgeschickt
        const warNochNichtZurueck = !zurueckDate || zurueckDate > historicalDateObj;
        
        // Wenn Paket am historischen Datum NICHT im Regal war, ausschließen
        if (!(wasEingelagert && warNochNichtAusgegeben && warNochNichtZurueck)) {
          return false;
        }
      }
      
      const matchesSearch = !searchLower ||
        matchesPaketPrimary(paket, searchLower) ||
        matchesPaketSender(paket, searchLower);

      const matchesRegalFilter = !regalFilter || paket.regalplatz?.startsWith(regalFilter);

      // Nur filtern, wenn ein Regalplatz ausgewählt ist
      const matchesSelectedRegalplatz = !selectedRegalplatz || paket.regalplatz === selectedRegalplatz;

      // 📅 DATUMSBEREICH-FILTER
      let matchesDateFilter = true;
      if (dateFilterFrom || dateFilterTo) {
        try {
          // Versuche verschiedene Datumsfelder
          let paketDatum = null;
          
          // Priorität: datum (Erfassungsdatum) -> updatedAt -> createdAt
          if (paket.datum) {
            paketDatum = new Date(paket.datum);
          } else if (paket.updatedAt) {
            paketDatum = new Date(paket.updatedAt);
          } else if (paket.createdAt) {
            paketDatum = new Date(paket.createdAt);
          }
          
          if (paketDatum && !isNaN(paketDatum)) {
            // Normalisiere auf Tagesbeginn (ohne Uhrzeit)
            paketDatum.setHours(0, 0, 0, 0);
            
            if (dateFilterFrom) {
              const fromDate = new Date(dateFilterFrom);
              fromDate.setHours(0, 0, 0, 0);
              if (paketDatum < fromDate) matchesDateFilter = false;
            }
            
            if (dateFilterTo) {
              const toDate = new Date(dateFilterTo);
              toDate.setHours(23, 59, 59, 999); // Ende des Tages
              if (paketDatum > toDate) matchesDateFilter = false;
            }
          } else {
            // Wenn kein gültiges Datum gefunden wurde, Filter nicht anwenden
            matchesDateFilter = true;
          }
        } catch (error) {
          console.warn('Fehler beim Datumsfilter:', error);
          matchesDateFilter = true; // Im Fehlerfall: Paket anzeigen
        }
      }

      // #15: Bei aktiver Textsuche den Lagerfrist-Filter (showOld) NICHT anwenden — sonst blendet
      // ein zuvor aktivierter Filter jüngere Treffer aus und das Paket scheint „nicht da".
      if (showOld && !searchLower) {
        const now = new Date();
        const paketDatum = new Date(paket.datum);
        const businessDays = calculateBusinessDays(paketDatum, now, holidays);
        // 🌍 Internationale Pakete: 14 Werktage, nationale: 7 Werktage
        const schwellwert = paket.international ? filterDaysInternational : filterDays;
        return matchesSearch && matchesRegalFilter && matchesSelectedRegalplatz && matchesDateFilter && businessDays > schwellwert;
      }

      // Zeige alle Pakete, wenn kein Regalplatz ausgewählt ist
      return matchesSearch && matchesRegalFilter && matchesSelectedRegalplatz && matchesDateFilter;
    });
}





















